import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../auth/AuthContext';
import { usePractice } from '../context/PracticeContext';
import useConfigSync from '../hooks/useConfigSync';
import axios from '../utils/axiosInstance';
import Modal from 'react-modal'; // Assuming react-modal or similar is used or can be added
import * as XLSX from 'xlsx';

// "X" in the priority matrix: never book and never move with this therapist for this therapy.
const PRIORITY_NEVER = -1;
const priorityClass = (v) => (v === PRIORITY_NEVER ? 'priority-x' : `priority-${v}`);

// Priority picker cell — portal-rendered dropdown that opens upward so it isn't clipped by the table
function PriorityCell({ prio, isOpen, toggleOpen, closeOpen, onSelect, onMouseEnter, onMouseLeave }) {
  const pillRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!isOpen || !pillRef.current) return;
    const update = () => {
      const r = pillRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ bottom: window.innerHeight - r.top + 4, left: r.left + r.width / 2 });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  return (
    <div
      className="priority-dropdown-wrapper"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div ref={pillRef} className={`priority-pill ${priorityClass(prio)}`} onClick={toggleOpen}>
        {prio === PRIORITY_NEVER ? 'X' : prio === 0 ? '' : prio}
      </div>
      {isOpen && pos && ReactDOM.createPortal(
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={closeOpen} />
          <div
            className="priority-dropdown-menu"
            style={{
              position: 'fixed',
              bottom: pos.bottom,
              left: pos.left,
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}
            onClick={e => e.stopPropagation()}
          >
            {[0, 1, 2, 3, PRIORITY_NEVER].map(val => (
              <div
                key={val}
                className={`priority-dropdown-option ${priorityClass(val)}`}
                onClick={() => onSelect(val)}
              >
                {val === PRIORITY_NEVER ? 'X' : val === 0 ? <span style={{ opacity: 0.4 }}>–</span> : val}
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// Frequency picker cell — portal-rendered dropdown that won't be clipped by table containers
function FrequencyCell({ therapyType, isAccepted, isOpen, toggleOpen, closeOpen, currentFrequency, freqClassName, freqOption, frequencyOptions, freqClassMap, suffix, handleSaveCellEdit }) {
  const badgeRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!isOpen || !badgeRef.current) return;
    const update = () => {
      const r = badgeRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 4, left: r.left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  return (
    <>
      <span
        ref={badgeRef}
        className={`freq-badge ${freqClassName}`}
        onClick={isAccepted ? toggleOpen : undefined}
        title={isAccepted ? "Frequenz ändern" : undefined}
      >
        {freqOption?.display ?? currentFrequency}
      </span>

      {isOpen && pos && ReactDOM.createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={closeOpen}
          />
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: 6,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minWidth: 130,
            }}
            onClick={e => e.stopPropagation()}
          >
            {frequencyOptions.map(option => {
              const isActive = currentFrequency === option.value;
              const optionClassName = (freqClassMap && freqClassMap[option.value]) || '';
              return (
                <span
                  key={option.value}
                  className={`freq-badge ${optionClassName}`}
                  onClick={e => {
                    e.stopPropagation();
                    if (!isActive) {
                      handleSaveCellEdit(therapyType, { [`frequency${suffix}`]: option.value });
                    }
                    closeOpen();
                  }}
                  style={{
                    cursor: 'pointer',
                    opacity: isActive ? 1 : 0.7,
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = isActive ? '1' : '0.7'; e.currentTarget.style.transform = isActive ? 'scale(1.02)' : 'scale(1)'; }}
                >
                  {option.display}
                </span>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// Tooltip rendered via portal so it cannot be clipped by modal/scroll containers
function InfoTooltip({ text }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const show = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: r.left });
  };
  const hide = () => setPos(null);

  return (
    <>
      <span
        ref={ref}
        className="th-info-icon"
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        ⓘ
      </span>
      {pos && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            maxWidth: 320,
            padding: '10px 12px',
            background: '#1f2937',
            color: '#fff',
            fontSize: '0.8rem',
            fontWeight: 400,
            lineHeight: 1.45,
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
            whiteSpace: 'normal',
            textAlign: 'left',
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

// Textarea that grows with its content (single-line by default).
// Uses useEffect on value so external loads (e.g., from API) also trigger resize.
function AutoGrowTextarea({ value, onChange, style, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea
      {...rest}
      ref={ref}
      value={value}
      rows={1}
      onChange={onChange}
      style={{ resize: 'none', overflow: 'hidden', minHeight: 0, fontFamily: 'inherit', ...style }}
    />
  );
}

// A comma list as chips: trimmed, empty parts dropped, order and repeats kept ("KG, KG" is two
// chips). A PVS-Bezeichnung is stored as ONE such string, "A, B, C", as the server normalises it.
const splitCommaList = (value) => (value || '').split(',').map(p => p.trim()).filter(Boolean);

// The browser's own <input type="email"> rule ('' passes: the field is optional). A
// detached input, so it also holds for a flush after the form has unmounted.
const isBrowserValidEmail = (value) => {
  const input = document.createElement('input');
  input.type = 'email';
  input.value = value || '';
  return input.checkValidity();
};

// Chip input of the Anrufweiterleitung card, shared by the keyword rules (Schlüsselwörter)
// and by the PVS-Bezeichnung of a Behandlungsart. A comma,
// Enter or leaving the field turns the draft into chips; Backspace on an empty draft
// removes the last one. The caller owns the draft.
function FwdChipBox({ values, onChange, draft, setDraft, removeLabel, addLabel, placeholder = '' }) {
  return (
    <div
      className="fwd-chip-box"
      onClick={(e) => {
        const input = e.currentTarget.querySelector('input');
        if (input) input.focus();
      }}
    >
      {values.map((value, valueIndex) => (
        <span key={valueIndex} className="fwd-chip">
          {value}
          <button
            type="button"
            className="fwd-chip-remove"
            onClick={() => onChange(values.filter((_, i) => i !== valueIndex))}
            title={removeLabel}
            aria-label={removeLabel}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => {
          const val = e.target.value;
          if (val.includes(',')) {
            const parts = splitCommaList(val);
            if (parts.length > 0) onChange([...values, ...parts]);
            setDraft('');
          } else {
            setDraft(val);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = draft.trim();
            if (trimmed) {
              onChange([...values, trimmed]);
              setDraft('');
            }
          } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={() => {
          const trimmed = draft.trim();
          if (trimmed) {
            onChange([...values, trimmed]);
            setDraft('');
          }
        }}
        placeholder={placeholder}
        aria-label={addLabel}
      />
    </div>
  );
}

// Unified smooth fold/unfold container for switch-controlled sections on the Anrufablauf tab.
function SwitchCollapse({ open, compact = false, children }) {
  return (
    <div
      className={`fwd-group-collapse${compact ? ' fwd-group-collapse--sm' : ''}${open ? '' : ' is-collapsed'}`}
      aria-hidden={!open}
    >
      <div className="fwd-group-body">
        {children}
      </div>
    </div>
  );
}

// Collapsible sub-card on the Anrufablauf tab: reuses SwitchCollapse for smooth height + fade
// and shows a status badge or one-line preview when folded.
function CollapsibleSubCard({ title, tooltip, badge, preview, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = (e) => {
    if (e.target.closest('button, a, input, select, textarea, .th-info-icon')) return;
    setOpen((prev) => !prev);
  };
  return (
    <div className={`config-sub-card${open ? ' is-open' : ''}`}>
      <div
        className="config-sub-card-head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
      >
        <h4>
          {title}
          {tooltip && <InfoTooltip text={tooltip} />}
        </h4>
        <div className="config-sub-card-meta">
          {!open && badge && <span className="config-sub-card-badge">{badge}</span>}
          <span className="config-sub-card-chevron" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
      </div>
      {!open && preview && (
        <div className="config-sub-card-preview" onClick={toggle}>
          {preview}
        </div>
      )}
      <SwitchCollapse open={open}>
        <div className="config-sub-card-divider">
          {children}
        </div>
      </SwitchCollapse>
    </div>
  );
}

// Whitelist of the Direktweiterleitung: the card edits rows {number, name, type}, stored as
// two keys. whitelist_caller_ids stays the list of plain numbers the call path matches
// (ai_config_store.get_whitelist_forwarding); whitelist_caller_labels maps a number to its
// name and type and is read by this card only. A row without a number is not stored.
const whitelistEntriesFromConfig = (data) => {
  const labels = data.whitelist_caller_labels || {};
  return (Array.isArray(data.whitelist_caller_ids) ? data.whitelist_caller_ids : []).map((id) => {
    const number = String(id);
    return { number, name: labels[number]?.name || '', type: labels[number]?.type || '' };
  });
};
const whitelistEntriesToConfig = (entries) => {
  const rows = entries
    .map(e => ({ number: e.number.trim(), name: e.name.trim(), type: e.type.trim() }))
    .filter(e => e.number);
  return {
    whitelist_caller_ids: rows.map(e => e.number),
    whitelist_caller_labels: Object.fromEntries(
      rows.filter(e => e.name || e.type).map(e => [e.number, { name: e.name, type: e.type }])
    ),
  };
};

// A number the way the whitelist match compares it (prompt_builder.normalize_phone_number,
// for_sip_comparison=True): digits only, without +49 / 0049 and the leading 0. Tells whether
// a therapist's number is already listed, whatever format either side was typed in.
const comparablePhoneNumber = (value) => {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('0049')) digits = digits.slice(4);
  else if (digits.startsWith('49')) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
};

// Inline-editable text block in the style of the Tasks page description cell.
// Click sets the cursor at the click position; Enter saves, Shift+Enter inserts
// newline, Esc cancels, blur commits. Underlying state is updated via onChange
// so the auto-save useEffect picks it up automatically.
function InlineEditableTextarea({ value, onChange, placeholder = 'Klicken zum Bearbeiten', maxLength }) {
  const [editing, setEditing] = useState(false);
  const [initialCaret, setInitialCaret] = useState(null);
  const [initialHeight, setInitialHeight] = useState(null);
  const taRef = useRef(null);

  if (editing) {
    return (
      <div className="description-cell" style={{ position: 'relative' }}>
        <textarea
          ref={(el) => {
            taRef.current = el;
            if (!el) return;
            if (el.dataset.inlineInited === '1') return;
            el.dataset.inlineInited = '1';
            const len = el.value.length;
            const pos = initialCaret == null ? len : Math.max(0, Math.min(initialCaret, len));
            el.focus({ preventScroll: true });
            try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
            // Auto-grow once visible
            el.style.height = 'auto';
            el.style.height = el.scrollHeight + 'px';
          }}
          value={value}
          maxLength={maxLength}
          style={initialHeight ? { height: `${initialHeight}px` } : undefined}
          onChange={(e) => {
            onChange(e);
          }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          onBlur={() => {
            setEditing(false);
            setInitialCaret(null);
            setInitialHeight(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
              setInitialCaret(null);
              setInitialHeight(null);
            } else if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              setEditing(false);
              setInitialCaret(null);
              setInitialHeight(null);
            }
          }}
          className="description-inline-edit"
        />
        <div className="description-edit-hint">
          <kbd>Enter</kbd> speichern · <kbd>Shift</kbd>+<kbd>Enter</kbd> Zeilenumbruch · <kbd>Esc</kbd> abbrechen
        </div>
      </div>
    );
  }

  const isEmpty = !value || !String(value).trim();
  return (
    <div
      className="description-readonly"
      title="Klicken zum Bearbeiten"
      style={{
        minHeight: 24,
        color: isEmpty ? '#bbb' : 'var(--text-primary)',
        fontStyle: isEmpty ? 'italic' : 'normal',
      }}
      onClick={(e) => {
        e.stopPropagation();
        const container = e.currentTarget;
        setInitialHeight(container.offsetHeight);
        let caret = null;
        try {
          let pos;
          if (document.caretPositionFromPoint) {
            pos = document.caretPositionFromPoint(e.clientX, e.clientY);
            if (pos) pos = { node: pos.offsetNode, offset: pos.offset };
          } else if (document.caretRangeFromPoint) {
            const r = document.caretRangeFromPoint(e.clientX, e.clientY);
            if (r) pos = { node: r.startContainer, offset: r.startOffset };
          }
          if (pos && pos.node && container.contains(pos.node)) {
            const range = document.createRange();
            range.setStart(container, 0);
            range.setEnd(pos.node, pos.offset);
            caret = range.toString().length;
          }
        } catch { /* ignore — fallback to end of text */ }
        setInitialCaret(caret);
        setEditing(true);
      }}
    >
      {isEmpty ? placeholder : value}
    </div>
  );
}

// Action button with a portaled hover tooltip so the label is never
// clipped by table containers. Positioned just above the button.
// Custom (non-native) dropdown that matches the existing status-filter look in
// TasksPage. Used wherever a native <select> would be clunky or inconsistent
// with the rest of the UI.
function CustomDropdown({ value, options, onChange, size = 'md', minWidth, disabled, fontSize: fontSizeProp, fontWeight }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left, width, openUp }
  const ref = useRef(null);
  const triggerRef = useRef(null);

  // Compute portal position relative to viewport so the menu escapes any
  // ancestor with overflow: auto/hidden (e.g. modal body).
  const updatePos = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const estimatedMenuHeight = Math.min(280, options.length * 32 + 12);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < estimatedMenuHeight + 8 && r.top > spaceBelow;
    setPos({
      top: openUp ? r.top - 4 : r.bottom + 4,
      left: r.left,
      width: r.width,
      openUp,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    updatePos();
    const close = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      // Click inside the portal menu must NOT close — handled by stopPropagation on items.
      setOpen(false);
    };
    const onScroll = () => updatePos();
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selected = options.find(o => o.value === value);
  const label = selected ? selected.label : (value ?? '');

  const isSm = size === 'sm';
  const padding = isSm ? '0.25rem 1.5rem 0.25rem 0.5rem' : '0.5rem 2rem 0.5rem 0.875rem';
  // fontSize/fontWeight override the size preset for dropdowns that sit INSIDE a
  // sentence: there the control has to carry the surrounding text's typography,
  // otherwise the row reads as two different fonts. Unset = the old presets.
  const fontSize = fontSizeProp || (isSm ? '0.85rem' : '0.95rem');

  return (
    <div style={{ position: 'relative', minWidth: minWidth || (isSm ? 'auto' : 180), opacity: disabled ? 0.55 : 1 }} ref={ref}>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen(prev => !prev); }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(prev => !prev); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        style={{
          width: '100%',
          padding,
          borderRadius: 6,
          border: '1px solid #ccc',
          background: '#fff',
          fontSize,
          fontWeight: fontWeight || 'inherit',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='%23555' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 10px center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 'normal',
          color: 'var(--text-primary)',
        }}
      >
        {label}
      </div>
      {open && pos && ReactDOM.createPortal(
        <div
          role="listbox"
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            minWidth: pos.width,
            width: 'max-content',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            zIndex: 999999,
            padding: '6px 0',
            transform: pos.openUp ? 'translateY(-100%)' : 'none',
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {options.map(opt => {
            const isActive = opt.value === value;
            return (
              <div
                key={String(opt.value)}
                role="option"
                aria-selected={isActive}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: '6px 14px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  background: isActive ? '#f0f4ff' : 'transparent',
                  color: isActive ? 'var(--color-main)' : 'var(--text-primary)',
                  fontWeight: isActive ? 500 : 400,
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isActive ? '#f0f4ff' : 'transparent'; }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// Bubble sits above the trigger; the horizontal half depends on which edge it is
// anchored to (see the placement effect below).
const TOOLTIP_TRANSFORMS = {
  center: 'translate(-50%, -100%)',
  left: 'translate(0, -100%)',
  right: 'translate(-100%, -100%)',
};

// `tooltipAnchor` pins which side of the bubble meets the trigger: 'left' grows it to
// the top right, 'right' to the top left. Left unset the placement is decided by the
// viewport-overflow pass below, which is right for a lone icon but makes a ROW of
// buttons inconsistent — a long label overflows and flips to the side, a short one
// stays centred over its button. Pin the row and they all read the same.
function IconTooltipButton({ tooltip, tooltipAnchor, onClick, className, children, ariaLabel, disabled, ...rest }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const tipRef = useRef(null);

  const show = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    // cx/left/right are the raw trigger rect the placement is derived from; x + anchor
    // are what the bubble actually renders with.
    const anchor = tooltipAnchor || 'center';
    const x = anchor === 'left' ? r.left : anchor === 'right' ? r.right : r.left + r.width / 2;
    setPos({ top: r.top - 6, cx: r.left + r.width / 2, left: r.left, right: r.right, x, anchor });
  }, [tooltipAnchor]);
  const hide = () => setPos(null);

  // Focus alone must NOT bring the bubble up: a pointer click focuses the button as
  // well, and react-modal hands that focus BACK to it when a dialog opened from it
  // closes — the pointer is elsewhere by then, no mouseleave ever arrives, and the
  // bubble would sit on screen until the next click. :focus-visible is exactly the
  // browser's "this focus deserves an indicator" (keyboard) heuristic, so the
  // keyboard path keeps its tooltip and the mouse path leaves nothing behind.
  const showOnFocus = (e) => {
    const el = e.currentTarget;
    let keyboardFocus = true;
    try { keyboardFocus = el.matches(':focus-visible'); } catch (err) { keyboardFocus = true; }
    if (keyboardFocus) show();
  };

  // The click usually takes the pointer off this button — a modal opens over it, the
  // view swaps — and no mouseleave ever arrives for that, so the bubble goes here.
  const handleClick = (e) => {
    hide();
    if (onClick) onClick(e);
  };

  // A control that goes disabled while hovered stops firing mouse events, so its
  // mouseleave would never arrive either.
  useEffect(() => { if (disabled) setPos(null); }, [disabled]);

  // The bubble is centred on its trigger by default, which pushes half of it off-screen
  // for a trigger near a viewport edge (it is position:fixed, nothing clips it back in).
  // Measure it once it exists and, if centring would overflow, ANCHOR it to that edge of
  // the trigger instead: near the left edge the bubble grows to the top right of the
  // button, near the right edge to the top left. The clamp afterwards is the safety net
  // for a bubble wider than the space its anchor leaves. The decision reads the untouched
  // trigger rect (cx/left/right), so the second pass reaches the same result and stops.
  useLayoutEffect(() => {
    if (!pos || !tipRef.current) return;
    const w = tipRef.current.offsetWidth;
    // clientWidth, not innerWidth: it excludes the vertical scrollbar.
    const vw = document.documentElement.clientWidth || window.innerWidth;
    let anchor = pos.anchor;
    let x = pos.x;
    // A pinned anchor keeps its side; only the auto mode re-decides. The clamp below
    // still runs for both, so a pinned bubble can never leave the viewport either.
    if (!tooltipAnchor) {
      anchor = 'center';
      x = pos.cx;
      if (pos.cx - w / 2 < 8) { anchor = 'left'; x = pos.left; }
      else if (pos.cx + w / 2 > vw - 8) { anchor = 'right'; x = pos.right; }
    }
    const lead = anchor === 'center' ? w / 2 : anchor === 'left' ? 0 : w;   // px left of x
    x = Math.min(Math.max(x, 8 + lead), vw - 8 - (w - lead));
    if (anchor !== pos.anchor || Math.abs(x - pos.x) > 0.5) {
      setPos(prev => (prev ? { ...prev, x, anchor } : prev));
    }
  }, [pos, tooltipAnchor]);

  return (
    <>
      <button
        ref={ref}
        type="button"
        className={className}
        onClick={handleClick}
        disabled={disabled}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={showOnFocus}
        onBlur={hide}
        aria-label={ariaLabel || tooltip}
        {...rest}
      >
        {children}
      </button>
      {pos && tooltip && ReactDOM.createPortal(
        <div
          ref={tipRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.x,
            transform: TOOLTIP_TRANSFORMS[pos.anchor],
            padding: '5px 9px',
            background: '#fff',
            color: 'color-mix(in srgb, var(--color-main) 80%, transparent)',
            fontSize: '0.75rem',
            fontWeight: 500,
            border: '1px solid #e0e0e0',
            borderRadius: 6,
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            whiteSpace: 'nowrap',
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          {tooltip}
        </div>,
        document.body
      )}
    </>
  );
}

// The ONE chooser for every "which category?" decision in the category dialog
// (edit: merge and rename, add, dropNew, delete-with-move). One stacked bubble per option,
// full width of the narrow modal — a pill and a radio dot are two affordances for
// one choice, so only the pill remains. `value` marks the picked option in the
// modes that confirm afterwards; the one-click lists (edit, add, dropNew) pass none
// because the click itself is the action. `isNew` on an option means "this category does not exist
// yet" and is drawn as a dashed ghost pill, the same dashed language the rail's
// "add category" control uses — a heading would be more words for the same signal.
// An existing group in a list WITHOUT a selection model (the edit state's merge
// targets) sits on the neutral surface instead of the blue tint: the tint is the
// picked/current look, and a plain action must not read as the group being edited.
function CategoryOptionList({ options, value, onChange, disabled }) {
  // No `value` means the list has no selection model at all (edit, add, dropNew): the pills are
  // plain action buttons then, so they must not announce a pressed state.
  const selectable = value !== null && value !== undefined;
  return (
    <div className="tt-cat-chip-stack">
      {options.map(opt => {
        const picked = selectable && value === opt.value;
        const tone = opt.isNew ? ' tt-cat-chip--new' : (selectable ? '' : ' tt-cat-chip--neutral');
        return (
          <button
            key={opt.value}
            type="button"
            className={`tt-cat-chip tt-cat-chip--block${tone}${picked ? ' is-selected' : ''}`}
            aria-pressed={selectable ? picked : undefined}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
          >
            {opt.display}
          </button>
        );
      })}
    </div>
  );
}

// Valid tab keys mirror the conditional renders below; used to validate the
// `?tab=` query param so an unknown value can't break the UI.
const VALID_CONFIG_TABS = ['practice', 'team', 'sip', 'callflow', 'ai', 'pvs', 'members'];

// Basic styling for modal (can be moved to a CSS file)
const customModalStyles = {
  overlay: {
    zIndex: 2000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '560px',
    padding: '20px',
  },
};

// Bind modal to app element for accessibility (usually in your root component)
// For simplicity here, we might need to set it if not already done globally.
// Modal.setAppElement('#root'); // Example, ensure #root exists or use your app's root ID

// Valid Gemini model options (must match dropdown values exactly)
const VALID_GEMINI_MODELS = ['', 'google/gemini-2.5-pro', 'google/gemini-2.5-flash'];

// Default smartAB keywords pre-filled for practices that have not yet configured trigger_keywords.
// Mirrors the backend runtime fallback list in langgraph_prompts.py (DEFAULT_SMARTAB_KEYWORDS) — keep in sync.
const DEFAULT_SMARTAB_KEYWORDS = 'bewerben, bewerbung, praktikant, praktikum, beschwerde, reklamation, hausbesuch';

// Normalize a practice row from the API into the shape `practiceInfo` expects.
// Shared by the initial hydration and by the post-save re-seed, so both produce
// identical state — a divergence here would make a plain save look like an edit.
//   * opening hours: backfill `closed` and substitute the weekday defaults for
//     empty strings (weekends stay blank).
//   * scalars: coerce null/undefined to '' so React's controlled inputs don't warn
//     about switching from uncontrolled to controlled.
//   * object-typed fields must stay null/object, never '': coercing them makes the
//     Praxis-Information PUT send a string for a Dict field, which the backend
//     rejects with 422. This bit every fresh practice whose trigger_keywords /
//     insurance_type_settings were still null.
// Cutoff times for "Keine Termine am Anruftag": half-hour steps 08:00 to 20:00,
// starting at the earliest hour a practice opens. '00:00' is the all-day entry:
// every call time is at/after midnight, so today is never offered. It needs no
// backend branch, but it DOES change the sentence in the UI (see the toggle).
// Static, so it lives outside the component instead of being rebuilt per render.
const SAME_DAY_CUTOFF_ALL_DAY = '00:00';
const SAME_DAY_CUTOFF_OPTIONS = [
  { value: SAME_DAY_CUTOFF_ALL_DAY, label: 'Ganztägig' },
  ...Array.from({ length: 25 }, (_, i) => {
    const value = `${String(8 + Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`;
    return { value, label: `${value} Uhr` };
  }),
];

// Short-notice therapist notification: which therapist column the one address
// field writes, per selected channel. The columns stay separate, so switching
// channel never loses the address of the other two. SMS has no entry: it goes to
// the therapist's Telefonnummer, a field of its own (the whitelist adopts it too).
const THERAPIST_CHANNEL_FIELD = {
  slack: {
    key: 'slack_user_id', label: 'Slack-ID', type: 'text', placeholder: 'z.B. C0123ABCDEF oder U0123ABCDEF',
    tooltip: 'Slack-Kanal-ID (C...) oder Mitglieds-ID (U...) für eine Direktnachricht. Das Bot-Token wird unter PVS-Integration hinterlegt.',
  },
  email: {
    key: 'email', label: 'E-Mail-Adresse', type: 'email', placeholder: 'z.B. therapeut@praxis.de',
    tooltip: 'Die Nachricht wird von der verifizierten E-Mail-Adresse der Praxis versendet (siehe Tab Praxis-Informationen).',
  },
};

// Per-message save wiring for the Anrufablauf tab, keyed by the SAME message key
// the preview/status endpoints use. `type` is the filler-messages row the text is
// stored under; `configKey` means it lives in the per-practice config instead and
// names the field there — exactly one of the two is set.
// `path` is the pre-synthesis endpoint that rebuilds it. Same endpoints the big
// Speichern calls, narrowed to a single message via the `only` query param.
const MESSAGE_SAVE_ACTIONS = {
  greeting:               { type: 'greeting',               path: 'pre-synthesize-greeting' },
  greeting_away:          { type: 'greeting_away',          path: 'pre-synthesize-greeting' },
  greeting_pvs_demo:      { type: 'greeting_pvs',           path: 'pre-synthesize-pvs-demo' },
  greeting_pvs_away_demo: { type: 'greeting_pvs_away',      path: 'pre-synthesize-pvs-demo' },
  farewell_general:       { type: 'farewell_general',       path: 'pre-synthesize-farewell' },
  farewell_appointment:   { type: 'farewell_appointment',   path: 'pre-synthesize-farewell' },
  voicemail_announcement: { type: 'voicemail_announcement', path: 'pre-synthesize-voicemail' },
  hold_message:           { type: 'hold_message',           path: 'pre-synthesize-hold-message' },
  consent:                { configKey: 'consent_question',  path: 'pre-synthesize-consent' },
  forwarding_message:     { configKey: 'forwarding_message', path: 'pre-synthesize-forwarding' },
};

// A forwarding rule's Ansage is keyed by the rule's own id, so it cannot be a static
// entry above. Its text lives on the PRACTICE ROW (trigger_keywords) — a third storage
// shape next to "filler-messages row" and "per-practice config", flagged by `rules`.
const FORWARDING_RULE_KEY = /^forwarding_rule_[a-z0-9]{4,16}$/;
const messageSaveAction = (messageKey) =>
  MESSAGE_SAVE_ACTIONS[messageKey] ||
  (FORWARDING_RULE_KEY.test(messageKey) ? { rules: true, path: 'pre-synthesize-forwarding' } : null);

// Mirrors the backend's RULE_ID_RE (routers/practices.py): 4-16 lowercase alphanumerics,
// because it ends up in a cache filename and a preview URL.
// `crypto.randomUUID` needs a secure context — same guard and same reason as TAB_ID in
// utils/axiosInstance.js, since the legacy single-tenant VMs are served over plain http
// by IP and would otherwise throw here. base36 is [0-9a-z], so both branches fit the regex.
const newForwardingRuleId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.slice(-8);

// These endpoints rebuild SEVERAL messages unless told which one; `only` is always the
// message key, so it needs no second copy in the map above. (Forwarding rebuilds the
// practice-wide sentence plus one message per rule that has its own Ansage.)
const PAIRED_PRESYNTH = new Set([
  'pre-synthesize-greeting', 'pre-synthesize-farewell', 'pre-synthesize-pvs-demo',
  'pre-synthesize-forwarding',
]);

// Messages whose EMPTY text is meaningful rather than nothing to record: both fall back
// to a canonical default sentence, which is what gets synthesized. Every other message
// with an empty textarea has nothing to say, so its save button stays disabled.
const EMPTY_MEANS_DEFAULT = new Set(['consent', 'forwarding_message']);

const PRACTICE_OBJECT_FIELDS = new Set(['opening_hours', 'trigger_keywords', 'insurance_type_settings']);
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

// Therapy umbrella categories. Mirrors backend/src/api/therapy_categories.py:
// keys are stored, labels are constants, the order below is canonical for every
// tab, list and dialog. The categories GET also returns the options, so the
// server list wins whenever it is available and this stays the offline mirror.
const DEFAULT_THERAPY_CATEGORY = 'physio_osteo';
// Sentinel drop-target id for the rail's "+" control: dropTargetCategory otherwise
// holds a real category key, and "+" is not a category.
const CATEGORY_DROP_NEW = '__new__';
// Sentinel key for the non-offered therapies manager tab on the category rail
const EXCLUDED_THERAPIES_TAB = '__excluded__';

// Two different drags happen on the same rail: a therapy ROW dragged onto a tab
// (moves that therapy into the category) and a TAB dragged along the rail (reorders
// the tabs). Only the tab drag carries this MIME type, and dataTransfer.types is the
// one thing readable during dragover (getData() is blocked there), so it is what
// tells the two apart. Custom types are lowercased by the spec; keep it that way.
const CATEGORY_DRAG_TYPE = 'application/x-deskbot-category';
const isCategoryDrag = (e) => Array.from(e.dataTransfer?.types || []).includes(CATEGORY_DRAG_TYPE);

const THERAPY_CATEGORY_OPTIONS = [
  { value: 'physio_osteo', display: 'Physiotherapie & Osteopathie' },
  { value: 'ergo',         display: 'Ergotherapie' },
  { value: 'logo',         display: 'Logopädie' },
  { value: 'podo',         display: 'Podologie' },
  { value: 'weitere',      display: 'Weitere' },
];

// Neupatienten- and Bestandspatientenbehandlung: the stored shape is a PAIR —
// <cohort>_patient_handling ('normal' | 'smartab', plus 'send_link' and 'waiting_list' for new patients)
// and the sibling rider <cohort>_patient_send_form_link, which the backend reads ONLY
// when handling is 'normal'/absent ("book AND send the link"). Each UI select presents
// that pair as one value, so these two map between the two representations. `cohort` is
// 'new' or 'existing'; the key names are the only difference between the two rows.
const handlingSelectValue = (settings, cohort) => {
  const handling = settings?.[`${cohort}_patient_handling`] || 'normal';
  if (handling === 'normal' && settings?.[`${cohort}_patient_send_form_link`] === true) {
    return 'normal_send_link';
  }
  return handling;
};

const handlingPatch = (selectValue, cohort) => (
  selectValue === 'normal_send_link'
    ? { [`${cohort}_patient_handling`]: 'normal', [`${cohort}_patient_send_form_link`]: true }
    : { [`${cohort}_patient_handling`]: selectValue, [`${cohort}_patient_send_form_link`]: false }
);

// The five insurance types in the one order every rail, grid and payload uses.
// The two "(Tage)" fields are stored as integer-or-null and the settings endpoint
// rejects anything else. <input type="number"> hands back the raw string, so "12.5"
// would 400 the whole group write, and the only symptom would be a console line
// behind an optimistic update that already redrew the dialog.
const parseDays = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const INSURANCE_KEYS = ['private', 'public', 'selbstzahler', 'bg', 'entlassmanagement'];
// Patientenfilter gender dimension. Genders and insurance types are stored only
// when the therapist takes a STRICT SUBSET — an all-selected set means "alle" and
// the backend drops it, so the two representations can never drift.
const PF_GENDER_KEYS = ['m', 'w'];
const PF_GENDER_LABEL = { m: 'Männlich', w: 'Weiblich' };

// The patient groups (one per set row) of the "Exklusive Spezialisierung" summary sentence, or null when
// nothing is set. Normalises like handleSaveTherapistDetails (empty = open, a group
// with both fields empty is dropped) and skips groups the backend rejects, so the
// sentence describes what Speichern stores. Both age bounds are inclusive in the
// matcher (_pf_age_ok), hence "bis 30", never "unter 30".
const pfSpecializationWho = (form) => {
  const genders = form.pf_genders || [];
  const gender = genders.length === 1 ? genders[0] : null;
  const symptoms = form.pf_symptoms || [];
  const groups = (form.pf_age_groups || [])
    .map(g => ({
      min: String(g.min ?? '').trim() === '' ? null : Number(g.min),
      max: String(g.max ?? '').trim() === '' ? null : Number(g.max),
    }))
    .filter(g => (g.min !== null || g.max !== null)
      && !(g.min !== null && g.min < 0)
      && !(g.min !== null && g.max !== null && g.min > g.max))
    .sort((a, b) => (a.min || 0) - (b.min || 0));
  if (!gender && !symptoms.length && !groups.length) return null;

  // One part per set row: each row claims patients ON ITS OWN (OR), in the order
  // age, gender, symptoms.
  const parts = [];
  if (groups.length) {
    if (groups.some(g => !g.min && g.max === null)) {
      parts.push('Patienten jeden Alters');
    } else {
      const ranges = groups.map(g => (
        !g.min ? `bis ${g.max}`
          : g.max === null ? `ab ${g.min}`
          : g.min === g.max ? `mit ${g.min}`
          : `von ${g.min} bis ${g.max}`
      ));
      const loneBis = ranges.length === 1 && ranges[0].startsWith('bis ');
      parts.push(`Patienten ${ranges.join(' oder ')} ${loneBis ? 'Jahre' : 'Jahren'}`);
    }
  }
  if (gender) parts.push(gender === 'w' ? 'alle Patientinnen' : 'alle männlichen Patienten');
  if (symptoms.length) {
    parts.push(symptoms.length <= 2
      ? `Patienten mit ${symptoms.map(x => `„${x}“`).join(' oder ')}`
      : `Patienten mit einem der ${symptoms.length} Symptome oben`);
  }
  return parts;
};

// Verordnungstyp scope (practices.therapy_category_settings.scope). A string and
// not an inverted boolean on purpose: the switch reads "für alle Heilmittelgruppen"
// while the interesting state is the opposite one.
const VT_SCOPE_ALL = 'all';
const VT_SCOPE_PER_CATEGORY = 'per_category';

// Which link "Buchen + Link senden" sends: the practice-wide Formular-URL or a
// personal, prefilled DOC-IQ form. Stored per cohort as
// <cohort>_patient_form_link_source ('general' | 'doc_iq'), and an absent key means
// 'general'. The key is only ever written once someone picks a link, so a practice
// that never touches the choice keeps saving exactly the body it saved before.
const FORM_LINK_SOURCE_KEYS = ['new_patient_form_link_source', 'existing_patient_form_link_source'];
const pickFormLinkSources = (block) => Object.fromEntries(
  FORM_LINK_SOURCE_KEYS
    .filter(k => block?.[k])
    .map(k => [k, block[k] === 'doc_iq' ? 'doc_iq' : 'general'])
);

// Copy ONE insurance block down to exactly the fields the per-group endpoint
// accepts, in the types it demands. Its sibling insurance_type_settings is written
// by an endpoint that validates nothing, so an unknown or mistyped key carried over
// from there would 400 the whole group write on the very first edit of a group.
const pickInsuranceFields = (block) => {
  return {
    verordnungsfrist_days: parseDays(block?.verordnungsfrist_days),
    max_terminabstand_days: parseDays(block?.max_terminabstand_days),
    behandlungszeitraum_bis6_days: parseDays(block?.behandlungszeitraum_bis6_days),
    behandlungszeitraum_ab7_days: parseDays(block?.behandlungszeitraum_ab7_days),
    new_patient_handling: block?.new_patient_handling || 'normal',
    new_patient_send_form_link: block?.new_patient_send_form_link === true,
    existing_patient_handling: block?.existing_patient_handling || 'normal',
    existing_patient_send_form_link: block?.existing_patient_send_form_link === true,
    ...pickFormLinkSources(block),
  };
};

// Does this rule set send the intake form link anywhere? Used to decide whether the
// practice-wide Formular-URL row is reachable. "Buchen + Link senden" counts, and so
// does the Bestandspatienten variant, which shares the same one URL.
const usesFormLink = (settings) => INSURANCE_KEYS.some(k =>
  settings?.[k]?.new_patient_handling === 'send_link'
  || settings?.[k]?.new_patient_send_form_link === true
  || settings?.[k]?.existing_patient_send_form_link === true);


// What a DOC-IQ choice does today. `status` is the practice's connection
// ({enabled, praxis_name}) or null while unknown, which renders nothing: without a
// connection the call path sends the general link by itself, so there is nothing
// to warn about until we know.
const DocIqStatusNote = ({ status, style }) => {
  if (!status) return null;
  if (status.enabled) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)', ...style }}>
        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-quaternary)', flexShrink: 0 }} />
        {status.praxis_name ? `Verbunden mit DOC-IQ: ${status.praxis_name}` : 'Verbunden mit DOC-IQ'}
      </div>
    );
  }
  return (
    <div
      role="status"
      style={{
        fontSize: '0.8rem',
        lineHeight: 1.45,
        color: 'var(--text-primary)',
        background: 'color-mix(in srgb, var(--color-secondary) 6%, #fff)',
        borderLeft: '3px solid var(--color-secondary)',
        borderRadius: 6,
        padding: '8px 12px',
        ...style,
      }}
    >
      Noch nicht mit DOC-IQ verbunden. Die Verbindung stellen Sie in DOC-IQ her: System → Integrationen → „Mit Deskbot verbinden“. Bis dahin wird der allgemeine Link verschickt.
    </div>
  );
};

const preparePracticePayload = (info) => {
  const practiceData = { ...(info || {}) };
  delete practiceData.insurance_type_settings;
  delete practiceData.trigger_keywords;

  ['name', 'address', 'phone_number'].forEach((f) => {
    if (!practiceData[f] || !String(practiceData[f]).trim()) {
      practiceData[f] = ' ';
    }
  });

  if (practiceData.opening_hours) {
    Object.keys(practiceData.opening_hours).forEach((day) => {
      if (!practiceData.opening_hours[day].hasOwnProperty('closed')) {
        practiceData.opening_hours[day].closed = false;
      }
    });
  }
  return practiceData;
};

const serializePracticeDetails = (info) => JSON.stringify({
  name: (info?.name || '').trim(),
  address: (info?.address || '').trim(),
  area_code: (info?.area_code || '').trim(),
  phone_number: (info?.phone_number || '').trim(),
  fax_number: (info?.fax_number || '').trim(),
  email: (info?.email || '').trim(),
  website: (info?.website || '').trim(),
  opening_hours: info?.opening_hours || {},
});

const normalizePracticeRow = (data) => {
  const processedOpeningHours = {};
  Object.keys(data.opening_hours || {}).forEach((day) => {
    const hours = data.opening_hours[day] || {};
    const isWeekday = WEEKDAYS.includes(day);
    processedOpeningHours[day] = {
      open: hours.open === '' ? (isWeekday ? '09:00' : '') : hours.open,
      close: hours.close === '' ? (isWeekday ? '17:00' : '') : hours.close,
      closed: hours.closed === undefined ? false : hours.closed,
    };
  });

  const safeData = { ...data, opening_hours: processedOpeningHours };
  Object.keys(safeData).forEach((key) => {
    if (!PRACTICE_OBJECT_FIELDS.has(key) && (safeData[key] === null || safeData[key] === undefined)) {
      safeData[key] = '';
    }
  });
  return safeData;
};

const AZURE_USE_CASE_CONFIG = [
  { key: 'langgraph_routing',        label: 'LangGraph: Routing' },
  { key: 'langgraph_classification', label: 'LangGraph: Klassifizierung & Extraktion' },
  { key: 'langgraph_negotiation',    label: 'LangGraph: Slot-Verhandlung' },
  { key: 'langgraph_chitchat',       label: 'LangGraph: Chitchat' },
  { key: 'langgraph_praxisinfo',     label: 'LangGraph: Praxisinfo' },
  { key: 'agentic',                  label: 'SmartAB / Agentic Modus' },
  { key: 'extraction',               label: 'Informations-Extraktion' },
  { key: 'validation',               label: 'Validierung & Matching' },
  { key: 'batch_transcribe',          label: 'Batch-Transkription (GPT-4o)' },
  { key: 'default_smart',            label: 'Standard Smart (Fallback)', bold: true },
  { key: 'default_mini',             label: 'Standard Mini (Fallback)', bold: true },
];

// Normalize Gemini model value from DB to match dropdown options
// Returns empty string if value doesn't match any valid option (triggers fallback)
const normalizeGeminiModel = (value) => {
  if (!value) return '';
  // If already valid, return as-is
  if (VALID_GEMINI_MODELS.includes(value)) return value;
  // Try adding google/ prefix
  const withPrefix = value.startsWith('google/') ? value : `google/${value}`;
  if (VALID_GEMINI_MODELS.includes(withPrefix)) return withPrefix;
  // Invalid value - return empty to trigger fallback
  console.warn(`Invalid Gemini model value "${value}" - resetting to empty (will use primary fallback)`);
  return '';
};

// Action icons matching TasksPage.js style
const actionIcons = {
  delete: (
    <svg width="18" height="18" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <polyline points="3,6 5,6 21,6"/>
      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
      <line x1="10" y1="11" x2="10" y2="17"/>
      <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  ),
  save: (
    <svg width="18" height="18" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
      <polyline points="17 21 17 13 7 13 7 21"/>
      <polyline points="7 3 7 8 15 8"/>
    </svg>
  ),
  settings: (
    <svg width="18" height="18" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M12 20h9"/>
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
    </svg>
  ),
  duplicate: (
    <svg width="18" height="18" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  // Same pen as `settings` above, geometry for geometry, but sized to the rail's "+"
  // and stroked with currentColor so it takes the category tab's hover/active colour
  // (the tab's "bearbeiten" control). `settings` keeps its gray for the table rows.
  pen: (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20h9"/>
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
    </svg>
  ),
  // Counter-clockwise circular arrow: "hand this back to the default". Sized and
  // stroked exactly like `pen` above, because it sits in the same overlay slot on a
  // folder tab: `pen` on the Behandlungsarten rail, this one on the Verordnungstypen
  // dialog's rail.
  revert: (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="1 4 1 10 7 10"/>
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
    </svg>
  ),
  // Same bin as `delete` above, geometry for geometry, but stroked with currentColor
  // instead of the hardcoded red — used where the button itself owns the colour (the
  // category dialog's delete, which is neutral at rest and only turns destructive in
  // its armed "Wirklich löschen?" state). `delete` keeps its red for the table rows.
  trash: (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="3,6 5,6 21,6"/>
      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
      <line x1="10" y1="11" x2="10" y2="17"/>
      <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  ),
  // Confirm the dialog's primary action.
  check: (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  // "Record this again": the two-arrow cycle, on the per-message button of the
  // Anrufablauf tab. Deliberately TWO arrows chasing each other, where `revert`
  // above is one arrow turning back — that one undoes, this one repeats. Sized a
  // touch larger than the text glyph it replaces, which read as a speck at 0.75rem.
  refresh: (
    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }} aria-hidden="true">
      <polyline points="23 4 23 10 17 10"/>
      <polyline points="1 20 1 14 7 14"/>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>
  ),
  // "Not offered here": the prohibition sign (ring plus one diagonal bar), on the
  // rail's static "Nicht angeboten" tab. 14px like the rail's pen and "+".
  excluded: (
    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9"/>
      <line x1="5.6" y1="5.6" x2="18.4" y2="18.4"/>
    </svg>
  ),
  // "A new patient arrives": the person-plus figure, on the Aufnahmegespräch marker
  // and on the badge of every therapy that books one first. Small on purpose — it
  // rides inside a text tag, not on a button.
  intake: (
    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="8.5" cy="7" r="4"/>
      <line x1="20" y1="8" x2="20" y2="14"/>
      <line x1="23" y1="11" x2="17" y2="11"/>
    </svg>
  ),
  // "Blankorezept": a blank sheet with its corner folded, the prescription the practice
  // fills in itself. Sized and stroked like `intake` above: it rides in the same tag and
  // on the same preset chip.
  blanko: (
    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
      <polyline points="13 2 13 9 20 9"/>
    </svg>
  ),
  // "Ergänzendes Heilmittel": layers, the add-on that sits on top of a main therapy
  // (Heiße Rolle on KG). Sized and stroked like `intake` and `blanko`: same tag, same chip.
  supplementary: (
    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
      <polyline points="2 17 12 22 22 17"/>
      <polyline points="2 12 12 17 22 12"/>
    </svg>
  )
};

// Erstzuschlag, Sitzungen and Frequenz all read "-" on an Aufnahmegespräch, so they
// all say the same thing about why.
const INTAKE_CELL_NA_TITLE = 'Für ein Aufnahmegespräch nicht relevant.';
// The two "fest" chips of a Blankorezept: the server saves both as on, whatever is sent.
const BLANKO_FIXED_TITLE = 'Beim Blankorezept immer fest';
// Ergänzendes Heilmittel (Heiße Rolle, Fango): a catalog row only the MOVE reads for now,
// when the PVS returns an appointment as "KG20, HR". Its one control reads as a phrase,
// "[Termin verlängern] [Früher kommen] um [n] Min": `supplementary_mode` is the active option, or
// 'included' with none active (part of the main treatment: no extra time, no notice).
// Each glyph is a tiny timeline: the extra block after the appointment, or (dashed, not
// booked) ahead of it.
const SUPPLEMENTARY_MODES = [
  {
    value: 'add_time',
    label: 'Termin verlängern',
    icon: (
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="7" width="13" height="10" rx="2"/>
        <rect x="17" y="7" width="5" height="10" rx="1.5" fill="currentColor"/>
      </svg>
    ),
  },
  {
    value: 'arrive_early',
    label: 'Früher kommen',
    icon: (
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="7" width="5" height="10" rx="1.5" strokeDasharray="2.5 2.5"/>
        <rect x="9" y="7" width="13" height="10" rx="2"/>
      </svg>
    ),
  },
];
const SUPPLEMENTARY_INCLUDED = 'included';
const SUPPLEMENTARY_INCLUDED_TITLE = 'In der Hauptbehandlung enthalten, ohne Extrazeit';
// Above the table's section, and as a sentence under the dialog's heading.
const SUPPLEMENTARY_NOTE = 'Beim Buchen und Verschieben';
// A new row: 10 minutes, "Termin verlängern" (the "+" preset's mode).
const SUPPLEMENTARY_NEW_MINUTES = 10;
const SUPPLEMENTARY_DRAFT_NEW = { minutes: String(SUPPLEMENTARY_NEW_MINUTES), minutesByIns: {}, perIns: false };

const sameMinutesEverywhere = (row) => INSURANCE_KEYS.every(k => row[`duration_${k}`] === row.duration_public);

// The dialog's view of a row's five durations: ONE minutes value (the one most insurances
// share, ties go to Gesetzlich) plus, per insurance, only a value that differs from it.
// "Je Versicherungsart" opens by itself when there is such a value or an own PVS name.
const supplementaryDraftOf = (row) => {
  const values = INSURANCE_KEYS.map(k => row[`duration_${k}`]);
  const count = (v) => values.filter(x => x === v).length;
  const minutes = values.reduce((best, v) => (count(v) > count(best) ? v : best), row.duration_public);
  const minutesByIns = Object.fromEntries(INSURANCE_KEYS.map(k => [k, row[`duration_${k}`] === minutes ? '' : String(row[`duration_${k}`] ?? '')]));
  return {
    minutes: String(minutes ?? SUPPLEMENTARY_NEW_MINUTES),
    minutesByIns,
    perIns: !!row.pvs_name_per_insurance || Object.values(minutesByIns).some(Boolean),
  };
};

// The one control of an Ergänzendes Heilmittel, the same in its table row and its dialog:
// two toggles (at most one on; a click on the active one turns it off) and the minutes as
// the tail of the phrase. With none on, the tail greys out: the minutes are kept and stay
// editable, they just change nothing. `minutesProps` wires the field to its owner (the row
// saves on blur, the dialog on Speichern); `note` names the insurance whose minutes the
// table shows when they differ per insurance; `groupLabel` names the row for screen readers
// (every table row has one of these groups). The included meaning is not only a title: the
// minutes field is described by a hidden copy of it.
const SupplementaryPhrase = ({ mode, onToggle, minutesProps, note = null, groupLabel = 'Beim Verschieben' }) => {
  const included = !SUPPLEMENTARY_MODES.some(m => m.value === mode);
  const includedHintId = React.useId();
  return (
    <div className={`tt-supp-phrase${included ? ' is-included' : ''}`}>
      <div className="tt-supp-options" role="group" aria-label={groupLabel}>
        {SUPPLEMENTARY_MODES.map((m) => {
          const active = mode === m.value;
          return (
            <button
              key={m.value}
              type="button"
              className={`tt-supp-option${active ? ' is-active' : ''}`}
              aria-pressed={active}
              onClick={() => onToggle(active ? SUPPLEMENTARY_INCLUDED : m.value)}
            >
              {m.icon}
              {m.label}
            </button>
          );
        })}
      </div>
      <label className="tt-supp-tail" title={included ? SUPPLEMENTARY_INCLUDED_TITLE : undefined}>
        um
        <input
          type="text"
          inputMode="numeric"
          className="tt-supp-minutes"
          aria-describedby={included ? includedHintId : undefined}
          {...minutesProps}
        />
        Min
        {note && <span className="tt-supp-ins-note" title="Je Versicherungsart unterschiedlich">{note}</span>}
      </label>
      {included && <span id={includedHintId} className="visually-hidden">{SUPPLEMENTARY_INCLUDED_TITLE}</span>}
    </div>
  );
};

// Geometry of the "+" popover, measured the way CustomDropdown.updatePos does it:
// it opens below its anchor unless there is no room there and more room above. The
// height is an estimate, as it is there: 240 measured with two preset chips, plus 44
// for the third (Ergänzendes Heilmittel: a 38px block chip and the stack's 6px gap).
// `hints`: how many of the two one-per-group
// presets the active group already has (the Aufnahmegespräch shown there, its
// Blankorezept). A click on such a preset swaps in the "Öffnen" chip and its hint
// without a re-flip.
const ADD_POPOVER_EST_HEIGHT = 284;
const ADD_POPOVER_HINT_HEIGHT = 80;
const measureAddPopover = (el, hints = 0) => {
  const r = el.getBoundingClientRect();
  const spaceBelow = window.innerHeight - r.bottom;
  const height = ADD_POPOVER_EST_HEIGHT + hints * ADD_POPOVER_HINT_HEIGHT;
  return {
    left: r.left,
    top: r.top,
    bottom: r.bottom,
    openUp: spaceBelow < height + 8 && r.top > spaceBelow,
  };
};

// A group re-key as the server applies it to one row, for the optimistic update of a
// category rename, merge or delete: the row's category moves, and the Aufnahmegespräch's
// group list swaps the key (a merge just de-duplicates; a null target drops it). Without
// the list half the pinned Aufnahmegespräch would drop out of the tab until the refetch.
const rekeyTherapyGroup = (tt, fromKey, toKey) => {
  let next = tt;
  if (toKey && (tt.category ?? DEFAULT_THERAPY_CATEGORY) === fromKey) next = { ...next, category: toKey };
  const list = tt.intake_categories;
  if (Array.isArray(list) && list.includes(fromKey)) {
    next = { ...next, intake_categories: Array.from(new Set(list.map(k => (k === fromKey ? toKey : k)).filter(Boolean))) };
  }
  return next;
};

// Mirrors the backend's _is_theorg (routers/config.py): the worker names itself freely
// ('TheOrg', 'theorg-worker', ...), so the comparison runs on letters and digits only.
const isTheOrg = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '').includes('theorg');

// Full-area overlay that blocks a tab's content while its per-practice data is
// still loading (Jul 2026 cross-tenant incident: editing a half-loaded form and
// saving is how practice data bled between tenants). Rendered as the first
// child of a position:relative .tab-content wrapper so it covers and
// click-blocks everything below the tab strip — the nav and tabs stay usable.
const LoadingShroud = ({ loading }) => {
  if (!loading) return null;
  return (
    <div
      aria-busy="true"
      style={{
        position: 'absolute', inset: 0, zIndex: 30,
        background: 'rgba(255,255,255,0.65)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        cursor: 'progress',
      }}
    >
      <div style={{
        marginTop: '4rem', background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: '8px', padding: '0.6rem 1.1rem',
        boxShadow: '0 2px 10px rgba(15,23,42,0.12)',
        color: '#334155', fontSize: '0.9rem', fontWeight: 500,
      }}>
        Praxisdaten werden geladen…
      </div>
    </div>
  );
};

// Shared save-status slot for the four top-level Save buttons — the single
// spinner markup + @keyframes spin (App.css) lives here so every button reuses
// it with zero copy-paste.
// Save-status slot rendered to the LEFT of a fixed-width "Speichern" button:
// while a save is in flight it shows the spinning arc; when the save resolves the
// spinner is replaced in-place by the success/error confirmation text. Keeping it
// beside the button (never inside) stops the button from resizing on click.
const SaveStatusSlot = ({ saving, status }) => {
  if (saving) {
    return (
      <svg className="save-spinner" viewBox="0 0 24 24" width="18" height="18" role="status" aria-label="Speichern…">
        <circle className="save-spinner__track" cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="15 45" />
      </svg>
    );
  }
  if (status.message && status.message !== 'Speichere...') {
    return (
      <span className={`save-status ${status.saved ? 'success' : 'error'}`} style={{ margin: 0, padding: '4px 8px', fontSize: '0.85rem' }}>
        {status.message}
      </span>
    );
  }
  return null;
};

const ConfigPage = () => {
  const { keycloak } = useAuth();
  // Session 10c/10d: practice context provides the authoritative practiceId,
  // role, and loading flag — replaces the keycloak.sub → userId → practiceId
  // chain that previously lived in two local useEffect hooks. The userId
  // resolver below is kept for SIP/test-call endpoints (plan Step 10.4).
  const {
    practiceId, practiceRole, loading: practiceLoading, availablePractices,
    isUserAdmin: userIsAdmin, isUserSuperAdmin: userIsSuperAdmin, ownerView,
  } = usePractice();
  const queryClient = useQueryClient();
  const canManageMembers = userIsSuperAdmin || practiceRole === 'owner';
  const [searchParams, setSearchParams] = useSearchParams();
  // The active tab lives ONLY in the URL (`?tab=…`, absent = Praxis-Informationen),
  // so a refresh, a shared link and browser back/forward all show the same tab.
  // Never mirror it into state: opposing state<->URL sync effects flip-flopped
  // forever once a navigation (plain /config link, Back/Forward) changed ?tab.
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_CONFIG_TABS.includes(tabParam) ? tabParam : 'practice';
  // replace, not push: clicking between tabs must not pollute browser history.
  const setActiveTab = useCallback((tab) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'practice') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    if (params.toString() === searchParams.toString()) return; // already active
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Guard: a restricted tab can be carried over via ?tab= (e.g. a prior admin
  // session left 'members'/'sip' selected, then a plain member logs in). The
  // nav only renders that button for privileged roles, so the page would show
  // no active tab and a blank panel. Fall back to the first always-visible tab.
  // Wait for the practice role to load so a real owner isn't bounced off 'members'.
  useEffect(() => {
    if (practiceLoading) return;
    const allowed =
      activeTab === 'practice' || activeTab === 'team' || activeTab === 'callflow' ||
      (activeTab === 'sip' && userIsAdmin) ||
      ((activeTab === 'ai' || activeTab === 'pvs') && userIsSuperAdmin) ||
      (activeTab === 'members' && canManageMembers);
    if (!allowed) setActiveTab('practice');
  }, [activeTab, practiceLoading, userIsAdmin, userIsSuperAdmin, canManageMembers, setActiveTab]);
  
  // API base URL configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
  const [practiceInfo, setPracticeInfo] = useState({
    name: '',
    address: '',
    phone_number: '',
    area_code: '',
    fax_number: '',
    email: '',
    website: '',
    forwarding_phone_number: '',
    recording_enabled: false,
    opening_hours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '', close: '', closed: true },
      sunday: { open: '', close: '', closed: true }
    }
  });

  const [sesVerificationStatus, setSesVerificationStatus] = useState(null); // null | 'Pending' | 'Success' | 'NotStarted'
  const [sesVerifyLoading, setSesVerifyLoading] = useState(false);

  // Default accepted insurance types (all enabled)
  const DEFAULT_ACCEPTED_INSURANCE = { private: true, public: true, selbstzahler: true, bg: true, entlassmanagement: true };

  // Helper: opacity for insurance-type-dependent cells (1 if accepted, dimmed if not)
  const insuranceOpacity = (types, key) => (types?.[key] !== false) ? 1 : 0.4;

  // Therapists and Therapy Types state
  const [therapists, setTherapists] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [therapyTypeModalOpen, setTherapyTypeModalOpen] = useState(false);
  const [editingTherapyType, setEditingTherapyType] = useState(null);
  // The "+" popover: which kind of Behandlungsart is being added. null = closed,
  // otherwise the anchor rect the portal positions itself against.
  const [addTherapyPopover, setAddTherapyPopover] = useState(null);
  const [addTherapyName, setAddTherapyName] = useState('');
  const addPopoverRef = useRef(null);
  const addPopoverAnchorRef = useRef(null);
  // The footer "+" itself, so the `n` shortcut can anchor the popover to it too
  const addPlusWrapRef = useRef(null);
  // Aufnahmegespräch: while the modal shows it, the ids of the dialog group's therapies
  // that book it first. Saved through PUT …/groups/{key} for that group AFTER the row.
  const [intakeLinkedIds, setIntakeLinkedIds] = useState([]);
  // Which edges of that dialog's small link window have chips running past them (they
  // fade). Measured when the window mounts and on every scroll; unchanged values keep
  // the same object so a scroll inside one state does not re-render.
  const [linkListFade, setLinkListFade] = useState({ top: false, bottom: false });
  const measureLinkListFade = useCallback((el) => {
    if (!el) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setLinkListFade(prev => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);
  const [hoveredTherapyId, setHoveredTherapyId] = useState(null);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [therapyTypeForm, setTherapyTypeForm] = useState({
    name: '',
    // Umbrella category (Kategorie) — seeded from the active tab on create
    category: DEFAULT_THERAPY_CATEGORY,
    // Private insurance configuration
    duration_private: 30,
    sessions_private: 6,
    frequency_private: '1-3 mal pro Woche',
    // Public insurance configuration
    duration_public: 30,
    sessions_public: 6,
    frequency_public: '1-3 mal pro Woche',
    // Selbstzahler (self-payer) configuration
    duration_selbstzahler: 30,
    sessions_selbstzahler: 6,
    frequency_selbstzahler: '1-3 mal pro Woche',
    // BG (Berufsgenossenschaft) configuration
    duration_bg: 30,
    sessions_bg: 6,
    frequency_bg: '1-3 mal pro Woche',
    // Entlassmanagement configuration
    duration_entlassmanagement: 30,
    sessions_entlassmanagement: 6,
    frequency_entlassmanagement: '1-3 mal pro Woche',
    // First-session extra minutes (Ersttermin bonus) per insurance type
    first_session_extra_private: 0,
    first_session_extra_public: 0,
    first_session_extra_selbstzahler: 0,
    first_session_extra_bg: 0,
    first_session_extra_entlassmanagement: 0,
    description: '',
    aliases: [],
    symptoms: [],
    pvs_name: '',
    // Per-insurance-type PVS article name (opt-in; empty variant falls back to base pvs_name)
    pvs_name_per_insurance: false,
    pvs_name_private: '',
    pvs_name_public: '',
    pvs_name_selbstzahler: '',
    pvs_name_bg: '',
    pvs_name_entlassmanagement: '',
    // Session recommendation always enabled (hidden from UI)
    recommend_sessions_enabled: true,
    // Fixed values: when on, the configured value is used and the caller is never asked
    force_sessions_enabled: false,
    force_frequency_enabled: false,
    double_appointments_enabled: false,
    // Aufnahmegespräch: this row IS the practice's one intake appointment, or it links
    // to it so its new patients get that booked first (null = books normally)
    is_intake_appointment: false,
    intake_therapy_type_id: null,
    // Blankorezept: this row is its Heilmittelgruppe's one Blanko row (fixed sessions
    // and frequency, no Doppeltermine: the server pins those three on save)
    is_blanko: false,
    // Ergänzendes Heilmittel: 'add_time' | 'arrive_early' | 'included' (SUPPLEMENTARY_MODES),
    // null = a normal row
    supplementary_mode: null,
    // Per-insurance-type acceptance toggles
    accepted_insurance_types: DEFAULT_ACCEPTED_INSURANCE
  });

  // Draft text for the alias chip input (typed but not yet committed as a chip)
  const [aliasDraft, setAliasDraft] = useState('');

  // Draft text for the symptom chip input (typed but not yet committed as a chip)
  const [symptomDraft, setSymptomDraft] = useState('');

  // Draft text of the PVS-Bezeichnung chip inputs, keyed by form field ('pvs_name' and
  // the five 'pvs_name_<insurance>' cells), typed but not yet committed as a chip
  const [pvsNameDrafts, setPvsNameDrafts] = useState({});

  // The dialog of an Ergänzendes Heilmittel: the phrase's minutes, the insurances that
  // differ from them and whether "Je Versicherungsart" is open (supplementaryDraftOf).
  // Turned into the five durations only when it is saved.
  const [suppDraft, setSuppDraft] = useState(SUPPLEMENTARY_DRAFT_NEW);

  // ---- Excluded ("Nicht angebotene") therapies state ----
  // Therapies the practice does NOT offer; the matcher anti-matches against them
  // and the bot politely declines + hangs up.
  const [excludedRows, setExcludedRows] = useState([]);            // [{id,name,aliases,reject_reason,enabled,...}] — all fully editable
  const [excludedLoading, setExcludedLoading] = useState(false);
  const [excludedError, setExcludedError] = useState('');
  const [excludedBusy, setExcludedBusy] = useState({});            // {id: bool} per-row in-flight guard
  // Add/edit form (null editingId => create). Separate alias draft so it never collides with `aliasDraft`.
  const [excludedModalOpen, setExcludedModalOpen] = useState(false);
  const [excludedEditingId, setExcludedEditingId] = useState(null);
  const [excludedForm, setExcludedForm] = useState({ name: '', aliases: [], reject_reason: '', enabled: true });
  const [excludedAliasDraft, setExcludedAliasDraft] = useState('');
  const [excludedSaving, setExcludedSaving] = useState(false);

  // Frequency pill className map — purely presentational, keyed by canonical value
  const FREQUENCY_CLASS_MAP = {
    'Täglich':           'freq-daily',
    'Alle zwei Tage':    'freq-biday',
    '1-3 mal pro Woche': 'freq-multiweek',
    'Wöchentlich':       'freq-weekly',
    'Alle zwei Wochen':  'freq-biweekly',
    'Alle 4-5 Wochen':   'freq-monthly',
  };

  // How many curated symptoms to preview inline in the therapy list row (rest folded into "+X", full list on hover)
  const SYMPTOM_PREVIEW = 4;

  // Fallback list mirroring the API response shape, used if /therapy-types/frequencies fetch fails
  const FREQUENCY_FALLBACK = [
    { value: 'Täglich',           display: 'Täglich' },
    { value: 'Alle zwei Tage',    display: 'Alle 2 Tage' },
    { value: '1-3 mal pro Woche', display: '1-3 mal pro Woche' },
    { value: 'Wöchentlich',       display: 'Wöchentlich' },
    { value: 'Alle zwei Wochen',  display: 'Alle 2 Wochen' },
    { value: 'Alle 4-5 Wochen',   display: 'Alle 4-5 Wochen' },
  ];

  // Frequency options fetched from backend on mount; falls back to FREQUENCY_FALLBACK on failure
  const [frequencyOptions, setFrequencyOptions] = useState(FREQUENCY_FALLBACK);

  // Insurance tab selector for the Behandlungsarten table view
  const [therapyInsuranceTab, setTherapyInsuranceTab] = useState('private');
  // Category (umbrella) rail for the Behandlungsarten table view. therapyCategories
  // is the practice's effective set of keys, categoryOptions the label source from
  // the server. The row count per key is NOT mirrored here: it is derived from
  // therapyTypes (see categoryCounts below), which every therapy write already
  // refreshes, so the dialog texts can never drift from the table.
  const [therapyCategories, setTherapyCategories] = useState([DEFAULT_THERAPY_CATEGORY]);
  const [categoryOptions, setCategoryOptions] = useState(THERAPY_CATEGORY_OPTIONS);
  const [therapyCategoryTab, setTherapyCategoryTab] = useState(DEFAULT_THERAPY_CATEGORY);
  const [therapySubView, setTherapySubView] = useState('main'); // 'main' | 'supplementary' (scoped to active category)
  // The practice whose category rail already opened on its first tab (see the
  // categories load in the practice hydration effect).
  const categoryTabSeededForRef = useRef(null);
  // null | { mode: 'add' | 'edit' | 'dropNew', key?, therapyId?, ... } — see the
  // category dialog; a 409 on rename enriches it with the merge fields instead of alerting.
  const [categoryDialog, setCategoryDialog] = useState(null);
  // In-flight lock for every category mutation (disables the rail controls and the dialog primary)
  const [categoryBusy, setCategoryBusy] = useState(false);
  // False once the categories GET 404s (new SPA against an old backend during a
  // deploy window): the rail then renders read-only from the distinct row categories.
  const [categoriesApiAvailable, setCategoriesApiAvailable] = useState(true);
  // Drag and drop of a therapy row onto a category tab: the ref carries the id
  // into the drop handler, the two states drive the row opacity and the tab highlight.
  const draggedTherapyIdRef = useRef(null);
  const [draggingTherapyId, setDraggingTherapyId] = useState(null);
  const [dropTargetCategory, setDropTargetCategory] = useState(null);
  // Drag and drop of a category TAB along the rail (reorder). Same dual tracker as
  // above: the ref carries the key into the handlers, the state drives the lifted
  // look. catDrop is the insertion marker — { index, x, top, height } measured from
  // the rail, so the bar is absolutely positioned and the rail never reflows.
  const catRailRef = useRef(null);
  const draggedCategoryRef = useRef(null);
  const [draggingCategory, setDraggingCategory] = useState(null);
  const [catDrop, setCatDrop] = useState(null);
  // In-flight lock for the "Akzeptiert" bulk column toggle (prevents the double-click race)
  const [togglingAllAccepted, setTogglingAllAccepted] = useState(false);
  // Per-cell click-to-edit state: { therapyId_fieldName: true }
  const [editingCell, setEditingCell] = useState(null); // "therapyId_field"
  const [cellEditValue, setCellEditValue] = useState('');
  // Frequenz pill dropdown state
  const [editingFrequenzId, setEditingFrequenzId] = useState(null);
  const [frequenzDropdownPos, setFrequenzDropdownPos] = useState({ top: 0, left: 0 });

  // Priority Matrix state
  const [priorityMatrix, setPriorityMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [newTherapistName, setNewTherapistName] = useState('');
  const [addingTherapist, setAddingTherapist] = useState(false);
  const [importingPvsTherapists, setImportingPvsTherapists] = useState(false);
  const [pvsImportResult, setPvsImportResult] = useState(''); // transient success chip beside the PVS-sync button
  const [openPriorityDropdown, setOpenPriorityDropdown] = useState(null); // key: "therapistId-ttId"
  const priorityDropdownRef = useRef(null);
  const [hoveredPriorityCell, setHoveredPriorityCell] = useState(null); // { therapistId, therapyTypeId }

  // Location Matrix state
  const [locationMatrix, setLocationMatrix] = useState(null);
  const [locationMatrixLoading, setLocationMatrixLoading] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [addingLocation, setAddingLocation] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ faq: '', pvs_name: '' });
  // Inline FAQ editor (lifted from the location modal so it lives at the top
  // of the practice tab without forcing the user to dig into Standorte).
  const [faqLocationId, setFaqLocationId] = useState(null);
  const [faqDraft, setFaqDraft] = useState('');
  const [faqSaving, setFaqSaving] = useState(false);
  const faqAutoCreateRef = useRef(false);

  // Therapist detail modal state
  const [therapistModalOpen, setTherapistModalOpen] = useState(false);
  const [editingTherapist, setEditingTherapist] = useState(null);
  const [therapistForm, setTherapistForm] = useState({
    name: '', pvs_name: '', phone: '', email: '', notify_short_notice_changes: false, notify_channel: 'sms', slack_user_id: '',
    // Patientenfilter (models.Therapist.patient_filter). Empty list = no restriction
    // on that dimension, which is what every therapist starts with.
    pf_symptoms: [], pf_age_groups: [], pf_genders: [...PF_GENDER_KEYS], pf_insurance_types: [...INSURANCE_KEYS],
  });
  const [therapistSymptomDraft, setTherapistSymptomDraft] = useState('');
  const [therapistFormError, setTherapistFormError] = useState('');
  // Undefined for SMS: that message goes to the Telefonnummer row.
  const therapistChannelField = THERAPIST_CHANNEL_FIELD[therapistForm.notify_channel];

  // Therapist-Location Matrix state
  const [therapistLocationMatrix, setTherapistLocationMatrix] = useState(null);
  const [therapistLocationMatrixLoading, setTherapistLocationMatrixLoading] = useState(false);

  // Zuordnung tab (therapists vs locations vs therapist-locations)
  const [zuordnungTab, setZuordnungTab] = useState('therapists');

  // Calendar JSON import state
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [pvsImportLoading, setPvsImportLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [sipConfig, setSipConfig] = useState({
    server_address: '',
    username: '',
    password: '',
    port: '',
    enabled: false,
    proxy_url: '',
    sip_phone_number: '',
    enable_multi_call: false,
    parallel_call_mode: 'voicemail'
  });
  
  const [saveStatus, setSaveStatus] = useState({ 
    practice: { saved: false, message: '' },
    sip: { saved: false, message: '', error: false }
  });
  
  const [testStatus, setTestStatus] = useState({
    testing: false,
    success: false,
    message: ''
  });

  const [pvsHealthStatus, setPvsHealthStatus] = useState({
    testing: false,
    success: false,
    message: ''
  });

  // The last PVS the worker identified itself as. Loaded from the saved config so
  // it survives a reload, refreshed on every click of "PVS ermitteln".
  const [pvsSystem, setPvsSystem] = useState({ name: '', detectedAt: '' });
  // `message` is only ever set on failure, so the display hardcodes the error style.
  const [pvsSystemStatus, setPvsSystemStatus] = useState({ loading: false, message: '' });

  // State for Outbound Call Test Modal
  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [outboundPhoneNumber, setOutboundPhoneNumber] = useState('');
  const [outboundCallStatus, setOutboundCallStatus] = useState({ 
    testing: false, 
    success: false, 
    message: '' 
  });
  
  const [userId, setUserId] = useState(null);
  // practiceId now comes from usePractice() (declared at top of component).
  const [sipConfigId, setSipConfigId] = useState(null);
  // Session 10d regression guard — see the auto-save useEffect below.
  const practiceInfoLoadedRef = useRef(false);
  // Cross-tenant write guard (Jul 2026 incident): the practice id the form's
  // data was actually LOADED for. Null while a load is in flight. Every save
  // handler and the auto-save must verify it equals the live practiceId before
  // writing — otherwise a stale load (data from practice A landing after a
  // switch to B) gets persisted under the wrong practice. The ref is the
  // authoritative value for handlers; the state mirror drives button disabling.
  const loadedForPracticeIdRef = useRef(null);
  const [formLoadedFor, setFormLoadedFor] = useState(null);
  // Per-section load completion (practice id each section's data was loaded
  // for). Drive the per-tab LoadingShroud overlays: each tab unlocks as soon
  // as ITS data arrives, so a slow loader never blocks an unrelated tab.
  // Null while in flight; reset on practice switch.
  const [callflowLoadedFor, setCallflowLoadedFor] = useState(null);
  const [messagesLoadedFor, setMessagesLoadedFor] = useState(null);
  const [sipLoadedFor, setSipLoadedFor] = useState(null);
  const [pvsLoadedFor, setPvsLoadedFor] = useState(null);
  // Practice id the `therapists` list was fetched for; null while a roster fetch is
  // in flight and after a failed one. Gates the roster clamp of maxTherapistsPerSeries.
  const [therapistsLoadedFor, setTherapistsLoadedFor] = useState(null);

  // --- Live config sync (see hooks/useConfigSync.js) -----------------------
  // Bumping a nonce re-runs the corresponding hydration effect. Both hydrators
  // read through React Query's 3-minute cache, so a re-run alone would be a
  // cache no-op — the reload helpers below invalidate FIRST, then bump.
  const [practiceReloadNonce, setPracticeReloadNonce] = useState(0);
  const [callflowReloadNonce, setCallflowReloadNonce] = useState(0);
  // Which practice the hydration effect last ran for. Lets it tell a practice
  // SWITCH (blank everything first — data must never cross tenants) apart from a
  // same-practice refresh (must be invisible: no blanking, no scroll jump).
  const lastHydratedPracticeRef = useRef(null);
  // Unsaved-input flags. A remote change never overwrites a tab that has drafts;
  // it raises a banner instead. Set by the change handlers, cleared on save and
  // on reload. Deliberately per-tab, not per-field: it only decides
  // "silently reload" vs "ask first", so coarse is the safe direction.
  const [practiceTabDirty, setPracticeTabDirty] = useState(false);
  const [callflowTabDirty, setCallflowTabDirty] = useState(false);

  // Session 10c: Mitglieder tab state. Members table + invite form + inline username edit.
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  // Ticked by default: a new member is assignable in the Tasks page unless the
  // owner unticks it (e.g. the practice boss, who reads tasks but is never assigned).
  const [inviteAssignable, setInviteAssignable] = useState(true);
  const [membersError, setMembersError] = useState('');
  const [membersSuccess, setMembersSuccess] = useState('');
  // Reveal-once temporary-password card. Lives only in component state, so a
  // page refresh (or leaving the members tab) makes it vanish — re-reveal by
  // generating/resetting again. Shape: { username, password }.
  const [revealedCredential, setRevealedCredential] = useState(null);
  const [credentialCopied, setCredentialCopied] = useState(false);
  const credentialCopyTimerRef = useRef(null);
  const [sipCopied, setSipCopied] = useState(false);
  const sipCopyTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (credentialCopyTimerRef.current) clearTimeout(credentialCopyTimerRef.current);
      if (sipCopyTimerRef.current) clearTimeout(sipCopyTimerRef.current);
    };
  }, []);
  const [editingUsername, setEditingUsername] = useState(null);
  const [editUsernameValue, setEditUsernameValue] = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [editFirstNameValue, setEditFirstNameValue] = useState('');
  const [editLastNameValue, setEditLastNameValue] = useState('');
  // Import-from-other-practice panel (super_admin or the practice owner). Lets
  // the caller reuse an existing owner/member of another practice they are
  // assigned to, instead of typing the email again. Uses the existing POST
  // members endpoint, which already short-circuits on existing-email + adds a
  // fresh user_practices row.
  // Names prefixed with importMember to avoid clashing with the unrelated
  // PVS import-CSV state (importLoading) elsewhere in this file.
  const [importMemberOpen, setImportMemberOpen] = useState(false);
  const [importMemberSourceId, setImportMemberSourceId] = useState('');
  const [importMemberSourceList, setImportMemberSourceList] = useState([]);
  const [importMemberLoading, setImportMemberLoading] = useState(false);
  const [importMemberInFlight, setImportMemberInFlight] = useState(null);

  // Define the correct order of days
  const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  // German weekday labels (shared by the reception-hours editor + validation).
  const DAY_NAME_MAP = {
    monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch',
    thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag',
  };

  // Inbound test call state
  const [inboundTestStatus, setInboundTestStatus] = useState({ status: 'idle', message: '' });
  const [inboundTestPolling, setInboundTestPolling] = useState(false);
  const inboundTestIntervalRef = React.useRef(null);

  // Forwarding test call state
  const [forwardingTestStatus, setForwardingTestStatus] = useState({ status: 'idle', message: '' });
  const [forwardingPhoneNumber, setForwardingPhoneNumber] = useState('');
  const [forwardingTestPolling, setForwardingTestPolling] = useState(false);
  const forwardingTestIntervalRef = React.useRef(null);

  // LLM config state - Provider selection and model configuration
  const [provider, setProvider] = useState('bedrock'); // 'bedrock' or 'azure'
  
  // Bedrock configuration - Dual model support
  const [primaryModel, setPrimaryModel] = useState(''); // For tool routing decisions
  const [secondaryModel, setSecondaryModel] = useState(''); // For human response generation
  const [llmModel, setLlmModel] = useState(''); // Legacy support
  
  // Azure OpenAI configuration - dynamic connections and use-case mapping
  const [azureConnections, setAzureConnections] = useState([]);
  const [azureUseCaseMapping, setAzureUseCaseMapping] = useState({});
  // Per-connection show/hide API key state: { connId: bool }
  const [azureKeyVisible, setAzureKeyVisible] = useState({});
  // Drag-to-reorder state for Azure connection cards
  const azureDragIndexRef = useRef(null);         // ref for synchronous access inside handlers
  const [azureDraggingIndex, setAzureDraggingIndex] = useState(null); // state for re-render (opacity)

  // Gemini configuration (Vertex AI with EU region)
  const [geminiServiceAccountJson, setGeminiServiceAccountJson] = useState('');  // Service account JSON
  const [geminiProjectId, setGeminiProjectId] = useState('');           // GCP Project ID
  const [geminiRegion, setGeminiRegion] = useState('europe-west1');     // EU region for GDPR
  const [geminiPrimaryModel, setGeminiPrimaryModel] = useState('');     // Smart 1 - Intent
  const [geminiSecondaryModel, setGeminiSecondaryModel] = useState(''); // Mini 1 - Action
  const [geminiTertiaryModel, setGeminiTertiaryModel] = useState('');   // Smart 2 - Slots
  const [geminiQuaternaryModel, setGeminiQuaternaryModel] = useState(''); // Mini 2 - Chat

  const [persistLlmConfig, setPersistLlmConfig] = useState(true);
  const [llmSaveStatus, setLlmSaveStatus] = useState({ saved: false, message: '' });
  const [kiMode, setKiMode] = useState('smart_ab'); // KI-Modus: 'smart_ab' = info collection (default), 'digital_ab' = voicemail only, 'langgraph' = KI-Praxisassistent, 'agentic' = LEGACY (hidden, do not use)
  const [requireEmail, setRequireEmail] = useState(false); // NEW: Email requirement toggle for booking flow
  const [requireBirthdate, setRequireBirthdate] = useState(true); // NEW: Birthdate requirement toggle for booking flow
  const [requirePhone, setRequirePhone] = useState(true); // NEW: Phone requirement toggle for booking flow
  const [requireAddress, setRequireAddress] = useState(false); // NEW: Home-address collection toggle for NEW patients (refusal blocks booking → reception handles)
  const [trustCallerId, setTrustCallerId] = useState(true); // Trust caller ID from SIP or always ask for phone
  const [rejectCallerIds, setRejectCallerIds] = useState([]); // Per-number carve-out: caller IDs to NEVER trust (always ask + hide from task page). Only relevant when trustCallerId is on.
  const [rejectDraft, setRejectDraft] = useState(''); // Draft text for the reject-number chip input
  const [requireVerordnung, setRequireVerordnung] = useState(false); // NEW: Verordnung (prescription) requirement toggle for booking flow
  const [allowMultiTherapistSeries, setAllowMultiTherapistSeries] = useState(false); // NEW: Allow multi-appointment series to span multiple therapists
  const [maxTherapistsPerSeries, setMaxTherapistsPerSeries] = useState(3); // Total distinct therapists allowed in one series (primary + borrowed)
  // "alle" = no practical cap: the borrow chain can only ever draw on therapists that
  // actually exist, so any number above the roster size means "all of them". A sentinel
  // keeps this frontend-only — the backend needs no new semantics.
  // NOT 0: the backend reads 0 as the OPPOSITE (max(1, cap) -> 1, blocking every borrow).
  const MAX_THERAPISTS_ALL = 99;
  const [requireInsuranceCompany, setRequireInsuranceCompany] = useState(false); // NEW: Insurance company (Krankenkasse) requirement toggle for booking flow
  const [requireWhatsappOptin, setRequireWhatsappOptin] = useState(false);
  const [enableForwarding, setEnableForwarding] = useState(true); // GENERAL forwarding: caller just wants a human -> practice-wide number
  const [enableKeywordForwarding, setEnableKeywordForwarding] = useState(true); // KEYWORD forwarding: a configured rule -> that rule's number. Independent of the general switch.
  const [forwardNoanswerVoicemail, setForwardNoanswerVoicemail] = useState(false); // A forward nobody takes goes to the practice's answering machine
  const [forwardNoanswerRingSeconds, setForwardNoanswerRingSeconds] = useState(10); // How long that forward rings first
  const [enableDirectForwarding, setEnableDirectForwarding] = useState(false); // DIRECT forwarding: every caller goes to directForwardingNumber before the bot answers.
  const [directForwardingNumber, setDirectForwardingNumber] = useState('');
  const [enableWhitelistForwarding, setEnableWhitelistForwarding] = useState(false); // WHITELIST forwarding: only callers on whitelist_caller_ids go to whitelistForwardingNumber before the bot answers.
  const [whitelistForwardingNumber, setWhitelistForwardingNumber] = useState('');
  const [whitelistEntries, setWhitelistEntries] = useState([]); // [{number, name, type}], stored via whitelistEntriesToConfig
  // Per-tenant trigger keywords. UI keeps `keywords` as comma-separated string for natural typing.
  const [triggerKeywords, setTriggerKeywords] = useState({
    forwarding_rules: [], // [{keywords: "Augenarzt, Sehtest", phone_number: "0123", message: "optional"}, ...]
    smartab_keywords: ''  // "beschwerde, reklamation"
  });
  // Per-row draft text for chip-input keyword fields, keyed by rule index.
  const [keywordDrafts, setKeywordDrafts] = useState({});
  const [smartabDraft, setSmartabDraft] = useState('');
  const [langgraphCancelEnabled, setLanggraphCancelEnabled] = useState(true); // Per-flow: cancel stays in LangGraph
  const [langgraphMoveEnabled, setLanggraphMoveEnabled] = useState(true); // Per-flow: move stays in LangGraph
  const [langgraphBookEnabled, setLanggraphBookEnabled] = useState(true); // Per-flow: booking stays in LangGraph
  const [cancellationFeePeriod, setCancellationFeePeriod] = useState(0); // Hours before appointment when cancellation fee applies (0 = disabled)
  const [cancellationFeeNotice, setCancellationFeeNotice] = useState('Es kann eine Ausfallgebühr anfallen'); // Middle sentence injected into cancellation fee warning messages
  const [editingFeeNotice, setEditingFeeNotice] = useState(false);
  const [consentQuestion, setConsentQuestion] = useState('Bevor wir loslegen darf ich das Gespräch transkribieren und aufzeichnen, damit das Praxisteam es bearbeiten kann?'); // Consent question appended to first-message filler
  const [consentUpfront, setConsentUpfront] = useState(false); // Upfront consent: the consent question plays right after the greeting (KI-Praxisassistent only)
  const [dataRetentionEnabled, setDataRetentionEnabled] = useState(false); // Data retention: the nightly job deletes a call's personal data after dataRetentionDays
  const [dataRetentionDays, setDataRetentionDays] = useState(30); // Data retention period in days (7-999)
  const [forwardingMessage, setForwardingMessage] = useState(''); // Practice-wide sentence spoken before a transfer. Empty = use the standard sentence (the pre-synthesized one).
  const [patientIdentificationMethod, setPatientIdentificationMethod] = useState('birthdate'); // Which field to use for caller identification: 'phone' or 'birthdate' (default 'birthdate')
  const [bookUnidentifiedReturningAsNew, setBookUnidentifiedReturningAsNew] = useState(false); // Booking: a caller who says they were here before but is not found in the PVS continues as a new patient instead of escaping to SmartAB
  const [smsToCallerEnabled, setSmsToCallerEnabled] = useState(false); // Send SMS confirmation to the caller after booking/cancellation
  const [smsToNumberEnabled, setSmsToNumberEnabled] = useState(false); // Send SMS confirmation to a fixed practice number (silent path)
  const [smsNotificationPhoneNumber, setSmsNotificationPhoneNumber] = useState(''); // Target phone number for the sms_to_number path
  const [smsProvider, setSmsProvider] = useState('seven'); // PRIMARY SMS gateway — GLOBAL; the other one is the automatic fallback
  const [sevenApiKey, setSevenApiKey] = useState(''); // seven.io token — GLOBAL, one account for all practices
  const [smsSenderId, setSmsSenderId] = useState(''); // Displayed SMS sender, per practice (seven.io only; empty = DeskBot)
  const [emailConfirmationEnabled, setEmailConfirmationEnabled] = useState(false); // Send Email confirmation after booking/cancellation
  const [callAmbienceEnabled, setCallAmbienceEnabled] = useState(false); // Play office MOH ambience on the caller leg during the call (per-practice)
  const [callAmbienceSound, setCallAmbienceSound] = useState('office1'); // Which preset ambience sound to play (per-practice): 'office1' | 'office2'
  const [acceptableCancellationReasonsEnabled, setAcceptableCancellationReasonsEnabled] = useState(false); // Enable acceptable cancellation reasons feature
  const [acceptableCancellationReasons, setAcceptableCancellationReasons] = useState(''); // Comma-separated acceptable reasons
  const [reasonsDraft, setReasonsDraft] = useState('');
  const [mentionTherapistInCancelMove, setMentionTherapistInCancelMove] = useState(true); // Include therapist name in cancel/move propose phrasing
  const [askCancelMoveReasonEnabled, setAskCancelMoveReasonEnabled] = useState(false); // Proactively ask the caller why they cancel/move (never insists, never re-asks)
  const [askSymptomsEnabled, setAskSymptomsEnabled] = useState(true); // Ask for the caller's Beschwerden; a match with therapists' Symptome narrows the pool, a refusal moves on
  const [enableDualVad, setEnableDualVad] = useState(true); // Dual-VAD toggle (True = Asterisk + Silero, False = Asterisk only)
  const [enableMultiLocation, setEnableMultiLocation] = useState(false); // Multi-location toggle
  const [slotRasterEnabled, setSlotRasterEnabled] = useState(false); // Terminraster: snap proposed slot starts to a fixed grid
  const [slotRasterMinutes, setSlotRasterMinutes] = useState(30); // Grid size: 15 (:00/:15/:30/:45), 20 (:00/:20/:40), 30 (:00/:30) or 60 (:00 only)
  const [sameDayCutoffEnabled, setSameDayCutoffEnabled] = useState(false); // Past the cutoff time, no slots for the day of the call are offered
  const [sameDayCutoffTime, setSameDayCutoffTime] = useState('15:00'); // Local HH:MM cutoff

  // --- Feiertage & Schließtage (holiday closures) ---
  // Self-contained state for the holidays sub-tab inside the Öffnungszeiten
  // card. Loaded lazily when the practice tab is active (see effect below).
  // Holidays are NOT booking blockers; on enabled days the assistant only
  // announces callbacks without a time promise.
  const [hoursCardTab, setHoursCardTab] = useState('opening'); // 'opening' | 'reception' | 'holidays'
  const [closuresLoadedFor, setClosuresLoadedFor] = useState(null);
  const [holidaysData, setHolidaysData] = useState(null); // {region, year, available_regions, holidays:[]} | null
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidaysError, setHolidaysError] = useState(''); // top-level (load/sync) error line
  const [holidaysSyncing, setHolidaysSyncing] = useState(false); // Bundesland sync in flight
  const [holidayRowBusy, setHolidayRowBusy] = useState({}); // {closureId: true} per-row in-flight flag
  const [holidayRowError, setHolidayRowError] = useState({}); // {closureId: 'message'} per-row inline error
  const [holidayRangeOpen, setHolidayRangeOpen] = useState({}); // {closureId: true} reveal end-date input for a single-day entry

  // --- Empfangszeiten (weekly reception hours) ---
  // When the front desk actually answers phones. Outside these hours the bot
  // softens its callback promise (backend already implemented). Stored on the
  // practice as the SAME JSON shape as opening_hours ({monday..sunday:{open,
  // close,closed}}); NULL = not configured = feature off. Editor state is
  // separate from practiceInfo.opening_hours and never writes back to it;
  // persisted on its own by the reception-hours auto-save (PUT .../reception-hours).
  const [receptionHours, setReceptionHours] = useState(null); // editor state: {monday..sunday:{open,close,closed}} | null
  const [receptionEnabled, setReceptionEnabled] = useState(false); // mirrors receptionHours != null while editing (master switch)
  const [receptionLoading, setReceptionLoading] = useState(false); // GET in flight
  const [receptionDirty, setReceptionDirty] = useState(false);
  // In-flight flags for the four top-level Save buttons — drive the spinner
  // (SaveStatusSlot) and the disabled guard. One per async handler; llmSaving
  // is shared by the Anrufablauf + KI buttons (both call handleLlmConfigSave).
  const [practiceSaving, setPracticeSaving] = useState(false);
  const [sipSaving, setSipSaving] = useState(false);
  const [llmSaving, setLlmSaving] = useState(false);
  const [receptionError, setReceptionError] = useState(''); // inline error (PUT 400 detail / network / client guard)

  // --- Praxis-Informationen Auto-Save Refs --------------------------------
  const isMountedRef = useRef(true);
  const practiceBaselineRef = useRef(null);
  const pendingPracticeWriteRef = useRef(null);
  const practiceAutoSaveTimerRef = useRef(null);
  const practiceInfoRef = useRef(practiceInfo);
  practiceInfoRef.current = practiceInfo;
  const saveStatusClearTimerRef = useRef(null);

  const receptionHoursRef = useRef(receptionHours);
  receptionHoursRef.current = receptionHours;
  const pendingReceptionWriteRef = useRef(null);
  const receptionSaveTimerRef = useRef(null);

  const pendingFaqWriteRef = useRef(null);
  const faqSaveTimerRef = useRef(null);

  // Two-click delete confirmation (mirrors TasksPage). The key encodes both
  // entity type and id so all delete buttons share one confirm slot.
  const [confirmingDeleteKey, setConfirmingDeleteKey] = useState(null);
  const confirmDeleteTimerRef = useRef(null);
  const requestDelete = (key, deleteFn) => {
    if (confirmingDeleteKey === key) {
      if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
      confirmDeleteTimerRef.current = null;
      setConfirmingDeleteKey(null);
      deleteFn();
      return;
    }
    if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
    setConfirmingDeleteKey(key);
    confirmDeleteTimerRef.current = setTimeout(() => setConfirmingDeleteKey(null), 4000);
  };
  useEffect(() => () => {
    if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
  }, []);

  // If multi-location is disabled, the Standorte / Therapeuten & Standorte
  // tabs are hidden — keep the active tab in a valid state.
  useEffect(() => {
    if (!enableMultiLocation && zuordnungTab !== 'therapists') {
      setZuordnungTab('therapists');
    }
  }, [enableMultiLocation, zuordnungTab]);

  // Insurance type settings (per-insurance-type Verordnungsfrist and max Terminabstand)
  const [insuranceTypeSettings, setInsuranceTypeSettings] = useState({
    private: { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false },
    public: { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false },
    selbstzahler: { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false },
    bg: { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false },
    entlassmanagement: { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false },
  });
  const [insuranceSettingsModalOpen, setInsuranceSettingsModalOpen] = useState(false);
  const [insuranceSettingsModalTab, setInsuranceSettingsModalTab] = useState(null); // which insurance type is being edited
  const [insuranceSettingsForm, setInsuranceSettingsForm] = useState({ verordnungsfrist_days: '', max_terminabstand_days: '', new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false });
  // Collapsible "Defaults aller Verordnungstypen" matrix toggle.
  const [defaultsMatrixOpen, setDefaultsMatrixOpen] = useState(false);
  const [matrixFormUrlDraft, setMatrixFormUrlDraft] = useState(null);
  // Per-Heilmittelgruppe Verordnungstyp settings: { scope, by_category }. Its own
  // practice column and its own endpoint, so a stale whole-row PUT cannot clobber
  // it. An untouched practice stores {} and behaves exactly as before.
  const [therapyCategorySettings, setTherapyCategorySettings] = useState({});
  // False against a backend that does not know the field (deploy window): the GET
  // then omits it and the write endpoint would 404, so the switch must not render.
  const [categorySettingsApiAvailable, setCategorySettingsApiAvailable] = useState(false);
  // Which Heilmittelgruppe the matrix edits while the switch is off. Seeded from the
  // rail's active category when the dialog opens; clamped to the live list below.
  const [matrixCategoryTab, setMatrixCategoryTab] = useState(DEFAULT_THERAPY_CATEGORY);
  // Every write this dialog makes is chained onto the previous one, so a burst of
  // keystrokes lands in order and "Speichern" can simply await the tail. The counter
  // drives the button's in-flight label, the sequence number decides which response
  // is still the newest and may overwrite the optimistic state.
  const matrixWriteChainRef = useRef(Promise.resolve());
  const matrixWriteSeqRef = useRef(0);
  const [matrixWritesPending, setMatrixWritesPending] = useState(0);

  // DOC-IQ connection of this practice ({enabled, praxis_name}): on once the practice
  // connected in DOC-IQ, checked and switched off by our admins on the PVS tab.
  // Fetched only while a screen that shows it is open, and
  // tagged with its practice so a late answer is never shown under another one.
  const [docIqStatusState, setDocIqStatusState] = useState({ for: null, data: null, failed: false });
  const [docIqBusy, setDocIqBusy] = useState(false);
  const [docIqError, setDocIqError] = useState({ for: null, text: '' });
  const docIqStatus = docIqStatusState.for === practiceId ? docIqStatusState.data : null;
  const docIqStatusShown = defaultsMatrixOpen || (userIsSuperAdmin && activeTab === 'pvs');
  useEffect(() => {
    if (!practiceId || !docIqStatusShown) return;
    let cancelled = false;
    axios.get(`${API_BASE_URL}/doc-iq/practice/${practiceId}/status`)
      .then(res => { if (!cancelled) setDocIqStatusState({ for: practiceId, data: res.data || null, failed: false }); })
      .catch(err => {
        console.warn('Failed to load DOC-IQ status:', err);
        if (!cancelled) setDocIqStatusState({ for: practiceId, data: null, failed: true });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, docIqStatusShown]);

  // The practice on screen right now, read when a PUT answers: the status has one
  // slot, so a late answer for the practice the admin just left must not overwrite
  // the status already loaded for the one they switched to.
  const docIqLivePracticeRef = useRef(practiceId);
  useEffect(() => { docIqLivePracticeRef.current = practiceId; }, [practiceId]);

  // Super-admin switch. Turning it on makes the backend ask DOC-IQ first, so it only
  // sticks once the practice has linked DeskBot on their side; when it does not, the
  // backend's German reason (409/502/503) is shown under the button.
  const handleDocIqConnection = async (enabled) => {
    const pid = practiceId;
    setDocIqBusy(true);
    setDocIqError({ for: pid, text: '' });
    try {
      const res = await axios.put(`${API_BASE_URL}/doc-iq/practice/${pid}/connection`, { enabled });
      if (docIqLivePracticeRef.current === pid) {
        setDocIqStatusState({ for: pid, data: res.data || null, failed: false });
      }
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setDocIqError({
        for: pid,
        text: [409, 502, 503].includes(code) && typeof detail === 'string'
          ? detail
          : 'Die DOC-IQ-Verbindung konnte nicht geändert werden.',
      });
    } finally {
      setDocIqBusy(false);
    }
  };

  const [greetingMessage, setGreetingMessage] = useState(''); // NEW: Customizable greeting message per practice
  const [farewellGeneral, setFarewellGeneral] = useState(''); // NEW: General farewell message
  const [farewellAppointment, setFarewellAppointment] = useState(''); // NEW: Appointment-specific farewell message
  const [voicemailMessage, setVoicemailMessage] = useState(''); // NEW: Voicemail announcement when AI is busy
  const [holdMessage, setHoldMessage] = useState(''); // Hold-in-line "please hold" message (parallel_call_mode='hold')
  const [greetingMessagePvs, setGreetingMessagePvs] = useState(''); // Personalized greeting for PVS-recognized callers
  const [greetingMessageAway, setGreetingMessageAway] = useState(''); // Optional greeting played while reception is unavailable (absence/holiday/outside reception+opening hours)
  const [greetingMessagePvsAway, setGreetingMessagePvsAway] = useState(''); // Optional PVS greeting variant for the reception-unavailable case
  const [enableGreetingAway, setEnableGreetingAway] = useState(false);
  const [enableGreetingPvs, setEnableGreetingPvs] = useState(false);
  const [enableFarewellAppointment, setEnableFarewellAppointment] = useState(false);
  const storedVariantFlagsRef = useRef({});

  // PVS Integration config state
  const [pvsEnabled, setPvsEnabled] = useState(false);
  const [pvsBaseUrl, setPvsBaseUrl] = useState('');
  const [pvsApiKey, setPvsApiKey] = useState('');
  const [pvsDebugMode, setPvsDebugMode] = useState('off');
  const [pvsGchatWebhookUrl, setPvsGchatWebhookUrl] = useState('');
  const [pvsSlackBotToken, setPvsSlackBotToken] = useState('');
  const [pvsTimeoutFind, setPvsTimeoutFind] = useState(30);
  const [pvsTimeoutFreeslots, setPvsTimeoutFreeslots] = useState(20);
  const [pvsTimeoutFindAppointments, setPvsTimeoutFindAppointments] = useState(45);
  const [pvsTimeoutBook, setPvsTimeoutBook] = useState(120);
  const [pvsTimeoutCancel, setPvsTimeoutCancel] = useState(60);
  const [pvsTimeoutReschedule, setPvsTimeoutReschedule] = useState(30);
  const [pvsTimeoutCreatePatient, setPvsTimeoutCreatePatient] = useState(90);
  const [pvsSyncTimeoutSeconds, setPvsSyncTimeoutSeconds] = useState(10);
  const [pvsAdvancedTimeoutsOpen, setPvsAdvancedTimeoutsOpen] = useState(false);
  const [pvsSaveStatus, setPvsSaveStatus] = useState({ saved: false, message: '' });

  // Google Sheet publisher (for book/cancel/move issue tasks)
  const [gsheetEnabled, setGsheetEnabled] = useState(false);
  const [gsheetServiceAccountJson, setGsheetServiceAccountJson] = useState('');
  const [gsheetSheetId, setGsheetSheetId] = useState('');
  const [gsheetJsonError, setGsheetJsonError] = useState('');

  // Voice AI config state - STT and TTS provider selection
  const [sttProvider, setSttProvider] = useState('aws'); // 'aws' or 'azure'
  const [ttsProvider, setTtsProvider] = useState('aws'); // 'aws' or 'azure'

  // AWS Credentials for Transcribe/Polly
  const [awsAccessKeyId, setAwsAccessKeyId] = useState('');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState('');

  // Azure Speech credentials (shared for both STT and TTS)
  const [azureSpeechKey, setAzureSpeechKey] = useState('');
  const [azureSpeechRegion, setAzureSpeechRegion] = useState('');

  // Azure TTS voice selection (credentials reuse azureSpeechKey/Region)
  const [azureTtsVoice, setAzureTtsVoice] = useState('de-DE-Seraphina:DragonHDLatestNeural');

  // AWS Polly voice selection
  const [awsPollyVoice, setAwsPollyVoice] = useState('Daniel');
  const [awsPollyUseGenerative, setAwsPollyUseGenerative] = useState(false);

  // Batch transcription config (for unclear_answer and voicemail)
  const [batchTranscribeProvider, setBatchTranscribeProvider] = useState('gpt-transcribe'); // 'azure', 'gpt4o', 'gpt-transcribe', or 'voxtral'
  const [gpt4oEmergencyDeployment, setGpt4oEmergencyDeployment] = useState('gpt-4o-mini-transcribe');
  const [emergencyTranscribeProvider, setEmergencyTranscribeProvider] = useState('gpt-transcribe'); // 'gpt4o', 'gpt4o-mini', 'gpt-transcribe', or 'voxtral'

  // Mistral AI credentials (for Voxtral STT)
  const [mistralApiKey, setMistralApiKey] = useState('');

  // ElevenLabs TTS configuration
  const [elevenlabsApiKey, setElevenlabsApiKey] = useState('');
  const [deepgramApiKey, setDeepgramApiKey] = useState(''); // Deepgram Flux STT
  const [elevenlabsVoiceId, setElevenlabsVoiceId] = useState('XB0fDUnXU5powFXDhCwa');
  const [elevenlabsModelId, setElevenlabsModelId] = useState('eleven_flash_v2_5');

  // Kugel Audio TTS configuration (voice ids are integers, sent as strings here)
  const [kugelApiKey, setKugelApiKey] = useState('');
  const [kugelVoiceId, setKugelVoiceId] = useState('1704');
  const [kugelModelId, setKugelModelId] = useState('kugel-3');
  const [kugelUseWebsocket, setKugelUseWebsocket] = useState(false);

  // Voice preview: fetch the selected ElevenLabs/Kugel voice's cached sample and play it.
  // The server synthesizes the sample once per voice (Flash) then caches it, so
  // auditioning never triggers the full per-practice pre-synthesis.
  const [voicePreviewLoading, setVoicePreviewLoading] = useState(false);
  const currentPreviewAudioRef = useRef(null);
  const stopCurrentPreview = () => {
    const prev = currentPreviewAudioRef.current;
    if (prev) {
      prev.pause();
      if (prev.src) URL.revokeObjectURL(prev.src);
      currentPreviewAudioRef.current = null;
    }
  };
  const playVoicePreview = async (previewVoiceId) => {
    if (!previewVoiceId || voicePreviewLoading) return;
    // Stop any preview already playing so they don't overlap.
    stopCurrentPreview();
    let audioUrl;
    try {
      setVoicePreviewLoading(true);
      // cache-buster (?t=) so the browser never replays a stale preview after the
      // voice tuning changes and the server regenerates the file.
      const res = await axios.get(`${API_BASE_URL}/tts/voice-preview/${previewVoiceId}?t=${Date.now()}`, { responseType: 'blob' });
      audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      currentPreviewAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        if (currentPreviewAudioRef.current === audio) currentPreviewAudioRef.current = null;
      };
      audio.onerror = () => URL.revokeObjectURL(audioUrl);
      await audio.play();
    } catch (err) {
      console.error('Voice preview failed:', err);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    } finally {
      setVoicePreviewLoading(false);
    }
  };

  // Pre-synthesized message previews (greeting/farewell/voicemail/hold): play
  // the ACTUAL cached call audio. The backend resolves the practice's current
  // provider+voice directory at request time (same wiring as the call path),
  // so a provider/voice switch automatically serves the matching file.
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(null); // message key or null
  const [messagePreviewMissing, setMessagePreviewMissing] = useState(null); // key whose audio isn't generated yet
  // Keys currently being re-synthesized by the save flow — their play buttons
  // grey out until the corresponding pre-synthesize POST resolves.
  const [regeneratingMessages, setRegeneratingMessages] = useState(new Set());
  const markRegenerating = (keys, on) => {
    setRegeneratingMessages(prev => {
      const next = new Set(prev);
      keys.forEach(k => (on ? next.add(k) : next.delete(k)));
      return next;
    });
  };
  const playMessagePreview = async (messageKey) => {
    if (!practiceId || messagePreviewLoading || regeneratingMessages.has(messageKey)) return;
    stopCurrentPreview();
    let audioUrl;
    try {
      setMessagePreviewLoading(messageKey);
      setMessagePreviewMissing(null);
      // cache-buster (?t=) — the file changes on every save under the same URL.
      const res = await axios.get(`${API_BASE_URL}/tts/message-preview/${practiceId}/${messageKey}?t=${Date.now()}`, { responseType: 'blob' });
      audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      currentPreviewAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        if (currentPreviewAudioRef.current === audio) currentPreviewAudioRef.current = null;
      };
      audio.onerror = () => URL.revokeObjectURL(audioUrl);
      await audio.play();
    } catch (err) {
      if (err.response?.status === 404) setMessagePreviewMissing(messageKey);
      console.error('Message preview failed:', err);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    } finally {
      setMessagePreviewLoading(null);
    }
  };
  // Both buttons in a message heading share this: the house icon-button chrome
  // plus a fixed box. Without the fixed width they come out different sizes — a
  // ▶ text glyph is far narrower than the 15px refresh SVG beside it.
  const msgBtnStyle = (disabled) => ({
    ...iconBtnStyle(disabled),
    // Fixed box, border-box: the glyph button is sized by line-height while the
    // SVG one is sized by its 15px icon, so left to themselves they come out
    // 32x26 and 33x17. Centring happens in the flex box instead of the padding.
    boxSizing: 'border-box',
    width: '2rem',
    height: '1.625rem',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  // Compact ▶ button rendered inside each message heading. Greys out (disabled,
  // but still a ▶) while that message's audio is being regenerated — the hourglass
  // belongs to the save/refresh button beside it, which is the one doing the work.
  const msgPreviewBtn = (messageKey) => {
    const regenerating = regeneratingMessages.has(messageKey);
    const loading = messagePreviewLoading === messageKey;
    const disabled = regenerating || loading;
    return (
      <button
        type="button"
        onClick={() => playMessagePreview(messageKey)}
        disabled={disabled}
        title={
          regenerating ? 'Audio wird neu generiert…'
            : messagePreviewMissing === messageKey ? 'Noch nicht generiert, bitte speichern'
            : 'Gespeicherte Ansage anhören'
        }
        style={msgBtnStyle(disabled)}
      >
        {loading ? '…' : '▶'}
      </button>
    );
  };

  // Which messages already have audio for the practice's CURRENT voice, so the
  // per-message button can show 💾 (nothing generated yet) vs ⟳ (replace it).
  const [messageStatus, setMessageStatus] = useState({});
  const fetchMessageStatus = async (pid) => {
    if (!pid) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/tts/message-status/${pid}`);
      // Drop a late response from a previously-selected practice (same staleness
      // guard the other per-practice loads on this page use).
      if (loadedForPracticeIdRef.current === pid) setMessageStatus(res.data || {});
    } catch (err) {
      console.error('Message status load failed:', err);
    }
  };

  // A message is "dirty" while its text differs from what was last saved — that
  // is what turns ⟳ back into 💾, per message, as soon as the textarea changes.
  // Reads the render-time mirror rather than closing over the text state, so the
  // button re-evaluates on every keystroke without being rebuilt.
  const msgDirty = (messageKey) => {
    const baseline = callflowDraftBaselineRef.current;
    // ?? '' so a key the baseline never had — a forwarding rule added since it was
    // taken — reads as unchanged while its Ansage is still empty, instead of flagging
    // the whole tab dirty the moment "+ Regel hinzufügen" is clicked.
    return !!baseline && (baseline[messageKey] ?? '') !== (callflowTextsRef.current[messageKey] ?? '');
  };

  // 💾 = pressing it produces something new (no audio yet, or the text changed);
  // ⟳ = the audio matches the text. Read by both the button and its handler, so
  // the icon on screen and what the click does can never drift apart.
  const msgNeedsSave = (messageKey) => !messageStatus[messageKey]?.generated || msgDirty(messageKey);

  // Save ONE message: persist its text, rebuild only its audio, then re-baseline
  // just that key. Same two calls the big Speichern makes for it, nothing more.
  const saveOneMessage = async (messageKey) => {
    const action = messageSaveAction(messageKey);
    if (!practiceId || !action || regeneratingMessages.has(messageKey)) return;
    if (loadedForPracticeIdRef.current !== practiceId) return;  // cross-tenant write guard
    const text = callflowTextsRef.current[messageKey];
    // A ⟳ press must re-record even though the text is unchanged: the TTS voice
    // re-samples, so a bad read can be rolled again — that is the point of the
    // button. (How different it sounds is provider- and tuning-dependent: Polly is
    // deterministic, and Kugel runs at a deliberately low temperature.) A 💾 press
    // sends no force because a missing file or a changed text misses the cache on
    // its own.
    const params = { force: !msgNeedsSave(messageKey) };
    if (PAIRED_PRESYNTH.has(action.path)) params.only = messageKey;
    markRegenerating([messageKey], true);
    try {
      if (action.type) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: action.type,
          message: text,
          practice_id: practiceId,
        });
      } else if (action.rules) {
        // A rule's Ansage lives on the practice row. Same payload the 700 ms auto-save
        // builds, against the same PATCH endpoint (only the fields sent are written),
        // so this cannot disturb the keywords or the number beside it.
        await axios.put(`${API_BASE_URL}/practices/${practiceId}/callflow-settings`, {
          forwarding_rules: callflowRowPayload().forwarding_rules,
        });
        queryClient.invalidateQueries(['practiceInfo', practiceId]);
      } else {
        // Lives in the per-practice config (consent question, forwarding sentence).
        // That PUT merges, so a one-key payload leaves every other setting untouched.
        await axios.put(`${API_BASE_URL}/test-call/practice-llm-config/${practiceId}`, {
          [action.configKey]: text,
        });
        // Same invalidation the other config writes do: the hydrator serves this
        // query from cache for 3 minutes, so without it a practice switch and back
        // would repaint the pre-save text and the button would claim to be in sync.
        queryClient.invalidateQueries(['practiceLlmConfig', practiceId]);
      }
      await axios.post(`${API_BASE_URL}/tts/${action.path}/${practiceId}`, null, { params });
      // Re-check after the awaits: a practice switch mid-save nulls the baseline,
      // and writing into it here would leave the NEW practice holding a one-key
      // baseline of the old one's text (every button stuck on 💾, live sync blocked).
      if (loadedForPracticeIdRef.current !== practiceId) return;
      // This message is in sync now — move ITS baseline entry, leave the rest.
      callflowDraftBaselineRef.current = { ...callflowDraftBaselineRef.current, [messageKey]: text };
      setCallflowTabDirty(callflowDraftDirty());
      // Mark it generated up front. fetchMessageStatus is the only writer of that
      // flag, so if its GET fails the button would stay on 💾 and the next press
      // would send force=false, hit the hash and silently do nothing.
      setMessageStatus(prev => ({ ...prev, [messageKey]: { generated: true } }));
      await fetchMessageStatus(practiceId);
    } catch (err) {
      console.error(`Saving message ${messageKey} failed:`, err);
      setLlmSaveStatus({ saved: false, message: 'Ansage konnte nicht gespeichert werden.' });
    } finally {
      markRegenerating([messageKey], false);
    }
  };

  // Compact save/refresh button beside each message heading: 💾 when there is no
  // audio yet or the text was edited since it was last generated, the refresh
  // icon when the audio matches the text (⟳ is the shorthand used in comments).
  const msgSaveBtn = (messageKey) => {
    const busy = regeneratingMessages.has(messageKey);
    const needsSave = msgNeedsSave(messageKey);
    // An empty text has nothing to synthesize (the big save skips it too), except
    // where empty means "use the canonical default" — which IS recordable.
    const empty = !((callflowTextsRef.current[messageKey] || '').trim()) && !EMPTY_MEANS_DEFAULT.has(messageKey);
    const disabled = busy || empty;
    return (
      <button
        type="button"
        onClick={() => saveOneMessage(messageKey)}
        disabled={disabled}
        title={
          busy ? 'Audio wird generiert…'
            : empty ? 'Kein Text hinterlegt'
            : needsSave ? 'Text speichern und Ansage generieren'
            : 'Ansage neu generieren (neue Sprachaufnahme)'
        }
        style={msgBtnStyle(disabled)}
      >
        {busy ? '⏳' : needsSave ? '💾' : actionIcons.refresh}
      </button>
    );
  };

  // Same compact icon-button chrome as msgPreviewBtn, reused for any small
  // icon action (upload, play/stop, delete) that should read as one visual
  // family. `danger` tints border/text red for destructive actions.
  const iconBtnStyle = (disabled, danger = false) => ({
    whiteSpace: 'nowrap',
    padding: '0 0.5rem',
    borderRadius: '6px',
    border: `1px solid ${disabled ? '#d1d5db' : danger ? '#fca5a5' : '#d1d5db'}`,
    background: disabled ? '#f3f4f6' : '#ffffff',
    color: disabled ? '#9ca3af' : danger ? 'var(--destructive)' : '#374151',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: '0.75rem',
    lineHeight: '1.5rem',
    fontWeight: 'normal',
  });

  // Ambience preview: compact inline audio player for the selected preset.
  // All UI-visible aspects are STATE-driven (never ref reads) so the player
  // never gets stuck. Refs hold only the Audio object + its blob URL.
  const [ambiencePreviewLoading, setAmbiencePreviewLoading] = useState(false);
  const [ambienceIsPlaying, setAmbienceIsPlaying] = useState(false);
  const [ambienceCurrentTime, setAmbienceCurrentTime] = useState(0);
  const [ambienceDuration, setAmbienceDuration] = useState(0);
  const [ambiencePreviewError, setAmbiencePreviewError] = useState(false);
  const ambiencePreviewAudioRef = useRef(null);
  const ambiencePreviewUrlRef = useRef(null);
  // m:ss helper for the time read-out.
  const formatAmbienceTime = (secs) => {
    if (!Number.isFinite(secs) || secs < 0) secs = 0;
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  // Full teardown: stop audio, revoke blob, drop refs, reset ALL display state.
  const resetAmbiencePreview = () => {
    const audio = ambiencePreviewAudioRef.current;
    if (audio) {
      // Detach handlers first: clearing src fires a spurious 'error' event on the
      // dying element, which would re-set the error message after teardown.
      audio.ontimeupdate = audio.onloadedmetadata = audio.onplay = audio.onpause = audio.onended = audio.onerror = null;
      audio.pause();
      audio.src = '';
    }
    if (ambiencePreviewUrlRef.current) {
      URL.revokeObjectURL(ambiencePreviewUrlRef.current);
      ambiencePreviewUrlRef.current = null;
    }
    ambiencePreviewAudioRef.current = null;
    setAmbienceIsPlaying(false);
    setAmbienceCurrentTime(0);
    setAmbienceDuration(0);
  };
  // Wire an Audio element's events to state.
  const attachAmbienceAudioEvents = (audio) => {
    audio.ontimeupdate = () => setAmbienceCurrentTime(audio.currentTime);
    audio.onloadedmetadata = () => setAmbienceDuration(audio.duration || 0);
    audio.onplay = () => setAmbienceIsPlaying(true);
    audio.onpause = () => setAmbienceIsPlaying(false);
    audio.onended = () => { setAmbienceIsPlaying(false); setAmbienceCurrentTime(0); audio.currentTime = 0; };
    audio.onerror = () => { resetAmbiencePreview(); setAmbiencePreviewError(true); };
  };
  // Play/pause toggle. First play fetches the blob; pause keeps position so the
  // next click resumes. Auth is handled by the shared axios interceptor.
  const toggleAmbiencePreview = async () => {
    if (ambiencePreviewLoading) return;
    const existing = ambiencePreviewAudioRef.current;
    if (existing) {
      if (existing.paused) { existing.play().catch(() => {}); }
      else { existing.pause(); }
      return;
    }
    try {
      setAmbiencePreviewError(false);
      setAmbiencePreviewLoading(true);
      const res = await axios.get(`${API_BASE_URL}/tts/ambience-preview/${callAmbienceSound}?t=${Date.now()}`, { responseType: 'blob' });
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      ambiencePreviewUrlRef.current = audioUrl;
      ambiencePreviewAudioRef.current = audio;
      attachAmbienceAudioEvents(audio);
      await audio.play();
    } catch (err) {
      console.error('Ambience preview failed:', err);
      resetAmbiencePreview();
      setAmbiencePreviewError(true);
    } finally {
      setAmbiencePreviewLoading(false);
    }
  };
  // Seek (click/drag anywhere on the bar) — works while paused too.
  const seekAmbiencePreview = (value) => {
    const audio = ambiencePreviewAudioRef.current;
    if (audio) audio.currentTime = value;
    setAmbienceCurrentTime(value);
  };
  // Selecting a new sound (or unmounting) fully resets the player so it shows a
  // fresh ▶ ready to play the new selection — never stuck on the prior audio.
  useEffect(() => {
    resetAmbiencePreview();
    setAmbiencePreviewError(false);
    return () => resetAmbiencePreview();
  }, [callAmbienceSound]);

  // Hold background-music (Wartemusik): a per-practice audio file played on loop
  // to callers waiting in the hold queue (parallel_call_mode='hold'). Uploaded
  // IMMEDIATELY on file pick — independent of the section Save button (like a
  // logo). Every call is threaded with the SELECTED practiceId (usePractice()).
  const [holdMusicExists, setHoldMusicExists] = useState(false);
  const [holdMusicUploading, setHoldMusicUploading] = useState(false);
  const [holdMusicPreviewLoading, setHoldMusicPreviewLoading] = useState(false);
  const [holdMusicPlaying, setHoldMusicPlaying] = useState(false);
  const [holdMusicError, setHoldMusicError] = useState('');
  const holdMusicInputRef = useRef(null);

  const handleHoldMusicUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ''; // allow re-picking the same filename later
    if (!file || !practiceId || holdMusicUploading) return;
    setHoldMusicError('');
    setHoldMusicUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file); // backend contract: field name is `file`
      await axios.post(`${API_BASE_URL}/practices/${practiceId}/hold-music`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHoldMusicExists(true);
    } catch (err) {
      console.error('Hold-music upload failed:', err);
      const status = err.response && err.response.status;
      setHoldMusicError(
        status === 415 || status === 422
          ? 'Ungültige Audiodatei. Bitte eine .wav- oder .mp3-Datei hochladen.'
          : 'Upload fehlgeschlagen. Bitte erneut versuchen.'
      );
    } finally {
      setHoldMusicUploading(false);
    }
  };

  // Audition the uploaded music, toggling play/stop on the same button. Reuses
  // the shared preview audio ref so it never overlaps a voice/message preview;
  // onpause covers both natural end AND being stopped by another preview
  // elsewhere, so the button always reflects the real playback state.
  const toggleHoldMusicPreview = async () => {
    if (holdMusicPlaying) {
      stopCurrentPreview();
      setHoldMusicPlaying(false);
      return;
    }
    if (!practiceId || holdMusicPreviewLoading) return;
    stopCurrentPreview();
    let audioUrl;
    try {
      setHoldMusicError('');
      setHoldMusicPreviewLoading(true);
      // cache-buster (?t=) — the file changes on re-upload under the same URL.
      const res = await axios.get(`${API_BASE_URL}/practices/${practiceId}/hold-music?t=${Date.now()}`, { responseType: 'blob' });
      audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      currentPreviewAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        if (currentPreviewAudioRef.current === audio) currentPreviewAudioRef.current = null;
      };
      audio.onerror = () => URL.revokeObjectURL(audioUrl);
      audio.onpause = () => setHoldMusicPlaying(false);
      await audio.play();
      setHoldMusicPlaying(true);
    } catch (err) {
      console.error('Hold-music preview failed:', err);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setHoldMusicPlaying(false);
    } finally {
      setHoldMusicPreviewLoading(false);
    }
  };

  // Remove the uploaded music → the caller then hears the repeated hold message.
  const removeHoldMusic = async () => {
    if (!practiceId || holdMusicUploading) return;
    setHoldMusicError('');
    stopCurrentPreview();
    setHoldMusicPlaying(false);
    try {
      await axios.delete(`${API_BASE_URL}/practices/${practiceId}/hold-music`);
      setHoldMusicExists(false);
    } catch (err) {
      console.error('Hold-music delete failed:', err);
      setHoldMusicError('Entfernen fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  // Revoke any in-flight preview blob URL if the page unmounts mid-playback.
  useEffect(() => stopCurrentPreview, []);

  // Voxtral TTS voice configuration
  const [voxtralVoice, setVoxtralVoice] = useState('gb_oliver_neutral');
  const [voxtralVoices, setVoxtralVoices] = useState([]);
  const [voxtralCustomExpanded, setVoxtralCustomExpanded] = useState(false);
  const [voxtralCustomName, setVoxtralCustomName] = useState('');
  const [voxtralCustomAudio, setVoxtralCustomAudio] = useState(null);
  const [voxtralCustomFileName, setVoxtralCustomFileName] = useState('');
  const [voxtralRecording, setVoxtralRecording] = useState(false);
  const [voxtralMediaRecorder, setVoxtralMediaRecorder] = useState(null);
  const [voxtralPreviewUrl, setVoxtralPreviewUrl] = useState(null);
  const [voxtralSaving, setVoxtralSaving] = useState(false);
  const voxtralFileInputRef = useRef(null);

  // Auto-disable generative when switching to non-generative voice
  useEffect(() => {
    const GENERATIVE_VOICES = ['Daniel', 'Vicki', 'Hannah'];
    if (!GENERATIVE_VOICES.includes(awsPollyVoice) && awsPollyUseGenerative) {
      setAwsPollyUseGenerative(false);
    }
  }, [awsPollyVoice, awsPollyUseGenerative]);

  // Fetch Voxtral voices when TTS provider is voxtral
  useEffect(() => {
    if (ttsProvider === 'voxtral') {
      refreshVoxtralVoices();
    }
  }, [ttsProvider]);

  // Reset voxtral voice if it doesn't match any known preset or custom voice
  const VOXTRAL_PRESET_SLUGS = [
    'fr_marie_happy', 'fr_marie_neutral', 'fr_marie_sad',
    'gb_jane_confident', 'gb_jane_neutral', 'gb_jane_sad',
    'gb_oliver_cheerful', 'gb_oliver_neutral', 'gb_oliver_sad',
    'en_paul_happy', 'en_paul_neutral', 'en_paul_sad',
  ];
  useEffect(() => {
    if (ttsProvider !== 'voxtral') return;
    // Don't validate until voice list has loaded (avoid race condition resetting custom voice UUIDs)
    if (voxtralVoices.length === 0) return;
    const isPreset = VOXTRAL_PRESET_SLUGS.includes(voxtralVoice);
    const isCustom = voxtralVoices.some(v => v.user_id != null && v.id === voxtralVoice);
    if (!isPreset && !isCustom) {
      setVoxtralVoice('gb_oliver_neutral');
    }
  }, [ttsProvider, voxtralVoice, voxtralVoices]);

  // Close priority dropdown on outside click
  useEffect(() => {
    if (!openPriorityDropdown) return;
    const handleOutsideClick = (e) => {
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(e.target)) {
        setOpenPriorityDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openPriorityDropdown]);

  // Fetch frequency options once on mount; keep fallback list on failure
  useEffect(() => {
    const fetchFrequencyOptions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/therapy-types/frequencies`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setFrequencyOptions(response.data);
        }
      } catch (err) {
        console.warn('Failed to fetch frequency options, using fallback list:', err);
      }
    };
    fetchFrequencyOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Feiertage & Schließtage + Empfangszeiten: lazy load + handlers ---
  // ISO UTC string -> local "YYYY-MM-DD" for <input type="date"> values.
  const holidayInputDate = (iso) => {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  };
  // Whole-day entries: start == end means a single day.
  const holidayIsSingleDay = (h) => holidayInputDate(h.starts_at) === holidayInputDate(h.ends_at);

  // Load holidays + reception hours (initial tab hydration + silent live sync).
  const loadClosures = async (targetPracticeId, { silent = false, isCancelled } = {}) => {
    if (!targetPracticeId) return;
    if (!silent) {
      setHolidaysLoading(true);
      setReceptionLoading(true);
      setHolidaysError('');
      setReceptionError('');
    }
    const [holRes, recRes] = await Promise.allSettled([
      axios.get(`${API_BASE_URL}/closures/practice/${targetPracticeId}/holidays`),
      axios.get(`${API_BASE_URL}/closures/practice/${targetPracticeId}/reception-hours`),
    ]);
    if (isCancelled?.()) {
      if (!silent) { setHolidaysLoading(false); setReceptionLoading(false); }
      return;
    }
    if (holRes.status === 'fulfilled') {
      setHolidaysData(holRes.value.data);
      if (silent) setHolidaysError('');
    } else if (!silent) {
      console.warn('Failed to load holidays:', holRes.reason);
      setHolidaysError('Feiertage konnten nicht geladen werden.');
    }
    if (recRes.status === 'fulfilled') {
      const rh = recRes.value.data?.reception_hours;
      setReceptionEnabled(Boolean(rh));
      setReceptionHours(rh || null);
      if (silent) setReceptionError('');
    } else if (!silent) {
      console.warn('Failed to load reception hours:', recRes.reason);
      setReceptionError('Empfangszeiten konnten nicht geladen werden.');
    }
    if (!silent) {
      setHolidaysLoading(false);
      setReceptionLoading(false);
      setClosuresLoadedFor(targetPracticeId);
    }
  };

  useEffect(() => {
    if (!practiceId || activeTab !== 'practice' || closuresLoadedFor === practiceId) return;
    let cancelled = false;
    loadClosures(practiceId, { isCancelled: () => cancelled });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, activeTab, closuresLoadedFor]);

  // Set the Bundesland and seed the year's holidays.
  const handleHolidaySync = async (region) => {
    if (!region || !practiceId) return;
    setHolidaysSyncing(true);
    setHolidaysError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/closures/practice/${practiceId}/holidays/sync`, { region });
      setHolidaysData(res.data);
    } catch (err) {
      console.warn('Failed to sync holidays:', err);
      setHolidaysError('Feiertage konnten nicht geladen werden. Bitte erneut versuchen.');
    } finally {
      setHolidaysSyncing(false);
    }
  };

  // Merge one holiday entry from a server response over the existing local
  // entry. PATCH/PUT responses carry no `scope` field — spreading over the
  // previous entry preserves it (a bare replace drops the row from its group).
  const holidayMergeEntry = (entry) => {
    setHolidaysData(prev => prev ? {
      ...prev,
      holidays: prev.holidays.map(h => h.id === entry.id ? { ...h, ...entry } : h)
    } : prev);
  };

  // Toggle a single entry on/off.
  const handleHolidayToggle = async (closureId, enabled) => {
    if (holidayRowBusy[closureId]) return;
    setHolidayRowBusy(prev => ({ ...prev, [closureId]: true }));
    setHolidayRowError(prev => ({ ...prev, [closureId]: '' }));
    try {
      const res = await axios.patch(`${API_BASE_URL}/closures/practice/${practiceId}/${closureId}`, { enabled });
      holidayMergeEntry(res.data);
    } catch (err) {
      console.warn('Failed to toggle holiday:', err);
      setHolidayRowError(prev => ({ ...prev, [closureId]: 'Änderung fehlgeschlagen.' }));
    } finally {
      setHolidayRowBusy(prev => ({ ...prev, [closureId]: false }));
    }
  };

  // Update an entry's date range. startDate/endDate are "YYYY-MM-DD".
  const handleHolidayDates = async (closureId, startDate, endDate) => {
    if (holidayRowBusy[closureId]) return;
    if (endDate < startDate) {
      setHolidayRowError(prev => ({ ...prev, [closureId]: 'Das Enddatum muss nach dem Startdatum liegen.' }));
      return;
    }
    setHolidayRowBusy(prev => ({ ...prev, [closureId]: true }));
    setHolidayRowError(prev => ({ ...prev, [closureId]: '' }));
    try {
      const res = await axios.put(`${API_BASE_URL}/closures/practice/${practiceId}/holidays/${closureId}/dates`, {
        starts_at: `${startDate}T00:00:00`,
        ends_at: `${endDate}T00:00:00`
      });
      holidayMergeEntry(res.data);
      // Single day again? collapse the range affordance.
      if (startDate === endDate) {
        setHolidayRangeOpen(prev => ({ ...prev, [closureId]: false }));
      }
    } catch (err) {
      console.warn('Failed to update holiday dates:', err);
      setHolidayRowError(prev => ({ ...prev, [closureId]: 'Änderung fehlgeschlagen.' }));
    } finally {
      setHolidayRowBusy(prev => ({ ...prev, [closureId]: false }));
    }
  };

  // Default reception-hours editor state when nothing is configured yet:
  // seed from the practice's existing opening hours if available (deep-copied,
  // normalized to {open,close,closed}), else weekdays 09:00-17:00 / weekend
  // closed. NEVER mutates practiceInfo.opening_hours (JSON deep-copy).
  const buildDefaultReceptionHours = () => {
    const src = practiceInfo?.opening_hours;
    if (src && typeof src === 'object') {
      const copy = JSON.parse(JSON.stringify(src));
      const normalized = {};
      daysOrder.forEach(day => {
        const d = copy[day] || {};
        const closed = d.closed === true || !d.open;
        normalized[day] = {
          open: d.open || '09:00',
          close: d.close || '17:00',
          closed,
        };
      });
      return normalized;
    }
    const fallback = {};
    daysOrder.forEach(day => {
      const isWeekend = day === 'saturday' || day === 'sunday';
      fallback[day] = { open: '09:00', close: '17:00', closed: isWeekend };
    });
    return fallback;
  };

  // Shared day-row state transition for opening_hours + reception_hours:
  // opening a closed day backfills '09:00'/'17:00', closing clears stale times.
  const applyDayHoursUpdate = (hoursMap, day, field, value) => {
    const currentDay = hoursMap?.[day] || { open: '09:00', close: '17:00', closed: false };
    const nextVal = field === 'closed' && typeof value !== 'boolean' ? !currentDay.closed : value;
    const updatedDay = { ...currentDay, [field]: nextVal };
    if (field === 'closed' && nextVal === false) {
      if (!currentDay.open) updatedDay.open = '09:00';
      if (!currentDay.close) updatedDay.close = '17:00';
    }
    if (field === 'closed' && nextVal === true) {
      updatedDay.open = '';
      updatedDay.close = '';
    }
    return { ...(hoursMap || {}), [day]: updatedDay };
  };

  const commitReceptionHours = async (pending = null) => {
    const toCommit = pending || pendingReceptionWriteRef.current;
    if (!toCommit) return;
    const { targetPracticeId, hoursToSave } = toCommit;
    pendingReceptionWriteRef.current = null;
    if (receptionSaveTimerRef.current) {
      clearTimeout(receptionSaveTimerRef.current);
      receptionSaveTimerRef.current = null;
    }

    if (!targetPracticeId || loadedForPracticeIdRef.current !== targetPracticeId) return;

    if (hoursToSave) {
      for (const day of daysOrder) {
        const h = hoursToSave[day];
        if (!h || h.closed) continue;
        if (!h.open || !h.close || !(h.open < h.close)) {
          if (isMountedRef.current) {
            setReceptionError(`'${DAY_NAME_MAP[day]}': Startzeit muss vor Endzeit liegen.`);
          }
          return;
        }
      }
    }
    if (isMountedRef.current) setReceptionError('');

    if (isMountedRef.current) {
      setPracticeSaving(true);
      setSaveStatus(prev => ({ ...prev, practice: { saved: false, message: 'Speichere...' } }));
    }

    try {
      await axios.put(`${API_BASE_URL}/closures/practice/${targetPracticeId}/reception-hours`, {
        reception_hours: hoursToSave,
      });

      if (loadedForPracticeIdRef.current !== targetPracticeId) return;

      if (isMountedRef.current) {
        setReceptionDirty(false);
        setPracticeTabDirty(false);
        setSaveStatus(prev => ({ ...prev, practice: { saved: true, message: 'Empfangszeiten gespeichert' } }));
        if (saveStatusClearTimerRef.current) clearTimeout(saveStatusClearTimerRef.current);
        saveStatusClearTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setSaveStatus(prev => ({ ...prev, practice: { saved: false, message: '' } }));
          }
        }, 2500);
      }
    } catch (err) {
      console.error('Failed to save reception hours:', err);
      if (isMountedRef.current && loadedForPracticeIdRef.current === targetPracticeId) {
        const msg = formatApiError(err?.response?.data?.detail) || 'Fehler beim Speichern der Empfangszeiten.';
        setReceptionError(msg);
        setSaveStatus(prev => ({ ...prev, practice: { saved: false, message: msg } }));
      }
    } finally {
      if (isMountedRef.current) {  // the save is over even if the practice changed meanwhile
        setPracticeSaving(false);
      }
    }
  };

  const handleToggleReception = async (on) => {
    setReceptionEnabled(on);
    setReceptionDirty(false);
    setReceptionError('');
    const targetHours = on ? (receptionHoursRef.current || buildDefaultReceptionHours()) : null;
    receptionHoursRef.current = targetHours;
    if (on && !receptionHours) setReceptionHours(targetHours);
    if (!on) setReceptionHours(null);

    const pending = { targetPracticeId: practiceId, hoursToSave: targetHours };
    await commitReceptionHours(pending);
  };

  const handleReceptionHoursChange = (day, field, value) => {
    setReceptionDirty(true);
    setPracticeTabDirty(true);
    setReceptionError('');
    const updatedHours = applyDayHoursUpdate(receptionHoursRef.current || buildDefaultReceptionHours(), day, field, value);
    receptionHoursRef.current = updatedHours;
    setReceptionHours(updatedHours);

    const pending = { targetPracticeId: practiceId, hoursToSave: updatedHours };
    pendingReceptionWriteRef.current = pending;

    if (field === 'closed') {
      commitReceptionHours(pending);
    } else {
      if (receptionSaveTimerRef.current) clearTimeout(receptionSaveTimerRef.current);
      receptionSaveTimerRef.current = setTimeout(() => {
        commitReceptionHours(pendingReceptionWriteRef.current);
      }, 500);
    }
  };

  // 15-minute time intervals for 2026 schedule picker
  const TIME_OPTIONS_15MIN = (() => {
    const opts = [];
    for (let h = 6; h <= 23; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hh = h.toString().padStart(2, '0');
        const mm = m.toString().padStart(2, '0');
        opts.push(`${hh}:${mm}`);
      }
    }
    return opts;
  })();

  const getTimeOptions = (currentTime) => {
    if (currentTime && !TIME_OPTIONS_15MIN.includes(currentTime)) {
      return [...TIME_OPTIONS_15MIN, currentTime].sort();
    }
    return TIME_OPTIONS_15MIN;
  };

  // Shared 7-day schedule editor used by both Öffnungszeiten and Empfangszeiten.
  const renderWeeklyHoursRows = (hoursMap, onDayChange, { disabled = false } = {}) => (
    <div className="opening-hours-container" style={disabled ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : undefined}>
      <div className="opening-hours-head-row">
        <span className="opening-hours-head-col-day">Wochentag</span>
        <span className="opening-hours-head-col-times">Zeiten</span>
        <span className="opening-hours-head-col-status">Status</span>
      </div>

      <div className="opening-hours-list">
        {daysOrder.map(day => {
          const hours = hoursMap?.[day] || { open: '', close: '', closed: false };
          const isClosed = !!hours.closed;
          const openTime = hours.open || '09:00';
          const closeTime = hours.close || '17:00';

          return (
            <div className={`modern-day-row ${isClosed ? 'is-closed' : ''}`} key={day}>
              <div className="day-name-badge">
                <span className="day-name-text">{DAY_NAME_MAP[day]}</span>
              </div>

              <div className="day-time-slot">
                {isClosed ? (
                  <div className="day-closed-pill">
                    <span className="day-closed-dash">—</span>
                    <span className="day-closed-text">Geschlossen</span>
                  </div>
                ) : (
                  <div className="day-time-range">
                    <select
                      value={openTime}
                      onChange={(e) => onDayChange(day, 'open', e.target.value)}
                      disabled={disabled}
                      className="modern-time-select"
                      aria-label={`${DAY_NAME_MAP[day]} Öffnungszeit`}
                    >
                      {getTimeOptions(openTime).map(t => (
                        <option key={t} value={t}>{t} Uhr</option>
                      ))}
                    </select>

                    <span className="time-range-separator">bis</span>

                    <select
                      value={closeTime}
                      onChange={(e) => onDayChange(day, 'close', e.target.value)}
                      disabled={disabled}
                      className="modern-time-select"
                      aria-label={`${DAY_NAME_MAP[day]} Schließzeit`}
                    >
                      {getTimeOptions(closeTime).map(t => (
                        <option key={t} value={t}>{t} Uhr</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="day-status-toggle">
                <input
                  type="checkbox"
                  id={`switch-${day}`}
                  className="as-switch as-switch--sm"
                  checked={!isClosed}
                  disabled={disabled}
                  onChange={(e) => onDayChange(day, 'closed', !e.target.checked)}
                  aria-label={`${DAY_NAME_MAP[day]} Status`}
                />
                <label htmlFor={`switch-${day}`} className={`hours-status-text ${isClosed ? 'is-closed' : 'is-open'}`}>
                  {isClosed ? 'Geschlossen' : 'Geöffnet'}
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // --- Proxy URL fix: always send null if empty string ---
  const getSanitizedSipConfig = (config) => ({
    ...config,
    proxy_url: config.proxy_url === '' ? null : config.proxy_url
  });
  
  // Get user from Keycloak - simplified authentication logic
  useEffect(() => {
    console.log("=== CONFIG PAGE AUTH DEBUG ===");
    console.log("Keycloak authenticated:", keycloak?.authenticated);
    console.log("Keycloak token sub:", keycloak?.tokenParsed?.sub);
    console.log("Current userId state:", userId);
    
    // Only proceed if we have a valid Keycloak authentication
    if (!keycloak?.authenticated || !keycloak?.tokenParsed?.sub) {
      console.log("No valid Keycloak authentication - cannot proceed");
      return;
    }
    
    // Simplified: directly get user from backend using Keycloak ID
    const getUserData = async () => {
      try {
        console.log("Making API call to get user by Keycloak ID:", keycloak.tokenParsed.sub);
        console.log("Using API base URL:", API_BASE_URL);
        const response = await axios.get(`${API_BASE_URL}/users/by-keycloak/${keycloak.tokenParsed.sub}`);
        console.log("Backend user found:", response.data);
        console.log("Response data keys:", Object.keys(response.data));
        console.log("User ID field:", response.data.id);
        console.log("User ID type:", typeof response.data.id);
        
        // Check if id exists and is valid
        if (response.data.id) {
          console.log("Setting userId state to:", response.data.id);
          setUserId(response.data.id);
        } else {
          console.error("No valid id field in user response:", response.data);
        }
      } catch (error) {
        console.error('Failed to get user from backend:', error);
        if (error.response?.status === 404) {
          console.log("User not found in backend - this should not happen in production");
        }
        // Don't set any fallback - let the user see the authentication error
      }
    };
    
    getUserData();
  }, [keycloak]);
  
  // Check configuration status on component mount
  const checkConfigurationStatus = async () => {
    if (!practiceId) return;

    try {
      // Check SIP status (Phase 2 follow-up: scoped to selected practice)
      const sipStatusResponse = await axios.get(`${API_BASE_URL}/sip/status/${practiceId}`);
      if (sipStatusResponse.data.registered) {
        console.log("SIP configuration is already active and registered");
      }
      
      // Check AI config status
      const aiStatusResponse = await axios.get(`${API_BASE_URL}/test-call/llm-config/status`);
      if (aiStatusResponse.data.client_available) {
        console.log("AI configuration is already active");
      }
    } catch (error) {
      console.log("Could not check configuration status:", error);
    }
  };
  
  // Keep the active tab visible: at narrow widths the tab bar scrolls
  // horizontally (.config-tabs overflow-x), so a deep link like ?tab=members
  // or shrinking the window could leave the ACTIVE tab scrolled out of view.
  // Deps include practiceLoading (the page early-returns a loading screen, so
  // the tab bar only EXISTS after it flips false) and the role gates (the
  // SIP/KI/PVS/Mitglieder buttons mount late, shifting the bar's layout).
  useEffect(() => {
    document.querySelector('.config-tabs .tab-button.active')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab, practiceLoading, canManageMembers, userIsAdmin, userIsSuperAdmin]);

  // Session 10d: warn (don't error — falls back to empty form + create-on-save)
  // when PracticeContext settles with no accessible practice. Super_admins use
  // /admin/practices to create one; regular users shouldn't reach this state.
  useEffect(() => {
    if (!practiceLoading && !practiceId) {
      console.warn('[ConfigPage] PracticeContext resolved with no accessible practice. Form will start empty; save will create a new practice.');
    }
  }, [practiceLoading, practiceId]);

  // Session 10d: load practice info when PracticeContext resolves practiceId,
  // and load SIP config / status when userId resolves. Two separate effects so
  // SIP loads on the cheaper userId trigger and isn't blocked by PracticeContext.
  useEffect(() => {
    if (!practiceId) return;

    // Block the call-flow auto-save until this load completes, so a practice
    // switch (or initial mount) can't PUT stale empty practiceInfo to the new
    // practiceId before the fresh data arrives.
    practiceInfoLoadedRef.current = false;
    // Unbind the form from any previously-loaded practice: no save may fire
    // until THIS practice's data has arrived (see loadedForPracticeIdRef decl).
    loadedForPracticeIdRef.current = null;
    setFormLoadedFor(null);
    setMessagesLoadedFor(null);
    setTherapistsLoadedFor(null);
    // The spoken messages and the keyword chips re-hydrate below, so the baselines
    // they feed must be re-captured — otherwise the incoming server values read as
    // user edits and get saved straight back out (live config sync).
    callflowDraftBaselineRef.current = null;
    callflowRowBaselineRef.current = null;
    // Otherwise the per-message buttons keep painting the previous practice's
    // state until this one's status GET lands.
    setMessageStatus({});
    setPracticeTabDirty(false);

    let cancelled = false;

    // Is this a real practice SWITCH, or the same practice refreshing because a
    // colleague changed something (live config sync bumps the nonce)?
    //
    // Everything below blanks the screen on purpose so one practice's data can
    // never be shown — or saved — under another. On a same-practice refresh that
    // blanking is pure damage: nulling the matrices unmounts the whole table, the
    // page collapses to nothing, and the browser throws the reader back to the top
    // mid-scroll. A refresh has to be invisible, so the clears are skipped and the
    // loaders below simply overwrite each value in place.
    const isPracticeSwitch = lastHydratedPracticeRef.current !== practiceId;
    lastHydratedPracticeRef.current = practiceId;

    const cachedPriorityMatrix = queryClient.getQueryData(['priorityMatrix', practiceId]);
    const cachedLocationMatrix = queryClient.getQueryData(['locationMatrix', practiceId]);

    if (isPracticeSwitch) {
      // Staleness guard for the per-practice Anrufablauf messages. Those live in
      // unscoped component state; without this, a rapid A→B switch (overlapping
      // loads) or a slow/failed practice GET could leave the previous practice's
      // greeting/farewell/consent/voicemail text in state — and the next Save
      // would then persist it under the newly-selected practice.
      setGreetingMessage('');
      setGreetingMessagePvs('');
      setGreetingMessageAway('');
      setGreetingMessagePvsAway('');
      setFarewellGeneral('');
      setFarewellAppointment('');
      setEnableGreetingAway(false);
      setEnableGreetingPvs(false);
      setEnableFarewellAppointment(false);
      setVoicemailMessage('');
      setHoldMessage('');

      // The therapist-location matrix is loaded LAZILY (effect below) when its
      // section is actually viewable (team tab + multi-location). Clear it here
      // so a practice switch can't briefly show the previous practice's data.
      setTherapistLocationMatrix(null);
      // Same for the excluded list (loaded when its rail tab opens, kept across a reopen).
      setExcludedRows([]);

      // Clear lazily-hydrated holidays + reception hours so switching practices
      // never flashes or persists the previous practice's schedule/Bundesland.
      setClosuresLoadedFor(null);
      setHolidaysData(null);
      setHolidaysError('');
      setHolidayRowError({});
      setHolidayRangeOpen({});
      setReceptionHours(null);
      setReceptionEnabled(false);
      setReceptionDirty(false);
      setReceptionError('');

      practiceBaselineRef.current = null;
      pendingPracticeWriteRef.current = null;
      pendingReceptionWriteRef.current = null;
      pendingFaqWriteRef.current = null;
      if (practiceAutoSaveTimerRef.current) {
        clearTimeout(practiceAutoSaveTimerRef.current);
        practiceAutoSaveTimerRef.current = null;
      }
      if (receptionSaveTimerRef.current) {
        clearTimeout(receptionSaveTimerRef.current);
        receptionSaveTimerRef.current = null;
      }
      if (faqSaveTimerRef.current) {
        clearTimeout(faqSaveTimerRef.current);
        faqSaveTimerRef.current = null;
      }

      // Paint-from-cache: seed the matrix state synchronously from the React
      // Query cache so the FAQ + matrices render immediately on a revisit
      // instead of waiting for the network chain below, which then refreshes
      // them in the background (stale-while-revalidate). On a practice switch
      // without cache this clears to null instead of showing the old practice.
      setPriorityMatrix(cachedPriorityMatrix || null);
      setLocationMatrix(cachedLocationMatrix || null);
    }

    const loadPracticeInfo = async () => {
      console.log(`Attempting to load practice info for practiceId: ${practiceId}`);
      try {
        // Fetch the active practice directly — PracticeContext already resolved which one.
        // Routed through React Query's cache (fetchQuery) so revisiting the
        // Configuration tab within staleTime returns instantly without a network
        // round-trip, and refreshes in the background otherwise. All downstream
        // hydration / staleness-guard / auto-save-gate logic is unchanged — only
        // how `response` is obtained differs. Invalidated after a save below.
        const response = { data: await queryClient.fetchQuery(
          ['practiceInfo', practiceId],
          async () => (await axios.get(`${API_BASE_URL}/practices/${practiceId}`)).data
        ) };
        // Staleness guard (Jul 2026 cross-tenant incident): if the practice was
        // switched while this GET was in flight, drop the ENTIRE run — most
        // importantly setPracticeInfo + the loaded/bound flags below. Without
        // this, practice A's info lands in the form while practice B is
        // selected, and the next save (manual or auto) writes A's data to B.
        if (cancelled) return;
        console.log('Practice info loaded successfully:', response.data);
        
        // Normalize opening hours + null scalars — see normalizePracticeRow.
        const data = response.data;
        const safeData = normalizePracticeRow(data);
        setPracticeInfo(safeData);
        practiceBaselineRef.current = serializePracticeDetails(safeData);
        // Allow the call-flow auto-save effect to fire from now on. Before
        // this point, practiceInfo is still empty defaults and any auto-save
        // PUT would wipe the practice. See Session 10d regression note.
        practiceInfoLoadedRef.current = true;
        // Bind the form to the practice this data belongs to. Only a completed,
        // non-cancelled load for the CURRENT practice may set this — it is the
        // token every save handler checks against the live practiceId.
        loadedForPracticeIdRef.current = practiceId;
        setFormLoadedFor(practiceId);
        console.log(`Practice info loaded for practice ${practiceId}`);
        console.log('Processed opening hours:', safeData.opening_hours);

        // Load insurance type settings from practice data
        if (data.insurance_type_settings) {
          setInsuranceTypeSettings(data.insurance_type_settings);
        }

        // Per-Heilmittelgruppe Verordnungstyp settings. An ABSENT key means an older
        // backend (no column, no write endpoint) and hides the switch entirely; an
        // empty object means a practice that has never used it, which is the default.
        setCategorySettingsApiAvailable(data.therapy_category_settings !== undefined);
        setTherapyCategorySettings(data.therapy_category_settings || {});

        // Hydrate trigger keywords (forwarding rules + smartab keywords).
        // SmartAB list is pre-filled with defaults whenever it's empty so the
        // field shows the recommended words instead of staying blank.
        if (data.trigger_keywords) {
          const tk = data.trigger_keywords;
          const smartabList = tk.smartab_keywords || [];
          setTriggerKeywords({
            forwarding_rules: (tk.forwarding_rules || []).map(r => ({
              // The rule's stable id. Carrying it is NOT optional: this map rebuilds the
              // rule field by field and drops anything it doesn't name, so without it the
              // auto-save ships id-less rules, the backend mints new ids, and every page
              // load orphans the rule's pre-synthesized Ansage and re-records it.
              id: r.id,
              keywords: (r.keywords || []).join(', '),
              phone_number: r.phone_number || '',
              message: r.message || ''
            })),
            smartab_keywords: smartabList.length > 0 ? smartabList.join(', ') : DEFAULT_SMARTAB_KEYWORDS
          });
        } else {
          setTriggerKeywords({ forwarding_rules: [], smartab_keywords: DEFAULT_SMARTAB_KEYWORDS });
        }

        // The loads below are independent of each other (each needs only the
        // practice id / the already-fetched `data`), so they run CONCURRENTLY.
        // They used to be awaited sequentially, which made the location matrix
        // (the FAQ's data) wait for the SUM of all round-trips on every
        // remount. Each loader keeps its own try/catch so one failure never
        // breaks the others; the `cancelled` guards are preserved verbatim.
        // The therapist-location matrix is NOT loaded here anymore — it loads
        // lazily when its section is viewable (team tab + multi-location).

        // Check SES verification status for practice email
        const checkSesStatus = async () => {
          if (!data.email) return;
          try {
            const sesRes = await axios.get(`${API_BASE_URL}/practices/verify-email-status`, { params: { email: data.email } });
            if (cancelled) return;
            setSesVerificationStatus(sesRes.data.status);
          } catch (sesErr) {
            console.error('Failed to check SES verification status:', sesErr);
            if (!cancelled) setSesVerificationStatus(null);
          }
        };

        const fetchFillerText = async (type) => {
          try {
            const res = await axios.get(`${API_BASE_URL}/filler-messages/practice/${data.id}/type/${type}`);
            return res.data.length > 0 ? (res.data[0].message || '') : '';
          } catch (err) {
            console.error(`Failed to load ${type} message:`, err);
            return '';
          }
        };

        // Load greeting and farewell messages for this practice. Explicit per-practice
        // switch values in ai_config win; when absent (pre-switch practices), auto-enable
        // each switch whenever a non-empty message exists in the DB.
        const loadGreetingsAndFarewells = async () => {
          const [greeting, awayMsg, pvsMsg, pvsAwayMsg, generalFarewell, apptFarewell] = await Promise.all([
            fetchFillerText('greeting'),
            fetchFillerText('greeting_away'),
            fetchFillerText('greeting_pvs'),
            fetchFillerText('greeting_pvs_away'),
            fetchFillerText('farewell_general'),
            fetchFillerText('farewell_appointment'),
          ]);
          if (cancelled) return;
          setGreetingMessage(greeting);
          setGreetingMessageAway(awayMsg);
          setGreetingMessagePvs(pvsMsg);
          setGreetingMessagePvsAway(pvsAwayMsg);
          setFarewellGeneral(generalFarewell);
          setFarewellAppointment(apptFarewell);
          const flags = storedVariantFlagsRef.current;
          setEnableGreetingAway(flags.away !== undefined ? flags.away : Boolean(awayMsg.trim() || pvsAwayMsg.trim()));
          setEnableGreetingPvs(flags.pvs !== undefined ? flags.pvs : Boolean(pvsMsg.trim() || pvsAwayMsg.trim()));
          setEnableFarewellAppointment(flags.appt !== undefined ? flags.appt : Boolean(apptFarewell.trim()));
        };

        // Load voicemail announcement for this practice
        const loadVoicemail = async () => {
          try {
            const voicemailRes = await axios.get(`${API_BASE_URL}/filler-messages/practice/${data.id}/type/voicemail_announcement`);
            if (cancelled) return;
            if (voicemailRes.data.length > 0) {
              setVoicemailMessage(voicemailRes.data[0].message);
              console.log('Voicemail message loaded successfully:', voicemailRes.data[0].message);
            } else {
              setVoicemailMessage('');
              console.log('No voicemail message configured for this practice');
            }
          } catch (voicemailErr) {
            console.error('Failed to load voicemail message:', voicemailErr);
            if (!cancelled) setVoicemailMessage('');
          }
          // Load hold-in-line "please hold" message (same pattern as voicemail).
          try {
            const holdRes = await axios.get(`${API_BASE_URL}/filler-messages/practice/${data.id}/type/hold_message`);
            if (cancelled) return;
            setHoldMessage(holdRes.data.length > 0 ? holdRes.data[0].message : '');
          } catch (holdErr) {
            console.error('Failed to load hold message:', holdErr);
            if (!cancelled) setHoldMessage('');
          }
          // Reflect whether background hold-music is already uploaded for this
          // practice (data.id === the currently selected practiceId).
          try {
            const holdMusicRes = await axios.get(`${API_BASE_URL}/practices/${data.id}/hold-music/status`);
            if (cancelled) return;
            setHoldMusicExists(!!(holdMusicRes.data && holdMusicRes.data.exists));
            setHoldMusicError('');
          } catch (holdMusicErr) {
            console.error('Failed to load hold-music status:', holdMusicErr);
            if (!cancelled) setHoldMusicExists(false);
          }
        };

        // Load therapists for this practice
        const loadTherapistsList = async () => {
          try {
            const therapistsRes = await axios.get(`${API_BASE_URL}/therapists/practice/${data.id}`);
            if (cancelled) return;
            setTherapists(therapistsRes.data);
            setTherapistsLoadedFor(practiceId);
            console.log('Therapists loaded successfully:', therapistsRes.data);
          } catch (therapistErr) {
            console.error('Failed to load therapists:', therapistErr);
            if (!cancelled) {
              setTherapists([]);
              setTherapistsLoadedFor(null);
            }
          }
        };

        // Load therapy types for this practice
        const loadTherapyTypesList = async () => {
          try {
            const therapyTypesRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${data.id}`);
            if (cancelled) return;
            setTherapyTypes(therapyTypesRes.data);
            console.log('Therapy types loaded successfully:', therapyTypesRes.data);
          } catch (therapyTypeErr) {
            console.error('Failed to load therapy types:', therapyTypeErr);
            if (!cancelled) setTherapyTypes([]);
          }
        };

        // Load therapy categories (the umbrella rail) for this practice
        const loadTherapyCategoriesList = async () => {
          try {
            const categoriesRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${data.id}/categories`);
            if (cancelled) return;
            applyCategoriesPayload(categoriesRes.data);
            // Arriving on the page (every visit remounts it) or on another practice
            // opens the FIRST tab of the practice's own order. The initial tab is the
            // physio key, and the tab-reconciling effect keeps it as long as it
            // exists, wherever the practice moved it. A same-practice refresh (live
            // config sync) leaves the reader's tab alone. Keyed on the practice whose
            // categories LANDED, not on isPracticeSwitch: dev StrictMode runs this
            // effect twice and cancels the first run, so the run that lands would
            // never see the switch.
            if (categoryTabSeededForRef.current !== data.id) {
              categoryTabSeededForRef.current = data.id;
              setTherapyCategoryTab(categoriesRes.data?.categories?.[0] || DEFAULT_THERAPY_CATEGORY);
            }
            console.log('Therapy categories loaded successfully:', categoriesRes.data?.categories);
          } catch (categoryErr) {
            if (cancelled) return;
            // 404 = old backend during a deploy window. Degrade to the read-only
            // rail derived from the rows (see the effect near visibleTherapyTypes).
            if (categoryErr.response?.status === 404) {
              setCategoriesApiAvailable(false);
            } else {
              console.error('Failed to load therapy categories:', categoryErr);
            }
          }
        };

        // Load priority matrix for this practice
        const loadPriorityMatrixCached = async () => {
          try {
            // Spinner only when there is nothing on screen to keep showing: no
            // cache to paint from AND this is a real practice switch. A
            // same-practice refresh must never blank the table (scroll jump).
            if (!cachedPriorityMatrix && isPracticeSwitch) setMatrixLoading(true);
            const matrixData = await queryClient.fetchQuery(
              ['priorityMatrix', data.id],
              async () => (await axios.get(`${API_BASE_URL}/therapists/practice/${data.id}/matrix`)).data
            );
            if (cancelled) return;
            setPriorityMatrix(matrixData);
            console.log('Priority matrix loaded successfully:', matrixData);
          } catch (matrixErr) {
            console.error('Failed to load priority matrix:', matrixErr);
            // A failed background refresh must not wipe the visible table.
            if (!cancelled && isPracticeSwitch) setPriorityMatrix(null);
          } finally {
            setMatrixLoading(false);
          }
        };

        // Load location matrix for this practice (feeds the inline FAQ editor)
        const loadLocationMatrixCached = async () => {
          try {
            // Spinner only when there is nothing on screen to keep showing.
            if (!cachedLocationMatrix && isPracticeSwitch) setLocationMatrixLoading(true);
            const locMatrixData = await queryClient.fetchQuery(
              ['locationMatrix', data.id],
              async () => (await axios.get(`${API_BASE_URL}/locations/practice/${data.id}/matrix`)).data
            );
            if (cancelled) return;
            setLocationMatrix(locMatrixData);
            console.log('Location matrix loaded successfully:', locMatrixData);
          } catch (locMatrixErr) {
            console.error('Failed to load location matrix:', locMatrixErr);
            if (!cancelled) setLocationMatrix(null);
          } finally {
            setLocationMatrixLoading(false);
          }
        };

        // Message loaders get their own completion signal (drives the
        // Anrufablauf tab's LoadingShroud) — same concurrency as before, the
        // outer Promise.all still awaits everything. Each loader catches its
        // own errors, so this then() always runs.
        const messagesDone = Promise.all([
          loadGreetingsAndFarewells(),
          loadVoicemail(),
        ]).then(() => { if (!cancelled) setMessagesLoadedFor(practiceId); });

        await Promise.all([
          checkSesStatus(),
          messagesDone,
          loadTherapistsList(),
          loadTherapyTypesList(),
          loadTherapyCategoriesList(),
          loadPriorityMatrixCached(),
          loadLocationMatrixCached(),
        ]);

      } catch (error) {
        // 404 is unexpected now — PracticeContext only hands out practice IDs
        // the user can access. Log loudly so we notice if it ever happens.
        if (error.response) {
          console.error(`Error loading practice info for practice ${practiceId}: Status ${error.response.status}`, error.response.data || error.message);
        } else {
          console.error(`Network or other error loading practice info for practice ${practiceId}:`, error.message);
        }
      }
    };

    loadPracticeInfo();
    // Invalidate this run's message setters when the practice switches (or the
    // component unmounts) so an in-flight load can't write the previous
    // practice's text after a newer load has already populated state.
    return () => { cancelled = true; };
    // practiceReloadNonce: bumped by the live config sync to re-run this whole
    // hydration after someone else changed the practice row / messages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, practiceReloadNonce]);

  // Lazy-load the therapist-location matrix only when its UI is actually
  // viewable: Behandlungen & Team tab AND multi-location enabled (the section
  // is hidden entirely otherwise). Single-location practices — the common
  // case — never pay this query on page mount. Cleared on practice switch by
  // the main load effect above; refreshed after mutations via
  // loadTherapistLocationMatrix as before.
  useEffect(() => {
    if (!practiceId) return;
    if (activeTab !== 'team' || !enableMultiLocation) return;
    if (therapistLocationMatrix) return;
    loadTherapistLocationMatrix(practiceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, activeTab, enableMultiLocation, therapistLocationMatrix]);

  // Load SIP config + status when practiceId resolves. Phase 2 follow-up:
  // SIP endpoints are now per-practice — switching the practice context
  // reloads this effect and pulls the right trunk row.
  useEffect(() => {
    if (!practiceId) return;

    checkConfigurationStatus();

    // Staleness guard (Jul 2026 cross-tenant incident): a slow GET from a
    // previously-selected practice must never populate sipConfig/sipConfigId
    // after a switch — a save would then PUT the OTHER practice's trunk row.
    let cancelled = false;
    setSipLoadedFor(null);

    const loadSipConfig = async () => {
      try {
        console.log("=== LOADING SIP CONFIG ===");
        console.log("Practice ID:", practiceId);
        console.log("API call:", `${API_BASE_URL}/sip/practice/${practiceId}`);

        const response = await axios.get(`${API_BASE_URL}/sip/practice/${practiceId}`);
        if (cancelled) return;

        console.log("SIP config response:", response.data);
        console.log("SIP config ID from response:", response.data.id);

        // Back-compat: older backends return only the legacy boolean. Derive the
        // 3-way mode so the dropdown is never blank.
        if (response.data && response.data.parallel_call_mode == null) {
          response.data.parallel_call_mode = response.data.enable_multi_call ? 'parallel' : 'voicemail';
        }
        setSipConfig(response.data);
        setSipConfigId(response.data.id);
        setSipLoadedFor(practiceId);

        console.log("SIP config loaded successfully, ID set to:", response.data.id);
      } catch (error) {
        if (cancelled) return;
        console.log("=== SIP CONFIG LOAD ERROR ===");
        console.log("Error:", error);
        console.log("Error response:", error.response);

        // If not found (404), use defaults - explicitly reset SIP config to defaults
        if (error.response && error.response.status === 404) {
          console.log(`No SIP config found for practice ${practiceId}, using empty defaults.`);
          setSipConfig({
            server_address: '',
            username: '',
            password: '',
            port: '',
            enabled: false,
            proxy_url: '',
            sip_phone_number: '',
            parallel_call_mode: 'voicemail'
          });
          setSipConfigId(null);
          // 404 = no config yet: the tab is safely editable (creates a new row).
          // Any OTHER error keeps the tab shrouded — editing defaults over an
          // unloaded config is exactly the incident class we're preventing.
          setSipLoadedFor(practiceId);
          console.log("SIP config ID set to null");
        } else if (error.response) {
          console.error('Error loading SIP config:', error);
        }
      }
    };

    loadSipConfig();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const handlePracticeInfoChange = (e) => {
    const { name, value } = e.target;

    setPracticeInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value
    }));
    setPracticeTabDirty(true);

    // Reset SES status when email changes
    if (name === 'email') {
      setSesVerificationStatus(null);
    }

    // Reset save status when field changes
    setSaveStatus(prev => ({
      ...prev,
      practice: { saved: false, message: '' }
    }));
  };

  const handleVerifyEmail = async () => {
    if (!practiceInfo.email) return;
    setSesVerifyLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/practices/verify-email`, { email: practiceInfo.email });
      setSesVerificationStatus('Pending');
    } catch (err) {
      console.error('SES verify email failed:', err);
      alert('Fehler beim Senden der Verifizierungs-E-Mail. Bitte prüfen Sie die AWS-Zugangsdaten.');
    } finally {
      setSesVerifyLoading(false);
    }
  };

  const handleCheckEmailStatus = async () => {
    if (!practiceInfo.email) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/practices/verify-email-status`, { params: { email: practiceInfo.email } });
      setSesVerificationStatus(res.data.status);
    } catch (err) {
      console.error('SES status check failed:', err);
    }
  };
  
  const handleOpeningHoursChange = (day, field, value) => {
    setPracticeTabDirty(true);
    const updatedHours = applyDayHoursUpdate(practiceInfoRef.current?.opening_hours, day, field, value);
    const updatedInfo = {
      ...practiceInfoRef.current,
      opening_hours: updatedHours,
    };
    practiceInfoRef.current = updatedInfo;
    setPracticeInfo(updatedInfo);

    const serialized = serializePracticeDetails(updatedInfo);
    const payload = preparePracticePayload(updatedInfo);
    const pending = { targetPracticeId: practiceId, payload, serialized };
    pendingPracticeWriteRef.current = pending;

    if (field === 'closed') {
      commitPracticeInfo(pending);
    } else {
      if (practiceAutoSaveTimerRef.current) clearTimeout(practiceAutoSaveTimerRef.current);
      practiceAutoSaveTimerRef.current = setTimeout(() => {
        commitPracticeInfo(pendingPracticeWriteRef.current);
      }, 500);
    }
  };
  
  const handleSipConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSipConfig((prevConfig) => ({
      ...prevConfig,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Reset save status when field changes
    setSaveStatus(prev => ({ 
      ...prev, 
      sip: { saved: false, message: '', error: false } 
    }));
    // Reset test status when field changes
    setTestStatus({ testing: false, success: false, message: '' });
  };
  
  const commitPracticeInfo = async (pending = null) => {
    const toCommit = pending || pendingPracticeWriteRef.current;
    if (!toCommit) return;
    // Speichern submitted the form, so the browser refused an invalid e-mail (the sender
    // of the confirmation mails). The auto-save keeps that rule: the edit stays pending.
    if (!isBrowserValidEmail(toCommit.payload?.email)) return;
    const { targetPracticeId, payload, serialized } = toCommit;
    pendingPracticeWriteRef.current = null;
    if (practiceAutoSaveTimerRef.current) {
      clearTimeout(practiceAutoSaveTimerRef.current);
      practiceAutoSaveTimerRef.current = null;
    }

    if (!targetPracticeId || loadedForPracticeIdRef.current !== targetPracticeId) return;

    if (isMountedRef.current) {
      setPracticeSaving(true);
      setSaveStatus(prev => ({
        ...prev,
        practice: { saved: false, message: 'Speichere...' }
      }));
    }

    try {
      await axios.put(`${API_BASE_URL}/practices/${targetPracticeId}`, payload);

      if (loadedForPracticeIdRef.current !== targetPracticeId) return;

      practiceBaselineRef.current = serialized;
      queryClient.invalidateQueries(['practiceInfo', targetPracticeId]);

      if (isMountedRef.current) {
        setPracticeTabDirty(false);
        setSaveStatus(prev => ({
          ...prev,
          practice: { saved: true, message: 'Praxis-Informationen gespeichert' }
        }));

        if (saveStatusClearTimerRef.current) clearTimeout(saveStatusClearTimerRef.current);
        saveStatusClearTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setSaveStatus(prev => ({
              ...prev,
              practice: { saved: false, message: '' }
            }));
          }
        }, 2500);
      }
    } catch (err) {
      console.error('Practice auto-save failed:', err);
      if (isMountedRef.current && loadedForPracticeIdRef.current === targetPracticeId) {
        const errorDetail = err.response?.data?.detail || err.message;
        setSaveStatus(prev => ({
          ...prev,
          practice: { saved: false, message: `Fehler beim Speichern: ${errorDetail}` }
        }));
      }
    } finally {
      if (isMountedRef.current) {  // the save is over even if the practice changed meanwhile
        setPracticeSaving(false);
      }
    }
  };

  const handlePracticeFieldBlur = () => {
    const serialized = serializePracticeDetails(practiceInfoRef.current);
    if (practiceBaselineRef.current !== serialized) {
      const payload = preparePracticePayload(practiceInfoRef.current);
      const pending = { targetPracticeId: practiceId, payload, serialized };
      pendingPracticeWriteRef.current = pending;
      commitPracticeInfo(pending);
    }
  };

  useEffect(() => {
    if (!practiceId || !practiceInfoLoadedRef.current || loadedForPracticeIdRef.current !== practiceId || formLoadedFor !== practiceId) {
      return;
    }
    const serialized = serializePracticeDetails(practiceInfo);
    if (practiceBaselineRef.current === null) {
      practiceBaselineRef.current = serialized;
      return;
    }
    if (practiceBaselineRef.current === serialized) {
      pendingPracticeWriteRef.current = null;
      return;
    }

    const payload = preparePracticePayload(practiceInfo);
    const pending = { targetPracticeId: practiceId, payload, serialized };
    pendingPracticeWriteRef.current = pending;

    if (practiceAutoSaveTimerRef.current) clearTimeout(practiceAutoSaveTimerRef.current);
    practiceAutoSaveTimerRef.current = setTimeout(() => {
      commitPracticeInfo(pendingPracticeWriteRef.current);
    }, 800);

    return () => {
      if (practiceAutoSaveTimerRef.current) clearTimeout(practiceAutoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    practiceId, formLoadedFor,
    practiceInfo.name, practiceInfo.address,
    practiceInfo.area_code, practiceInfo.phone_number,
    practiceInfo.fax_number, practiceInfo.email,
    practiceInfo.website, practiceInfo.opening_hours
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pendingPracticeWriteRef.current) {
        commitPracticeInfo(pendingPracticeWriteRef.current);
      }
      if (pendingReceptionWriteRef.current) {
        commitReceptionHours(pendingReceptionWriteRef.current);
      }
      if (pendingFaqWriteRef.current) {
        commitFaq(pendingFaqWriteRef.current);
      }
      if (saveStatusClearTimerRef.current) clearTimeout(saveStatusClearTimerRef.current);
      if (practiceAutoSaveTimerRef.current) clearTimeout(practiceAutoSaveTimerRef.current);
      if (receptionSaveTimerRef.current) clearTimeout(receptionSaveTimerRef.current);
      if (faqSaveTimerRef.current) clearTimeout(faqSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleSaveSipConfig = async (e) => {
    e.preventDefault();
    if (!userId) {
      setSaveStatus(prev => ({
        ...prev,
        sip: {
          saved: false,
          message: 'Benutzer nicht authentifiziert'
        }
      }));
      return;
    }
    // Cross-tenant write guard (Jul 2026 incident): sipConfigId may still hold
    // ANOTHER practice's trunk row while its load is in flight — a save then
    // PUTs that other practice's SIP credentials. Only save once the page's
    // data is bound to the currently selected practice.
    if (!practiceId || loadedForPracticeIdRef.current !== practiceId) {
      setSaveStatus(prev => ({
        ...prev,
        sip: { saved: false, message: 'Praxisdaten werden noch geladen – bitte einen Moment warten und erneut speichern.' }
      }));
      return;
    }
    try {
      setSipSaving(true);
      console.log("=== SAVING SIP CONFIG ===");
      console.log("User ID:", userId);
      console.log("SIP Config ID:", sipConfigId);
      console.log("Will use:", sipConfigId ? "PUT (update)" : "POST (create)");
      
      setSaveStatus(prev => ({ 
        ...prev, 
        sip: { saved: false, message: 'Speichere...', error: false } 
      }));
      let response;
      const sanitizedConfig = getSanitizedSipConfig(sipConfig);
      console.log("Sanitized config:", sanitizedConfig);
      
      if (sipConfigId) {
        console.log("Making PUT request to:", `${API_BASE_URL}/sip/${sipConfigId}`);
        response = await axios.put(`${API_BASE_URL}/sip/${sipConfigId}`, sanitizedConfig);
      } else {
        console.log("Making POST request to:", `${API_BASE_URL}/sip/`);
        response = await axios.post(`${API_BASE_URL}/sip/`, sanitizedConfig, {
          params: { practice_id: practiceId }
        });
        setSipConfigId(response.data.id);
      }
      setSaveStatus(prev => ({
        ...prev,
        sip: { saved: true, message: 'SIP-Konfiguration erfolgreich gespeichert', error: false }
      }));
      console.log('SIP config saved for practice:', practiceId, sipConfig);
    } catch (error) {
      console.error('Error saving SIP config:', error);
      setSaveStatus(prev => ({
        ...prev,
        sip: { saved: false, message: `Fehler beim Speichern: ${error.response?.data?.detail || error.message}`, error: true }
      }));
    } finally {
      setSipSaving(false);
    }
  };
  
  const handleTestSipConnection = async () => {
    if (!userId) {
      setTestStatus({ 
        testing: false, 
        success: false, 
        message: 'Benutzer nicht authentifiziert' 
      });
      return;
    }
    
    try {
      setTestStatus({ 
        testing: true, 
        success: false, 
        message: 'Verbindung wird getestet...' 
      });
      
      // Test SIP connection
      const response = await axios.post(`${API_BASE_URL}/sip/test`, sipConfig);
      
      setTestStatus({ 
        testing: false, 
        success: response.data.success, 
        message: response.data.message 
      });
      setTimeout(() => setTestStatus({ testing: false, success: false, message: '' }), 3000);
    } catch (error) {
      console.error('Error testing SIP connection:', error);
      setTestStatus({ 
        testing: false, 
        success: false, 
        message: `Verbindungstest fehlgeschlagen: ${error.response?.data?.detail || error.message}` 
      });
    }
  };
  
  const handleDeleteSipConfig = async () => {
    if (!sipConfigId) return;
    try {
      await axios.delete(`${API_BASE_URL}/sip/${sipConfigId}`);
      setSipConfig({
        server_address: '',
        username: '',
        password: '',
        port: '',
        enabled: false,
        proxy_url: '',
        sip_phone_number: ''
      });
      setSipConfigId(null);
      setSaveStatus(prev => ({ ...prev, sip: { saved: false, message: 'SIP-Konfiguration gelöscht', error: false } }));
    } catch (error) {
      setSaveStatus(prev => ({ ...prev, sip: { saved: false, message: `Fehler beim Löschen: ${error.response?.data?.detail || error.message}`, error: true } }));
    }
  };
  
  // --- Refactor Test Phone Call Modal ---
  const [testCallTab, setTestCallTab] = useState('outbound');
  const handleTestSipPhoneCall = async () => {
    if (!userId) {
      setTestStatus({ testing: false, success: false, message: 'Benutzer-ID nicht verfügbar. Kann Test nicht durchführen.' });
      return;
    }
    if (!sipConfigId || !sipConfig.enabled) {
      setTestStatus({ testing: false, success: false, message: 'SIP-Konfiguration ist nicht aktiv oder nicht gespeichert.' });
      return;
    }
    setShowOutboundModal(true);
    setTestCallTab('outbound');
    setOutboundPhoneNumber('');
    setOutboundCallStatus({ testing: false, success: false, message: '' });
    setInboundTestStatus({ status: 'idle', message: '' });
  };

  const handleInitiateOutboundCall = async () => {
    if (!outboundPhoneNumber) {
      setOutboundCallStatus({ testing: false, success: false, message: 'Bitte geben Sie eine Telefonnummer ein, um anzurufen.' });
      return;
    }
    setOutboundCallStatus({ testing: true, success: false, message: `Versuche, ${outboundPhoneNumber} anzurufen...` });

    try {
      const response = await axios.post(`${API_BASE_URL}/sip/initiate-outbound-call/${practiceId}`, {
        target_phone_number: outboundPhoneNumber
      });
      setOutboundCallStatus({ testing: false, success: response.data.success, message: response.data.message });
      // Optionally close modal on success, or let user close it
      // if (response.data.success) { 
      //   setTimeout(() => setShowOutboundModal(false), 2000); // Close after 2s on success
      // }
    } catch (error) {
      let errorMessage = 'Fehler beim Initiieren des Ausgehenden Anrufs.';
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = typeof error.response.data.detail === 'string' ? error.response.data.detail : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      setOutboundCallStatus({ testing: false, success: false, message: errorMessage });
    }
  };

  const closeOutboundModal = () => {
    setShowOutboundModal(false);
    // Stop inbound test mode if it's active
    if (inboundTestPolling && practiceId) {
      handleStopInboundTest();
    }
    // Stop forwarding test mode if it's active
    if (forwardingTestPolling && practiceId) {
      handleStopForwardingTest();
    }
  };

  const handleStopInboundTest = async () => {
    if (inboundTestIntervalRef.current) {
      clearInterval(inboundTestIntervalRef.current);
    }
    setInboundTestPolling(false);
    setInboundTestStatus({ status: 'waiting', message: '' });

    if (practiceId) {
      try {
        await axios.post(`${API_BASE_URL}/sip/stop-inbound-test/${practiceId}`);
      } catch (e) {
        console.error('Failed to stop inbound test mode:', e);
      }
    }
  };

  const handleStartInboundTest = async () => {
    if (!practiceId || !sipConfig.sip_phone_number) {
      setInboundTestStatus({ status: 'error', message: 'SIP-Telefonnummer ist nicht gesetzt.' });
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/sip/start-inbound-test/${practiceId}`);

      setInboundTestStatus({ status: 'waiting', message: 'Warte auf eingehenden Anruf...' });
      setInboundTestPolling(true);

      if (inboundTestIntervalRef.current) clearInterval(inboundTestIntervalRef.current);
      inboundTestIntervalRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/sip/inbound-test-status/${practiceId}`);
          if (res.data.status === 'received') {
            setInboundTestStatus({ status: 'success', message: 'Eingehender Anruf erhalten - Test erfolgreich!' });
            setInboundTestPolling(false);
            clearInterval(inboundTestIntervalRef.current);
            // Test mode is automatically stopped by the backend after successful test
          }
        } catch (e) {
          setInboundTestStatus({ status: 'error', message: 'Fehler beim Überprüfen des Status des eingehenden Anrufs.' });
          setInboundTestPolling(false);
          clearInterval(inboundTestIntervalRef.current);
        }
      }, 2000);
    } catch (e) {
      setInboundTestStatus({ status: 'error', message: 'Fehler beim Starten des eingehenden Tests.' });
    }
  };

  React.useEffect(() => {
    return () => {
      if (inboundTestIntervalRef.current) clearInterval(inboundTestIntervalRef.current);
      if (forwardingTestIntervalRef.current) clearInterval(forwardingTestIntervalRef.current);
    };
  }, []);

  // Forwarding test handlers
  const handleStartForwardingTest = async () => {
    if (!practiceId || !sipConfig.sip_phone_number) {
      setForwardingTestStatus({ status: 'error', message: 'SIP-Telefonnummer ist nicht gesetzt.' });
      return;
    }
    if (!forwardingPhoneNumber) {
      setForwardingTestStatus({ status: 'error', message: 'Bitte geben Sie eine Zieltelefonnummer ein.' });
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/sip/start-forwarding-test/${practiceId}`, {
        target_phone_number: forwardingPhoneNumber
      });

      setForwardingTestStatus({ status: 'waiting', message: 'Warte auf eingehenden Anruf zum Weiterleiten...' });
      setForwardingTestPolling(true);

      if (forwardingTestIntervalRef.current) clearInterval(forwardingTestIntervalRef.current);
      forwardingTestIntervalRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/sip/forwarding-test-status/${practiceId}`);
          if (res.data.status === 'completed') {
            setForwardingTestStatus({ status: 'success', message: 'Anruf erfolgreich weitergeleitet!' });
            setForwardingTestPolling(false);
            clearInterval(forwardingTestIntervalRef.current);
            // Test mode is automatically stopped by the backend after successful test
          }
        } catch (e) {
          setForwardingTestStatus({ status: 'error', message: 'Fehler beim Überprüfen des Weiterleitungsstatus.' });
          setForwardingTestPolling(false);
          clearInterval(forwardingTestIntervalRef.current);
        }
      }, 2000);
    } catch (e) {
      setForwardingTestStatus({ status: 'error', message: 'Fehler beim Starten des Weiterleitungstests.' });
    }
  };

  const handleStopForwardingTest = async () => {
    if (forwardingTestIntervalRef.current) {
      clearInterval(forwardingTestIntervalRef.current);
    }
    setForwardingTestPolling(false);
    setForwardingTestStatus({ status: 'idle', message: '' });

    if (practiceId) {
      try {
        await axios.post(`${API_BASE_URL}/sip/stop-forwarding-test/${practiceId}`);
      } catch (e) {
        console.error('Failed to stop forwarding test mode:', e);
      }
    }
  };
  
  // Fetch persisted LLM config — re-runs when the selected practice changes
  // so the Anrufablauf-Konfiguration tab swaps to the right per-practice values.
  // Uses the per-practice endpoint which returns the EFFECTIVE config (global
  // merged with per-practice overrides), so the KI-Konfiguration tab still
  // shows the global provider/keys/connections.
  useEffect(() => {
    if (!practiceId) return;
    // Gate the auto-save listener so it cannot fire with the previous
    // practice's React state while the new practice's config is in flight —
    // otherwise switching practices would overwrite the just-selected
    // practice's saved values with the outgoing practice's stale state.
    callflowConfigLoadedRef.current = false;
    setCallflowLoadedFor(null);
    // New baseline will be captured on the first auto-save-effect run after
    // this practice's config hydrates (see callflowBaselineRef).
    callflowBaselineRef.current = null;
    callflowDraftBaselineRef.current = null;
    storedVariantFlagsRef.current = {};
    // Staleness guard for the consent question (an Anrufablauf message field
    // that, like greeting/farewell/voicemail, is read back into the Save body).
    // Clear it up-front and ignore an older run's setter so a practice switch
    // can't leave the previous practice's consent text in state.
    let cancelled = false;
    setConsentQuestion('');
    // Routed through React Query's cache (fetchQuery) so revisiting the
    // Configuration tab within staleTime returns instantly without a network
    // round-trip. All hydration / staleness-guard / auto-save-gate logic below
    // is unchanged — only how `res` is obtained differs. Invalidated on save.
    queryClient.fetchQuery(
      ['practiceLlmConfig', practiceId],
      async () => (await axios.get(`${API_BASE_URL}/test-call/practice-llm-config/${practiceId}`)).data
    ).then(data => {
      const res = { data };
      if (cancelled) return;
      if (res.data) {
        // Set provider
        setProvider(res.data.provider || 'bedrock');
        // Load KI mode with backward compatibility (convert old boolean to string)
        let loadedKiMode = res.data.ki_mode || res.data.agentic_mode;
        if (typeof loadedKiMode === 'boolean') {
          loadedKiMode = loadedKiMode ? 'agentic' : 'smart_ab';
        }
        // Recompose unified mode: if use_langgraph is true, override to 'langgraph'
        if (res.data.use_langgraph) {
          setKiMode('langgraph');
        } else {
          setKiMode(loadedKiMode || 'smart_ab');
        }
        setRequireEmail(res.data.require_email !== undefined ? res.data.require_email : false);
        setRequireBirthdate(res.data.require_birthdate !== undefined ? res.data.require_birthdate : true);
        setRequirePhone(res.data.require_phone !== undefined ? res.data.require_phone : true);
        setRequireAddress(res.data.require_address !== undefined ? res.data.require_address : false);
        setTrustCallerId(res.data.trust_caller_id !== undefined ? res.data.trust_caller_id : true);
        setRejectCallerIds(Array.isArray(res.data.reject_caller_ids) ? res.data.reject_caller_ids : []);
        setRequireVerordnung(res.data.require_verordnung !== undefined ? res.data.require_verordnung : false);
        setAllowMultiTherapistSeries(res.data.allow_multi_therapist_series !== undefined ? res.data.allow_multi_therapist_series : false);
        // Not clamped against the therapist roster: it may not have loaded yet, and
        // clamping here would silently rewrite a valid stored value. The roster
        // clamp effect does it once the roster has arrived.
        const storedMaxTherapists = Number(res.data.max_therapists_per_series);
        setMaxTherapistsPerSeries(
          Number.isFinite(storedMaxTherapists) && storedMaxTherapists >= 2
            ? Math.min(storedMaxTherapists, MAX_THERAPISTS_ALL)
            : 3
        );
        setRequireInsuranceCompany(res.data.require_insurance_company !== undefined ? res.data.require_insurance_company : false);
        setRequireWhatsappOptin(res.data.require_whatsapp_optin !== undefined ? res.data.require_whatsapp_optin : false);
        setEnableForwarding(res.data.enable_forwarding !== undefined ? res.data.enable_forwarding : true);
        // Mirrors the backend inherit (is_keyword_forwarding_enabled): a practice that
        // has not saved since the two switches were split has no stored value, and
        // keyword forwarding was a strict subset of the general switch back then — so
        // show that, and don't flip anything on the next save.
        setEnableKeywordForwarding(
          res.data.enable_keyword_forwarding !== undefined
            ? res.data.enable_keyword_forwarding
            : (res.data.enable_forwarding !== undefined ? res.data.enable_forwarding : true)
        );
        setForwardNoanswerVoicemail(res.data.forward_noanswer_voicemail === true);
        setForwardNoanswerRingSeconds(Number(res.data.forward_noanswer_ring_seconds) || 10);
        const loadedEntries = whitelistEntriesFromConfig(res.data);
        const isLegacyDirect = res.data.enable_direct_forwarding === undefined && res.data.enable_whitelist_forwarding === true && loadedEntries.length === 0;
        setEnableDirectForwarding(res.data.enable_direct_forwarding === true || isLegacyDirect);
        setDirectForwardingNumber(res.data.direct_forwarding_number !== undefined ? (res.data.direct_forwarding_number || '') : (isLegacyDirect ? (res.data.whitelist_forwarding_number || '') : ''));
        setEnableWhitelistForwarding(isLegacyDirect ? false : res.data.enable_whitelist_forwarding === true);
        setWhitelistForwardingNumber(res.data.whitelist_forwarding_number || '');
        setWhitelistEntries(loadedEntries);
        const lgFlows = res.data.langgraph_flows || {};
        setLanggraphCancelEnabled(lgFlows.cancel !== undefined ? lgFlows.cancel : true);
        setLanggraphMoveEnabled(lgFlows.move !== undefined ? lgFlows.move : true);
        setLanggraphBookEnabled(lgFlows.book !== undefined ? lgFlows.book : true);
        setCancellationFeePeriod(res.data.cancellation_fee_period !== undefined ? res.data.cancellation_fee_period : 0);
        setCancellationFeeNotice(res.data.cancellation_fee_notice !== undefined ? res.data.cancellation_fee_notice : 'Es kann eine Ausfallgebühr anfallen');
        setConsentQuestion(res.data.consent_question !== undefined ? res.data.consent_question : 'Bevor wir loslegen darf ich das Gespräch transkribieren und aufzeichnen, damit das Praxisteam es bearbeiten kann?');
        setConsentUpfront(res.data.consent_upfront === true);
        setDataRetentionEnabled(res.data.data_retention_enabled === true);
        setDataRetentionDays(Math.max(7, Number(res.data.data_retention_days) || 30));
        // Empty is the meaningful default here — it means "use the standard sentence".
        setForwardingMessage(res.data.forwarding_message || '');
        storedVariantFlagsRef.current = {
          pvs: res.data.enable_greeting_pvs !== undefined ? !!res.data.enable_greeting_pvs : undefined,
          away: res.data.enable_greeting_away !== undefined ? !!res.data.enable_greeting_away : undefined,
          appt: res.data.enable_farewell_appointment !== undefined ? !!res.data.enable_farewell_appointment : undefined,
        };
        if (res.data.enable_greeting_pvs !== undefined) setEnableGreetingPvs(!!res.data.enable_greeting_pvs);
        if (res.data.enable_greeting_away !== undefined) setEnableGreetingAway(!!res.data.enable_greeting_away);
        if (res.data.enable_farewell_appointment !== undefined) setEnableFarewellAppointment(!!res.data.enable_farewell_appointment);
        // Patient identification method: 'phone' or 'birthdate'.
        // Backend derives this from legacy flags for older configs, so we just trust the response.
        setPatientIdentificationMethod(
          res.data.patient_identification_method === 'phone' ? 'phone' : 'birthdate'
        );
        setBookUnidentifiedReturningAsNew(!!res.data.book_unidentified_returning_as_new);
        // Backward compatibility: if the new sms_to_caller flag isn't present in the
        // saved config, fall back to the legacy sms_confirmation_enabled flag so
        // existing practices keep their previous behavior.
        setSmsToCallerEnabled(
          res.data.sms_to_caller_enabled !== undefined
            ? !!res.data.sms_to_caller_enabled
            : !!res.data.sms_confirmation_enabled
        );
        setSmsToNumberEnabled(!!res.data.sms_to_number_enabled);
        setSmsNotificationPhoneNumber(res.data.sms_notification_phone_number || '');
        // Unknown/absent -> 'seven', matching the backend default in get_sms_provider().
        setSmsProvider(res.data.sms_provider === 'aws' ? 'aws' : 'seven');
        setSevenApiKey(res.data.seven_api_key || '');
        setSmsSenderId(res.data.sns_sender_id || '');
        setEmailConfirmationEnabled(res.data.email_confirmation_enabled || false);
        setCallAmbienceEnabled(!!res.data.call_ambience_enabled);
        setCallAmbienceSound(res.data.call_ambience_sound || 'office1');
        setAcceptableCancellationReasonsEnabled(res.data.acceptable_cancellation_reasons_enabled !== undefined ? res.data.acceptable_cancellation_reasons_enabled : false);
        setAcceptableCancellationReasons(res.data.acceptable_cancellation_reasons !== undefined ? res.data.acceptable_cancellation_reasons : '');
        setMentionTherapistInCancelMove(res.data.mention_therapist_in_cancel_move !== undefined ? !!res.data.mention_therapist_in_cancel_move : true);
        setAskCancelMoveReasonEnabled(!!res.data.ask_cancel_move_reason_enabled);
        setAskSymptomsEnabled(res.data.ask_symptoms_enabled !== undefined ? res.data.ask_symptoms_enabled : true);
        setEnableDualVad(res.data.enable_dual_vad !== undefined ? res.data.enable_dual_vad : true); // Load dual-VAD toggle, default to true
        setEnableMultiLocation(res.data.enable_multi_location !== undefined ? res.data.enable_multi_location : false);
        setSlotRasterEnabled(!!res.data.slot_raster_enabled);
        setSlotRasterMinutes([15, 20, 30, 60].includes(res.data.slot_raster_minutes) ? res.data.slot_raster_minutes : 30);
        setSameDayCutoffEnabled(!!res.data.same_day_cutoff_enabled);
        setSameDayCutoffTime(SAME_DAY_CUTOFF_OPTIONS.some(o => o.value === res.data.same_day_cutoff_time)
          ? res.data.same_day_cutoff_time : '15:00');
        setPvsSyncTimeoutSeconds(res.data.pvs_sync_timeout_seconds ?? 10); // PVS pre-answer sync window (per-practice ai_config row)

        // Set Bedrock models with fallback to legacy modelId
        setPrimaryModel(res.data.primaryModelId || res.data.modelId || '');
        setSecondaryModel(res.data.secondaryModelId || res.data.modelId || '');
        setLlmModel(res.data.modelId || ''); // Legacy support
        
        // Set Azure configuration - connections and use-case mapping
        let loadedConns = res.data.azure_connections || [];
        // Apply saved drag order from localStorage
        const savedOrder = localStorage.getItem('azureConnectionOrder');
        if (savedOrder && loadedConns.length > 0) {
          try {
            const orderIds = JSON.parse(savedOrder);
            const byId = Object.fromEntries(loadedConns.map(c => [c.id, c]));
            const ordered = orderIds.filter(id => byId[id]).map(id => byId[id]);
            // Append any new connections not in saved order
            const orderedIds = new Set(orderIds);
            loadedConns.forEach(c => { if (!orderedIds.has(c.id)) ordered.push(c); });
            if (ordered.length === loadedConns.length) loadedConns = ordered;
          } catch (e) { /* ignore corrupt localStorage */ }
        }
        setAzureConnections(loadedConns);
        setAzureUseCaseMapping(res.data.azure_use_case_mapping || {});

        // Set Gemini configuration (Vertex AI with EU region)
        setGeminiServiceAccountJson(res.data.gemini_service_account_json || '');
        setGeminiProjectId(res.data.gemini_project_id || '');
        setGeminiRegion(res.data.gemini_region || 'europe-west1');
        // Normalize Gemini models to match dropdown options - invalid/stale values become empty (fallback)
        setGeminiPrimaryModel(normalizeGeminiModel(res.data.gemini_primary_model));
        setGeminiSecondaryModel(normalizeGeminiModel(res.data.gemini_secondary_model));
        setGeminiTertiaryModel(normalizeGeminiModel(res.data.gemini_tertiary_model));
        setGeminiQuaternaryModel(normalizeGeminiModel(res.data.gemini_quaternary_model));

        // STT Provider
        setSttProvider(res.data.stt_provider || 'aws');

        // TTS Provider
        setTtsProvider(res.data.tts_provider || 'aws');

        // Azure Speech credentials (shared for STT and TTS)
        setAzureSpeechKey(res.data.stt_azure_speech_key || '');
        setAzureSpeechRegion(res.data.stt_azure_speech_region || '');

        // Azure TTS voice selection
        setAzureTtsVoice(res.data.tts_azure_voice_name || 'de-DE-Seraphina:DragonHDLatestNeural');

        // AWS Polly voice selection
        setAwsPollyVoice(res.data.tts_aws_polly_voice_id || 'Daniel');
        setAwsPollyUseGenerative(res.data.tts_aws_polly_use_generative || false);

        // Voxtral TTS voice selection
        setVoxtralVoice(res.data.tts_voxtral_voice_id || 'gb_oliver_neutral');

        // ElevenLabs TTS configuration
        setElevenlabsApiKey(res.data.tts_elevenlabs_api_key || '');
        setDeepgramApiKey(res.data.deepgram_api_key || '');
        setElevenlabsVoiceId(res.data.tts_elevenlabs_voice_id || 'XB0fDUnXU5powFXDhCwa');
        setElevenlabsModelId(res.data.tts_elevenlabs_model_id || 'eleven_flash_v2_5');

        // Kugel Audio TTS configuration
        setKugelApiKey(res.data.tts_kugel_api_key || '');
        setKugelVoiceId(res.data.tts_kugel_voice_id || '1704');
        setKugelModelId(res.data.tts_kugel_model_id || 'kugel-3');
        setKugelUseWebsocket(res.data.tts_kugel_use_websocket || false);

        // AWS credentials (used for Transcribe/Polly)
        setAwsAccessKeyId(res.data.aws_access_key_id || '');
        setAwsSecretAccessKey(res.data.aws_secret_access_key || '');

        // Batch transcription provider (for unclear_answer and voicemail)
        setBatchTranscribeProvider(res.data.batch_transcribe_provider || 'gpt-transcribe');
        setGpt4oEmergencyDeployment(res.data.gpt4o_emergency_transcribe_deployment || 'gpt-4o-mini-transcribe');
        setEmergencyTranscribeProvider(res.data.emergency_transcribe_provider || 'gpt-transcribe');

        // Mistral AI credentials
        setMistralApiKey(res.data.mistral_api_key || '');
      }
      // Mark config as fully loaded so auto-save can fire on subsequent
      // switcher changes without persisting the initial-load state.
      callflowConfigLoadedRef.current = true;
      setCallflowLoadedFor(practiceId);
    }).catch(() => {});
    // Invalidate this run's consent setter when the practice switches (or the
    // component unmounts) so an in-flight load can't write the previous
    // practice's consent text after a newer load has populated state.
    return () => { cancelled = true; };
    // callflowReloadNonce: bumped by the live config sync to re-run this
    // hydration after someone else changed the per-practice call-flow config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, callflowReloadNonce]);

  // Auto-save the call-flow tab whenever a switcher (boolean toggle) — or the
  // background-ambience enable/preset — changes.
  // Skipped until the initial load has populated state. Debounced 500ms so
  // rapid clicks coalesce into a single request.
  const callflowConfigLoadedRef = useRef(false);
  const callflowAutoSaveTimerRef = useRef(null);
  // Hydration-echo suppression: serialized view of every auto-saved toggle.
  // Loading a practice's config changes the same effect dependencies a user
  // click does, so without a baseline every practice switch fired a silent
  // save burst (the mechanism behind the Jul 2026 cross-tenant incident).
  // The baseline is captured on the first effect run after hydration and
  // advanced only after a successful persist — so only a REAL user change
  // (snapshot differs from baseline) arms the auto-save.
  const callflowBaselineRef = useRef(null);
  // Keyed by the BACKEND config key (not positional) so a silent auto-save can
  // ship only the toggles that actually changed. Sending the whole set is what
  // made two people flipping two DIFFERENT switches clobber each other: the
  // second PUT carried the first's key at its own stale value. The server merges
  // key-by-key (ai_config_store._save_practice_config), so a partial body is
  // already supported — no backend change needed.
  const callflowSnapshotObj = () => ({
    // Betriebsmodus. `use_langgraph` is derived from this and is wired as a
    // companion key below, the same way sms_confirmation_enabled is.
    ki_mode: kiMode === 'langgraph' ? 'agentic' : kiMode,
    require_birthdate: requireBirthdate,
    require_phone: requirePhone,
    require_address: requireAddress,
    trust_caller_id: trustCallerId,
    reject_caller_ids: rejectCallerIds,
    require_email: requireEmail,
    require_verordnung: requireVerordnung,
    allow_multi_therapist_series: allowMultiTherapistSeries,
    max_therapists_per_series: maxTherapistsPerSeries,
    require_insurance_company: requireInsuranceCompany,
    enable_multi_location: enableMultiLocation,
    require_whatsapp_optin: requireWhatsappOptin,
    enable_forwarding: enableForwarding,
    enable_keyword_forwarding: enableKeywordForwarding,
    forward_noanswer_voicemail: forwardNoanswerVoicemail,
    forward_noanswer_ring_seconds: forwardNoanswerRingSeconds,
    enable_direct_forwarding: enableDirectForwarding,
    direct_forwarding_number: directForwardingNumber,
    enable_whitelist_forwarding: enableWhitelistForwarding,
    whitelist_forwarding_number: whitelistForwardingNumber,
    ...whitelistEntriesToConfig(whitelistEntries),
    sms_to_caller_enabled: smsToCallerEnabled,
    email_confirmation_enabled: emailConfirmationEnabled,
    sms_to_number_enabled: smsToNumberEnabled,
    acceptable_cancellation_reasons_enabled: acceptableCancellationReasonsEnabled,
    mention_therapist_in_cancel_move: mentionTherapistInCancelMove,
    ask_cancel_move_reason_enabled: askCancelMoveReasonEnabled,
    ask_symptoms_enabled: askSymptomsEnabled,
    consent_upfront: consentUpfront,
    data_retention_enabled: dataRetentionEnabled,
    data_retention_days: dataRetentionDays,
    langgraph_book_enabled: langgraphBookEnabled,
    langgraph_cancel_enabled: langgraphCancelEnabled,
    langgraph_move_enabled: langgraphMoveEnabled,
    call_ambience_enabled: callAmbienceEnabled,
    call_ambience_sound: callAmbienceSound,
    slot_raster_enabled: slotRasterEnabled,
    slot_raster_minutes: slotRasterMinutes,
    same_day_cutoff_enabled: sameDayCutoffEnabled,
    same_day_cutoff_time: sameDayCutoffTime,
    // Non-switch settings that behave exactly like the switches above: a choice or
    // a short value, no audio to re-synthesize. They used to need an explicit
    // Speichern purely because they were never added here.
    cancellation_fee_period: cancellationFeePeriod,
    cancellation_fee_notice: cancellationFeeNotice,
    acceptable_cancellation_reasons: acceptableCancellationReasons,
    patient_identification_method: patientIdentificationMethod,
    book_unidentified_returning_as_new: bookUnidentifiedReturningAsNew,
    sms_notification_phone_number: smsNotificationPhoneNumber,
    sns_sender_id: smsSenderId,
    forwarding_message: forwardingMessage,
    enable_greeting_pvs: enableGreetingPvs,
    enable_greeting_away: enableGreetingAway,
    enable_farewell_appointment: enableFarewellAppointment,
  });
  const callflowSnapshot = () => JSON.stringify(callflowSnapshotObj());
  // Session 10d regression guard: handleLlmConfigSave PUTs the full practiceInfo
  // back to the backend (forwarding_phone_number + trigger_keywords live on the
  // practice row). When practiceId comes from PracticeContext (resolved on first
  // render) but loadPracticeInfo hasn't finished yet, practiceInfo is still its
  // empty defaults — firing the auto-save now wipes name/address/etc. Wait
  // until loadPracticeInfo has populated practiceInfo before allowing auto-save.
  // (Pre-10d, practiceId was set synchronously alongside practiceInfo so this
  // race was masked.)
  useEffect(() => {
    if (!callflowConfigLoadedRef.current || !practiceInfoLoadedRef.current || !practiceId) return;
    // Cross-tenant write guard: only auto-save when the form's data was loaded
    // for the CURRENTLY selected practice. practiceInfoLoadedRef alone is not
    // enough — a stale load for another practice used to re-set it to true.
    if (loadedForPracticeIdRef.current !== practiceId) return;
    if (messagesLoadedFor !== practiceId) return;
    const snapshot = callflowSnapshot();
    if (callflowBaselineRef.current === null) {
      // First run after this practice's config hydrated: record the baseline
      // and do NOT save — these dep changes are the load echo, not the user.
      callflowBaselineRef.current = snapshot;
      return;
    }
    if (callflowBaselineRef.current === snapshot) return; // no real change
    if (callflowAutoSaveTimerRef.current) clearTimeout(callflowAutoSaveTimerRef.current);
    callflowAutoSaveTimerRef.current = setTimeout(() => {
      handleLlmConfigSave({ preventDefault: () => {} }, { silent: true });
    }, 500);
    return () => {
      if (callflowAutoSaveTimerRef.current) clearTimeout(callflowAutoSaveTimerRef.current);
    };
    // formLoadedFor / callflowLoadedFor: the two "hydration finished" signals.
    // Without them a toggle clicked WHILE a load is in flight is lost for good —
    // the guards above early-return, and since the load flags are refs the effect
    // never re-runs to reconsider. The switch would sit in its new position,
    // never persisted. Harmless when nothing changed (snapshot === baseline) and
    // loop-safe after a re-hydration (the baseline is nulled first, so the fresh
    // server values are recorded rather than saved back out).
  }, [
    practiceId, formLoadedFor, callflowLoadedFor, messagesLoadedFor,
    enableGreetingPvs, enableGreetingAway, enableFarewellAppointment,
    requireBirthdate, requirePhone, requireAddress, trustCallerId, rejectCallerIds, requireEmail,
    requireVerordnung, allowMultiTherapistSeries, maxTherapistsPerSeries, requireInsuranceCompany,
    enableMultiLocation, requireWhatsappOptin, enableForwarding, enableKeywordForwarding, forwardNoanswerVoicemail, forwardNoanswerRingSeconds,
    enableDirectForwarding, directForwardingNumber, enableWhitelistForwarding, whitelistForwardingNumber, whitelistEntries,
    smsToCallerEnabled, emailConfirmationEnabled, smsToNumberEnabled,
    acceptableCancellationReasonsEnabled, mentionTherapistInCancelMove,
    askCancelMoveReasonEnabled, askSymptomsEnabled, consentUpfront,
    dataRetentionEnabled, dataRetentionDays,
    langgraphBookEnabled, langgraphCancelEnabled, langgraphMoveEnabled,
    callAmbienceEnabled, callAmbienceSound,
    slotRasterEnabled, slotRasterMinutes,
    sameDayCutoffEnabled, sameDayCutoffTime,
    cancellationFeePeriod, cancellationFeeNotice, acceptableCancellationReasons,
    patientIdentificationMethod, bookUnidentifiedReturningAsNew, smsNotificationPhoneNumber, smsSenderId, forwardingMessage,
    kiMode,
  ]);

  // The series cap follows the roster down: once therapists are removed below it,
  // lower it to what is left (never under 2, "alle" stays) and let the auto-save
  // above persist it, so it does not jump back up when therapists are re-added.
  // Gated on the auto-save's own hydration signals (formLoadedFor, callflowLoadedFor),
  // so its baseline already holds the stored value and the lowered one goes out as a
  // change, and on this practice's roster having arrived: clamping against a missing
  // or failed one ([]) would overwrite a valid cap.
  const rosterCap = Math.max(2, (therapists || []).filter(t => t.is_active !== false).length);
  useEffect(() => {
    if (formLoadedFor !== practiceId || callflowLoadedFor !== practiceId || therapistsLoadedFor !== practiceId) return;
    if (maxTherapistsPerSeries < MAX_THERAPISTS_ALL && maxTherapistsPerSeries > rosterCap) {
      setMaxTherapistsPerSeries(rosterCap);
    }
  }, [practiceId, formLoadedFor, callflowLoadedFor, therapistsLoadedFor, rosterCap, maxTherapistsPerSeries]);

  // Auto-save the SmartAB keyword chips, exactly like the switches around them.
  //
  // These chips used to be the ONLY control on this tab that needed an explicit
  // Speichern, on a page where 25 neighbouring switches persist themselves. Users
  // reasonably deleted a keyword, saw it disappear, left — and it was back on the
  // next load because nothing had ever been written. That was the single biggest
  // cause of the "the deleted keyword keeps coming back" reports.
  //
  // Goes through the dedicated single-field endpoint, never the whole-row
  // PUT /practices/{id}: that one would re-ship this browser's mount-time copy of
  // name / address / opening hours and revert whatever a colleague changed since.
  // Same guards as the switch auto-save: never fire before this practice's data
  // has loaded, never under a different practice, and treat the first post-
  // hydration run as the baseline rather than as a user edit.
  const callflowRowBaselineRef = useRef(null);
  const callflowRowSaveTimerRef = useRef(null);
  // The edit waiting out the debounce, if any. Carries its own practice id so a
  // flush can never write one practice's keywords onto another.
  const pendingCallflowRowRef = useRef(null);
  // Writes the row and clears the pending slot. Reads nothing from the render
  // scope (everything arrives as an argument or is stable across renders), so the
  // unmount effect below can safely hold on to the first render's copy.
  const commitCallflowRow = async (pending) => {
    if (!pending) return;
    const { targetPracticeId, payload, serialized } = pending;
    // Claim it up front: whoever gets here owns the write, so the timer and the
    // unmount flush can never both send it.
    pendingCallflowRowRef.current = null;
    try {
      await axios.put(`${API_BASE_URL}/practices/${targetPracticeId}/callflow-settings`, payload);
      callflowRowBaselineRef.current = serialized;
      // Keep the practice snapshot in step so a later whole-row save can't
      // re-ship the pre-edit values.
      setPracticeInfo(prev => ({
        ...prev,
        forwarding_phone_number: payload.forwarding_phone_number,
        recording_enabled: payload.recording_enabled,
        trigger_keywords: {
          smartab_keywords: payload.smartab_keywords,
          forwarding_rules: payload.forwarding_rules,
        },
      }));
      // The state above dies with this component; the CACHED practice row is what
      // the next mount hydrates from (fetchQuery, staleTime 3 min). Without this
      // drop, coming back to the tab within that window repaints the pre-edit
      // keywords — the author is the only client that misses the change, because
      // useConfigSync ignores our own broadcast echo (origin === TAB_ID).
      queryClient.invalidateQueries(['practiceInfo', targetPracticeId]);
    } catch (err) {
      console.error('Anrufablauf auto-save failed:', err);
    }
  };
  const splitCsvList = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);
  // The Anrufablauf controls that live on the PRACTICE ROW rather than in the
  // per-practice config: keyword chips, the forwarding number, the keyword-based
  // forwarding rules, and the recording toggle.
  const callflowRowPayload = () => ({
    // Include text still sitting in a chip input: a keyword typed without pressing
    // Enter is something the user believes they entered, and losing it on reload is
    // exactly the bug this whole change set exists to kill.
    smartab_keywords: [...new Set([
      ...splitCsvList(triggerKeywords.smartab_keywords),
      ...splitCsvList(smartabDraft),
    ])],
    // Rules are sent AS TYPED — incomplete ones included. Filtering them out here
    // made a rule vanish the moment the user cleared its number to retype it. The
    // backend stores them verbatim and the call path ignores any rule without a
    // number, so an unfinished rule is inert rather than lost.
    forwarding_rules: (triggerKeywords.forwarding_rules || []).map((r, idx) => ({
      id: r.id,  // round-trip the identity its pre-synthesized Ansage is filed under
      keywords: [...new Set([
        ...splitCsvList(r.keywords),
        ...splitCsvList(keywordDrafts[idx]),
      ])],
      phone_number: (r.phone_number || '').trim(),
      message: (r.message || '').trim(),
    })),
    forwarding_phone_number: practiceInfo.forwarding_phone_number || '',
    recording_enabled: !!practiceInfo.recording_enabled,
  });
  useEffect(() => {
    if (!practiceId || !practiceInfoLoadedRef.current) return;
    if (loadedForPracticeIdRef.current !== practiceId) return;
    if (formLoadedFor !== practiceId) return;

    const payload = callflowRowPayload();
    const serialized = JSON.stringify(payload);
    if (callflowRowBaselineRef.current === null) {
      callflowRowBaselineRef.current = serialized;
      return;
    }
    if (callflowRowBaselineRef.current === serialized) {
      // Back to what is stored — an edit made and undone inside the debounce
      // window. Drop the pending write, or the unmount flush would resurrect it.
      pendingCallflowRowRef.current = null;
      return;
    }

    if (callflowRowSaveTimerRef.current) clearTimeout(callflowRowSaveTimerRef.current);
    // Held for the unmount flush below: the cleanup that debounces this effect also
    // runs when the page unmounts, so without this the PUT is simply dropped.
    pendingCallflowRowRef.current = { targetPracticeId: practiceId, payload, serialized };
    callflowRowSaveTimerRef.current = setTimeout(() => {
      commitCallflowRow(pendingCallflowRowRef.current);
    }, 700);

    return () => {
      if (callflowRowSaveTimerRef.current) clearTimeout(callflowRowSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    practiceId, formLoadedFor,
    triggerKeywords.smartab_keywords, triggerKeywords.forwarding_rules,
    smartabDraft, keywordDrafts,
    practiceInfo.forwarding_phone_number, practiceInfo.recording_enabled,
  ]);

  // Leaving the page inside the debounce window must not throw the edit away.
  // Navigating to another tab unmounts this page, and the debounce cleanup above
  // cancels the pending timer — so a keyword added and then abandoned within 700ms
  // was silently lost. Fire it instead: a route change is not a page unload, so
  // the request outlives the component. Empty deps on purpose — this must run at
  // unmount only, never on a dependency change (that is the debounce doing its
  // job), and everything it needs comes from refs.
  // NOT covered: closing the tab or reloading the browser. That needs sendBeacon,
  // which cannot carry the auth header.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { commitCallflowRow(pendingCallflowRowRef.current); }, []);

  // Unsaved-draft detector for the Anrufablauf tab (live config sync).
  // Everything on that tab that does NOT auto-save: the keyword/forwarding chips
  // and the spoken-message textareas. Tracked as one snapshot rather than a
  // touched-flag per input so no field can be forgotten — and so reverting an
  // edit by hand clears the flag again.
  //
  // Uses the same hydration-echo suppression as callflowBaselineRef: loading a
  // practice changes these values exactly like a user edit does, so the first
  // run after BOTH loaders finish records the baseline instead of flagging it.
  const callflowDraftBaselineRef = useRef(null);
  // ONLY the spoken texts remain here. Everything else on this tab now persists
  // itself, so it can never be an unsaved draft (one exception: a whitelist row
  // without a number lives in state only, and a re-hydration drops it). These
  // stay manual on purpose: saving them re-synthesizes the audio the caller
  // actually hears, which is too expensive to fire on every keystroke.
  // Keyed by message key (MESSAGE_SAVE_ACTIONS) so ONE map answers both "is the
  // tab dirty" (below) and "is THIS message dirty" (msgDirty, used by the
  // per-message save button). The baseline ref holds the same shape.
  const callflowMessageTexts = () => ({
    greeting: greetingMessage,
    greeting_away: greetingMessageAway,
    greeting_pvs_demo: greetingMessagePvs,
    greeting_pvs_away_demo: greetingMessagePvsAway,
    farewell_general: farewellGeneral,
    farewell_appointment: farewellAppointment,
    voicemail_announcement: voicemailMessage,
    hold_message: holdMessage,
    consent: consentQuestion,
    forwarding_message: forwardingMessage,
    // One entry per forwarding rule that HAS an id, keyed by it rather than by
    // position — a positional key would move under the user when a rule is deleted and
    // the baseline entry would then describe a different rule.
    ...Object.fromEntries(
      (triggerKeywords.forwarding_rules || [])
        .filter(r => r.id)
        .map(r => [`forwarding_rule_${r.id}`, r.message || ''])
    ),
  });
  // Mirrored into a ref on every render so the async handlers above read live
  // text (house pattern — see useConfigSync's handlersRef).
  const callflowTextsRef = useRef(null);
  callflowTextsRef.current = callflowMessageTexts();
  // The tab is dirty when ANY message is — same per-key comparison the buttons
  // use, so there is only one notion of "changed" on this tab.
  const callflowDraftDirty = () => Object.keys(callflowTextsRef.current).some(msgDirty);
  useEffect(() => {
    // Gate on messagesLoadedFor too: practiceInfoLoadedRef flips as soon as the
    // practice row lands, while the eight message GETs are still in flight — a
    // baseline captured then would be all-empty and every arriving message would
    // read as a user edit.
    if (!callflowConfigLoadedRef.current || !practiceId) return;
    if (loadedForPracticeIdRef.current !== practiceId) return;
    if (callflowLoadedFor !== practiceId || messagesLoadedFor !== practiceId) return;

    if (callflowDraftBaselineRef.current === null) {
      callflowDraftBaselineRef.current = callflowTextsRef.current;
      setCallflowTabDirty(false);
      return;
    }
    setCallflowTabDirty(callflowDraftDirty());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    practiceId, callflowLoadedFor, messagesLoadedFor,
    greetingMessage, greetingMessagePvs, greetingMessageAway, greetingMessagePvsAway,
    farewellGeneral, farewellAppointment, voicemailMessage, holdMessage,
    consentQuestion, forwardingMessage, triggerKeywords.forwarding_rules,
  ]);

  // Per-message audio status. Gated on formLoadedFor because that is set together
  // with loadedForPracticeIdRef, which fetchMessageStatus checks before it paints.
  useEffect(() => {
    if (practiceId && formLoadedFor === practiceId) fetchMessageStatus(practiceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, formLoadedFor]);

  // Load PVS config when practiceId becomes available (Session 9c: per-practice).
  useEffect(() => {
    if (!practiceId) return;
    // Staleness guard (Jul 2026 cross-tenant incident): drop a late response
    // from a previously-selected practice so its PVS worker URL/API key can't
    // populate the form (and get saved) under the newly-selected practice.
    let cancelled = false;
    setPvsLoadedFor(null);
    // Drop a detection result from the previously-selected practice; the name
    // itself is re-hydrated below, but a failure message would otherwise linger.
    setPvsSystemStatus({ loading: false, message: '' });
    axios.get(`${API_BASE_URL}/test-call/pvs-config?practice_id=${encodeURIComponent(practiceId)}`).then(res => {
      if (cancelled) return;
      if (res.data) {
        setPvsEnabled(res.data.enabled || false);
        setPvsBaseUrl(res.data.base_url || '');
        setPvsSystem({ name: res.data.pvs_system || '', detectedAt: res.data.pvs_system_detected_at || '' });
        setPvsApiKey(res.data.api_key || '');
        setPvsGchatWebhookUrl(res.data.gchat_webhook_url || '');
        setPvsSlackBotToken(res.data.slack_bot_token || '');
        // Normalize legacy boolean to string
        const dm = res.data.debug_mode;
        setPvsDebugMode(dm === true ? 'debug' : (dm || 'off'));
        setPvsTimeoutFind(res.data.timeout_find ?? 30);
        setPvsTimeoutFreeslots(res.data.timeout_freeslots ?? 20);
        setPvsTimeoutFindAppointments(res.data.timeout_find_appointments ?? 45);
        setPvsTimeoutBook(res.data.timeout_book ?? 120);
        setPvsTimeoutCancel(res.data.timeout_cancel ?? 60);
        setPvsTimeoutReschedule(res.data.timeout_reschedule ?? 30);
        setPvsTimeoutCreatePatient(res.data.timeout_create_patient ?? 90);
        setGsheetEnabled(res.data.gsheet_enabled || false);
        setGsheetServiceAccountJson(res.data.gsheet_service_account_json || '');
        setGsheetSheetId(res.data.gsheet_sheet_id || '');
      }
      // Unlock the PVS tab only after a successful load (an error keeps it
      // shrouded — saving defaults over an unloaded config is the incident
      // class we're preventing).
      setPvsLoadedFor(practiceId);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [practiceId]);

  // --- Session 10c: Mitglieder (members) management ---
  // Members endpoints live at /api/practices/{practiceId}/members. Reload happens on
  // tab open, practiceId change, and after every successful add/remove/update.

  // FastAPI/Pydantic v2 returns `detail` as an array of objects on 422
  // validation errors. Rendering that array directly as JSX children throws
  // "Objects are not valid as a React child". Normalize to a single string.
  const formatApiError = (detail) => {
    if (!detail) return null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc)
          ? d.loc.filter((p) => p !== 'body').join('.')
          : '';
        const msg = d?.msg || d?.message || 'Validierungsfehler';
        return loc ? `${loc}: ${msg}` : msg;
      }).join('; ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return String(detail);
  };

  // At most ONE member-list request in flight per tab. On 2026-09-15 a single
  // tab fired ~30 of these within a second (trigger still unknown); each call
  // holds a DB connection across several Keycloak round-trips, the pool filled
  // and the whole backend froze for every practice. A call that arrives while
  // one is running is folded into at most one trailing reload, so the refresh
  // the mutation handlers rely on still happens. The trailing reload goes
  // through the ref so it always runs the LATEST callback (current practiceId).
  const membersLoadInFlightRef = useRef(false);
  const membersReloadQueuedRef = useRef(false);
  const loadMembersRef = useRef(null);
  const loadMembers = React.useCallback(async () => {
    if (!practiceId) return;
    if (membersLoadInFlightRef.current) {
      membersReloadQueuedRef.current = true;
      return;
    }
    membersLoadInFlightRef.current = true;
    setMembersLoading(true);
    setMembersError('');
    try {
      // include_credentials=true enriches each member's has_password so the row
      // can offer "Passwort generieren" (never had one) vs "Passwort zurücksetzen".
      // In the owner view exclude_creator also drops the platform admin's row,
      // which the backend lists only for a super_admin: the owner's list.
      const res = await axios.get(`${API_BASE_URL}/practices/${practiceId}/members?include_credentials=true&exclude_creator=${ownerView}`);
      setMembers(Array.isArray(res.data) ? res.data : []);
      // The Tasks page builds its assignee dropdown from the SAME member list,
      // cached under ['practiceMembers', practiceId] with the global 3-min
      // staleTime — so without this it keeps serving the pre-change list until
      // that window passes or the page is hard-reloaded. Every member mutation
      // here (add / remove / role / name / Zuweisbar / import) ends in
      // loadMembers(), so one invalidation covers them all. The Tasks page is
      // unmounted at this point, so this only marks the entry stale; the refetch
      // happens when you next open it. Deliberately NOT setQueryData: that list
      // is fetched with exclude_creator=true and a different shape.
      queryClient.invalidateQueries(['practiceMembers', practiceId]);
    } catch (e) {
      console.error('[Members] load failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Mitglieder konnten nicht geladen werden.');
    } finally {
      setMembersLoading(false);
      membersLoadInFlightRef.current = false;
      if (membersReloadQueuedRef.current) {
        membersReloadQueuedRef.current = false;
        loadMembersRef.current?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, ownerView]);
  useEffect(() => { loadMembersRef.current = loadMembers; });

  useEffect(() => {
    if (activeTab !== 'members') return;
    if (!practiceId) return;
    // A revealed temp password is scoped to the moment it was issued — drop any
    // stale card when (re-)entering the tab or switching practice.
    setRevealedCredential(null);
    loadMembers();
  }, [activeTab, practiceId, loadMembers]);

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setMembersError('');
    setMembersSuccess('');
    if (!inviteUsername.trim()) {
      setMembersError('Benutzername ist erforderlich.');
      return;
    }
    try {
      const payload = {
        username: inviteUsername.trim(),
        role: inviteRole,
        assignable: inviteAssignable,
      };
      if (inviteEmail.trim()) payload.email = inviteEmail.trim();
      if (inviteFirstName.trim()) payload.first_name = inviteFirstName.trim();
      if (inviteLastName.trim()) payload.last_name = inviteLastName.trim();
      await axios.post(`${API_BASE_URL}/practices/${practiceId}/members`, payload);
      // The account is created WITHOUT a password. Prompt the admin to mint one
      // via the row's "Passwort generieren" button (revealed once, relayed by hand).
      setMembersSuccess('Mitglied hinzugefügt. Klicken Sie in der Mitgliederliste auf „Passwort generieren", um ein Anmeldepasswort zu erstellen.');
      setInviteEmail('');
      setInviteUsername('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('member');
      setInviteAssignable(true);
      await loadMembers();
    } catch (e) {
      console.error('[Members] invite failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Mitglied konnte nicht hinzugefügt werden.');
    }
  };

  const handleRemoveMember = async (memberId) => {
    setMembersError('');
    setMembersSuccess('');
    const isSelf = memberId === userId;
    const confirmMsg = isSelf
      ? 'Sie sind dabei, sich selbst aus dieser Praxis zu entfernen. ' +
        'Sie werden den Zugriff verlieren und automatisch abgemeldet. Fortfahren?'
      : 'Mitglied wirklich aus dieser Praxis entfernen?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}`);
      if (isSelf) {
        // L6: orphan-Keycloak cleanup happens server-side. Force logout immediately
        // because the JWT still references a Practice the user can no longer access.
        keycloak?.logout?.();
        return;
      }
      setMembersSuccess('Mitglied entfernt.');
      await loadMembers();
    } catch (e) {
      console.error('[Members] remove failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Mitglied konnte nicht entfernt werden.');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    setMembersError('');
    setMembersSuccess('');
    try {
      await axios.patch(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}`, { role: newRole });
      setMembersSuccess('Rolle aktualisiert.');
      await loadMembers();
    } catch (e) {
      console.error('[Members] role change failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Rolle konnte nicht aktualisiert werden.');
    }
  };

  const handleToggleAssignable = async (memberId, nextAssignable) => {
    setMembersError('');
    setMembersSuccess('');
    try {
      await axios.patch(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}`, { assignable: nextAssignable });
      setMembersSuccess('Zuweisbarkeit aktualisiert.');
      await loadMembers();
    } catch (e) {
      console.error('[Members] assignable toggle failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Zuweisbarkeit konnte nicht aktualisiert werden.');
    }
  };

  const handleSaveUsername = async (memberId) => {
    setMembersError('');
    setMembersSuccess('');
    const newUsername = editUsernameValue.trim();
    if (!newUsername) {
      setMembersError('Benutzername darf nicht leer sein.');
      return;
    }
    try {
      await axios.patch(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}`, { username: newUsername });
      setMembersSuccess('Benutzername aktualisiert.');
      setEditingUsername(null);
      setEditUsernameValue('');
      await loadMembers();
    } catch (e) {
      console.error('[Members] username change failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Benutzername konnte nicht aktualisiert werden.');
    }
  };

  const handleSaveEmail = async (memberId) => {
    setMembersError('');
    setMembersSuccess('');
    const newEmail = editEmailValue.trim();
    if (!newEmail) {
      setMembersError('E-Mail-Adresse darf nicht leer sein.');
      return;
    }
    try {
      await axios.patch(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}`, { email: newEmail });
      setMembersSuccess('E-Mail-Adresse aktualisiert.');
      setEditingEmail(null);
      setEditEmailValue('');
      await loadMembers();
    } catch (e) {
      console.error('[Members] email change failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'E-Mail-Adresse konnte nicht aktualisiert werden.');
    }
  };

  // Owner/admin can set first/last name (stored in Keycloak; used for task-
  // assignment display). Sends both fields so clearing one is honored.
  const handleSaveName = async (memberId) => {
    setMembersError('');
    setMembersSuccess('');
    try {
      await axios.patch(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}`, {
        first_name: editFirstNameValue.trim(),
        last_name: editLastNameValue.trim(),
      });
      setMembersSuccess('Name aktualisiert.');
      setEditingName(null);
      setEditFirstNameValue('');
      setEditLastNameValue('');
      await loadMembers();
    } catch (e) {
      console.error('[Members] name change failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Name konnte nicht aktualisiert werden.');
    }
  };

  // super_admin-only: mint a fresh one-time temp password for a member. Serves
  // both the first-time "generate" (account has no password yet) and a later
  // "reset" (lost password) — same backend action; only the wording differs.
  const handleResetPassword = async (memberId, username, hasPassword, shared) => {
    setMembersError('');
    setMembersSuccess('');
    setRevealedCredential(null);
    let confirmMsg = hasPassword
      ? `Passwort für „${username}" zurücksetzen? Das bisherige Passwort wird ungültig; das Mitglied muss das neue bei der nächsten Anmeldung ändern.`
      : `Temporäres Passwort für „${username}" generieren? Es wird einmalig angezeigt; das Mitglied muss es bei der ersten Anmeldung ändern.`;
    // One login per person, platform-wide: resetting here changes it for every
    // practice the account works in, so say so before the click goes through.
    if (shared) {
      confirmMsg += `\n\nAchtung: Dieses Konto wird auch in anderen Praxen verwendet. Das neue Passwort gilt überall.`;
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/practices/${practiceId}/members/${memberId}/reset-password`);
      const tempPw = res?.data?.temporary_password;
      if (!tempPw) {
        setMembersError('Das temporäre Passwort konnte nicht erzeugt werden. Bitte erneut versuchen.');
        return;
      }
      // Reveal once, and optimistically flip this row to "has a password" so its
      // button becomes "Passwort zurücksetzen" without a full reload.
      setCredentialCopied(false);
      setRevealedCredential({ username, password: tempPw });
      setMembers((prev) => prev.map((m) => (m.user_id === memberId ? { ...m, has_password: true } : m)));
    } catch (e) {
      console.error('[Members] temp-password issue failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Das temporäre Passwort konnte nicht erzeugt werden.');
    }
  };

  // Copy the revealed temp password; briefly flip the button to a "✓ Kopiert" state.
  const handleCopyCredential = async () => {
    if (!revealedCredential?.password) return;
    try {
      await navigator.clipboard.writeText(revealedCredential.password);
      setCredentialCopied(true);
      if (credentialCopyTimerRef.current) clearTimeout(credentialCopyTimerRef.current);
      credentialCopyTimerRef.current = setTimeout(() => setCredentialCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (older browsers / non-secure context) — the
      // password stays visible and selectable for manual copy.
    }
  };

  // --- Session 10g: import-from-other-practice helpers (super_admin + owner) ---
  const loadImportMemberSource = async (sourceId) => {
    if (!sourceId) {
      setImportMemberSourceList([]);
      return;
    }
    setImportMemberLoading(true);
    setMembersError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/practices/${sourceId}/members`);
      setImportMemberSourceList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('[Members] load source members failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Mitglieder der Quellpraxis konnten nicht geladen werden.');
      setImportMemberSourceList([]);
    } finally {
      setImportMemberLoading(false);
    }
  };

  const handleImportMember = async (sourceMember) => {
    if (!sourceMember?.username) return;
    setMembersError('');
    setMembersSuccess('');
    setImportMemberInFlight(sourceMember.user_id);
    try {
      // Reuse the standard add-member endpoint. Username is the identity key
      // since the username-centric onboarding change: members.add_member adopts
      // the existing user (matched by username) and just inserts a fresh
      // user_practices row with the role. Email is not sent — it's optional now
      // (many members have none) and is ignored on the adoption path anyway.
      // Carry the source practice's Zuweisbar setting instead of silently
      // falling back to the endpoint default (true) — importing someone who is
      // deliberately non-assignable elsewhere shouldn't re-enable them here.
      await axios.post(`${API_BASE_URL}/practices/${practiceId}/members`, {
        username: sourceMember.username,
        role: sourceMember.role || 'member',
        assignable: sourceMember.assignable !== false,
        // Only this panel may attach an existing account. The plain add form
        // omits the flag, so a taken username there is always a 409.
        adopt_existing: true,
      });
      setMembersSuccess(`${sourceMember.username} (${sourceMember.role}) importiert.`);
      await loadMembers();
    } catch (e) {
      console.error('[Members] import failed:', e);
      setMembersError(formatApiError(e?.response?.data?.detail) || 'Import fehlgeschlagen.');
    } finally {
      setImportMemberInFlight(null);
    }
  };

  // --- Voxtral TTS voice management handlers ---
  const refreshVoxtralVoices = () => {
    axios.get(`${API_BASE_URL}/voxtral-voices/`)
      .then(res => {
        const voices = res.data?.items || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setVoxtralVoices(voices);
      })
      .catch(err => console.warn('Failed to fetch Voxtral voices:', err));
  };

  const handleVoxtralFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVoxtralCustomFileName(file.name);
    setVoxtralPreviewUrl(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setVoxtralCustomAudio(base64);
    };
    reader.readAsDataURL(file);
  };

  const startVoxtralRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setVoxtralPreviewUrl(URL.createObjectURL(blob));
        setVoxtralCustomFileName('Aufnahme');
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          setVoxtralCustomAudio(base64);
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      setVoxtralMediaRecorder(recorder);
      setVoxtralRecording(true);
    } catch (err) {
      console.warn('Microphone access denied:', err);
    }
  };

  const stopVoxtralRecording = () => {
    if (voxtralMediaRecorder && voxtralMediaRecorder.state !== 'inactive') {
      voxtralMediaRecorder.stop();
    }
    setVoxtralRecording(false);
    setVoxtralMediaRecorder(null);
  };

  const handleSaveVoxtralVoice = async () => {
    if (!voxtralCustomName.trim() || !voxtralCustomAudio) return;
    setVoxtralSaving(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/voxtral-voices/`, {
        name: voxtralCustomName,
        audio_base64: voxtralCustomAudio
      });
      const newVoiceId = res.data?.voice_id || res.data?.id;
      refreshVoxtralVoices();
      if (newVoiceId) setVoxtralVoice(newVoiceId);
      setVoxtralCustomName('');
      setVoxtralCustomAudio(null);
      setVoxtralCustomFileName('');
      setVoxtralPreviewUrl(null);
      setVoxtralCustomExpanded(false);
    } catch (err) {
      console.error('Failed to save Voxtral voice:', err);
    } finally {
      setVoxtralSaving(false);
    }
  };

  const handleDeleteVoxtralVoice = async () => {
    if (!window.confirm('Eigene Stimme wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/voxtral-voices/${voxtralVoice}`);
      setVoxtralVoice('gb_oliver_neutral');
      refreshVoxtralVoices();
    } catch (err) {
      console.error('Failed to delete Voxtral voice:', err);
    }
  };

  // Handle LLM config submission.
  // `opts.silent` suppresses the success/"Speichere..." badge for auto-saves
  // triggered by switcher toggles. Errors are always surfaced so users notice
  // persistence failures even on silent saves.
  const handleLlmConfigSave = async (e, opts = {}) => {
    e.preventDefault();
    const isAutoSave = opts.silent === true;
    // Cross-tenant write guard (Jul 2026 incident): this handler PUTs the FULL
    // practiceInfo and re-POSTs every message — if the form still holds another
    // practice's data (stale load) or the load hasn't finished, writing it under
    // the live practiceId contaminates that practice. Abort instead.
    if (!practiceId || loadedForPracticeIdRef.current !== practiceId) {
      if (!isAutoSave) {
        setLlmSaveStatus({ saved: false, message: 'Praxisdaten werden noch geladen – bitte einen Moment warten und erneut speichern.' });
      }
      return;
    }
    try {
      if (!isAutoSave) {
        setLlmSaving(true);
        setLlmSaveStatus({ saved: false, message: 'Speichere...' });
      }
      
      const configData = {
        provider: provider,
        ki_mode: kiMode === 'langgraph' ? 'agentic' : kiMode,
        require_email: requireEmail,
        require_birthdate: requireBirthdate,
        require_phone: requirePhone,
        require_address: requireAddress,
        trust_caller_id: trustCallerId,
        reject_caller_ids: rejectCallerIds,
        pvs_sync_timeout_seconds: pvsSyncTimeoutSeconds,
        require_verordnung: requireVerordnung,
        allow_multi_therapist_series: allowMultiTherapistSeries,
        max_therapists_per_series: maxTherapistsPerSeries,
        require_whatsapp_optin: requireWhatsappOptin,
        require_insurance_company: requireInsuranceCompany,
        enable_forwarding: enableForwarding,
        enable_keyword_forwarding: enableKeywordForwarding,
        forward_noanswer_voicemail: forwardNoanswerVoicemail,
        forward_noanswer_ring_seconds: forwardNoanswerRingSeconds,
        enable_direct_forwarding: enableDirectForwarding,
        direct_forwarding_number: directForwardingNumber,
        enable_whitelist_forwarding: enableWhitelistForwarding,
        whitelist_forwarding_number: whitelistForwardingNumber,
        ...whitelistEntriesToConfig(whitelistEntries),
        use_langgraph: kiMode === 'langgraph',
        langgraph_cancel_enabled: langgraphCancelEnabled,
        langgraph_move_enabled: langgraphMoveEnabled,
        langgraph_book_enabled: langgraphBookEnabled,
        cancellation_fee_period: cancellationFeePeriod,
        cancellation_fee_notice: cancellationFeeNotice,
        consent_question: consentQuestion,
        consent_upfront: consentUpfront,
        data_retention_enabled: dataRetentionEnabled,
        data_retention_days: dataRetentionDays,
        // Sent even when empty so a practice can revert to the standard sentence.
        forwarding_message: forwardingMessage,
        enable_greeting_pvs: enableGreetingPvs,
        enable_greeting_away: enableGreetingAway,
        enable_farewell_appointment: enableFarewellAppointment,
        patient_identification_method: patientIdentificationMethod,
        book_unidentified_returning_as_new: bookUnidentifiedReturningAsNew,
        // Legacy key kept in sync with the OR of the new flags so any leftover
        // legacy reader still produces the right value.
        sms_confirmation_enabled: smsToCallerEnabled || smsToNumberEnabled,
        sms_to_caller_enabled: smsToCallerEnabled,
        sms_to_number_enabled: smsToNumberEnabled,
        sms_notification_phone_number: smsNotificationPhoneNumber,
        // Gateway choice and token are GLOBAL (the per-practice endpoint strips both;
        // the global one super-admin-gates the gateway and routes the token to
        // Secrets). Only the displayed sender is per-practice.
        sms_provider: smsProvider,
        seven_api_key: sevenApiKey,
        // Historical key name — the sender shown when seven.io carries the message,
        // as primary or as fallback. AWS SNS always displays the platform brand ID.
        sns_sender_id: smsSenderId,
        email_confirmation_enabled: emailConfirmationEnabled,
        call_ambience_enabled: callAmbienceEnabled,
        call_ambience_sound: callAmbienceSound,
        acceptable_cancellation_reasons_enabled: acceptableCancellationReasonsEnabled,
        acceptable_cancellation_reasons: acceptableCancellationReasons,
        mention_therapist_in_cancel_move: mentionTherapistInCancelMove,
        ask_cancel_move_reason_enabled: askCancelMoveReasonEnabled,
        ask_symptoms_enabled: askSymptomsEnabled,
        enable_dual_vad: enableDualVad,
        enable_multi_location: enableMultiLocation,
        slot_raster_enabled: slotRasterEnabled,
        slot_raster_minutes: slotRasterMinutes,
        same_day_cutoff_enabled: sameDayCutoffEnabled,
        same_day_cutoff_time: sameDayCutoffTime,
        persist: persistLlmConfig
      };

      if (provider === 'azure') {
        // Connections list and use-case mapping
        configData.azure_connections = JSON.stringify(azureConnections);
        configData.azure_use_case_mapping = JSON.stringify(azureUseCaseMapping);
      } else if (provider === 'gemini') {
        // Gemini configuration (Vertex AI with EU region)
        configData.gemini_service_account_json = geminiServiceAccountJson;
        configData.gemini_project_id = geminiProjectId;
        configData.gemini_region = geminiRegion;
        configData.gemini_primary_model = geminiPrimaryModel;
        configData.gemini_secondary_model = geminiSecondaryModel;
        configData.gemini_tertiary_model = geminiTertiaryModel;
        configData.gemini_quaternary_model = geminiQuaternaryModel;
      } else {
        // Bedrock configuration
        configData.primaryModelId = primaryModel;
        configData.secondaryModelId = secondaryModel;
        configData.modelId = primaryModel; // Legacy support - use primary as default
      }
      
      // STT Configuration
      configData.stt_provider = sttProvider;
      configData.stt_aws_access_key_id = awsAccessKeyId;
      configData.stt_aws_secret_access_key = awsSecretAccessKey;

      // TTS Configuration
      configData.tts_provider = ttsProvider;

      // Azure Speech credentials (shared for both STT and TTS)
      configData.stt_azure_speech_key = azureSpeechKey;
      configData.stt_azure_speech_region = azureSpeechRegion;
      configData.tts_azure_speech_key = azureSpeechKey;  // Same as STT
      configData.tts_azure_speech_region = azureSpeechRegion;  // Same as STT

      // Azure TTS voice selection
      configData.tts_azure_voice_name = azureTtsVoice;

      // AWS Polly voice selection
      configData.tts_aws_polly_voice_id = awsPollyVoice;
      configData.tts_aws_polly_use_generative = awsPollyUseGenerative;

      // Voxtral TTS voice selection
      configData.tts_voxtral_voice_id = voxtralVoice;

      // ElevenLabs TTS configuration
      configData.tts_elevenlabs_api_key = elevenlabsApiKey;
      configData.deepgram_api_key = deepgramApiKey;
      configData.tts_elevenlabs_voice_id = elevenlabsVoiceId;
      configData.tts_elevenlabs_model_id = elevenlabsModelId;

      // Kugel Audio TTS configuration
      configData.tts_kugel_api_key = kugelApiKey;
      configData.tts_kugel_voice_id = kugelVoiceId;
      configData.tts_kugel_model_id = kugelModelId;
      configData.tts_kugel_use_websocket = kugelUseWebsocket;

      // AWS credentials (legacy - for backward compatibility)
      configData.aws_access_key_id = awsAccessKeyId;
      configData.aws_secret_access_key = awsSecretAccessKey;
      configData.aws_region = 'eu-central-1';

      // Batch transcription provider (for unclear_answer and voicemail)
      configData.batch_transcribe_provider = batchTranscribeProvider;
      configData.gpt4o_emergency_transcribe_deployment = gpt4oEmergencyDeployment;
      configData.emergency_transcribe_provider = emergencyTranscribeProvider;

      // Mistral AI credentials
      configData.mistral_api_key = mistralApiKey;

      // Silent auto-saves exist ONLY to persist the per-practice call-flow
      // toggles — they must never touch the GLOBAL config (provider/models/
      // keys shared by all tenants). Only an explicit Speichern writes global.
      if (!isAutoSave) {
        await axios.post(`${API_BASE_URL}/test-call/llm-config`, configData);
      }

      // Phase 2 follow-up: per-practice fields (ki_mode, require_*, sms_*,
      // tts_provider, stt_provider, langgraph_flows, etc.) must be persisted
      // via the per-practice endpoint — the global POST above strips them so
      // the KI-Konfiguration tab can't stomp every practice's behavior at once.
      // The backend silently drops any non-per-practice key in this payload.
      if (practiceId) {
        const langgraphFlows = {
          cancel: langgraphCancelEnabled,
          move: langgraphMoveEnabled,
          book: langgraphBookEnabled,
        };
        let perPracticeData;
        if (isAutoSave && callflowBaselineRef.current) {
          // DIFF-PUT. A silent auto-save exists only to persist the ONE toggle the
          // user just clicked, so it must not re-ship the other ~50 keys from this
          // tab's possibly-stale view — that is what silently reverted a colleague's
          // switch (and re-shipped draft text fields like consent_question).
          // Compare the keyed snapshot against the baseline captured at hydration /
          // last successful save and wire only the keys that differ.
          const current = callflowSnapshotObj();
          let baseline = {};
          try { baseline = JSON.parse(callflowBaselineRef.current) || {}; } catch { baseline = {}; }
          perPracticeData = {};
          Object.keys(current).forEach((key) => {
            if (JSON.stringify(current[key]) !== JSON.stringify(baseline[key])) {
              perPracticeData[key] = current[key];
            }
          });
          // Derived/companion keys the toggles above stand for.
          if ('sms_to_caller_enabled' in perPracticeData || 'sms_to_number_enabled' in perPracticeData) {
            perPracticeData.sms_confirmation_enabled = smsToCallerEnabled || smsToNumberEnabled;
          }
          if ('ki_mode' in perPracticeData) {
            perPracticeData.use_langgraph = kiMode === 'langgraph';
          }
          // enable_keyword_forwarding INHERITS enable_forwarding while it has no
          // stored value (backend: is_keyword_forwarding_enabled). Shipping the
          // general switch ALONE would therefore drag keyword forwarding down with
          // it — which is exactly the combination the split exists to allow. Pin
          // the current value alongside it so the inherit resolves on first write.
          if ('enable_forwarding' in perPracticeData) {
            perPracticeData.enable_keyword_forwarding = enableKeywordForwarding;
          }
          // A practice saved before the direct/whitelist split has no stored
          // enable_direct_forwarding; this tab derives "direct on" from its old
          // whitelist. Ship the forwarding block as one unit, so one changed key never
          // lands next to that unstored state (backend: get_whitelist_forwarding).
          const fwdSplitKeys = ['enable_direct_forwarding', 'direct_forwarding_number', 'enable_whitelist_forwarding', 'whitelist_forwarding_number', 'whitelist_caller_ids'];
          if (fwdSplitKeys.some((key) => key in perPracticeData)) {
            fwdSplitKeys.forEach((key) => { perPracticeData[key] = current[key]; });
          }
          if (
            'langgraph_book_enabled' in perPracticeData
            || 'langgraph_cancel_enabled' in perPracticeData
            || 'langgraph_move_enabled' in perPracticeData
          ) {
            perPracticeData.langgraph_flows = langgraphFlows;
          }
          if (!Object.keys(perPracticeData).length) {
            // Nothing actually changed (defensive — the effect gates on this too).
            perPracticeData = null;
          }
        } else {
          // Explicit Speichern: the user is committing the whole tab, so the full
          // payload is correct.
          perPracticeData = { ...configData, langgraph_flows: langgraphFlows };
        }

        if (perPracticeData) {
          await axios.put(
            `${API_BASE_URL}/test-call/practice-llm-config/${practiceId}`,
            perPracticeData,
          );
        }
      }

      // The toggle values just persisted become the new auto-save baseline
      // (hydration-echo suppression — see callflowBaselineRef).
      callflowBaselineRef.current = callflowSnapshot();
      // The practice/messages reload re-derives the three variant switches from this
      // ref; left stale, it would restore the old values and the auto-save would
      // write them back.
      storedVariantFlagsRef.current = { pvs: enableGreetingPvs, away: enableGreetingAway, appt: enableFarewellAppointment };

      // Silent auto-save stops here: no global audio re-synthesis, no message
      // re-POSTs, no practice-info PUT. Those belong to the explicit Speichern
      // — narrowing the auto-save's blast radius to one practice's toggle row.
      if (isAutoSave) {
        queryClient.invalidateQueries(['practiceLlmConfig', practiceId]);
        return;
      }

      // Save forwarding phone number + trigger keywords (update practice info).
      // Deliberately FIRST among the explicit-save steps: the eight filler-message
      // POSTs below are not individually guarded, so any one of them throwing used
      // to abort the handler before this line ever ran — the user got a generic red
      // badge and their keyword edit was silently lost while the toggles above had
      // already committed. Persisting the practice row up front makes the durable
      // part of the save independent of the audio pipeline.
      if (practiceInfo.forwarding_phone_number !== undefined) {
        const practiceData = { ...practiceInfo };
        // insurance_type_settings belongs to the Behandlungen-&-Team tab, which
        // persists it instantly through its own endpoint. `practiceInfo` still
        // holds the MOUNT-time copy, so shipping it here rewinds a Verordnungs-
        // typen change made since — including one this same user just made on the
        // other tab. Mirrors the delete in preparePracticePayload.
        delete practiceData.insurance_type_settings;
        // Convert UI comma-strings to arrays for backend storage
        const splitCsv = (s) => (s || '')
          .split(',')
          .map(x => x.trim())
          .filter(x => x.length > 0);
        const forwardingRulesPayload = (triggerKeywords.forwarding_rules || [])
          .map(r => ({
            id: r.id,  // see callflowRowPayload — the Ansage audio is filed under this
            keywords: splitCsv(r.keywords),
            phone_number: (r.phone_number || '').trim(),
            message: (r.message || '').trim()
          }))
          .filter(r => r.keywords.length > 0 && r.phone_number.length > 0);
        practiceData.trigger_keywords = {
          forwarding_rules: forwardingRulesPayload,
          smartab_keywords: splitCsv(triggerKeywords.smartab_keywords)
        };
        const practiceRes = await axios.put(`${API_BASE_URL}/practices/${practiceId}`, practiceData);
        // Re-seed from the stored row so the mount-time snapshot stops going stale.
        if (practiceRes?.data) setPracticeInfo(normalizePracticeRow(practiceRes.data));
        // This PUT ships the whole practiceInfo, so it has just COMMITTED the
        // Praxis tab's pending edits too. Leaving the flag set would keep that
        // scope permanently "dirty" and turn every colleague change into a banner.
        setPracticeTabDirty(false);
      }

      // Re-synthesize all global pre-synthesized audio for the current voice
      // This ensures fillers/fallbacks match the selected voice
      try {
        await axios.post(`${API_BASE_URL}/tts/pre-synthesize-all-globals`);
        console.log('Global pre-synthesis triggered successfully');
      } catch (preSynthErr) {
        console.warn('Global pre-synthesis failed, will fall back to real-time TTS:', preSynthErr);
      }

      // Save greeting and farewell messages (ensure practiceId exists)
      if (!practiceId) {
        throw new Error('Practice ID ist nicht verfügbar. Bitte speichern Sie zuerst die Praxis-Informationen.');
      }

      // Pre-synthesize the consent-bearing first message (3 filler variants ×
      // the practice's consent question, in the practice's voice). The consent
      // text itself was already persisted by the practice-llm-config PUT above.
      // Runs even if the field is empty — the backend falls back to the default
      // consent sentence, which is then what calls speak (and cache).
      markRegenerating(['consent'], true);
      try {
        await axios.post(`${API_BASE_URL}/tts/pre-synthesize-consent/${practiceId}`);
        console.log('Consent pre-synthesis triggered successfully');
      } catch (preSynthErr) {
        console.warn('Consent pre-synthesis failed, will fall back to real-time TTS:', preSynthErr);
        // Non-blocking error - don't fail the save operation
      } finally {
        markRegenerating(['consent'], false);
      }

      // Pre-synthesize the forwarding announcements: the practice-wide sentence plus
      // one per rule that has its own Ansage. Both texts were persisted above — the
      // sentence by the practice-llm-config PUT, the rules by the practices PUT — and
      // this call reads them back, so it has to run after both. Like consent, it runs
      // even with an empty field: the backend then records the canonical default.
      // A full run (no `only`) also prunes audio for rules that were just deleted.
      const forwardingKeys = Object.keys(callflowTextsRef.current)
        .filter(k => k === 'forwarding_message' || FORWARDING_RULE_KEY.test(k));
      markRegenerating(forwardingKeys, true);
      try {
        await axios.post(`${API_BASE_URL}/tts/pre-synthesize-forwarding/${practiceId}`);
        console.log('Forwarding pre-synthesis triggered successfully');
      } catch (preSynthErr) {
        console.warn('Forwarding pre-synthesis failed, will fall back to real-time TTS:', preSynthErr);
      } finally {
        markRegenerating(forwardingKeys, false);
      }

      // Save greeting message. Each filler POST echoes practice_id in the body
      // (cross-tenant guard, Jul 2026 incident): the backend rejects the write
      // with 409 if the echo and the URL ever disagree.
      if (greetingMessage.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'greeting',
          message: greetingMessage,
          practice_id: practiceId
        });
      }

      // Save away-variant greeting (played while reception is unavailable; optional)
      if (greetingMessageAway.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'greeting_away',
          message: greetingMessageAway,
          practice_id: practiceId
        });
      }

      // Trigger pre-synthesis of the greeting once if either the normal or the
      // away greeting is configured. The backend pre-synthesize-greeting call
      // regenerates BOTH the normal and the away audio, so it must fire only
      // once here (not per greeting type).
      if (greetingMessage.trim() || greetingMessageAway.trim()) {
        markRegenerating(['greeting', 'greeting_away'], true);
        try {
          await axios.post(`${API_BASE_URL}/tts/pre-synthesize-greeting/${practiceId}`);
          console.log('Greeting pre-synthesis triggered successfully');
        } catch (preSynthErr) {
          console.warn('Pre-synthesis failed, will fall back to real-time TTS:', preSynthErr);
          // Non-blocking error - don't fail the save operation
        } finally {
          markRegenerating(['greeting', 'greeting_away'], false);
        }
      }

      // Save PVS personalized greeting message (only if PVS is enabled)
      if (pvsEnabled && greetingMessagePvs.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'greeting_pvs',
          message: greetingMessagePvs,
          practice_id: practiceId
        });
      }

      // Save away-variant PVS greeting (only if PVS is enabled; optional)
      if (pvsEnabled && greetingMessagePvsAway.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'greeting_pvs_away',
          message: greetingMessagePvsAway,
          practice_id: practiceId
        });
      }

      // Regenerate the audition-only DEMO audio for whichever PVS greeting(s) were
      // just saved (fixed placeholder name — never played on a real call). One POST
      // covers both variants; the backend skips any that aren't configured.
      if (pvsEnabled && (greetingMessagePvs.trim() || greetingMessagePvsAway.trim())) {
        markRegenerating(['greeting_pvs_demo', 'greeting_pvs_away_demo'], true);
        try {
          await axios.post(`${API_BASE_URL}/tts/pre-synthesize-pvs-demo/${practiceId}`);
          console.log('PVS demo pre-synthesis triggered successfully');
        } catch (preSynthErr) {
          console.warn('PVS demo pre-synthesis failed:', preSynthErr);
          // Non-blocking error - don't fail the save operation
        } finally {
          markRegenerating(['greeting_pvs_demo', 'greeting_pvs_away_demo'], false);
        }
      }

      // Save general farewell message
      if (farewellGeneral.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'farewell_general',
          message: farewellGeneral,
          practice_id: practiceId
        });
      }

      // Save appointment farewell message (only if agentic or langgraph mode)
      if ((kiMode === 'agentic' || kiMode === 'langgraph') && farewellAppointment.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'farewell_appointment',
          message: farewellAppointment,
          practice_id: practiceId
        });
      }

      // Trigger pre-synthesis of farewell messages (if either is configured)
      if (farewellGeneral.trim() || ((kiMode === 'agentic' || kiMode === 'langgraph') && farewellAppointment.trim())) {
        markRegenerating(['farewell_general', 'farewell_appointment'], true);
        try {
          await axios.post(`${API_BASE_URL}/tts/pre-synthesize-farewell/${practiceId}`);
          console.log('Farewell pre-synthesis triggered successfully');
        } catch (preSynthErr) {
          console.warn('Farewell pre-synthesis failed, will fall back to real-time TTS:', preSynthErr);
          // Non-blocking error - don't fail the save operation
        } finally {
          markRegenerating(['farewell_general', 'farewell_appointment'], false);
        }
      }

      // Save voicemail announcement message
      if (voicemailMessage.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'voicemail_announcement',
          message: voicemailMessage,
          practice_id: practiceId
        });

        // Trigger pre-synthesis of voicemail announcement
        markRegenerating(['voicemail_announcement'], true);
        try {
          await axios.post(`${API_BASE_URL}/tts/pre-synthesize-voicemail/${practiceId}`);
          console.log('Voicemail pre-synthesis triggered successfully');
        } catch (preSynthErr) {
          console.warn('Voicemail pre-synthesis failed, will fall back to real-time TTS:', preSynthErr);
          // Non-blocking error - don't fail the save operation
        } finally {
          markRegenerating(['voicemail_announcement'], false);
        }
      }

      // Save hold-in-line "please hold" message (parallel_call_mode='hold').
      // Same pattern as voicemail: persist text, then pre-synthesize in the
      // practice's TTS voice so Asterisk can play it to a parked caller.
      if (holdMessage.trim()) {
        await axios.post(`${API_BASE_URL}/filler-messages/practice/${practiceId}`, {
          type: 'hold_message',
          message: holdMessage,
          practice_id: practiceId
        });
        markRegenerating(['hold_message'], true);
        try {
          await axios.post(`${API_BASE_URL}/tts/pre-synthesize-hold-message/${practiceId}`);
          console.log('Hold-message pre-synthesis triggered successfully');
        } catch (preSynthErr) {
          console.warn('Hold-message pre-synthesis failed:', preSynthErr);
          // Non-blocking error - don't fail the save operation
        } finally {
          markRegenerating(['hold_message'], false);
        }
      }

      // Drop the cached config (and practice info, which this save may also
      // touch via trigger keywords) so the next mount/revisit reflects the save.
      queryClient.invalidateQueries(['practiceLlmConfig', practiceId]);
      queryClient.invalidateQueries(['practiceInfo', practiceId]);

      if (!isAutoSave) {
        // What is on screen is now what is stored — re-baseline so the live sync
        // treats this tab as clean and applies a colleague's next change silently.
        callflowDraftBaselineRef.current = callflowTextsRef.current;
        setCallflowTabDirty(false);
        // This save also regenerated audio, so the per-message buttons flip to ⟳.
        fetchMessageStatus(practiceId);
        setLlmSaveStatus({ saved: true, message: 'Anrufablauf-Konfiguration erfolgreich gespeichert' });
      }
    } catch (err) {
      console.error('Save error:', err);
      // Always surface errors, even for silent auto-saves.
      setLlmSaveStatus({ saved: false, message: 'Fehler beim Speichern der Anrufablauf-Konfiguration.' });
    } finally {
      if (!isAutoSave) setLlmSaving(false);
    }
  };

  // Handle PVS config submission
  const handlePvsConfigSave = async (e) => {
    e.preventDefault();
    // Frontend validation: parse Google Service-Account JSON when freshly entered
    setGsheetJsonError('');
    if (gsheetEnabled && gsheetServiceAccountJson && !gsheetServiceAccountJson.includes('********')) {
      try {
        const parsed = JSON.parse(gsheetServiceAccountJson);
        if (!parsed || typeof parsed !== 'object' || !parsed.client_email) {
          setGsheetJsonError('Google Service-Account JSON ungültig');
          setPvsSaveStatus({ saved: false, message: 'Google Service-Account JSON ungültig' });
          return;
        }
      } catch (parseErr) {
        setGsheetJsonError('Google Service-Account JSON ungültig');
        setPvsSaveStatus({ saved: false, message: 'Google Service-Account JSON ungültig' });
        return;
      }
    }
    try {
      // Cross-tenant write guard (Jul 2026 incident): the PVS form may still
      // hold another practice's worker URL/API key while a load is in flight.
      if (!practiceId || loadedForPracticeIdRef.current !== practiceId) {
        setPvsSaveStatus({ saved: false, message: 'Praxisdaten werden noch geladen – bitte einen Moment warten und erneut speichern.' });
        return;
      }
      setPvsSaveStatus({ saved: false, message: 'Speichere...' });
      await axios.post(`${API_BASE_URL}/test-call/pvs-config?practice_id=${encodeURIComponent(practiceId)}`, {
        enabled: pvsEnabled,
        base_url: pvsBaseUrl,
        api_key: pvsApiKey,
        debug_mode: pvsDebugMode,
        gchat_webhook_url: pvsGchatWebhookUrl,
        timeout_find: pvsTimeoutFind,
        timeout_freeslots: pvsTimeoutFreeslots,
        timeout_find_appointments: pvsTimeoutFindAppointments,
        timeout_book: pvsTimeoutBook,
        timeout_cancel: pvsTimeoutCancel,
        timeout_reschedule: pvsTimeoutReschedule,
        timeout_create_patient: pvsTimeoutCreatePatient,
        gsheet_enabled: gsheetEnabled,
        gsheet_service_account_json: gsheetServiceAccountJson,
        gsheet_sheet_id: gsheetSheetId,
        slack_bot_token: pvsSlackBotToken,
      });
      // The Sync-Lookup timeout is rendered in this tab's timeout section but is
      // stored per-practice in ai_config (PER_PRACTICE_FIELDS), not pvs_config —
      // persist it through the practice-llm-config PUT (partial payloads OK).
      await axios.put(`${API_BASE_URL}/test-call/practice-llm-config/${practiceId}`, {
        pvs_sync_timeout_seconds: pvsSyncTimeoutSeconds,
      });
      setPvsSaveStatus({ saved: true, message: 'PVS-Konfiguration erfolgreich gespeichert' });
      // Re-identify the PVS with the credentials just saved. Intentionally NOT
      // awaited: the save is already reported as done, and a worker that is slow,
      // unreachable, or has no /get_pvs yet must not hold up or taint that result.
      if (pvsEnabled) {
        detectPvsSystem(practiceId);
      } else {
        setPvsSystem({ name: '', detectedAt: '' });
        setPvsSystemStatus({ loading: false, message: '' });
      }
    } catch (err) {
      console.error('PVS config save error:', err);
      setPvsSaveStatus({ saved: false, message: 'Fehler beim Speichern der PVS-Konfiguration' });
    }
  };

  const handleTestPvsConnection = async () => {
    try {
      if (!practiceId) {
        setPvsHealthStatus({ testing: false, success: false, message: 'Praxis nicht geladen.' });
        return;
      }
      setPvsHealthStatus({ testing: true, success: false, message: 'Verbindung wird getestet...' });
      const res = await axios.get(`${API_BASE_URL}/test-call/pvs-healthz?practice_id=${encodeURIComponent(practiceId)}`);
      setPvsHealthStatus({
        testing: false,
        success: !!res.data.success,
        message: res.data.message || (res.data.success ? 'Verbindung erfolgreich' : 'Verbindungstest fehlgeschlagen')
      });
      if (res.data.success) {
        setTimeout(() => setPvsHealthStatus({ testing: false, success: false, message: '' }), 3000);
      }
    } catch (error) {
      setPvsHealthStatus({
        testing: false,
        success: false,
        message: `Verbindungstest fehlgeschlagen: ${error.response?.data?.detail || error.message}`
      });
    }
  };

  // Fired by the save handler once the config is persisted, so the detection uses
  // the credentials that were just saved. Deliberately never throws: the save has
  // already succeeded by this point and must not be reported as failed because the
  // PVS worker could not name itself. Always hits the worker, so the stored name is
  // a record of the last detection rather than a cache that answers in its place.
  const detectPvsSystem = async (targetPracticeId) => {
    setPvsSystemStatus({ loading: true, message: '' });
    // Cross-tenant staleness guard (Jul 2026 incident): this runs detached from the
    // save, so a practice switch can land before the response does. Dropping a late
    // result keeps one practice's PVS name off another practice's header.
    const isStale = () => loadedForPracticeIdRef.current !== targetPracticeId;
    try {
      const res = await axios.get(`${API_BASE_URL}/test-call/get-pvs?practice_id=${encodeURIComponent(targetPracticeId)}`);
      if (isStale()) return;
      if (res.data.success && res.data.system) {
        setPvsSystem({ name: res.data.system, detectedAt: res.data.detected_at || '' });
        setPvsSystemStatus({ loading: false, message: '' });
        return;
      }
      setPvsSystem({ name: '', detectedAt: '' });
      setPvsSystemStatus({ loading: false, message: res.data.message || 'Keine Antwort vom PVS' });
    } catch (error) {
      if (isStale()) return;
      setPvsSystem({ name: '', detectedAt: '' });
      setPvsSystemStatus({
        loading: false,
        message: error.response?.data?.detail || error.message
      });
    }
  };

  const pvsDetectedAtLabel = (() => {
    if (!pvsSystem.detectedAt) return '';
    const parsed = new Date(pvsSystem.detectedAt);
    return isNaN(parsed) ? '' : parsed.toLocaleString('de-DE');
  })();

  // Priority Matrix handlers
  // `opts.silent` refreshes the data WITHOUT flipping the loading flag. That flag
  // swaps the whole table for a "Lade Matrix..." line, which unmounts the table,
  // collapses the page height and bounces the reader to the top — unacceptable for
  // a background refresh triggered by a colleague's edit. React reconciles the
  // rows in place instead, so scroll position (vertical AND the table's own
  // horizontal scroll) is untouched. Keep the flag for the first, real load.
  const loadPriorityMatrix = async (targetPracticeId, opts = {}) => {
    if (!targetPracticeId) return;
    try {
      if (!opts.silent) setMatrixLoading(true);
      const matrixRes = await axios.get(`${API_BASE_URL}/therapists/practice/${targetPracticeId}/matrix`);
      setPriorityMatrix(matrixRes.data);
      // Write-through: keep the React Query cache in sync so a tab revisit
      // within staleTime doesn't repaint pre-mutation data.
      queryClient.setQueryData(['priorityMatrix', targetPracticeId], matrixRes.data);
    } catch (err) {
      console.error('Failed to load priority matrix:', err);
      // A failed BACKGROUND refresh must not wipe what the user is looking at.
      if (!opts.silent) setPriorityMatrix(null);
    } finally {
      if (!opts.silent) setMatrixLoading(false);
    }
  };

  const handlePriorityChange = async (therapistId, therapyTypeId, newPriority) => {
    if (!practiceId) return;

    // Optimistic update
    setPriorityMatrix(prev => {
      if (!prev) return prev;
      const newMatrix = { ...prev.matrix };
      if (!newMatrix[therapistId]) newMatrix[therapistId] = {};
      newMatrix[therapistId][therapyTypeId] = newPriority;
      return { ...prev, matrix: newMatrix };
    });

    try {
      await axios.put(`${API_BASE_URL}/therapists/practice/${practiceId}/matrix/cell`, {
        therapist_id: therapistId,
        therapy_type_id: therapyTypeId,
        priority: newPriority
      });
      // Optimistic update touched state only — mark the cached copy stale so a
      // tab revisit refetches instead of repainting the pre-edit matrix.
      queryClient.invalidateQueries(['priorityMatrix', practiceId]);
    } catch (error) {
      console.error('Error updating priority:', error);
      // Reload matrix to restore correct state
      await loadPriorityMatrix(practiceId);
      alert('Fehler beim Aktualisieren der Priorität');
    }
  };

  const handleQuickAddTherapist = async () => {
    if (!practiceId || !newTherapistName.trim()) return;

    try {
      setAddingTherapist(true);
      await axios.post(`${API_BASE_URL}/therapists/practice/${practiceId}/quick`, {
        name: newTherapistName.trim()
      });
      setNewTherapistName('');
      // Reload matrices to show new therapist
      await loadPriorityMatrix(practiceId);
      await loadTherapistLocationMatrix(practiceId);
      // The matrices are their own state — `therapists` feeds other surfaces
      // (e.g. the max-therapists-per-series options on the call-flow tab) and
      // would otherwise stay stale until a page reload.
      await refetchTherapists(practiceId);
    } catch (error) {
      console.error('Error adding therapist:', error);
      alert('Fehler beim Hinzufügen des Therapeuten');
    } finally {
      setAddingTherapist(false);
    }
  };

  // One-click pull of the therapist roster from the practice's connected PVS.
  // The worker only knows PVS names, so the backend seeds display name == pvs_name;
  // dedup is server-side, so re-clicking is harmless.
  const handleImportTherapistsFromPvs = async () => {
    if (!practiceId || importingPvsTherapists) return;
    try {
      setImportingPvsTherapists(true);
      setPvsImportResult('');
      const res = await axios.post(`${API_BASE_URL}/therapists/practice/${practiceId}/import-from-pvs`);
      const { created = 0, skipped = 0, available = 0 } = res.data || {};
      // Reload matrices so the imported therapists appear immediately.
      await loadPriorityMatrix(practiceId);
      await loadTherapistLocationMatrix(practiceId);
      await refetchTherapists(practiceId);
      if (available === 0) {
        alert('Es wurden keine Therapeuten im PVS gefunden.');
      } else if (created === 0) {
        setPvsImportResult(`Bereits aktuell · ${available} im PVS`);
      } else {
        setPvsImportResult(`${created} übernommen${skipped ? ` · ${skipped} bereits vorhanden` : ''}`);
      }
      setTimeout(() => setPvsImportResult(''), 5000);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      alert('Import aus dem PVS fehlgeschlagen: ' + detail);
    } finally {
      setImportingPvsTherapists(false);
    }
  };

  const handleDeleteTherapistFromMatrix = async (therapistId) => {

    try {
      await axios.delete(`${API_BASE_URL}/therapists/${therapistId}`);
      // Reload matrices to reflect deletion
      await loadPriorityMatrix(practiceId);
      await loadTherapistLocationMatrix(practiceId);
      await refetchTherapists(practiceId);
    } catch (error) {
      console.error('Error deleting therapist:', error);
      alert('Fehler beim Löschen des Therapeuten');
    }
  };

  const handleSaveTherapistName = async (therapistId, newName) => {
    if (!therapistId || !newName.trim()) return;
    try {
      await axios.put(`${API_BASE_URL}/therapists/${therapistId}/name`, {
        name: newName.trim()
      });
      await loadPriorityMatrix(practiceId);
      setEditingCell(null);
      setCellEditValue('');
    } catch (error) {
      console.error('Error updating therapist name:', error);
      alert('Fehler beim Aktualisieren des Namens');
    }
  };

  // ============================================================================
  // Location Matrix Handlers (mirror therapist matrix pattern)
  // ============================================================================

  const loadLocationMatrix = async (targetPracticeId, opts = {}) => {
    if (!targetPracticeId) return;
    try {
      if (!opts.silent) setLocationMatrixLoading(true);
      const res = await axios.get(`${API_BASE_URL}/locations/practice/${targetPracticeId}/matrix`);
      setLocationMatrix(res.data);
      // Write-through: keep the React Query cache in sync (FAQ lives in this
      // matrix — a revisit must not repaint a pre-save FAQ from cache).
      queryClient.setQueryData(['locationMatrix', targetPracticeId], res.data);
    } catch (err) {
      console.error('Failed to load location matrix:', err);
      if (!opts.silent) setLocationMatrix(null);
    } finally {
      if (!opts.silent) setLocationMatrixLoading(false);
    }
  };

  // Auto-create "Standort 1" the first time the practice has zero locations,
  // so the inline FAQ editor at the top of the Praxis tab always has a target.
  useEffect(() => {
    if (!practiceId) return;
    if (!locationMatrix || locationMatrixLoading) return;
    if (faqAutoCreateRef.current) return;
    const locations = locationMatrix.locations || [];
    if (locations.length === 0) {
      faqAutoCreateRef.current = true;
      // Use the practice name so the auto-created standort isn't a generic
      // "Standort 1" label. Falls back to "Hauptstandort" for empty practice
      // names (which can happen after the required-fields removal).
      const defaultName = (practiceInfo?.name || '').trim() || 'Hauptstandort';
      (async () => {
        try {
          await axios.post(`${API_BASE_URL}/locations/practice/${practiceId}/quick`, {
            name: defaultName
          });
          await loadLocationMatrix(practiceId);
        } catch (err) {
          console.error('Auto-create location failed:', err);
          faqAutoCreateRef.current = false;
        }
      })();
    }
  }, [practiceId, locationMatrix, locationMatrixLoading]);

  // When the matrix loads (or the user switches between locations), sync the
  // inline FAQ editor's draft text. We don't overwrite an in-progress draft
  // when the user is typing on the same location.
  useEffect(() => {
    if (!locationMatrix) return;
    const locations = locationMatrix.locations || [];
    if (locations.length === 0) return;
    const targetId = (faqLocationId && locations.find(l => l.id === faqLocationId))
      ? faqLocationId
      : locations[0].id;
    const target = locations.find(l => l.id === targetId);
    if (!target) return;
    if (faqLocationId !== targetId) {
      setFaqLocationId(targetId);
      setFaqDraft(target.faq || '');
    }
  }, [locationMatrix, faqLocationId]);

  const commitFaq = async (pending = null) => {
    const toCommit = pending || pendingFaqWriteRef.current;
    if (!toCommit) return;
    const { targetPracticeId, targetLocId, text } = toCommit;
    pendingFaqWriteRef.current = null;
    if (faqSaveTimerRef.current) {
      clearTimeout(faqSaveTimerRef.current);
      faqSaveTimerRef.current = null;
    }

    if (!targetPracticeId || loadedForPracticeIdRef.current !== targetPracticeId || !targetLocId) return;

    const location = locationMatrix?.locations?.find(l => l.id === targetLocId);
    if (!location) return;
    if ((location.faq || '') === text) return;

    if (isMountedRef.current) {
      setFaqSaving(true);
      setSaveStatus(prev => ({ ...prev, practice: { saved: false, message: 'Speichere...' } }));
    }

    try {
      await axios.put(`${API_BASE_URL}/locations/${targetLocId}/details`, {
        faq: text,
        pvs_name: location.pvs_name || ''
      });

      if (loadedForPracticeIdRef.current !== targetPracticeId) return;

      // The page cache too, as loadLocationMatrix did before: a revisit repaints from
      // it, and a stale FAQ there would be auto-saved back over this one.
      const patchFaq = (m) => (m?.locations ? { ...m, locations: m.locations.map(loc => (loc.id === targetLocId ? { ...loc, faq: text } : loc)) } : m);
      const cachedMatrix = queryClient.getQueryData(['locationMatrix', targetPracticeId]);
      if (cachedMatrix) queryClient.setQueryData(['locationMatrix', targetPracticeId], patchFaq(cachedMatrix));

      if (isMountedRef.current) {
        setLocationMatrix(patchFaq);
        setSaveStatus(prev => ({ ...prev, practice: { saved: true, message: 'FAQ gespeichert' } }));
        if (saveStatusClearTimerRef.current) clearTimeout(saveStatusClearTimerRef.current);
        saveStatusClearTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setSaveStatus(prev => ({ ...prev, practice: { saved: false, message: '' } }));
          }
        }, 2500);
      }
    } catch (err) {
      console.error('Error saving FAQ:', err);
      if (isMountedRef.current && loadedForPracticeIdRef.current === targetPracticeId) {
        setSaveStatus(prev => ({ ...prev, practice: { saved: false, message: 'Fehler beim Speichern der FAQ' } }));
      }
    } finally {
      if (isMountedRef.current) {  // the save is over even if the practice changed meanwhile
        setFaqSaving(false);
      }
    }
  };

  const handleLocationToggle = async (locationId, therapyTypeId, checked) => {
    // Optimistic update
    setLocationMatrix(prev => {
      const newMatrix = { ...prev.matrix };
      if (!newMatrix[locationId]) newMatrix[locationId] = {};
      newMatrix[locationId] = { ...newMatrix[locationId], [therapyTypeId]: checked };
      return { ...prev, matrix: newMatrix };
    });

    try {
      await axios.put(`${API_BASE_URL}/locations/practice/${practiceId}/matrix/cell`, {
        location_id: locationId,
        therapy_type_id: therapyTypeId,
        checked: checked
      });
      // Optimistic update touched state only — mark the cached copy stale so a
      // tab revisit refetches instead of repainting the pre-edit matrix.
      queryClient.invalidateQueries(['locationMatrix', practiceId]);
    } catch (error) {
      console.error('Error toggling location:', error);
      alert('Fehler beim Speichern');
      await loadLocationMatrix(practiceId);
    }
  };

  const loadTherapistLocationMatrix = async (targetPracticeId, opts = {}) => {
    if (!targetPracticeId) return;
    try {
      if (!opts.silent) setTherapistLocationMatrixLoading(true);
      const res = await axios.get(`${API_BASE_URL}/therapist-locations/practice/${targetPracticeId}/matrix`);
      setTherapistLocationMatrix(res.data);
    } catch (err) {
      console.error('Failed to load therapist-location matrix:', err);
      if (!opts.silent) setTherapistLocationMatrix(null);
    } finally {
      if (!opts.silent) setTherapistLocationMatrixLoading(false);
    }
  };

  const handleTherapistLocationToggle = async (therapistId, locationId, checked) => {
    setTherapistLocationMatrix(prev => {
      const newMatrix = { ...prev.matrix };
      if (!newMatrix[therapistId]) newMatrix[therapistId] = {};
      newMatrix[therapistId] = { ...newMatrix[therapistId], [locationId]: checked };
      return { ...prev, matrix: newMatrix };
    });

    try {
      await axios.put(`${API_BASE_URL}/therapist-locations/practice/${practiceId}/matrix/cell`, {
        therapist_id: therapistId,
        location_id: locationId,
        checked: checked
      });
    } catch (error) {
      console.error('Error toggling therapist-location:', error);
      alert('Fehler beim Speichern');
      await loadTherapistLocationMatrix(practiceId);
    }
  };

  const handleQuickAddLocation = async () => {
    if (!newLocationName.trim() || !practiceId) return;
    setAddingLocation(true);
    try {
      await axios.post(`${API_BASE_URL}/locations/practice/${practiceId}/quick`, {
        name: newLocationName.trim()
      });
      setNewLocationName('');
      await loadLocationMatrix(practiceId);
      await loadTherapistLocationMatrix(practiceId);
    } catch (error) {
      console.error('Error adding location:', error);
      alert('Fehler beim Hinzufuegen des Standorts');
    } finally {
      setAddingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await axios.delete(`${API_BASE_URL}/locations/${locationId}`);
      await loadLocationMatrix(practiceId);
      await loadTherapistLocationMatrix(practiceId);
    } catch (error) {
      console.error('Error deleting location:', error);
      alert('Fehler beim Loeschen des Standorts');
    }
  };

  const handleSaveLocationField = async (locationId, fieldOverrides) => {
    if (!locationId) return;
    const location = locationMatrix?.locations?.find(l => l.id === locationId);
    if (!location) return;
    const name = fieldOverrides.name !== undefined ? fieldOverrides.name : location.name;
    if (!name.trim()) return;
    try {
      await axios.put(`${API_BASE_URL}/locations/${locationId}/name`, {
        name: name.trim()
      });
      await loadLocationMatrix(practiceId);
      setEditingCell(null);
      setCellEditValue('');
    } catch (error) {
      console.error('Error updating location:', error);
      alert('Fehler beim Aktualisieren des Standorts');
    }
  };

  const handleOpenLocationModal = (location) => {
    setEditingLocation(location);
    setLocationForm({
      faq: location.faq || '',
      pvs_name: location.pvs_name || ''
    });
    setLocationModalOpen(true);
  };

  const handleSaveLocationDetails = async () => {
    if (!editingLocation) return;
    try {
      await axios.put(`${API_BASE_URL}/locations/${editingLocation.id}/details`, locationForm);
      await loadLocationMatrix(practiceId);
      setLocationModalOpen(false);
    } catch (error) {
      console.error('Error saving location details:', error);
      alert('Fehler beim Speichern der Standort-Details');
    }
  };

  const handleOpenTherapistModal = (therapist) => {
    setEditingTherapist(therapist);
    const pf = therapist.patient_filter || {};
    setTherapistForm({
      name: therapist.name || '',
      pvs_name: therapist.pvs_name || '',
      phone: therapist.phone || '',
      email: therapist.email || '',
      notify_short_notice_changes: !!therapist.notify_short_notice_changes,
      notify_channel: therapist.notify_channel || 'sms',
      slack_user_id: therapist.slack_user_id || '',
      pf_symptoms: Array.isArray(pf.symptoms) ? pf.symptoms : [],
      // Inputs hold strings so an empty "bis" stays empty rather than becoming 0.
      pf_age_groups: Array.isArray(pf.age_groups)
        ? pf.age_groups.map(g => ({
            min: g?.min === null || g?.min === undefined ? '' : String(g.min),
            max: g?.max === null || g?.max === undefined ? '' : String(g.max),
          }))
        : [],
      // An absent list means "alle", so every box starts checked.
      pf_genders: Array.isArray(pf.genders) && pf.genders.length ? pf.genders : [...PF_GENDER_KEYS],
      pf_insurance_types: Array.isArray(pf.insurance_types) && pf.insurance_types.length
        ? pf.insurance_types : [...INSURANCE_KEYS],
    });
    setTherapistSymptomDraft('');
    setTherapistFormError('');
    setTherapistModalOpen(true);
  };

  const handleSaveTherapistDetails = async () => {
    if (!editingTherapist) return;
    setTherapistFormError('');
    // The name is not a detail field (TherapistDetailUpdate has none): it goes through
    // the matrix's own rename route below. That route stores an empty name as-is.
    const { name, pf_symptoms, pf_age_groups, pf_genders, pf_insurance_types, ...details } = therapistForm;
    const newName = name.trim();
    if (!newName) {
      setTherapistFormError('Bitte einen Namen angeben.');
      return;
    }
    // Assemble the Patientenfilter. Only restrictions travel: an empty symptom
    // list, no age group, or a fully selected gender/insurance set all mean
    // "nimmt alle" and are simply left out (the backend normalises the same way).
    const patient_filter = {};
    const symptoms = [...(pf_symptoms || []), ...(therapistSymptomDraft.trim() ? [therapistSymptomDraft.trim()] : [])]
      .map(x => x.trim()).filter(Boolean);
    if (symptoms.length) patient_filter.symptoms = symptoms;
    const ageGroups = (pf_age_groups || [])
      .map(g => ({
        min: String(g.min).trim() === '' ? null : Number(g.min),
        max: String(g.max).trim() === '' ? null : Number(g.max),
      }))
      .filter(g => g.min !== null || g.max !== null);
    if (ageGroups.length) patient_filter.age_groups = ageGroups;
    if ((pf_genders || []).length && pf_genders.length < PF_GENDER_KEYS.length) {
      patient_filter.genders = pf_genders;
    }
    if ((pf_insurance_types || []).length && pf_insurance_types.length < INSURANCE_KEYS.length) {
      patient_filter.insurance_types = pf_insurance_types;
    }
    try {
      await axios.put(`${API_BASE_URL}/therapists/${editingTherapist.id}/details`, { ...details, patient_filter });
      // Details first: a rejected filter then leaves the name untouched as well.
      if (newName !== editingTherapist.name) {
        await axios.put(`${API_BASE_URL}/therapists/${editingTherapist.id}/name`, { name: newName });
        // The Therapeuten & Standorte matrix lists names too; it is loaded lazily, so
        // only refresh it when it is there.
        if (therapistLocationMatrix) await loadTherapistLocationMatrix(practiceId, { silent: true });
      }
      await loadPriorityMatrix(practiceId);
      setTherapistModalOpen(false);
    } catch (error) {
      console.error('Error saving therapist details:', error);
      // Surface the backend message: a blocked age group explains what to switch on.
      setTherapistFormError(formatApiError(error?.response?.data?.detail) || 'Fehler beim Speichern der Therapeuten-Details');
      // The details may have landed before the rename failed: show what is stored.
      loadPriorityMatrix(practiceId, { silent: true });
    }
  };

  // --- Calendar JSON import handlers ---
  const processImportData = (data) => {
    const therapyMap = new Map();       // name → { name, duration }
    const locationSet = new Set();
    const therapistSet = new Set();
    const locationTherapies = new Map(); // locationName → Set<therapyName>
    const therapistTherapies = new Map(); // therapistName → Set<therapyName>
    const therapistLocations = new Map(); // therapistName → Set<locationName>
    let skippedCount = 0;

    for (const entry of data) {
      const calName = entry.calendarName || '';
      const sepIdx = calName.indexOf(' - ');
      if (sepIdx === -1) {
        skippedCount++;
        continue;
      }

      const locationName = calName.substring(0, sepIdx).trim();
      const therapyName = calName.substring(sepIdx + 3).trim();
      const duration = entry.duration || 30;

      if (!therapyName || !locationName) {
        skippedCount++;
        continue;
      }

      if (!therapyMap.has(therapyName)) {
        therapyMap.set(therapyName, { name: therapyName, duration });
      }

      locationSet.add(locationName);

      if (!locationTherapies.has(locationName)) {
        locationTherapies.set(locationName, new Set());
      }
      locationTherapies.get(locationName).add(therapyName);

      for (const user of (entry.users || [])) {
        const tName = user.name?.trim();
        if (!tName) continue;
        therapistSet.add(tName);
        if (!therapistTherapies.has(tName)) {
          therapistTherapies.set(tName, new Set());
        }
        therapistTherapies.get(tName).add(therapyName);
        if (!therapistLocations.has(tName)) {
          therapistLocations.set(tName, new Set());
        }
        therapistLocations.get(tName).add(locationName);
      }
    }

    const existingTherapyNames = new Set((therapyTypes || []).map(t => t.name));
    const existingTherapistNames = new Set((priorityMatrix?.therapists || []).map(t => t.name));
    const existingLocationNames = new Set((locationMatrix?.locations || []).map(l => l.name));

    const newTherapies = [];
    const existingTherapies = [];
    for (const [name, info] of therapyMap) {
      if (existingTherapyNames.has(name)) {
        existingTherapies.push(info);
      } else {
        newTherapies.push(info);
      }
    }

    const newLocations = [];
    const existingLocations = [];
    for (const name of locationSet) {
      if (existingLocationNames.has(name)) {
        existingLocations.push(name);
      } else {
        newLocations.push(name);
      }
    }

    const newTherapists = [];
    const existingTherapists = [];
    for (const name of therapistSet) {
      if (existingTherapistNames.has(name)) {
        existingTherapists.push(name);
      } else {
        newTherapists.push(name);
      }
    }

    // An Aufnahmegespräch or a Blankorezept is a DeskBot construct, not a PVS calendar
    // entry, and an ergänzendes Heilmittel is never a calendar of its own: none of them is
    // in the imported file, so all of them must survive the import's delete phase.
    const deletedTherapies = (therapyTypes || []).filter(t => !therapyMap.has(t.name) && !t.is_intake_appointment && !t.is_blanko && !t.supplementary_mode);
    const deletedTherapists = (priorityMatrix?.therapists || []).filter(t => !therapistSet.has(t.name));
    const deletedLocations = (locationMatrix?.locations || []).filter(l => !locationSet.has(l.name));

    setImportPreview({
      newTherapies, existingTherapies, deletedTherapies,
      newLocations, existingLocations, deletedLocations,
      newTherapists, existingTherapists, deletedTherapists,
      locationTherapies, therapistTherapies, therapistLocations,
      skippedCount
    });
  };

  // Import therapies from a PVS "Artikel/Leistungen" xlsx export.
  // Columns: Leist_Kurzzeichen | Leist_Leistungsbezeichnung__lang_ | Leist_Verkaufspreis
  //          | Dauer | Selbstzahler | Rezept | Bemerkung
  // Rule: keep only Selbstzahler rows, dedup by name, upsert by name (no deletes).
  const handleFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!rows.length) {
          alert('Die Datei enthält keine Daten.');
          return;
        }
        const header = rows[0].map(h => String(h).trim());
        const col = (name) => header.indexOf(name);
        const ci = {
          code: col('Leist_Kurzzeichen'),
          name: col('Leist_Leistungsbezeichnung__lang_'),
          price: col('Leist_Verkaufspreis'),
          dauer: col('Dauer'),
          selbst: col('Selbstzahler'),
          bemerkung: col('Bemerkung'),
        };
        if (ci.name < 0 || ci.selbst < 0) {
          alert('Unerwartetes Dateiformat: Spalten "Leist_Leistungsbezeichnung__lang_" und "Selbstzahler" werden erwartet.');
          return;
        }

        const cell = (row, idx) => (idx >= 0 ? String(row[idx] ?? '').trim() : '');
        const seen = new Set();
        const parsed = [];
        let skippedCount = 0;
        for (let r = 1; r < rows.length; r++) {
          const row = rows[r];
          if (!row || !row.length) continue;
          if (cell(row, ci.selbst).toLowerCase() !== 'x') { skippedCount++; continue; } // Selbstzahler only
          const name = cell(row, ci.name);
          if (!name) { skippedCount++; continue; }
          const key = name.toLowerCase();
          if (seen.has(key)) { skippedCount++; continue; } // dedup by name
          seen.add(key);

          const m = cell(row, ci.dauer).match(/\d+/);
          let dur = m ? parseInt(m[0], 10) : 30;
          dur = Math.max(5, Math.min(180, dur)); // model validator bounds

          const price = cell(row, ci.price);
          const bemerkung = cell(row, ci.bemerkung);
          const code = cell(row, ci.code);
          const descParts = [];
          if (price) descParts.push(`Preis: ${price} €`);
          if (bemerkung) descParts.push(bemerkung);

          const payload = {
            name,
            pvs_name: code || name,
            aliases: [],
            duration_private: dur, duration_public: dur, duration_selbstzahler: dur,
            duration_bg: dur, duration_entlassmanagement: dur,
            sessions_private: 6, sessions_public: 6, sessions_selbstzahler: 6,
            sessions_bg: 6, sessions_entlassmanagement: 6,
            frequency_private: '1-3 mal pro Woche', frequency_public: '1-3 mal pro Woche',
            frequency_selbstzahler: '1-3 mal pro Woche', frequency_bg: '1-3 mal pro Woche',
            frequency_entlassmanagement: '1-3 mal pro Woche',
            first_session_extra_private: 0, first_session_extra_public: 0,
            first_session_extra_selbstzahler: 0, first_session_extra_bg: 0,
            first_session_extra_entlassmanagement: 0,
            description: descParts.join('\n'),
            is_active: true,
            recommend_sessions_enabled: true,
            force_sessions_enabled: false,
            force_frequency_enabled: false,
            double_appointments_enabled: false,
            is_intake_appointment: false,
            intake_therapy_type_id: null,
            is_blanko: false,
            accepted_insurance_types: { private: false, public: false, selbstzahler: true, bg: false, entlassmanagement: false },
          };
          parsed.push({ name, duration: dur, payload });
        }

        if (!parsed.length) {
          alert('Keine Selbstzahler-Leistungen in der Datei gefunden.');
          return;
        }

        // Split into new vs existing (matched by name, case-insensitive)
        const existingByName = new Map((therapyTypes || []).map(t => [String(t.name).trim().toLowerCase(), t]));
        const newTherapies = [];
        const existingTherapies = [];
        for (const t of parsed) {
          const match = existingByName.get(t.name.toLowerCase());
          if (match) existingTherapies.push({ id: match.id, name: t.name, duration: t.duration, payload: {
            ...t.payload,
            // Preserve the row's curated symptoms — a full-replace PUT would otherwise wipe them
            symptoms: match.symptoms || [],
            // Preserve the row's per-insurance PVS names — a full-replace PUT would otherwise wipe them
            pvs_name_per_insurance: match.pvs_name_per_insurance ?? false,
            pvs_name_private: match.pvs_name_private || '',
            pvs_name_public: match.pvs_name_public || '',
            pvs_name_selbstzahler: match.pvs_name_selbstzahler || '',
            pvs_name_bg: match.pvs_name_bg || '',
            pvs_name_entlassmanagement: match.pvs_name_entlassmanagement || '',
            // Preserve the row's fixed-value switches — a full-replace PUT would otherwise reset them
            force_sessions_enabled: match.force_sessions_enabled ?? false,
            force_frequency_enabled: match.force_frequency_enabled ?? false,
            double_appointments_enabled: match.double_appointments_enabled ?? false,
            // Preserve the Aufnahmegespräch pair — a re-import must keep the marked
            // intake rows marked and every link to them intact
            is_intake_appointment: match.is_intake_appointment ?? false,
            intake_therapy_type_id: match.intake_therapy_type_id ?? null,
            // Preserve the Blankorezept mark the same way
            is_blanko: match.is_blanko ?? false,
            // ...and the Ergänzendes-Heilmittel mark
            supplementary_mode: match.supplementary_mode ?? null,
            // An Ergänzendes Heilmittel keeps its own five minutes: the xlsx Dauer above
            // (clamped to 5-180, empty = 30) would replace what the practice set, and every
            // move with it would then book or announce minutes nobody planned
            ...(match.supplementary_mode
              ? Object.fromEntries(INSURANCE_KEYS.map(k => [`duration_${k}`, match[`duration_${k}`]]))
              : {}),
            // Preserve the row's category — this block enumerates fields explicitly,
            // so without it the PUT would move the row back to the default
            category: match.category,
          } });
          else newTherapies.push(t);
        }

        setImportPreview({
          xlsx: true,
          newTherapies, existingTherapies, deletedTherapies: [],
          newLocations: [], existingLocations: [], deletedLocations: [],
          newTherapists: [], existingTherapists: [], deletedTherapists: [],
          locationTherapies: new Map(), therapistTherapies: new Map(), therapistLocations: new Map(),
          skippedCount,
          skippedNote: 'Zeilen übersprungen (nicht Selbstzahler oder doppelter Name).',
        });
      } catch (err) {
        alert('Fehler beim Lesen der Datei: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handlePvsImport = async () => {
    if (!practiceId) {
      alert('Praxis nicht geladen.');
      return;
    }
    setPvsImportLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/test-call/pvs-import?practice_id=${encodeURIComponent(practiceId)}`);
      const data = res.data;
      if (!Array.isArray(data)) {
        alert('Ungültiges Format: Die PVS-API hat kein JSON-Array zurückgegeben.');
        return;
      }
      processImportData(data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      alert('PVS-Import fehlgeschlagen: ' + detail);
    } finally {
      setPvsImportLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!importPreview || !practiceId) return;
    setImportLoading(true);

    try {
      // XLSX article import: therapy-only upsert. Never touches the therapist /
      // location priority matrices (the calendar import below does — xlsx must not).
      if (importPreview.xlsx) {
        let created = 0, updated = 0;
        for (const t of importPreview.newTherapies) {
          try {
            await axios.post(`${API_BASE_URL}/therapy-types/practice/${practiceId}`, t.payload);
            created++;
          } catch (err) {
            console.warn(`Therapy "${t.name}" create failed:`, err.response?.data?.detail);
          }
        }
        for (const t of importPreview.existingTherapies) {
          try {
            await axios.put(`${API_BASE_URL}/therapy-types/${t.id}`, t.payload);
            updated++;
          } catch (err) {
            console.warn(`Therapy "${t.name}" update failed:`, err.response?.data?.detail);
          }
        }
        const ttRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${practiceId}`);
        setTherapyTypes(ttRes.data);
        // Both matrix payloads embed the therapy_types list — drop the cached
        // copies so a tab revisit can't repaint pre-import therapy columns.
        queryClient.invalidateQueries(['priorityMatrix', practiceId]);
        queryClient.invalidateQueries(['locationMatrix', practiceId]);
        setImportPreview(null);
        alert(`Import abgeschlossen: ${created} neu, ${updated} aktualisiert.`);
        return;
      }

      const { newTherapies, newLocations, newTherapists, deletedTherapies, deletedLocations, deletedTherapists, locationTherapies, therapistTherapies, therapistLocations } = importPreview;

      // Phase 1: Create new therapy types
      for (const therapy of newTherapies) {
        try {
          await axios.post(`${API_BASE_URL}/therapy-types/practice/${practiceId}`, {
            name: therapy.name,
            pvs_name: therapy.name,
            duration_private: therapy.duration,
            duration_public: therapy.duration,
            duration_selbstzahler: therapy.duration,
            duration_bg: therapy.duration,
            duration_entlassmanagement: therapy.duration,
            first_session_extra_private: 0,
            first_session_extra_public: 0,
            first_session_extra_selbstzahler: 0,
            first_session_extra_bg: 0,
            first_session_extra_entlassmanagement: 0,
            sessions_private: 6,
            sessions_public: 6,
            sessions_selbstzahler: 6,
            sessions_bg: 6,
            sessions_entlassmanagement: 6,
            frequency_private: '1-3 mal pro Woche',
            frequency_public: '1-3 mal pro Woche',
            frequency_selbstzahler: '1-3 mal pro Woche',
            frequency_bg: '1-3 mal pro Woche',
            frequency_entlassmanagement: '1-3 mal pro Woche',
            description: '',
            aliases: [],
            is_active: true,
            recommend_sessions_enabled: true,
            force_sessions_enabled: false,
            force_frequency_enabled: false,
            double_appointments_enabled: false,
            is_intake_appointment: false,
            intake_therapy_type_id: null,
            is_blanko: false,
            accepted_insurance_types: DEFAULT_ACCEPTED_INSURANCE
          });
        } catch (err) {
          console.warn(`Therapy "${therapy.name}" creation failed (may already exist):`, err.response?.data?.detail);
        }
      }

      // Phase 2: Create new locations
      for (const locName of newLocations) {
        try {
          await axios.post(`${API_BASE_URL}/locations/practice/${practiceId}/quick`, {
            name: locName,
            pvs_name: locName
          });
        } catch (err) {
          console.warn(`Location "${locName}" creation failed:`, err.response?.data?.detail);
        }
      }

      // Phase 3: Create new therapists
      for (const thName of newTherapists) {
        try {
          await axios.post(`${API_BASE_URL}/therapists/practice/${practiceId}/quick`, {
            name: thName,
            pvs_name: thName
          });
        } catch (err) {
          console.warn(`Therapist "${thName}" creation failed:`, err.response?.data?.detail);
        }
      }

      // Phase 3a: Delete therapies not in file
      for (const therapy of deletedTherapies) {
        try {
          await axios.delete(`${API_BASE_URL}/therapy-types/${therapy.id}`);
        } catch (err) {
          console.warn(`Therapy "${therapy.name}" deletion failed:`, err.response?.data?.detail);
        }
      }

      // Phase 3b: Delete therapists not in file
      for (const therapist of deletedTherapists) {
        try {
          await axios.delete(`${API_BASE_URL}/therapists/${therapist.id}`);
        } catch (err) {
          console.warn(`Therapist "${therapist.name}" deletion failed:`, err.response?.data?.detail);
        }
      }

      // Phase 3c: Delete locations not in file
      for (const location of deletedLocations) {
        try {
          await axios.delete(`${API_BASE_URL}/locations/${location.id}`);
        } catch (err) {
          console.warn(`Location "${location.name}" deletion failed:`, err.response?.data?.detail);
        }
      }

      // Phase 4: Reload matrices to get all current IDs
      const [matrixRes, locMatrixRes, ttRes, tlMatrixRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/therapists/practice/${practiceId}/matrix`),
        axios.get(`${API_BASE_URL}/locations/practice/${practiceId}/matrix`),
        axios.get(`${API_BASE_URL}/therapy-types/practice/${practiceId}`),
        axios.get(`${API_BASE_URL}/therapist-locations/practice/${practiceId}/matrix`)
      ]);

      const freshMatrix = matrixRes.data;
      const freshLocMatrix = locMatrixRes.data;
      const freshTLMatrix = tlMatrixRes.data;

      // Build name→ID lookup maps
      const therapyNameToId = {};
      for (const tt of (freshMatrix.therapy_types || [])) {
        therapyNameToId[tt.name] = tt.id;
      }
      // Also check location matrix therapy types (should be same)
      for (const tt of (freshLocMatrix.therapy_types || [])) {
        therapyNameToId[tt.name] = tt.id;
      }

      const therapistNameToId = {};
      for (const th of (freshMatrix.therapists || [])) {
        therapistNameToId[th.name] = th.id;
      }

      const locationNameToId = {};
      for (const loc of (freshLocMatrix.locations || [])) {
        locationNameToId[loc.name] = loc.id;
      }

      // Phase 5: Sync therapist priorities
      // For each therapist × therapy: if in file → set to 1 (only if currently 0);
      // if NOT in file → set to 0 (only if currently 1; leave 2,3 untouched)
      const allTherapyIds = (freshMatrix.therapy_types || []).map(tt => tt.id);
      for (const therapist of (freshMatrix.therapists || [])) {
        const fileTherapies = therapistTherapies.get(therapist.name) || new Set();
        for (const ttId of allTherapyIds) {
          const currentPrio = freshMatrix.matrix[therapist.id]?.[ttId] || 0;
          const ttName = (freshMatrix.therapy_types || []).find(t => t.id === ttId)?.name;
          const inFile = ttName && fileTherapies.has(ttName);

          let newPrio = null;
          if (inFile && currentPrio === 0) {
            newPrio = 1;
          } else if (!inFile && currentPrio === 1) {
            newPrio = 0;
          }

          if (newPrio !== null) {
            try {
              await axios.put(`${API_BASE_URL}/therapists/practice/${practiceId}/matrix/cell`, {
                therapist_id: therapist.id,
                therapy_type_id: ttId,
                priority: newPrio
              });
            } catch (err) {
              console.warn(`Priority sync failed for ${therapist.name} → ${ttName}:`, err.response?.data?.detail);
            }
          }
        }
      }

      // Phase 6: Sync location toggles
      // For each location × therapy: if in file → check; if NOT in file → uncheck
      const allLocTherapyIds = (freshLocMatrix.therapy_types || []).map(tt => tt.id);
      for (const location of (freshLocMatrix.locations || [])) {
        const fileTherapies = locationTherapies.get(location.name) || new Set();
        for (const ttId of allLocTherapyIds) {
          const ttName = (freshLocMatrix.therapy_types || []).find(t => t.id === ttId)?.name;
          const inFile = ttName && fileTherapies.has(ttName);
          const currentlyChecked = freshLocMatrix.matrix[location.id]?.[ttId] || false;

          if (inFile !== currentlyChecked) {
            try {
              await axios.put(`${API_BASE_URL}/locations/practice/${practiceId}/matrix/cell`, {
                location_id: location.id,
                therapy_type_id: ttId,
                checked: inFile
              });
            } catch (err) {
              console.warn(`Location toggle sync failed for ${location.name} → ${ttName}:`, err.response?.data?.detail);
            }
          }
        }
      }

      // Phase 7: Sync therapist-location toggles
      if (therapistLocations) {
        for (const therapist of (freshTLMatrix.therapists || [])) {
          const fileLocations = therapistLocations.get(therapist.name) || new Set();
          for (const loc of (freshTLMatrix.locations || [])) {
            const inFile = fileLocations.has(loc.name);
            const currentlyChecked = freshTLMatrix.matrix[therapist.id]?.[loc.id] || false;

            if (inFile !== currentlyChecked) {
              try {
                await axios.put(`${API_BASE_URL}/therapist-locations/practice/${practiceId}/matrix/cell`, {
                  therapist_id: therapist.id,
                  location_id: loc.id,
                  checked: inFile
                });
              } catch (err) {
                console.warn(`Therapist-location sync failed for ${therapist.name} → ${loc.name}:`, err.response?.data?.detail);
              }
            }
          }
        }
      }

      // Reload all data after relationship changes
      setTherapyTypes(ttRes.data);
      await Promise.all([
        loadPriorityMatrix(practiceId),
        loadLocationMatrix(practiceId),
        loadTherapistLocationMatrix(practiceId),
        // This import creates and deletes therapists (phases 3 / 3b), so the
        // `therapists` list behind the other tabs has to move with it.
        refetchTherapists(practiceId)
      ]);

      setImportPreview(null);
      alert('Import erfolgreich abgeschlossen!');
    } catch (err) {
      console.error('Import error:', err);
      alert('Fehler beim Import: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  // Therapy Type handlers
  // The "+" and the `n` shortcut are ONE control, so both go through this.
  const openAddPopover = (anchorEl) => {
    if (!anchorEl) return;
    addPopoverAnchorRef.current = anchorEl;
    setAddTherapyName('');
    setAddTherapyPopover(measureAddPopover(anchorEl, addPopoverHints(therapyCategoryTab)));
  };

  // The "Blankorezept" preset of the "+" popover, for a NEW row only: named after its
  // Heilmittelgruppe, statutory insurance only (switchable in the dialog), one fixed
  // session at a fixed interval. "Physiotherapie & Osteopathie" is named "Physiotherapie"
  // here. The PVS articles are prefilled only for a TheOrg practice's Physiotherapie; the
  // detected system has to belong to the practice on screen (pvsSystem is not cleared on
  // a practice switch until the new config has loaded).
  const blankoPresetFields = (key) => ({
    name: `Blankorezept ${key === 'physio_osteo' ? 'Physiotherapie' : categoryLabel(key)}`,
    is_blanko: true,
    accepted_insurance_types: { private: false, public: true, selbstzahler: false, bg: false, entlassmanagement: false },
    ...Object.fromEntries(INSURANCE_KEYS.flatMap(k => [[`sessions_${k}`, 1], [`frequency_${k}`, '1-3 mal pro Woche']])),
    force_sessions_enabled: true,
    force_frequency_enabled: true,
    // No group words on purpose: the group is already the row's own.
    aliases: ['Blanko', 'Blankorezept', 'Blankoverordnung', 'Blanco'],
    pvs_name: key === 'physio_osteo' && pvsLoadedFor === practiceId && isTheOrg(pvsSystem.name)
      ? 'PD-BV, KG-BV, KG-BV'
      : '',
  });

  // Creating seeds the Kategorie field from the ACTIVE category tab (bottom-left "+"
  // and the `n` shortcut); editing takes the row's own category. `preset` is what the
  // "+" popover picked: a typed name, the Aufnahmegespräch preset which additionally
  // opens the modal in intake mode, the Blankorezept preset ({ is_blanko: true }), or
  // an Ergänzendes Heilmittel ({ supplementary_mode }, with the typed name if any).
  // The Aufnahmegespräch is ONE row shown in several groups, so its dialog always opens
  // in the view of the group it was opened from: the form's `category` carries that
  // group (the server ignores `category` on this row) and the checkboxes are that
  // group's therapies pointing at it.
  const handleOpenTherapyTypeModal = (therapyType = null, preset = null) => {
    setAliasDraft('');
    setSymptomDraft('');
    setPvsNameDrafts({});
    setSuppDraft(therapyType?.supplementary_mode ? supplementaryDraftOf(therapyType) : SUPPLEMENTARY_DRAFT_NEW);
    const groupKey = therapyCategoryTab || DEFAULT_THERAPY_CATEGORY;
    setIntakeLinkedIds(
      therapyType?.is_intake_appointment
        ? groupRows(groupKey).filter(t => t.intake_therapy_type_id === therapyType.id).map(t => t.id)
        : []
    );
    if (therapyType) {
      setEditingTherapyType(therapyType);
      setTherapyTypeForm({
        name: therapyType.name,
        category: therapyType.is_intake_appointment ? groupKey : (therapyType.category ?? DEFAULT_THERAPY_CATEGORY),
        duration_private: therapyType.duration_private || 30,
        sessions_private: therapyType.sessions_private || 1,
        frequency_private: therapyType.frequency_private || 'Wöchentlich',
        duration_public: therapyType.duration_public || 30,
        sessions_public: therapyType.sessions_public || 1,
        frequency_public: therapyType.frequency_public || 'Wöchentlich',
        duration_selbstzahler: therapyType.duration_selbstzahler || 30,
        sessions_selbstzahler: therapyType.sessions_selbstzahler || 1,
        frequency_selbstzahler: therapyType.frequency_selbstzahler || 'Wöchentlich',
        duration_bg: therapyType.duration_bg || 30,
        sessions_bg: therapyType.sessions_bg || 1,
        frequency_bg: therapyType.frequency_bg || 'Wöchentlich',
        duration_entlassmanagement: therapyType.duration_entlassmanagement || 30,
        sessions_entlassmanagement: therapyType.sessions_entlassmanagement || 1,
        frequency_entlassmanagement: therapyType.frequency_entlassmanagement || 'Wöchentlich',
        first_session_extra_private: therapyType.first_session_extra_private || 0,
        first_session_extra_public: therapyType.first_session_extra_public || 0,
        first_session_extra_selbstzahler: therapyType.first_session_extra_selbstzahler || 0,
        first_session_extra_bg: therapyType.first_session_extra_bg || 0,
        first_session_extra_entlassmanagement: therapyType.first_session_extra_entlassmanagement || 0,
        description: therapyType.description || '',
        aliases: therapyType.aliases || [],
        symptoms: therapyType.symptoms || [],
        pvs_name: therapyType.pvs_name || '',
        pvs_name_per_insurance: therapyType.pvs_name_per_insurance ?? false,
        pvs_name_private: therapyType.pvs_name_private || '',
        pvs_name_public: therapyType.pvs_name_public || '',
        pvs_name_selbstzahler: therapyType.pvs_name_selbstzahler || '',
        pvs_name_bg: therapyType.pvs_name_bg || '',
        pvs_name_entlassmanagement: therapyType.pvs_name_entlassmanagement || '',
        recommend_sessions_enabled: true,
        force_sessions_enabled: therapyType.force_sessions_enabled ?? false,
        force_frequency_enabled: therapyType.force_frequency_enabled ?? false,
        double_appointments_enabled: therapyType.double_appointments_enabled ?? false,
        is_intake_appointment: therapyType.is_intake_appointment === true,
        intake_therapy_type_id: therapyType.intake_therapy_type_id ?? null,
        is_blanko: therapyType.is_blanko === true,
        supplementary_mode: therapyType.supplementary_mode ?? null,
        accepted_insurance_types: therapyType.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE
      });
    } else {
      setEditingTherapyType(null);
      setTherapyTypeForm({
        name: preset?.name || '',
        category: therapyCategoryTab || DEFAULT_THERAPY_CATEGORY,
        duration_private: 30,
        sessions_private: 6,
        frequency_private: '1-3 mal pro Woche',
        duration_public: 30,
        sessions_public: 6,
        frequency_public: '1-3 mal pro Woche',
        duration_selbstzahler: 30,
        sessions_selbstzahler: 6,
        frequency_selbstzahler: '1-3 mal pro Woche',
        duration_bg: 30,
        sessions_bg: 6,
        frequency_bg: '1-3 mal pro Woche',
        duration_entlassmanagement: 30,
        sessions_entlassmanagement: 6,
        frequency_entlassmanagement: '1-3 mal pro Woche',
        first_session_extra_private: 0,
        first_session_extra_public: 0,
        first_session_extra_selbstzahler: 0,
        first_session_extra_bg: 0,
        first_session_extra_entlassmanagement: 0,
        description: '',
        aliases: [],
        symptoms: [],
        pvs_name: '',
        pvs_name_per_insurance: false,
        pvs_name_private: '',
        pvs_name_public: '',
        pvs_name_selbstzahler: '',
        pvs_name_bg: '',
        pvs_name_entlassmanagement: '',
        recommend_sessions_enabled: true,
        force_sessions_enabled: false,
        force_frequency_enabled: false,
        double_appointments_enabled: false,
        is_intake_appointment: preset?.is_intake_appointment === true,
        intake_therapy_type_id: null,
        is_blanko: false,
        // An Ergänzendes Heilmittel takes the preset's mode; its minutes come from suppDraft
        supplementary_mode: preset?.supplementary_mode ?? null,
        accepted_insurance_types: DEFAULT_ACCEPTED_INSURANCE,
        // The Blankorezept preset replaces the plain defaults above where it differs
        ...(preset?.is_blanko === true ? blankoPresetFields(groupKey) : {}),
      });
    }
    setAddTherapyPopover(null);
    setTherapyTypeModalOpen(true);
  };

  const handleSaveTherapyType = async () => {
    try {
      if (!practiceId) {
        alert('Bitte speichern Sie zuerst die Praxis-Informationen');
        return;
      }

      // Commit any pending alias draft, then filter out empty aliases before saving
      const draftCommitted = aliasDraft.trim();
      const mergedAliases = [
        ...(therapyTypeForm.aliases || []),
        ...(draftCommitted ? [draftCommitted] : []),
      ];
      // Commit any pending symptom draft, then filter out empty symptoms before saving
      const symptomDraftCommitted = symptomDraft.trim();
      const mergedSymptoms = [
        ...(therapyTypeForm.symptoms || []),
        ...(symptomDraftCommitted ? [symptomDraftCommitted] : []),
      ];
      // Same for a half-typed PVS-Bezeichnung chip (the chip box commits on blur, so only the
      // field that still has the focus can hold one).
      const pvsDraftFields = Object.fromEntries(Object.entries(pvsNameDrafts)
        .filter(([, draft]) => draft.trim())
        .map(([field, draft]) => [field, [...splitCommaList(therapyTypeForm[field]), draft.trim()].join(', ')]));
      // An Ergänzendes Heilmittel: the phrase's minutes go to every insurance, a "Je
      // Versicherungsart" cell (only while that is open) overrides its own one, and an own
      // PVS name is used only when one is filled in there.
      const suppMinutes = parseInt(suppDraft.minutes, 10);
      if (isSupplementaryForm && !(suppMinutes >= 1)) {
        alert('Bitte mindestens 1 Minute angeben.');
        return;
      }
      const suppFields = isSupplementaryForm ? {
        ...Object.fromEntries(INSURANCE_KEYS.map(k => {
          const own = suppDraft.perIns ? parseInt(suppDraft.minutesByIns[k], 10) : NaN;
          return [`duration_${k}`, Math.min(180, own >= 1 ? own : suppMinutes)];
        })),
        pvs_name_per_insurance: suppDraft.perIns
          && INSURANCE_KEYS.some(k => (therapyTypeForm[`pvs_name_${k}`] || '').trim() !== ''),
      } : {};
      const cleanedForm = {
        ...therapyTypeForm,
        ...pvsDraftFields,
        ...suppFields,
        aliases: mergedAliases.filter(a => a.trim() !== ''),
        symptoms: mergedSymptoms.filter(s => s.trim() !== ''),
        // A link exists only where the Aufnahmegespräch is shown: whatever the Kategorie
        // dropdown went through, the group the row is saved into decides.
        intake_therapy_type_id: intakeListsGroup(formCategory) ? therapyTypeForm.intake_therapy_type_id : null,
      };

      let savedId = editingTherapyType?.id || null;
      let createdRow = null;
      if (editingTherapyType) {
        // Update existing therapy type
        await axios.put(`${API_BASE_URL}/therapy-types/${editingTherapyType.id}`, cleanedForm);
      } else {
        // Create new therapy type
        const created = await axios.post(`${API_BASE_URL}/therapy-types/practice/${practiceId}`, cleanedForm);
        createdRow = created.data || null;
        savedId = createdRow?.id || null;
      }
      // The Aufnahmegespräch is shown per group: this call adds the dialog's group to the
      // row's list and sets exactly the checked links of THAT group (other groups are
      // never touched). The row write above only settles the shared fields.
      // Its own try: the row above is already committed, so a failure here must still
      // reach the reload below or the table stays stale against a saved row.
      let linkError = null;
      if (cleanedForm.is_intake_appointment && savedId) {
        try {
          await axios.put(
            `${API_BASE_URL}/therapy-types/practice/${practiceId}/${savedId}/groups/${cleanedForm.category}`,
            { therapy_ids: intakeLinkedIds }
          );
        } catch (err) {
          console.error('Error saving the intake appointment group:', err);
          linkError = err;
        }
      }

      // Reload therapy types and therapists (to update associations)
      const ttRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${practiceId}`);
      setTherapyTypes(ttRes.data);
      await refetchTherapists(practiceId);
      // Reload priority matrix and location matrix to show new/updated column
      await loadPriorityMatrix(practiceId);
      await loadLocationMatrix(practiceId);
      // Focus rule: the active tab follows the row the user just saved, otherwise a
      // therapy whose category changed (or a new one created for another category)
      // silently disappears from the tab they are looking at. The rail is refetched
      // because the save may have emptied a category or filled a new one.
      if (cleanedForm.category) setTherapyCategoryTab(cleanedForm.category);
      setTherapySubView(cleanedForm.supplementary_mode ? 'supplementary' : 'main');
      await refetchTherapyCategories(practiceId);
      if (linkError) {
        // The row is saved, the links are not: keep the modal open on the reloaded
        // state so the selection can be corrected and sent again. A row created just now
        // becomes the edited row, or the retry would POST a second Aufnahmegespräch (409).
        if (createdRow) setEditingTherapyType(createdRow);
        // A therapy moved out of the group meanwhile would make every retry fail: keep
        // only the checked ids that are still in the dialog's group.
        const inGroup = new Set(ttRes.data.filter(t => (t.category ?? DEFAULT_THERAPY_CATEGORY) === cleanedForm.category).map(t => t.id));
        setIntakeLinkedIds(prev => prev.filter(id => inGroup.has(id)));
        alert(formatApiError(linkError?.response?.data?.detail)
          || 'Die Verknüpfungen konnten nicht gespeichert werden.');
        return;
      }
      setTherapyTypeModalOpen(false);
    } catch (error) {
      console.error('Error saving therapy type:', error);
      alert(formatApiError(error?.response?.data?.detail) || 'Fehler beim Speichern der Behandlungsart');
    }
  };

  const handleDuplicateTherapyType = async (therapyType) => {
    try {
      if (!practiceId) {
        alert('Bitte speichern Sie zuerst die Praxis-Informationen');
        return;
      }
      const { id, created_at, updated_at, ...rest } = therapyType;
      const payload = {
        ...rest,
        name: `${therapyType.name} (Kopie)`,
        aliases: Array.isArray(therapyType.aliases) ? [...therapyType.aliases] : [],
        // A copy is a plain, unlinked Behandlungsart: the practice has exactly ONE
        // Aufnahmegespräch, and putting a therapy behind it stays a deliberate choice.
        is_intake_appointment: false,
        intake_therapy_type_id: null,
        // ...and never a second Blankorezept of the group (that would be a 409).
        // supplementary_mode is NOT stripped: a copy of an Ergänzendes Heilmittel is one too.
        is_blanko: false,
        accepted_insurance_types: { ...(therapyType.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE) },
      };
      await axios.post(`${API_BASE_URL}/therapy-types/practice/${practiceId}`, payload);
      const ttRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${practiceId}`);
      setTherapyTypes(ttRes.data);
      await loadPriorityMatrix(practiceId);
      await loadLocationMatrix(practiceId);
    } catch (error) {
      console.error('Error duplicating therapy type:', error);
      alert(formatApiError(error?.response?.data?.detail) || 'Fehler beim Duplizieren der Behandlungsart');
    }
  };

  const handleDeleteTherapyType = async (therapyTypeId) => {
    try {
      await axios.delete(`${API_BASE_URL}/therapy-types/${therapyTypeId}`);
      const ttRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${practiceId}`);
      setTherapyTypes(ttRes.data);
      await refetchTherapists(practiceId);
      // Reload priority matrix and location matrix to remove deleted column
      await loadPriorityMatrix(practiceId);
      await loadLocationMatrix(practiceId);
    } catch (error) {
      console.error('Error deleting therapy type:', error);
      alert('Fehler beim Löschen der Behandlungsart');
    }
  };

  // The bin on the Aufnahmegespräch removes it from ONE group: that group's links go,
  // the other groups keep theirs. Taking it out of its last group deletes the row
  // server-side, and only then do the matrices lose a column.
  const handleRemoveIntakeFromGroup = async (intakeId, key) => {
    try {
      const res = await axios.delete(`${API_BASE_URL}/therapy-types/practice/${practiceId}/${intakeId}/groups/${key}`);
      await refetchTherapyTypes(practiceId);
      if (res.data?.deleted) {
        await refetchTherapists(practiceId);
        await loadPriorityMatrix(practiceId);
        await loadLocationMatrix(practiceId);
      }
    } catch (error) {
      console.error('Error removing the intake appointment from a group:', error);
      alert(formatApiError(error?.response?.data?.detail)
        || 'Das Aufnahmegespräch konnte nicht aus dieser Gruppe entfernt werden.');
    }
  };

  // ===== Excluded ("Nicht angebotene") therapies — data layer + handlers =====
  // Re-GET the practice's excluded rows (used after every mutation).
  const refetchExcluded = async () => {
    const res = await axios.get(`${API_BASE_URL}/excluded-therapies/practice/${practiceId}`);
    setExcludedRows(res.data || []);
    return res.data || [];
  };

  // Explicit targetPracticeId (like loadPriorityMatrix / loadLocationMatrix /
  // loadTherapistLocationMatrix) so a late response can never write under a
  // practice the user has since switched away from.
  const refetchTherapyTypes = async (targetPracticeId) => {
    const res = await axios.get(`${API_BASE_URL}/therapy-types/practice/${targetPracticeId}`);
    setTherapyTypes(res.data);
    return res.data;
  };

  // The category rail travels with the therapy list, so it takes the same explicit
  // targetPracticeId. A 404 (new SPA, old backend) flips the read-only fallback
  // instead of failing the caller; every other error is logged and left alone so a
  // hiccup never blanks a rail the user is looking at.
  // Single place that folds a {categories, counts, options} payload into state.
  // Every category endpoint returns that shape, so the mutation handlers apply their
  // own response instead of issuing another GET. counts is ignored on purpose (it is
  // derived from therapyTypes); options is only overwritten when present, because the
  // mutating routes omit it.
  const applyCategoriesPayload = (payload) => {
    const body = payload || {};
    setCategoriesApiAvailable(true);
    setTherapyCategories(body.categories?.length ? body.categories : [DEFAULT_THERAPY_CATEGORY]);
    if (body.options?.length) setCategoryOptions(body.options);
    // A rename re-keys a group's Verordnungstyp set, and a merge or delete drops
    // one, server-side. Without adopting the map back the dialog would mark the old
    // key and the renamed group's next edit would re-seed from the shared values,
    // silently discarding the set that was just carried over.
    if (body.settings !== undefined) setTherapyCategorySettings(body.settings || {});
  };

  const refetchTherapyCategories = async (targetPracticeId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/therapy-types/practice/${targetPracticeId}/categories`);
      applyCategoriesPayload(res.data);
      return res.data || {};
    } catch (error) {
      if (error.response?.status === 404) {
        setCategoriesApiAvailable(false);
      } else {
        console.error('Failed to reload therapy categories:', error);
      }
      return null;
    }
  };

  const refetchTherapists = async (targetPracticeId) => {
    setTherapistsLoadedFor(null);
    const res = await axios.get(`${API_BASE_URL}/therapists/practice/${targetPracticeId}`);
    setTherapists(res.data);
    setTherapistsLoadedFor(targetPracticeId);
    return res.data;
  };

  // --- Live config sync registration --------------------------------------
  // One scope per reload action. The scope names are the backend's (see
  // middleware/config_broadcast.py) — keep the two lists in step.
  //
  // The two big tabs reload by re-running their hydration effect: invalidate the
  // React Query entry FIRST (both hydrators read through a 3-minute cache, so a
  // bare re-run would be a no-op) and then bump the nonce. That reuses all the
  // existing staleness guards and, for the call-flow tab, is loop-safe by
  // construction — the effect nulls callflowBaselineRef on entry, so the
  // re-hydrated toggle values are recorded as a new baseline rather than being
  // mistaken for a user edit and auto-saved straight back out.
  //
  // The Behandlungen & Team surfaces have no page-level drafts (every control
  // persists itself per row/cell), so they always apply live.
  const practiceScope = {
    reload: () => {
      queryClient.invalidateQueries(['practiceInfo', practiceId]);
      setPracticeReloadNonce((n) => n + 1);
    },
    isDirty: () => practiceTabDirty || callflowTabDirty,
  };
  useConfigSync({
    // The practice row and the spoken messages hydrate in the SAME effect, so the
    // two scopes must share one reload — and therefore one dirty test. Splitting
    // them would let a colleague's filler-message save silently re-run the
    // practice hydration and wipe unsaved Praxis-Information edits.
    practice: practiceScope,
    messages: practiceScope,
    callflow: {
      reload: () => {
        queryClient.invalidateQueries(['practiceLlmConfig', practiceId]);
        setCallflowReloadNonce((n) => n + 1);
      },
      // This one effect hydrates far more than the Anrufablauf drafts we track:
      // it also repopulates the whole KI-Konfiguration tab (provider, models,
      // Azure connections, every API key) and the PVS timeout. Those have no
      // draft tracking, so "the user is currently looking at that tab" is the
      // cheap, sound proxy — without it, typing an API key while a colleague
      // flips any switch loses the key with no prompt.
      isDirty: () => callflowTabDirty || activeTab === 'ai' || activeTab === 'pvs',
    },
    // All of these use the silent variants: a colleague's edit must land without a
    // spinner, without the table unmounting, and without moving the reader's
    // scroll position.
    therapy_types: {
      reload: async () => {
        await Promise.all([refetchTherapyTypes(practiceId), refetchTherapyCategories(practiceId), refetchTherapists(practiceId)]);
        await Promise.all([
          loadPriorityMatrix(practiceId, { silent: true }),
          loadLocationMatrix(practiceId, { silent: true }),
        ]);
      },
    },
    team_matrix: {
      reload: async () => {
        await refetchTherapists(practiceId);
        await loadPriorityMatrix(practiceId, { silent: true });
        if (therapistLocationMatrix) await loadTherapistLocationMatrix(practiceId, { silent: true });
      },
    },
    locations: {
      reload: async () => {
        await loadLocationMatrix(practiceId, { silent: true });
        if (therapistLocationMatrix) await loadTherapistLocationMatrix(practiceId, { silent: true });
      },
    },
    excluded_therapies: {
      // Only loaded while its rail tab is open; refetching otherwise would be a
      // pointless request against a list nobody is looking at.
      reload: async () => {
        if (activeTab === 'team' && therapyCategoryTab === EXCLUDED_THERAPIES_TAB) await refetchExcluded();
      },
    },
    closures: {
      reload: async () => { if (closuresLoadedFor === practiceId) await loadClosures(practiceId, { silent: true }); },
      isDirty: () => receptionDirty,
    },
  });

  // Lazy-load the practice's excluded rows when their rail tab opens; `cancelled` staleness guard.
  // Compares therapyCategoryTab directly: `isExcludedView` is declared further down.
  useEffect(() => {
    if (activeTab !== 'team' || therapyCategoryTab !== EXCLUDED_THERAPIES_TAB || !practiceId) return;
    let cancelled = false;
    (async () => {
      setExcludedLoading(true);
      setExcludedError('');
      try {
        const rowsRes = await axios.get(`${API_BASE_URL}/excluded-therapies/practice/${practiceId}`);
        if (cancelled) return;
        setExcludedRows(rowsRes.data || []);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading excluded therapies:', error);
        setExcludedError('Die nicht angebotenen Therapien konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setExcludedLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, activeTab, therapyCategoryTab]);

  // Open the add/edit modal (row=null => create).
  const handleOpenExcludedForm = (row = null) => {
    setExcludedAliasDraft('');
    setExcludedError('');
    if (row) {
      setExcludedEditingId(row.id);
      setExcludedForm({
        name: row.name || '',
        aliases: Array.isArray(row.aliases) ? [...row.aliases] : [],
        reject_reason: row.reject_reason || '',
        enabled: row.enabled !== false,
      });
    } else {
      setExcludedEditingId(null);
      setExcludedForm({ name: '', aliases: [], reject_reason: '', enabled: true });
    }
    setExcludedModalOpen(true);
  };

  const handleCancelExcludedForm = () => {
    setExcludedModalOpen(false);
    setExcludedEditingId(null);
    setExcludedAliasDraft('');
    setExcludedError('');
  };

  // Create or update an exclusion. source/prefilled_key are server-controlled — never sent.
  const handleSaveExcluded = async () => {
    const draftCommitted = excludedAliasDraft.trim();
    const mergedAliases = [...(excludedForm.aliases || []), ...(draftCommitted ? [draftCommitted] : [])];
    const name = excludedForm.name.trim();
    if (!name) {
      setExcludedError('Bitte einen Namen angeben.');
      return;
    }
    const payload = {
      name,
      aliases: mergedAliases.map(a => a.trim()).filter(Boolean),
      reject_reason: excludedForm.reject_reason.trim() || null,
      enabled: excludedForm.enabled !== false,
    };
    setExcludedSaving(true);
    setExcludedError('');
    try {
      if (excludedEditingId) {
        await axios.put(`${API_BASE_URL}/excluded-therapies/practice/${practiceId}/${excludedEditingId}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/excluded-therapies/practice/${practiceId}`, payload);
      }
      await refetchExcluded();
      handleCancelExcludedForm();
    } catch (error) {
      console.error('Error saving excluded therapy:', error);
      setExcludedError(error.response?.data?.detail || 'Die Therapie konnte nicht gespeichert werden.');
    } finally {
      setExcludedSaving(false);
    }
  };

  // One inline change of a row (switch, name, message). The PUT replaces all four
  // fields; the busy flag stops a second change going out with the stale row.
  const handleUpdateExcluded = async (row, changes) => {
    setExcludedBusy(prev => ({ ...prev, [row.id]: true }));
    setExcludedError('');
    try {
      await axios.put(`${API_BASE_URL}/excluded-therapies/practice/${practiceId}/${row.id}`, {
        name: row.name,
        aliases: row.aliases || [],
        reject_reason: row.reject_reason ?? null,
        enabled: row.enabled !== false,
        ...changes,
      });
      await refetchExcluded();
    } catch (error) {
      console.error('Error updating excluded therapy:', error);
      setExcludedError('Die Änderung konnte nicht gespeichert werden.');
    } finally {
      setExcludedBusy(prev => ({ ...prev, [row.id]: false }));
    }
  };

  // Confirmed by the table's two-click delete (requestDelete).
  const handleDeleteExcluded = async (row) => {
    setExcludedBusy(prev => ({ ...prev, [row.id]: true }));
    setExcludedError('');
    try {
      await axios.delete(`${API_BASE_URL}/excluded-therapies/practice/${practiceId}/${row.id}`);
      await refetchExcluded();
    } catch (error) {
      console.error('Error deleting excluded therapy:', error);
      setExcludedError('Die Therapie konnte nicht entfernt werden.');
    } finally {
      setExcludedBusy(prev => ({ ...prev, [row.id]: false }));
    }
  };

  // --- Therapy table helpers ---
  const insuranceSuffix = (tab) => ({ private: '_private', public: '_public', selbstzahler: '_selbstzahler', bg: '_bg', entlassmanagement: '_entlassmanagement' }[tab] || '_public');
  const insuranceLabel = (tab) => ({ private: 'Privat', public: 'Gesetzlich', selbstzahler: 'Selbstzahler', bg: 'BG', entlassmanagement: 'Entlassmgmt' }[tab] || tab);
  // Category labels come from the server option list when it is available, from the
  // local mirror otherwise, and fall back to the raw key so a future sixth option
  // never renders blank on an old SPA build.
  const categoryLabel = (key) => (
    categoryOptions.find(o => o.value === key)?.display
    || THERAPY_CATEGORY_OPTIONS.find(o => o.value === key)?.display
    || key
  );
  // The practice's OWN order is the order: the rail can be rearranged by dragging a
  // tab and the backend stores that arrangement (the categories GET returns it as
  // stored, no longer canonically sorted), so an optimistic list is taken exactly as
  // the caller built it and only de-duplicated. Callers append a new key at the end,
  // which is where the server appends it too.
  const orderCategories = (keys) => Array.from(new Set(keys));

  // Render a click-to-edit numeric cell (reused for Dauer, Erstzuschlag and Sitzungen).
  // `dashed` is for a column the row does not have at all (an Aufnahmegespräch has no
  // Erstzuschlag and no session count): it wins over `editable` and never opens an editor.
  const renderNumericCell = (cellKey, currentValue, defaultVal, fieldPrefix, therapyType, suffix, editable = true, dashed = false) => (
    <td>
      {dashed ? (
        <span className="tt-cell-na" title={INTAKE_CELL_NA_TITLE}>-</span>
      ) : editingCell === cellKey ? (
        <input
          autoFocus
          type="text"
          inputMode="numeric"
          className="tt-cell-input tt-cell-input-num"
          value={cellEditValue}
          onFocus={e => e.target.select()}
          onChange={e => setCellEditValue(e.target.value.replace(/\D/g, ''))}
          onBlur={() => {
            const val = cellEditValue === '' ? defaultVal : parseInt(cellEditValue);
            if (val !== currentValue) {
              handleSaveCellEdit(therapyType, { [`${fieldPrefix}${suffix}`]: val });
            }
            setEditingCell(null);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditingCell(null); }
          }}
        />
      ) : (
        <span
          className="tt-cell-text"
          onClick={editable ? () => { setEditingCell(cellKey); setCellEditValue(String(currentValue)); } : undefined}
          title={editable ? "Klicken zum Bearbeiten" : undefined}
        >
          {currentValue}
        </span>
      )}
    </td>
  );

  // Build the full PUT payload from a therapy object, optionally overriding specific fields
  const buildTherapyPayload = (therapyType, overrides = {}) => ({
    name: therapyType.name,
    // Load bearing: without this every inline cell edit would PUT without a
    // category, and a row moved out of the default would drift back on the next edit
    category: therapyType.category ?? DEFAULT_THERAPY_CATEGORY,
    // `??`, not `||`: the API accepts 0 minutes for an Ergänzendes Heilmittel, and an inline
    // edit of any other cell must not turn them into 30
    duration_private: therapyType.duration_private ?? 30,
    sessions_private: therapyType.sessions_private || 1,
    frequency_private: therapyType.frequency_private || 'Wöchentlich',
    duration_public: therapyType.duration_public ?? 30,
    sessions_public: therapyType.sessions_public || 1,
    frequency_public: therapyType.frequency_public || 'Wöchentlich',
    duration_selbstzahler: therapyType.duration_selbstzahler ?? 30,
    sessions_selbstzahler: therapyType.sessions_selbstzahler || 1,
    frequency_selbstzahler: therapyType.frequency_selbstzahler || 'Wöchentlich',
    duration_bg: therapyType.duration_bg ?? 30,
    sessions_bg: therapyType.sessions_bg || 1,
    frequency_bg: therapyType.frequency_bg || 'Wöchentlich',
    duration_entlassmanagement: therapyType.duration_entlassmanagement ?? 30,
    sessions_entlassmanagement: therapyType.sessions_entlassmanagement || 1,
    frequency_entlassmanagement: therapyType.frequency_entlassmanagement || 'Wöchentlich',
    first_session_extra_private: therapyType.first_session_extra_private || 0,
    first_session_extra_public: therapyType.first_session_extra_public || 0,
    first_session_extra_selbstzahler: therapyType.first_session_extra_selbstzahler || 0,
    first_session_extra_bg: therapyType.first_session_extra_bg || 0,
    first_session_extra_entlassmanagement: therapyType.first_session_extra_entlassmanagement || 0,
    description: therapyType.description || '',
    aliases: therapyType.aliases || [],
    symptoms: therapyType.symptoms || [],
    pvs_name: therapyType.pvs_name || '',
    pvs_name_per_insurance: therapyType.pvs_name_per_insurance ?? false,
    pvs_name_private: therapyType.pvs_name_private || '',
    pvs_name_public: therapyType.pvs_name_public || '',
    pvs_name_selbstzahler: therapyType.pvs_name_selbstzahler || '',
    pvs_name_bg: therapyType.pvs_name_bg || '',
    pvs_name_entlassmanagement: therapyType.pvs_name_entlassmanagement || '',
    recommend_sessions_enabled: true,
    force_sessions_enabled: therapyType.force_sessions_enabled ?? false,
    force_frequency_enabled: therapyType.force_frequency_enabled ?? false,
    double_appointments_enabled: therapyType.double_appointments_enabled ?? false,
    // Every inline save states the row's current Aufnahmegespräch pair. The server
    // reads an unchanged link as "keep" (on a category move it then keeps or clears it
    // by the Aufnahmegespräch's group list); an override can still unlink with null.
    is_intake_appointment: therapyType.is_intake_appointment ?? false,
    intake_therapy_type_id: therapyType.intake_therapy_type_id ?? null,
    is_blanko: therapyType.is_blanko ?? false,
    supplementary_mode: therapyType.supplementary_mode ?? null,
    accepted_insurance_types: therapyType.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE,
    ...overrides,
  });

  // Save a single field for a therapy, then refresh the list
  const handleSaveCellEdit = async (therapyType, fieldOverrides) => {
    try {
      const payload = buildTherapyPayload(therapyType, fieldOverrides);
      await axios.put(`${API_BASE_URL}/therapy-types/${therapyType.id}`, payload);
      const ttRes = await axios.get(`${API_BASE_URL}/therapy-types/practice/${practiceId}`);
      setTherapyTypes(ttRes.data);
      if (fieldOverrides.name) {
        await refetchTherapists(practiceId);
        await loadPriorityMatrix(practiceId);
        await loadLocationMatrix(practiceId);
      }
      return true;
    } catch (error) {
      console.error('Error saving cell edit:', error);
      // The 400/409 rules around the Aufnahmegespräch (a link into a group it is not
      // shown in, a second Aufnahmegespräch) arrive as a German string here and are the
      // only explanation the user gets.
      alert(formatApiError(error?.response?.data?.detail) || 'Fehler beim Speichern');
      return false;
    }
  };

  // Save accepted_insurance_types for a single therapy
  const handleSaveAccepted = async (therapyType, newAccepted) => {
    await handleSaveCellEdit(therapyType, { accepted_insurance_types: newAccepted });
  };

  // An Ergänzendes Heilmittel saves straight from its table row (an option on click, the
  // minutes on blur or Enter). The row shows the change at once and takes it back when the
  // save fails (handleSaveCellEdit has already said why).
  const saveSupplementaryRow = async (therapyType, fields) => {
    const before = therapyTypes;
    setTherapyTypes(list => list.map(t => (t.id === therapyType.id ? { ...t, ...fields } : t)));
    if (!(await handleSaveCellEdit(therapyType, fields))) setTherapyTypes(before);
  };

  // The row's minutes: at least 1 (an empty or 0 field keeps the stored ones). One value for
  // every insurance is written to all five; minutes that differ per insurance (set under
  // "Je Versicherungsart") change only for the insurance tab on screen.
  const commitSupplementaryMinutes = (therapyType, draft, suffix) => {
    const n = parseInt(draft, 10);
    if (!(n >= 1)) return;
    const minutes = Math.min(180, n);
    const fields = sameMinutesEverywhere(therapyType)
      ? Object.fromEntries(INSURANCE_KEYS.map(k => [`duration_${k}`, minutes]))
      : { [`duration${suffix}`]: minutes };
    if (Object.keys(fields).every(f => therapyType[f] === minutes)) return;
    saveSupplementaryRow(therapyType, fields);
  };

  // Insurance type settings modal handlers
  const handleOpenInsuranceSettings = (tab) => {
    const settings = insuranceTypeSettings[tab] || { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false };
    setInsuranceSettingsModalTab(tab);
    setInsuranceSettingsForm({
      verordnungsfrist_days: settings.verordnungsfrist_days != null ? String(settings.verordnungsfrist_days) : '',
      max_terminabstand_days: settings.max_terminabstand_days != null ? String(settings.max_terminabstand_days) : '',
      new_patient_handling: settings.new_patient_handling || 'normal',
      new_patient_send_form_link: settings.new_patient_send_form_link === true,
      existing_patient_handling: settings.existing_patient_handling || 'normal',
      existing_patient_send_form_link: settings.existing_patient_send_form_link === true,
      ...pickFormLinkSources(settings),
      new_patient_form_url: insuranceTypeSettings.new_patient_form_url || '',
    });
    setInsuranceSettingsModalOpen(true);
  };

  const handleSaveInsuranceSettings = async () => {
    const tab = insuranceSettingsModalTab;
    const updated = {
      ...insuranceTypeSettings,
      [tab]: {
        verordnungsfrist_days: insuranceSettingsForm.verordnungsfrist_days !== '' ? Number(insuranceSettingsForm.verordnungsfrist_days) : null,
        max_terminabstand_days: insuranceSettingsForm.max_terminabstand_days !== '' ? Number(insuranceSettingsForm.max_terminabstand_days) : null,
        new_patient_handling: insuranceSettingsForm.new_patient_handling || 'normal',
        // This save rebuilds the tab object from scratch — the riders MUST be
        // carried, or every modal save silently deletes them.
        new_patient_send_form_link: insuranceSettingsForm.new_patient_send_form_link === true,
        existing_patient_handling: insuranceSettingsForm.existing_patient_handling || 'normal',
        existing_patient_send_form_link: insuranceSettingsForm.existing_patient_send_form_link === true,
        ...pickFormLinkSources(insuranceSettingsForm),
      },
      // Store form URL at root level (shared across all insurance types)
      new_patient_form_url: insuranceSettingsForm.new_patient_form_url || null,
    };
    setInsuranceTypeSettings(updated);
    setInsuranceSettingsModalOpen(false);

    // Persist via dedicated practice endpoint
    if (practiceId) {
      try {
        await axios.put(`${API_BASE_URL}/practices/${practiceId}/insurance-type-settings`, updated);
      } catch (error) {
        console.error('Error saving insurance type settings:', error);
      }
    }
  };

  // Serialised write queue for the Verordnungstypen dialog. Both the shared blob and
  // the per-group map are FULL replaces, so two overlapping PUTs could land in the
  // wrong order and the older body would win. Chaining them keeps the last edit last,
  // and the tail is what the "Speichern" button awaits before it closes.
  const queueMatrixWrite = (run) => {
    const seq = ++matrixWriteSeqRef.current;
    setMatrixWritesPending(n => n + 1);
    const next = matrixWriteChainRef.current
      .then(() => run(seq), () => run(seq))       // a failed write must not stall the queue
      .catch(error => { console.error('Error saving Verordnungstyp settings:', error); })
      .then(() => setMatrixWritesPending(n => n - 1));
    matrixWriteChainRef.current = next;
    return next;
  };

  // Inline patch for the Defaults-Matrix. `tab` is the insurance key,
  // `patch` is a partial object of the per-tab settings. The new_patient_form_url
  // lives at root level (one shared URL across all insurance types).
  const handlePatchInsuranceSetting = (tab, patch) => {
    const merged = { ...insuranceTypeSettings };
    if (tab === '_root') {
      Object.assign(merged, patch);
    } else {
      merged[tab] = { ...(merged[tab] || { verordnungsfrist_days: null, max_terminabstand_days: null, new_patient_handling: 'normal', new_patient_send_form_link: false, existing_patient_send_form_link: false }), ...patch };
    }
    setInsuranceTypeSettings(merged);
    if (practiceId) {
      queueMatrixWrite(() => axios.put(`${API_BASE_URL}/practices/${practiceId}/insurance-type-settings`, merged));
    }
  };

  // --- Per-Heilmittelgruppe Verordnungstyp settings -------------------------
  // The switch and the group sets share ONE object and ONE endpoint, and that
  // endpoint is a full replace: a body without `by_category` DELETES every stored
  // group set. So every write here goes through this one funnel, which always
  // carries the current sets along, and only ever changes what it was asked to.
  const putCategorySettings = (body) => {
    setTherapyCategorySettings(body);                       // optimistic: the inputs are controlled
    if (!practiceId) return;
    queueMatrixWrite(async (seq) => {
      const res = await axios.put(`${API_BASE_URL}/practices/${practiceId}/therapy-category-settings`, body);
      // The server validates and prunes, so what it stored is the truth. Adopt it
      // only while this is still the newest write: a later keystroke has already
      // produced newer local state, and its own response will land in a moment.
      if (seq === matrixWriteSeqRef.current) setTherapyCategorySettings(res.data || {});
    });
  };

  // The master switch. Never destructive in either direction: `by_category` is sent
  // back exactly as it is, so turning per-group editing off keeps every customised
  // group intact and turning it on again finds them unchanged, never re-seeded.
  // Flipping the switch changes the SCOPE and nothing else, so the body carries only
  // that. The endpoint reads an absent by_category as "leave the groups alone", which
  // is what makes the flip structurally unable to lose a practice's customised rules:
  // it no longer matters what this component holds in memory. Re-sending the map from
  // local state was the bug - a flip before the practice finished loading sent an empty
  // map into an otherwise full-replace write and deleted every customised group.
  const handleToggleCategoryScope = (forAllGroups) => {
    putCategorySettings({ scope: forAllGroups ? VT_SCOPE_ALL : VT_SCOPE_PER_CATEGORY });
  };

  // One cell edit while the switch is off. A group with no entry yet is displaying
  // the shared values live, so the FIRST edit MATERIALISES it: all five insurance
  // blocks are copied down from the shared set, the edit is applied on top, and from
  // then on the group is independent and later edits to the shared set do not reach
  // it. A group that already has an entry is only patched.
  const handlePatchCategorySetting = (group, insurance, patch) => {
    const stored = therapyCategorySettings.by_category || {};
    const base = stored[group] || Object.fromEntries(
      INSURANCE_KEYS.map(k => [k, pickInsuranceFields(insuranceTypeSettings[k])])
    );
    putCategorySettings({
      scope: VT_SCOPE_PER_CATEGORY,
      by_category: {
        ...stored,
        [group]: { ...base, [insurance]: { ...pickInsuranceFields(base[insurance]), ...patch } },
      },
    });
  };

  // Give ONE group back to the shared rule. Materialise-on-first-edit is otherwise a
  // one-way door: the only other way out would be deleting the group. Dropping its key
  // is the whole operation, because an absent group is exactly what "follows the shared
  // set" means everywhere - here, and on the call path. The response comes back without
  // the key, which un-marks the tab and puts the grid back on the shared values, live.
  const handleResetCategorySetting = (group) => {
    const rest = Object.fromEntries(
      Object.entries(therapyCategorySettings.by_category || {}).filter(([k]) => k !== group)
    );
    // scope is carried unchanged: resetting a group must never flip the master switch.
    // by_category is ALWAYS explicit, the empty object included: an absent one now
    // means "unchanged", so omitting it while resetting the last customised group
    // would turn that reset into a no-op.
    putCategorySettings({ scope: VT_SCOPE_PER_CATEGORY, by_category: rest });
  };

  // "Speichern" closes the dialog. Everything is already written as it is changed;
  // waiting on the tail of the queue is what makes the word truthful.
  const handleCloseDefaultsMatrix = async () => {
    if (matrixFormUrlDraft !== null && matrixFormUrlDraft !== (insuranceTypeSettings.new_patient_form_url || '')) {
      handlePatchInsuranceSetting('_root', { new_patient_form_url: matrixFormUrlDraft });
    }
    // Always: a draft left behind would show (and save) in the next practice's dialog.
    setMatrixFormUrlDraft(null);
    await matrixWriteChainRef.current;
    setDefaultsMatrixOpen(false);
  };

  // Absent scope means "für alle", the state of every practice that never touched it.
  const categoryScopeIsPerCategory = therapyCategorySettings.scope === VT_SCOPE_PER_CATEGORY;
  // The switch appears once a second Heilmittelgruppe exists and STAYS visible for a
  // practice that used it and later deleted back down to one, so one shared rule set
  // is always reachable again. A practice that never had two groups never sees it.
  const showCategoryScopeSwitch = categorySettingsApiAvailable
    && (therapyCategories.length >= 2 || categoryScopeIsPerCategory);
  const perCategoryMode = showCategoryScopeSwitch && categoryScopeIsPerCategory;
  // Clamped to the live rail: the stored tab can name a group that was meanwhile
  // renamed, merged or deleted.
  const matrixCategory = therapyCategories.includes(matrixCategoryTab)
    ? matrixCategoryTab
    : (therapyCategories[0] || DEFAULT_THERAPY_CATEGORY);
  // What the grid shows and edits. A group without an entry of its own displays the
  // shared values LIVE — it is still following the general rule, not holding a copy.
  const matrixSettings = perCategoryMode
    ? (therapyCategorySettings.by_category?.[matrixCategory] || insuranceTypeSettings)
    : insuranceTypeSettings;
  // ONE definition of "this group has its own rules", read by a tab's dot and by the
  // reset control sitting on that same tab, so the two can never disagree about what
  // is customised. Asked per key, because both now render on every tab of the rail.
  const isCategoryCustomised = (key) => perCategoryMode && !!therapyCategorySettings.by_category?.[key];
  const matrixTitle = !showCategoryScopeSwitch
    ? 'Einstellungen der Verordnungstypen'
    : (perCategoryMode
      ? `Einstellungen der Verordnungstypen für „${categoryLabel(matrixCategory)}“`
      : 'Einstellungen der Verordnungstypen für alle Heilmittelgruppen');

  // --- Aufnahmegespräch (intake appointment) ---
  // ONE row per practice, shown in every group listed in its `intake_categories`; its
  // own `category` column means nothing. Inside a group it links that group's therapies.
  const intakeRow = therapyTypes.find(tt => tt.is_intake_appointment) || null;
  const intakeListsGroup = (key) => !!intakeRow && (intakeRow.intake_categories || []).includes(key);
  // A group's OWN rows: never the Aufnahmegespräch, whatever group lists it.
  const groupRows = (key) => therapyTypes.filter(
    tt => !tt.is_intake_appointment && (tt.category ?? DEFAULT_THERAPY_CATEGORY) === key
  );

  // --- Blankorezept ---
  // At most ONE per Heilmittelgruppe (the server answers a second with 409). It is an
  // ordinary row of its group otherwise, so it lives in groupRows.
  const tabBlankoRow = groupRows(therapyCategoryTab).find(tt => tt.is_blanko) || null;
  // How many "Öffnen" hints the "+" popover can grow by in a group: one per preset the
  // group already has (see measureAddPopover).
  const addPopoverHints = (key) => (intakeListsGroup(key) ? 1 : 0) + (groupRows(key).some(tt => tt.is_blanko) ? 1 : 0);
  // An older backend drops is_blanko silently, so the preset would create a plain row: offer it
  // only once the list carries the flag (the same idea as categoriesApiAvailable).
  const blankoApiAvailable = therapyTypes.length === 0 || 'is_blanko' in therapyTypes[0];
  // Same for the Ergänzendes Heilmittel: an older backend would not keep the mark.
  const supplementaryApiAvailable = therapyTypes.length === 0 || 'supplementary_mode' in therapyTypes[0];
  // One "+" popover preset. `row` is the one this group already shows: a click swaps the chip
  // for "Öffnen" with the reason under it (each preset keeps its own swap state, `hereKey`).
  // Without one, `pick` creates or opens it.
  const renderPresetChip = (hereKey, row, icon, label, pick) => (addTherapyPopover?.[hereKey] && row ? (
    <>
      {/* autoFocus: the clicked preset chip unmounts, which would drop focus to body */}
      <button type="button" className="tt-cat-chip tt-cat-chip--block" autoFocus onClick={() => handleOpenTherapyTypeModal(row)}>
        <span className="tt-add-popover-preset-icon">{icon}</span>
        Öffnen
      </button>
      <p className="tt-intake-hint">
        In dieser Gruppe gibt es das {label} bereits („{row.name}“). Sie können es umbenennen.
      </p>
    </>
  ) : (
    <button
      type="button"
      className="tt-cat-chip tt-cat-chip--block"
      onClick={() => (row ? setAddTherapyPopover(prev => (prev ? { ...prev, [hereKey]: true } : prev)) : pick())}
    >
      <span className="tt-add-popover-preset-icon">{icon}</span>
      {label}
    </button>
  ));

  // The active category's own rows — the header "Akzeptiert" toggle and its bulk PUT
  // scope to this list, which never holds the Aufnahmegespräch (its insurance toggles
  // are shared by every group it is shown in). A row without a category predates the
  // column and belongs to the default one.
  const visibleTherapyTypes = groupRows(therapyCategoryTab);
  // A group's plain rows: neither its Blankorezept nor an Ergänzendes Heilmittel (the
  // Aufnahmegespräch is never in groupRows).
  const isPlainRow = (tt) => !tt.is_blanko && !tt.supplementary_mode;
  // What the header "Akzeptiert" toggle flips and reads: the server's bulk route skips a
  // Blankorezept (its insurance set is part of the preset) and an Ergänzendes Heilmittel
  // (it has no acceptance), so this list does too.
  const bulkAcceptRows = visibleTherapyTypes.filter(isPlainRow);
  // What the table renders: each Heilmittelgruppe separates its regular Behandlungen
  // (Aufnahmegespräch + Blankorezept + plain rows) from its Ergänzende Heilmittel via
  // the left-hand sub-view switcher whenever supplementary rows exist in this group.
  const mainTherapyRows = [
    ...(intakeListsGroup(therapyCategoryTab) ? [intakeRow] : []),
    ...visibleTherapyTypes.filter(tt => tt.is_blanko),
    ...bulkAcceptRows,
  ];
  const supplementaryRows = visibleTherapyTypes.filter(tt => tt.supplementary_mode);
  const isSupplementaryView = therapySubView === 'supplementary' && supplementaryRows.length > 0;
  const tableTherapyTypes = isSupplementaryView ? supplementaryRows : mainTherapyRows;
  const isExcludedView = therapyCategoryTab === EXCLUDED_THERAPIES_TAB;

  // The dialog reads its group from the FORM: the Kategorie dropdown can move a normal
  // row before saving, and the Aufnahmegespräch's form carries the group it was opened in.
  const formCategory = therapyTypeForm.category ?? DEFAULT_THERAPY_CATEGORY;
  const isIntakeForm = therapyTypeForm.is_intake_appointment === true;
  const isBlankoForm = therapyTypeForm.is_blanko === true;
  const isSupplementaryForm = !!therapyTypeForm.supplementary_mode;
  // The minutes the dialog's preview of a moved appointment shows
  const suppDraftMinutes = parseInt(suppDraft.minutes, 10) || 0;
  // The therapies the Aufnahmegespräch can be put in front of, in the dialog's group.
  // No is_active filter: a deactivated therapy that is still linked has to be visible
  // (greyed) so the user can see it and untick it.
  // Never a Blankorezept: the two presets serve the same purpose and never combine.
  // Never an Ergänzendes Heilmittel: booking never reaches it (the server refuses it too).
  const linkableRowsInFormCategory = groupRows(formCategory).filter(isPlainRow);
  // Rows per category, derived rather than mirrored from the server: the list GET is
  // unfiltered and the server counts active and inactive rows alike, so this is the
  // same number computed locally, and it cannot go stale after a create, duplicate,
  // delete or import (all of which refresh therapyTypes but no category endpoint).
  // The Aufnahmegespräch belongs to no group's count, as on the server.
  const categoryCounts = therapyTypes.reduce((acc, tt) => {
    if (tt.is_intake_appointment) return acc;
    const key = tt.category ?? DEFAULT_THERAPY_CATEGORY;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // Header "select all": toggle the current insurance column for every therapy in
  // the ACTIVE category.
  // Optimistic + single bulk request: flip every visible row in local state immediately
  // so the UI feels instant, then persist in ONE call to the bulk endpoint (one JSONB
  // UPDATE server-side, replacing the old N-per-row PUTs). A re-entry guard kills the
  // double-click race; on failure we roll the local state back.
  const handleToggleAllAccepted = async (checked) => {
    // ignore clicks while in flight, and on a tab that only shows presets
    // (the Aufnahmegespräch, the Blankorezept), which the bulk route never touches
    if (togglingAllAccepted || bulkAcceptRows.length === 0) return;
    const prevTherapyTypes = therapyTypes;                 // snapshot for rollback
    const visibleIds = new Set(bulkAcceptRows.map(tt => tt.id));
    setTherapyTypes(prevTherapyTypes.map(tt => (visibleIds.has(tt.id) ? {
      ...tt,
      accepted_insurance_types: {
        ...(tt.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE),
        [therapyInsuranceTab]: checked,
      },
    } : tt)));
    setTogglingAllAccepted(true);
    try {
      await axios.put(`${API_BASE_URL}/therapy-types/practice/${practiceId}/accepted-insurance`, {
        insurance_type: therapyInsuranceTab,
        enabled: checked,
        // Only scope the flip when the backend understands categories. An older one
        // ignores the field and would flip every row of the practice while the UI
        // optimistically flipped just the visible ones.
        ...(categoriesApiAvailable ? { category: therapyCategoryTab } : {}),
      });
    } catch (error) {
      console.error('Error toggling all accepted:', error);
      setTherapyTypes(prevTherapyTypes);                   // rollback the optimistic update
    } finally {
      setTogglingAllAccepted(false);
    }
  };

  // --- Category (umbrella) mutations --------------------------------------
  // Same shape as handleToggleAllAccepted: one optimistic state swap, one request,
  // one snapshot rollback. The active tab follows the rows the user just acted on
  // (add -> the new key, rename/merge -> new_key, delete -> move_to, dropNew -> the
  // new key) and is therefore part of the SAME optimistic update, rolled back with
  // it. Sole exception: a drop onto an existing tab keeps the current tab.

  // One error sink for the category mutations. The 409 bodies carry a structured
  // detail ({error, key, count, message}), so never hand the raw detail to alert()
  // or it renders as [object Object]. While the dialog is open the message belongs
  // inside it (the inline coral line); only errors raised outside it fall back to
  // the page's alert idiom.
  const reportCategoryError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    const message =
      (typeof detail === 'string' && detail) ||
      (detail && typeof detail === 'object' && detail.message) ||
      fallback;
    if (categoryDialog) {
      setCategoryDialog(prev => (prev ? { ...prev, error: message } : prev));
    } else {
      alert(message);
    }
  };

  const handleAddCategory = async (key) => {
    if (categoryBusy || !practiceId || !key) return;
    const prevCategories = therapyCategories;              // snapshot for rollback
    const prevTab = therapyCategoryTab;
    setTherapyCategories(orderCategories([...prevCategories, key]));
    setTherapyCategoryTab(key);
    setEditingCell(null);
    setEditingFrequenzId(null);
    setCategoryBusy(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/therapy-types/practice/${practiceId}/categories`, { key });
      applyCategoriesPayload(res.data);
      setCategoryDialog(null);
    } catch (error) {
      console.error('Error adding therapy category:', error);
      setTherapyCategories(prevCategories);                // rollback the optimistic update
      setTherapyCategoryTab(prevTab);
      reportCategoryError(error, 'Die Kategorie konnte nicht angelegt werden.');
    } finally {
      setCategoryBusy(false);
    }
  };

  // Rename = re-key. With merge=false the backend answers 409 when the target
  // already holds rows; that is the contract, so we keep the dialog open (enriched
  // with the merge fields) and let the user confirm instead of alerting.
  const handleRekeyCategory = async (fromKey, newKey, merge = false) => {
    if (categoryBusy || !practiceId || !fromKey || !newKey) return;
    if (fromKey === newKey) { setCategoryDialog(null); return; }
    const prevCategories = therapyCategories;              // snapshot for rollback
    const prevTherapyTypes = therapyTypes;
    const prevTab = therapyCategoryTab;
    // Keep the rail's arrangement: a plain rename swaps the key in place, a merge
    // drops the source and leaves the target where it already sat — which is exactly
    // what the server does to the stored order, so nothing jumps when it answers.
    setTherapyCategories(orderCategories(
      prevCategories.includes(newKey)
        ? prevCategories.filter(k => k !== fromKey)
        : prevCategories.includes(fromKey)
          ? prevCategories.map(k => (k === fromKey ? newKey : k))
          : [...prevCategories, newKey]
    ));
    setTherapyTypes(prevTherapyTypes.map(tt => rekeyTherapyGroup(tt, fromKey, newKey)));
    setTherapyCategoryTab(newKey);
    setEditingCell(null);
    setEditingFrequenzId(null);
    setCategoryBusy(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/therapy-types/practice/${practiceId}/categories/${fromKey}`, {
        new_key: newKey,
        merge,
      });
      applyCategoriesPayload(res.data);
      setCategoryDialog(null);
      await refetchTherapyTypes(practiceId);
    } catch (error) {
      console.error('Error renaming therapy category:', error);
      setTherapyCategories(prevCategories);                // rollback the optimistic update
      setTherapyTypes(prevTherapyTypes);
      setTherapyCategoryTab(prevTab);
      if (error.response?.status === 409) {
        // Target exists: no alert. The dialog stays open and renders the merge
        // confirmation from these fields.
        // The dialog derives the moved-row count from categoryCounts itself, so the
        // only thing worth carrying over is WHICH key the rename collided with.
        setCategoryDialog(prev => (prev ? { ...prev, mergeTarget: newKey, error: null } : prev));
      } else {
        reportCategoryError(error, 'Die Kategorie konnte nicht geändert werden.');
      }
    } finally {
      setCategoryBusy(false);
    }
  };

  // moveTo may be a not-yet-created option; the backend creates it in the same
  // transaction, which is why the optimistic list adds it before the request.
  const handleDeleteCategory = async (key, moveTo = null) => {
    if (categoryBusy || !practiceId || !key) return;
    const prevCategories = therapyCategories;              // snapshot for rollback
    const prevTherapyTypes = therapyTypes;
    const prevTab = therapyCategoryTab;
    const remaining = prevCategories.filter(k => k !== key);
    const nextCategories = orderCategories(moveTo ? [...remaining, moveTo] : remaining);
    setTherapyCategories(nextCategories.length ? nextCategories : [DEFAULT_THERAPY_CATEGORY]);
    setTherapyTypes(prevTherapyTypes.map(tt => rekeyTherapyGroup(tt, key, moveTo || null)));
    setTherapyCategoryTab(moveTo || nextCategories[0] || DEFAULT_THERAPY_CATEGORY);
    setEditingCell(null);
    setEditingFrequenzId(null);
    setCategoryBusy(true);
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/therapy-types/practice/${practiceId}/categories/${key}`,
        moveTo ? { params: { move_to: moveTo } } : undefined
      );
      applyCategoriesPayload(res.data);
      setCategoryDialog(null);
      if (moveTo || res.data?.intake_deleted) await refetchTherapyTypes(practiceId);
      if (res.data?.intake_deleted) {
        // The Aufnahmegespräch went with its last group, so the matrices lose its column
        // (same follow-up as handleRemoveIntakeFromGroup).
        await refetchTherapists(practiceId);
        await loadPriorityMatrix(practiceId);
        await loadLocationMatrix(practiceId);
      }
    } catch (error) {
      console.error('Error deleting therapy category:', error);
      setTherapyCategories(prevCategories);                // rollback the optimistic update
      setTherapyTypes(prevTherapyTypes);
      setTherapyCategoryTab(prevTab);
      reportCategoryError(error, 'Die Kategorie konnte nicht gelöscht werden.');
    } finally {
      setCategoryBusy(false);
    }
  };

  // --- Tab reordering (drag a category along the rail) ---------------------
  // Where a dragged tab would land: the index it would take in the CURRENT list plus
  // the geometry of the insertion bar, measured against the rail because the rail is
  // the bar's positioned parent. Nothing in the rail moves while dragging — the bar
  // is absolutely positioned, so there is no reflow to chase the pointer. The rail
  // wraps, so only the tabs on the pointer's own line take part in the midpoint scan;
  // between two lines the whole rail is scanned.
  const categoryDropMarker = (clientX, clientY) => {
    const rail = catRailRef.current;
    if (!rail) return null;
    const rects = Array.from(rail.querySelectorAll('.tt-cat-tab')).map(el => el.getBoundingClientRect());
    if (!rects.length) return null;
    const railRect = rail.getBoundingClientRect();
    const line = rects.filter(r => clientY >= r.top && clientY <= r.bottom);
    const scan = line.length ? line : rects;
    let at = rects.indexOf(scan[scan.length - 1]) + 1;        // past the last tab of the line
    for (const r of scan) {
      if (clientX < r.left + r.width / 2) { at = rects.indexOf(r); break; }
    }
    // The 2px bar fills the 2px gap it points at: left of the tab it would push
    // right, or just past the last one.
    const edge = rects[at] || rects[at - 1];
    const x = at < rects.length ? edge.left - railRect.left - 2 : edge.right - railRect.left + 2;
    return { index: at, x, top: edge.top - railRect.top, height: edge.height };
  };

  // Persist the new tab order. Same optimistic + snapshot-rollback shape as the other
  // category mutations, with one difference: the ACTIVE TAB is never part of it. A
  // rearrangement moves tabs, it does not change which category is open.
  const handleReorderCategories = async (fromKey, toIndex) => {
    if (categoryBusy || !practiceId || !fromKey || toIndex == null) return;
    const prevCategories = therapyCategories;                // snapshot for rollback
    const from = prevCategories.indexOf(fromKey);
    if (from < 0) return;
    // toIndex counts slots in the ORIGINAL list, so a move to the right loses one
    // once the dragged tab is lifted out of it.
    const next = prevCategories.filter(k => k !== fromKey);
    next.splice(toIndex > from ? toIndex - 1 : toIndex, 0, fromKey);
    if (next.every((k, i) => k === prevCategories[i])) return;   // dropped where it already was
    setTherapyCategories(next);
    setCategoryBusy(true);
    try {
      // The body must be a permutation of the practice's categories, which is exactly
      // what moving one key inside the current list produces.
      const res = await axios.put(
        `${API_BASE_URL}/therapy-types/practice/${practiceId}/categories/order`,
        { keys: next }
      );
      applyCategoriesPayload(res.data);
    } catch (error) {
      console.error('Error reordering therapy categories:', error);
      setTherapyCategories(prevCategories);                  // rollback the optimistic update
      reportCategoryError(error, 'Die Reihenfolge konnte nicht gespeichert werden.');
    } finally {
      setCategoryBusy(false);
    }
  };

  // Drop-target wiring shared by every category tab and the "+" control: the four
  // handlers only differ in the guard and in what the drop does with the dragged id.
  // A dragged TAB passes straight through all of them (isCategoryDrag): the rail owns
  // that drag, and a reorder must never move a therapy.
  const categoryDropProps = (targetKey, onDropTherapyId, enabled = true) => ({
    onDragOver: e => { if (!enabled || isCategoryDrag(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDragEnter: e => { if (!enabled || isCategoryDrag(e)) return; e.preventDefault(); setDropTargetCategory(targetKey); },
    onDragLeave: e => {
      if (e.currentTarget.contains(e.relatedTarget)) return;   // ignore child enters
      setDropTargetCategory(prev => (prev === targetKey ? null : prev));
    },
    onDrop: e => {
      if (!enabled || isCategoryDrag(e)) return;
      e.preventDefault();
      const draggedId = draggedTherapyIdRef.current;
      draggedTherapyIdRef.current = null;
      setDraggingTherapyId(null);
      onDropTherapyId(draggedId);
    },
  });

  // Move ONE therapy (grip drag onto a tab, dropNew dialog). Goes through
  // handleSaveCellEdit so it reuses that one PUT plus its one reload; a key that is
  // not a tab yet is appended by the backend under its locked read, so this stays a
  // single request. Dropping onto an existing tab keeps the current tab (batch sorting).
  const handleMoveTherapyToCategory = async (therapyType, key) => {
    if (categoryBusy || !therapyType || !key) return;
    if ((therapyType.category ?? DEFAULT_THERAPY_CATEGORY) === key) { setCategoryDialog(null); return; }
    const prevCategories = therapyCategories;              // snapshot for rollback
    const prevTab = therapyCategoryTab;
    const isNewCategory = !prevCategories.includes(key);
    if (isNewCategory) {
      setTherapyCategories(orderCategories([...prevCategories, key]));
      setTherapyCategoryTab(key);
      setEditingCell(null);
      setEditingFrequenzId(null);
    }
    setCategoryBusy(true);
    try {
      // handleSaveCellEdit reports its own failure and resolves either way, so read
      // its result instead of relying on a throw: on failure the dropNew dialog must
      // stay open and the optimistically added tab must go back.
      // The payload re-sends the row's current link unchanged, so the server decides
      // whether it survives the move (kept when the Aufnahmegespräch is in the new group).
      const saved = await handleSaveCellEdit(therapyType, { category: key });
      if (!saved) {
        if (isNewCategory) {
          setTherapyCategories(prevCategories);            // rollback the optimistic update
          setTherapyCategoryTab(prevTab);
        }
        // handleSaveCellEdit has already alerted, so only mirror the failure onto the
        // dialog's inline line when one is open. Going through reportCategoryError
        // would raise a SECOND alert on the grip-drop path, which has no dialog.
        setCategoryDialog(prev => (prev
          ? { ...prev, error: 'Die Behandlungsart konnte nicht verschoben werden.' }
          : prev));
        return;
      }
      setCategoryDialog(null);
      await refetchTherapyCategories(practiceId);
    } finally {
      setCategoryBusy(false);
    }
  };

  // Keyboard shortcut: when hovering over a priority cell, press 0/1/2/3 or X to set priority directly
  useEffect(() => {
    if (!hoveredPriorityCell) return;
    const handler = (e) => {
      const ae = document.activeElement;
      const isFormField = ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName);
      if (isFormField) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const isNever = e.key === 'x' || e.key === 'X';
      if (!isNever && !['0', '1', '2', '3'].includes(e.key)) return;
      e.preventDefault();
      handlePriorityChange(hoveredPriorityCell.therapistId, hoveredPriorityCell.therapyTypeId, isNever ? PRIORITY_NEVER : parseInt(e.key));
      setOpenPriorityDropdown(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hoveredPriorityCell]);

  // Keep the rail honest. Without the categories endpoint (old backend) the effective
  // set is whatever the rows carry; and whichever way the set was produced, an active
  // tab that vanished (a colleague's rename or delete arriving via config sync) falls
  // back to the first one so the table can never be filtered to nothing.
  useEffect(() => {
    let effective = therapyCategories;
    if (!categoriesApiAvailable) {
      const derived = THERAPY_CATEGORY_OPTIONS
        .map(o => o.value)
        .filter(key => therapyTypes.some(tt => (tt.category ?? DEFAULT_THERAPY_CATEGORY) === key));
      effective = derived.length ? derived : [DEFAULT_THERAPY_CATEGORY];
      if (effective.length !== therapyCategories.length || effective.some((k, i) => k !== therapyCategories[i])) {
        setTherapyCategories(effective);
      }
    }
    if (effective.length > 0 && therapyCategoryTab !== EXCLUDED_THERAPIES_TAB && !effective.includes(therapyCategoryTab)) {
      setTherapyCategoryTab(effective[0]);
      setEditingCell(null);
      setEditingFrequenzId(null);
    }
  }, [categoriesApiAvailable, therapyTypes, therapyCategories, therapyCategoryTab]);

  // Compute header checkbox state for the current insurance tab, scoped to the active
  // category and to the rows the bulk toggle actually flips (no Blankorezept)
  const allAccepted = bulkAcceptRows.length > 0 && bulkAcceptRows.every(tt => tt.accepted_insurance_types?.[therapyInsuranceTab] !== false);
  const someAccepted = !allAccepted && bulkAcceptRows.some(tt => tt.accepted_insurance_types?.[therapyInsuranceTab] !== false);

  // Keyboard shortcuts for the Behandlungsarten section (active on the 'team' tab when no modal/input is open)
  useEffect(() => {
    if (activeTab !== 'team') return;
    const handler = (e) => {
      const ae = document.activeElement;
      const isFormField = ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName);
      if (isFormField) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // ? toggelt die Übersicht
      if (e.key === '?') {
        e.preventDefault();
        setShowShortcutHelp(prev => !prev);
        return;
      }
      // Esc schließt die Übersicht
      if (e.key === 'Escape' && showShortcutHelp) {
        e.preventDefault();
        setShowShortcutHelp(false);
        return;
      }
      // Wenn ein Modal offen ist, keine weiteren Shortcuts
      // categoryDialog joins the guard: without it, pressing n while the category
      // dialog is open stacks the therapy modal on top of it. Same for the "+"
      // popover, which is the step BEFORE that modal.
      if (therapyTypeModalOpen || insuranceSettingsModalOpen || showShortcutHelp || categoryDialog || addTherapyPopover) return;

      // n = neue Behandlungsart (in der aktuellen Kategorie). Same control as the
      // footer "+", so it opens the same popover, anchored to that button. Not in the
      // excluded folder: the popover would seed a Behandlungsart under '__excluded__'.
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        if (therapyCategoryTab !== EXCLUDED_THERAPIES_TAB) openAddPopover(addPlusWrapRef.current);
        return;
      }
      // 1-5 = Verordnungstyp Tab wechseln (NICHT wenn man auf einer Priorität-Zelle hovert — dort ist 1/2/3 für Priorität reserviert)
      const tabMap = { '1': 'private', '2': 'public', '3': 'selbstzahler', '4': 'bg', '5': 'entlassmanagement' };
      if (tabMap[e.key] && !hoveredPriorityCell) {
        e.preventDefault();
        setTherapyInsuranceTab(tabMap[e.key]);
        setEditingCell(null);
        setEditingFrequenzId(null);
        return;
      }
      // d = aktivieren/deaktivieren der gehoverten Zeile
      if (e.key === 'd' || e.key === 'D') {
        if (!hoveredTherapyId) return;
        const tt = therapyTypes.find(t => t.id === hoveredTherapyId);
        // An Ergänzendes Heilmittel has no acceptance (its row has no Akzeptiert switch)
        if (!tt || tt.supplementary_mode) return;
        e.preventDefault();
        const currentlyAccepted = tt.accepted_insurance_types?.[therapyInsuranceTab] !== false;
        const newAccepted = {
          ...(tt.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE),
          [therapyInsuranceTab]: !currentlyAccepted,
        };
        handleSaveAccepted(tt, newAccepted);
        return;
      }
      // e = Behandlung der gehoverten Zeile bearbeiten
      if (e.key === 'e' || e.key === 'E') {
        if (!hoveredTherapyId) return;
        const tt = therapyTypes.find(t => t.id === hoveredTherapyId);
        if (!tt) return;
        e.preventDefault();
        handleOpenTherapyTypeModal(tt);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab, therapyTypeModalOpen, insuranceSettingsModalOpen, showShortcutHelp, categoryDialog, addTherapyPopover, hoveredTherapyId, hoveredPriorityCell, therapyTypes, therapyInsuranceTab, therapyCategoryTab]);

  // The "+" popover owns its own dismissal: it is a portal, so no ancestor click
  // reaches it. Same listener shape as CustomDropdown, bubble phase included.
  useEffect(() => {
    if (!addTherapyPopover) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setAddTherapyPopover(null);
    };
    const close = (e) => {
      if (addPopoverRef.current?.contains(e.target)) return;
      if (addPopoverAnchorRef.current?.contains(e.target)) return;   // the trigger toggles itself
      setAddTherapyPopover(null);
    };
    // position:fixed does not follow the page, so the anchor rect is re-measured
    // while the popover is open.
    const reposition = () => {
      const el = addPopoverAnchorRef.current;
      if (!el) return;
      setAddTherapyPopover(prev => (prev ? { ...prev, ...measureAddPopover(el, addPopoverHints(therapyCategoryTab)) } : prev));
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!addTherapyPopover]);

  // Render-time guard: wait for PracticeContext to resolve before rendering.
  if (practiceLoading) {
    return <Layout><div className="loading">Laden...</div></Layout>;
  }

  // Options for the "Mehrere Therapeuten in einer Serie" cap. Bounded by how many
  // therapists the practice actually has (a cap above the roster is meaningless),
  // hard-ceilinged at 5, with "alle" last. 2 is always offered, so with 0 or 1
  // therapists the choice is "2" or "alle". Derived at render time — never stored —
  // so the ceiling grows by itself as therapists are added.
  const maxTherapistOptions = [];
  for (let n = 2; n <= Math.min(rosterCap, 5); n++) {
    maxTherapistOptions.push({ value: n, label: String(n) });
  }
  // A value outside these options: a cap above 5 set outside this UI, or one the
  // roster clamp effect has not lowered yet (roster still loading, or its load
  // failed). Keep it selectable. DISPLAY ONLY — never call the setter from render.
  if (maxTherapistsPerSeries < MAX_THERAPISTS_ALL
      && !maxTherapistOptions.some(o => o.value === maxTherapistsPerSeries)) {
    maxTherapistOptions.push({ value: maxTherapistsPerSeries, label: String(maxTherapistsPerSeries) });
  }
  maxTherapistOptions.push({ value: MAX_THERAPISTS_ALL, label: 'alle' });

  // The "Nicht angeboten" folder inside the category panel: the therapy table's chrome
  // over the practice's excluded rows. The tab is its title, so no heading here.
  const renderExcludedView = () => (
    <>
      {excludedError && !excludedModalOpen && (
        <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--color-tertiary)' }}>{excludedError}</p>
      )}
      {/* A reopen keeps the cached rows on screen while they refresh; only a first
          load shows the placeholder. With no rows, an error can only be the load's. */}
      {excludedLoading && excludedRows.length === 0 ? (
        <p className="empty-state">Wird geladen…</p>
      ) : excludedRows.length === 0 ? (
        !excludedError && <p className="empty-state">Noch keine Einträge.</p>
      ) : (
        <div className="tt-table-container">
          <table className="tt-table">
            <thead>
              <tr>
                <th className="tt-table-name-col">Name</th>
                <th className="tt-table-desc-col">
                  Nachricht
                  <InfoTooltip text="Wird dem Anrufer vorgelesen, danach folgt die Verabschiedung." />
                </th>
                <th className="tt-table-accepted-col">Aktiv</th>
                <th className="tt-table-actions-col">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {excludedRows.map(row => {
                const busy = !!excludedBusy[row.id];
                const enabled = row.enabled !== false;
                const nameKey = `ex_name_${row.id}`;
                const reasonKey = `ex_reason_${row.id}`;
                const deleteKey = `excluded-${row.id}`;
                const rowDimClass = !enabled ? 'tt-row-fully-dimmed' : '';

                return (
                  <tr key={row.id} className={rowDimClass}>
                    {/* Name — click-to-edit */}
                    <td className="tt-table-name-cell">
                      {editingCell === nameKey ? (
                        <input
                          autoFocus
                          type="text"
                          className="tt-cell-input"
                          value={cellEditValue}
                          onChange={e => setCellEditValue(e.target.value)}
                          onBlur={() => {
                            const trimmed = cellEditValue.trim();
                            if (trimmed && trimmed !== row.name) {
                              handleUpdateExcluded(row, { name: trimmed });
                            }
                            setEditingCell(null);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') e.target.blur();
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                        />
                      ) : (
                        <div className="tt-name-cell">
                          <div className="tt-name-cell-main">
                            <span
                              className="tt-cell-text tt-cell-text-name"
                              onClick={() => { setEditingCell(nameKey); setCellEditValue(row.name); }}
                              title="Klicken zum Bearbeiten"
                            >
                              {row.name}
                            </span>
                            {row.aliases?.length > 0 && (
                              <div className="tt-cell-aliases">{row.aliases.join(', ')}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Nachricht an den Anrufer (Beschreibung slot) — click-to-edit */}
                    <td className="tt-table-desc-cell">
                      {editingCell === reasonKey ? (
                        <input
                          autoFocus
                          type="text"
                          className="tt-cell-input"
                          value={cellEditValue}
                          onChange={e => setCellEditValue(e.target.value)}
                          onBlur={() => {
                            const trimmed = cellEditValue.trim();
                            if (trimmed !== (row.reject_reason || '')) {
                              handleUpdateExcluded(row, { reject_reason: trimmed || null });
                            }
                            setEditingCell(null);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') e.target.blur();
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                        />
                      ) : (
                        <span
                          className="tt-cell-description tt-cell-description--editable"
                          title={row.reject_reason || 'Klicken zum Bearbeiten'}
                          onClick={() => { setEditingCell(reasonKey); setCellEditValue(row.reject_reason || ''); }}
                        >
                          {/* Empty: the sentence the bot speaks instead (langgraph_nodes _therapy_excluded_response) */}
                          {row.reject_reason || <span style={{ color: '#bbb', fontStyle: 'italic' }}>Wir bieten {row.name} leider nicht an.</span>}
                        </span>
                      )}
                    </td>

                    {/* Aktiv — iOS switch */}
                    <td className="tt-table-accepted-cell">
                      <label className="ios-switch" aria-label="Aktiv">
                        <input
                          type="checkbox"
                          checked={enabled}
                          disabled={busy}
                          onChange={() => handleUpdateExcluded(row, { enabled: !enabled })}
                        />
                        <span className="ios-switch-slider" />
                      </label>
                    </td>

                    {/* Aktionen — edit full details in modal, delete with the table's two-click confirm */}
                    <td className="tt-table-actions-cell" style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <IconTooltipButton tooltip="Bearbeiten" className="icon-button tt-action-btn" disabled={busy} onClick={() => handleOpenExcludedForm(row)}>
                          {actionIcons.settings}
                        </IconTooltipButton>
                        {confirmingDeleteKey === deleteKey ? (
                          <button
                            type="button"
                            className="tt-action-btn--confirm-delete"
                            disabled={busy}
                            onClick={() => requestDelete(deleteKey, () => handleDeleteExcluded(row))}
                            title="Klick zum Bestätigen"
                          >
                            Wirklich?
                          </button>
                        ) : (
                          <IconTooltipButton
                            tooltip="Löschen"
                            className="icon-button tt-action-btn tt-action-btn-delete"
                            disabled={busy}
                            onClick={() => requestDelete(deleteKey, () => handleDeleteExcluded(row))}
                          >
                            {actionIcons.delete}
                          </IconTooltipButton>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <Layout>
      <div className="config-page">
        <h1 className="h1">Konfiguration</h1>
        <div className="config-tabs">
          <button className={`tab-button${activeTab === 'practice' ? ' active' : ''}`} onClick={() => setActiveTab('practice')}>Praxis-Informationen</button>
          <button className={`tab-button${activeTab === 'team' ? ' active' : ''}`} onClick={() => setActiveTab('team')}>Behandlungen &amp; Team</button>
          <button className={`tab-button${activeTab === 'callflow' ? ' active' : ''}`} onClick={() => setActiveTab('callflow')}>Anrufablauf-Konfiguration</button>
          {userIsAdmin && <button className={`tab-button${activeTab === 'sip' ? ' active' : ''}`} onClick={() => setActiveTab('sip')}>SIP-Konfiguration</button>}
          {userIsSuperAdmin && <button className={`tab-button${activeTab === 'ai' ? ' active' : ''}`} onClick={() => setActiveTab('ai')}>KI-Konfiguration</button>}
          {userIsSuperAdmin && <button className={`tab-button${activeTab === 'pvs' ? ' active' : ''}`} onClick={() => setActiveTab('pvs')}>PVS Integration</button>}
          {canManageMembers && <button className={`tab-button${activeTab === 'members' ? ' active' : ''}`} onClick={() => setActiveTab('members')}>Mitglieder</button>}
        </div>
        {activeTab === 'practice' && (
          <div className="tab-content" style={{ position: 'relative' }}>
            <LoadingShroud loading={formLoadedFor !== practiceId} />
            {/* Header: Auto-save status feedback without manual Speichern button */}
            <div style={{ position: 'absolute', top: -20, right: 0, zIndex: 3, pointerEvents: 'none' }}>
              <SaveStatusSlot saving={practiceSaving || faqSaving} status={saveStatus.practice} />
            </div>

            {/* Practice Tab Layout: Proposal B (Kontaktdaten left, Praxiszeiten right, FAQs bottom) */}
            <div className="practice-tab-container">
              {/* Never submits (no submit button, several text fields block Enter);
                  every field auto-saves. The preventDefault is only a safety net. */}
              <form id="practice-form" onSubmit={(e) => e.preventDefault()}>
                <div className="practice-cards-grid">
                  {/* Left Card: Practice Details (Kontaktdaten) */}
                  <div className="card practice-contact-card" style={{ margin: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <h3 className="h3" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        Kontaktdaten
                      </h3>
                    </div>

                    <div className="practice-contact-form">

                    {/* Row 1: Praxisname & Adresse */}
                    <div className="config-form-grid">
                      <div className="form-group">
                        <label className="form-label">Praxisname</label>
                        <div className="input-with-icon-wrapper">
                          <span className="input-icon">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect width="16" height="20" x="4" y="2" rx="2" ry="2"/>
                              <path d="M9 22v-4h6v4"/>
                              <path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M8 10h.01"/><path d="M16 10h.01"/><path d="M8 14h.01"/><path d="M16 14h.01"/>
                            </svg>
                          </span>
                          <input type="text" name="name" className="form-input" placeholder="z.B. Praxis Dr. Muster" value={practiceInfo.name || ''} onChange={handlePracticeInfoChange} onBlur={handlePracticeFieldBlur} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Adresse</label>
                        <div className="input-with-icon-wrapper">
                          <span className="input-icon">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
                              <circle cx="12" cy="10" r="3"/>
                            </svg>
                          </span>
                          <input type="text" name="address" className="form-input" placeholder="Musterstraße 1, 12345 Stadt" value={practiceInfo.address || ''} onChange={handlePracticeInfoChange} onBlur={handlePracticeFieldBlur} />
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Telefonnummer & Ortsvorwahl + Faxnummer */}
                    <div className="config-form-grid">
                      <div className="form-group">
                        <label className="form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          Telefonnummer
                          <InfoTooltip text="Festnetznummer der Praxis. Die Ortsvorwahl (z.B. 089) hilft DeskBot, eingehende Festnetzanrufe ohne Vorwahl Patienten im PVS zuzuordnen." />
                        </label>
                        <div className="phone-compound-box">
                          <div className="phone-prefix-addon" title="Ortsvorwahl für PVS-Zuordnung">
                            <span className="phone-addon-label">Vorwahl</span>
                            <input
                              type="text"
                              name="area_code"
                              className="phone-addon-input"
                              placeholder="089"
                              value={practiceInfo.area_code || ''}
                              onChange={handlePracticeInfoChange}
                              onBlur={handlePracticeFieldBlur}
                              maxLength={7}
                            />
                          </div>
                          <div className="phone-main-addon">
                            <span className="input-icon">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                              </svg>
                            </span>
                            <input
                              type="text"
                              name="phone_number"
                              className="phone-main-input"
                              placeholder="+49 123 456789"
                              value={practiceInfo.phone_number || ''}
                              onChange={handlePracticeInfoChange}
                              onBlur={handlePracticeFieldBlur}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Faxnummer</label>
                        <div className="input-with-icon-wrapper">
                          <span className="input-icon">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                              <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"/>
                              <rect x="6" y="14" width="12" height="8" rx="1"/>
                            </svg>
                          </span>
                          <input type="text" name="fax_number" className="form-input" placeholder="z.B. +49 123 456780" value={practiceInfo.fax_number || ''} onChange={handlePracticeInfoChange} onBlur={handlePracticeFieldBlur} />
                        </div>
                      </div>
                    </div>

                    {/* Read-only: Dedicated Inbound SIP Banner with compact icon copy */}
                    {sipLoadedFor === practiceId && sipConfig.sip_phone_number && (
                      <div className="deskbot-sip-banner">
                        <div className="deskbot-sip-left">
                          <span className="deskbot-sip-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            </svg>
                          </span>
                          <span className="deskbot-sip-title">
                            DeskBot-Rufnummer (eingehend):
                            <InfoTooltip text="Auf diese Nummer leitet Ihre Telefonanlage eingehende Anrufe weiter, damit DeskBot sie annimmt. Nicht zu verwechseln mit der Standard-Weiterleitungsnummer, an die DeskBot ein Gespräch zurück an die Praxis übergibt." />
                          </span>
                          <span className="deskbot-sip-number">{sipConfig.sip_phone_number}</span>
                          <button
                            type="button"
                            className="deskbot-sip-icon-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(sipConfig.sip_phone_number);
                              setSipCopied(true);
                              if (sipCopyTimerRef.current) clearTimeout(sipCopyTimerRef.current);
                              sipCopyTimerRef.current = setTimeout(() => setSipCopied(false), 2000);
                            }}
                            title={sipCopied ? 'Kopiert!' : 'Nummer kopieren'}
                            aria-label="SIP-Nummer kopieren"
                          >
                            {sipCopied ? (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Row 3: E-Mail & Website */}
                    <div className="config-form-grid" style={{ alignItems: 'start' }}>
                      <div className="form-group">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '6px' }}>
                          <label className="form-label" style={{ margin: 0 }}>E-Mail</label>
                          {sesVerificationStatus === 'Success' ? (
                            <span className="ses-pill ses-pill--success" title="E-Mail ist bei Amazon SES verifiziert">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                              Verifiziert
                            </span>
                          ) : sesVerificationStatus === 'Pending' ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span className="ses-pill ses-pill--pending">Ausstehend</span>
                              <button
                                type="button"
                                onClick={handleCheckEmailStatus}
                                className="ses-pill ses-pill--pending-btn"
                                title="Status bei SES prüfen"
                              >
                                Status prüfen
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <button
                                type="button"
                                onClick={handleVerifyEmail}
                                disabled={sesVerifyLoading || !practiceInfo.email}
                                className="ses-pill ses-pill--verify"
                                title={!practiceInfo.email ? 'Bitte zuerst eine E-Mail-Adresse eingeben' : 'Verifizierungs-E-Mail senden'}
                              >
                                {sesVerifyLoading ? 'Senden...' : 'Verifizieren'}
                              </button>
                              <InfoTooltip text="Damit Deskbot Bestätigungen von dieser E-Mail-Adresse versenden darf, muss die Adresse einmalig verifiziert werden. Klick sendet eine Bestätigungs-E-Mail an die angegebene Adresse." />
                            </div>
                          )}
                        </div>
                        <div className="input-with-icon-wrapper">
                          <span className="input-icon">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                            </svg>
                          </span>
                          <input type="email" name="email" className="form-input" placeholder="praxis@beispiel.de" value={practiceInfo.email || ''} onChange={handlePracticeInfoChange} onBlur={handlePracticeFieldBlur} />
                        </div>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Website</label>
                        <div className="input-with-icon-wrapper">
                          <span className="input-icon">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>
                            </svg>
                          </span>
                          <input type="text" name="website" className="form-input" placeholder="www.praxis-beispiel.de" value={practiceInfo.website || ''} onChange={handlePracticeInfoChange} onBlur={handlePracticeFieldBlur} />
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>

                  {/* Right Card: Praxiszeiten */}
                  <div className="card" style={{ margin: 0 }}>
                    <h3 className="h3" style={{ margin: practiceId ? '0 0 10px 0' : '0 0 16px 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      Praxiszeiten
                      <InfoTooltip text="Außerhalb dieser Zeiten: Keine Weiterleitung und keine Rückrufe mit Zeitversprechen. Zudem spielt der Assistent die unter „Konfiguration → Anrufablauf-Konfiguration“ eingerichtete „Eigene Begrüßung bei abwesendem Empfang“ ab." />
                    </h3>
                    {practiceId && (
                      <div className="tt-table-tabs tt-table-tabs--card" role="tablist" aria-label="Praxiszeiten">
                        {[
                          ['opening', 'Behandlungszeiten', null],
                          ['reception', 'Empfangszeiten', receptionEnabled],
                          ['holidays', 'Feiertage & Schließtage', Boolean(holidaysData?.region)],
                        ].map(([key, label, dotOn]) => (
                          <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={hoursCardTab === key}
                            className={`tt-table-tab${hoursCardTab === key ? ' active' : ''}`}
                            onClick={() => setHoursCardTab(key)}
                          >
                            {label}
                            {dotOn !== null && (
                              <span className={`vt-cat-dot${dotOn ? ' is-on' : ''}`} aria-hidden="true" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {(!practiceId || hoursCardTab === 'opening') && (
                      renderWeeklyHoursRows(practiceInfo.opening_hours, handleOpeningHoursChange)
                    )}

                    {practiceId && hoursCardTab === 'reception' && (
                      receptionLoading && closuresLoadedFor !== practiceId ? (
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Empfangszeiten werden geladen…</div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' }}>
                            <label className="ios-switch">
                              <input
                                type="checkbox"
                                checked={receptionEnabled}
                                disabled={receptionLoading}
                                onChange={(e) => handleToggleReception(e.target.checked)}
                              />
                              <span className="ios-switch-slider" />
                            </label>
                            <span className="pf-gate-label">Empfangszeiten verwenden</span>
                          </div>
                          {receptionError && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--color-tertiary)', marginBottom: '10px' }}>
                              {receptionError}
                            </div>
                          )}
                          {renderWeeklyHoursRows(
                            receptionHours || buildDefaultReceptionHours(),
                            handleReceptionHoursChange,
                            { disabled: !receptionEnabled }
                          )}
                        </>
                      )
                    )}

                    {practiceId && hoursCardTab === 'holidays' && (
                      <>
                        {holidaysError && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--color-tertiary)', marginBottom: '10px' }}>
                            {holidaysError}
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                          <label className="form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 0 }}>
                            Bundesland
                            <InfoTooltip text="An aktivierten Feiertagen und Schließtagen kündigt der Assistent Rückrufe ohne Zeitversprechen an. Termine werden dadurch nicht blockiert." />
                          </label>
                          <select
                            className="form-input"
                            style={{ flex: '1 1 200px', maxWidth: 280 }}
                            value={holidaysData?.region || ''}
                            disabled={holidaysSyncing || holidaysLoading}
                            onChange={(e) => handleHolidaySync(e.target.value)}
                          >
                            <option value="" disabled>Bundesland wählen…</option>
                            {Object.entries(holidaysData?.available_regions || {})
                              .sort((a, b) => a[1].localeCompare(b[1], 'de'))
                              .map(([code, name]) => (
                                <option key={code} value={code}>{name}</option>
                              ))}
                          </select>
                          {holidaysData?.year && (
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                              {holidaysData.year}
                            </span>
                          )}
                        </div>

                        {(holidaysLoading || holidaysSyncing) && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Feiertage werden geladen…</div>
                        )}

                        {!holidaysData?.region && !holidaysSyncing && !holidaysLoading && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            Wählen Sie Ihr Bundesland, um die gesetzlichen Feiertage zu laden.
                          </div>
                        )}

                        {holidaysData?.region && !holidaysSyncing && !holidaysLoading && (() => {
                          const regionName = holidaysData.available_regions?.[holidaysData.region] || holidaysData.region;
                          const groups = [
                            { scope: 'national', title: 'Bundesweite Feiertage', tooltip: null },
                            { scope: 'regional', title: `Regionale Feiertage (${regionName})`, tooltip: null },
                            { scope: 'optional', title: 'Optionale Schließtage', tooltip: 'Nicht gesetzlich bzw. nur in einzelnen Gemeinden - bei Bedarf aktivieren.' }
                          ];
                          return (
                            <div className="hours-holidays-scroll">
                              {groups.map((group, idx) => {
                                const items = (holidaysData.holidays || []).filter(h => h.scope === group.scope);
                                if (items.length === 0) return null;
                                return (
                                  <div key={group.scope} style={idx > 0 ? { marginTop: 16 } : undefined}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                      <span style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
                                        {group.title}
                                      </span>
                                      {group.tooltip && <InfoTooltip text={group.tooltip} />}
                                    </div>
                                    {items.map((h, rowIdx) => {
                                      const busy = !!holidayRowBusy[h.id];
                                      const rowErr = holidayRowError[h.id];
                                      const single = holidayIsSingleDay(h);
                                      const rangeOpen = !single || holidayRangeOpen[h.id];
                                      const startVal = holidayInputDate(h.starts_at);
                                      const endVal = holidayInputDate(h.ends_at);
                                      return (
                                        <div key={h.id} style={{ borderBottom: rowIdx < items.length - 1 ? '1px solid var(--border-light)' : 'none', padding: '8px 0' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <label className="ios-switch">
                                              <input
                                                type="checkbox"
                                                checked={h.enabled}
                                                disabled={busy}
                                                onChange={(e) => handleHolidayToggle(h.id, e.target.checked)}
                                              />
                                              <span className="ios-switch-slider" />
                                            </label>
                                            <span style={{ flex: '1 1 130px', minWidth: 0, fontSize: '0.875rem', color: 'var(--text-primary)', opacity: h.enabled ? 1 : 0.55 }}>
                                              {h.title}
                                            </span>
                                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                              <input
                                                type="date"
                                                className="form-input"
                                                style={{ width: 'auto', padding: '3px 6px', fontSize: '0.8rem', height: 30 }}
                                                value={startVal}
                                                disabled={busy}
                                                onChange={(e) => {
                                                  const ns = e.target.value;
                                                  if (!ns) return;
                                                  const ne = (rangeOpen && endVal >= ns) ? endVal : ns;
                                                  handleHolidayDates(h.id, ns, ne);
                                                }}
                                              />
                                              {rangeOpen ? (
                                                <>
                                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                                  <input
                                                    type="date"
                                                    className="form-input"
                                                    style={{ width: 'auto', padding: '3px 6px', fontSize: '0.8rem', height: 30 }}
                                                    value={endVal}
                                                    min={startVal}
                                                    disabled={busy}
                                                    onChange={(e) => {
                                                      if (!e.target.value) return;
                                                      handleHolidayDates(h.id, startVal, e.target.value);
                                                    }}
                                                  />
                                                </>
                                              ) : (
                                                <button
                                                  type="button"
                                                  className="button-secondary"
                                                  style={{ margin: 0, padding: '3px 8px', fontSize: '0.78rem', height: 30, whiteSpace: 'nowrap' }}
                                                  disabled={busy}
                                                  onClick={() => setHolidayRangeOpen(prev => ({ ...prev, [h.id]: true }))}
                                                >
                                                  + Zeitraum
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                          {rowErr && (
                                            <div style={{ fontSize: '0.8rem', color: 'var(--color-tertiary)', marginTop: 4 }}>{rowErr}</div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </form>

              {/* Bottom Card: FAQs */}
              {locationMatrix && (locationMatrix.locations || []).length > 0 && faqLocationId && (
                <div className="card" style={{ margin: "20px 0 1rem 0" }}>
                  <h3 className="h3" style={{ margin: '0 0 12px 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    FAQs
                    <InfoTooltip text="Häufig gestellte Fragen, die Deskbot den Anrufern beantworten kann (z.B. Anfahrt, Parken, Barrierefreiheit, Angebot)." />
                  </h3>
                  {(locationMatrix.locations || []).length > 1 && (
                    <div className="faq-location-switcher" role="tablist" aria-label="Standort wählen">
                      {locationMatrix.locations.map(loc => {
                        const isActive = loc.id === faqLocationId;
                        return (
                          <button
                            key={loc.id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={`faq-location-pill${isActive ? ' is-active' : ''}`}
                            onClick={() => {
                              if (pendingFaqWriteRef.current) {
                                commitFaq(pendingFaqWriteRef.current);
                              }
                              setFaqLocationId(loc.id);
                              setFaqDraft(loc.faq || '');
                            }}
                          >
                            {loc.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <textarea
                    className="form-input"
                    value={faqDraft}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFaqDraft(val);
                      pendingFaqWriteRef.current = { targetPracticeId: practiceId, targetLocId: faqLocationId, text: val };
                      if (faqSaveTimerRef.current) clearTimeout(faqSaveTimerRef.current);
                      faqSaveTimerRef.current = setTimeout(() => {
                        commitFaq(pendingFaqWriteRef.current);
                      }, 800);
                    }}
                    onBlur={() => {
                      if (pendingFaqWriteRef.current) {
                        commitFaq(pendingFaqWriteRef.current);
                      }
                    }}
                    rows="6"
                    style={{ width: "100%", resize: "vertical" }}
                  />
                </div>
              )}
            </div>

          </div>
        )}
        {activeTab === 'team' && (
          <div className="tab-content" style={{ position: 'relative' }}>
            <LoadingShroud loading={formLoadedFor !== practiceId} />
            {/* Therapy Types Section */}
            {practiceId && (
              <div className="card" style={{ margin: '0 0 20px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'var(--h2-margin-bottom, 16px)' }}>
                  <h2 className="h2" style={{ margin: 0 }}>Behandlungsarten</h2>
                  <button
                    type="button"
                    className="shortcuts-help-btn"
                    data-tooltip="Tastenkürzel anzeigen (?)"
                    aria-label="Tastenkürzel anzeigen"
                    onClick={() => setShowShortcutHelp(true)}
                  >
                    ?
                  </button>
                </div>
                {/* Category (umbrella) rail — the primary facet, drawn as file-folder tabs
                    that sit on the top edge of the panel holding the insurance rail and the
                    table. Every non-active tab and the "+" are drop targets for a therapy
                    row dragged by its grip. */}
                <div
                  ref={catRailRef}
                  className={`tt-cat-rail${draggingTherapyId != null ? ' is-row-dragging' : ''}${draggingCategory ? ' is-tab-dragging' : ''}`}
                  // The rail, not the individual tab, owns the reorder drop: only from
                  // here can the pointer be placed BETWEEN two tabs, before the first
                  // and after the last one. A therapy-row drag is left alone — its
                  // handlers sit on the tabs and the "+" and never reach this.
                  onDragOver={e => {
                    if (!isCategoryDrag(e)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    const next = categoryDropMarker(e.clientX, e.clientY);
                    setCatDrop(prev => (prev && next && prev.index === next.index ? prev : next));
                  }}
                  onDragLeave={e => {
                    if (e.currentTarget.contains(e.relatedTarget)) return;   // ignore child enters
                    setCatDrop(null);
                  }}
                  onDrop={e => {
                    if (!isCategoryDrag(e)) return;
                    e.preventDefault();
                    const fromKey = draggedCategoryRef.current;
                    const at = (categoryDropMarker(e.clientX, e.clientY) || catDrop || {}).index;
                    draggedCategoryRef.current = null;
                    setDraggingCategory(null);
                    setCatDrop(null);
                    if (fromKey) handleReorderCategories(fromKey, at);
                  }}
                >
                  {/* Where the dragged tab would land. Absolutely positioned, so the rail
                      keeps its exact layout while the bar follows the pointer. */}
                  {catDrop && (
                    <span
                      className="tt-cat-drop-bar"
                      aria-hidden="true"
                      style={{ left: catDrop.x, top: catDrop.top, height: catDrop.height }}
                    />
                  )}
                  {therapyCategories.map(key => (
                    <div key={key} className={`tt-cat-tab${categoriesApiAvailable ? ' has-swap' : ''}${draggingCategory === key ? ' is-dragging' : ''}`}>
                      <button
                        type="button"
                        className={`tt-cat-folder-tab${therapyCategoryTab === key ? ' active' : ''}${dropTargetCategory === key ? ' is-drop-target' : ''}`}
                        // Press and hold a tab to rearrange the rail. The label button is
                        // the drag source, not the wrapper, so the edit control laid over
                        // its right padding keeps its own click and never starts a drag.
                        draggable={categoriesApiAvailable && !categoryBusy && therapyCategories.length > 1}
                        onDragStart={e => {
                          draggedCategoryRef.current = key;
                          setDraggingCategory(key);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData(CATEGORY_DRAG_TYPE, key);
                          e.dataTransfer.setData('text/plain', key);
                        }}
                        onDragEnd={() => {
                          draggedCategoryRef.current = null;
                          setDraggingCategory(null);
                          setCatDrop(null);
                        }}
                        onClick={() => { setTherapyCategoryTab(key); setEditingCell(null); setEditingFrequenzId(null); }}
                        onDoubleClick={() => { if (categoriesApiAvailable && therapyCategoryTab === key) setCategoryDialog({ mode: 'edit', key }); }}
                        {...categoryDropProps(key, draggedId => {
                          const dragged = therapyTypes.find(tt => tt.id === draggedId);
                          // Stay on the current tab (batch sorting) — the target keeps the
                          // highlight for ~600 ms as the confirmation that the row landed.
                          setDropTargetCategory(key);
                          setTimeout(() => setDropTargetCategory(prev => (prev === key ? null : prev)), 600);
                          if (dragged) handleMoveTherapyToCategory(dragged, key);
                        }, categoriesApiAvailable && therapyCategoryTab !== key)}
                      >
                        {categoryLabel(key)}
                      </button>
                      {/* Edit THIS category (merge, rename, delete), active or not. It sits
                          on the tab's reserved right padding as a sibling (never a nested
                          button), so the tab keeps its full hover surface and its width
                          never shifts when the icon is revealed on hover. */}
                      {categoriesApiAvailable && (
                        <IconTooltipButton
                          tooltip={`Heilmittelgruppe „${categoryLabel(key)}“ bearbeiten`}
                          className="tt-cat-swap"
                          disabled={categoryBusy}
                          onClick={() => setCategoryDialog({ mode: 'edit', key })}
                        >
                          {actionIcons.pen}
                        </IconTooltipButton>
                      )}
                    </div>
                  ))}
                  {/* Rail controls are hidden against an old backend (categories GET 404) */}
                  {categoriesApiAvailable && categoryOptions.some(o => !therapyCategories.includes(o.value)) && (
                    <IconTooltipButton
                      tooltip="Kategorie hinzufügen"
                      className={`tt-cat-add${dropTargetCategory === CATEGORY_DROP_NEW ? ' is-drop-target' : ''}`}
                      disabled={categoryBusy}
                      onClick={() => setCategoryDialog({ mode: 'add' })}
                      {...categoryDropProps(CATEGORY_DROP_NEW, draggedId => {
                        setDropTargetCategory(null);
                        if (draggedId != null) setCategoryDialog({ mode: 'dropNew', therapyId: draggedId });
                      })}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </IconTooltipButton>
                  )}
                  {/* Fixed tab for non-offered therapies (cross-cutting across all disciplines).
                      Uses .tt-cat-tab-static so rail.querySelectorAll('.tt-cat-tab') only matches
                      reorderable dynamic categories. */}
                  <div className="tt-cat-tab-static">
                    <div className="tt-cat-rail-divider" aria-hidden="true" />
                    <button
                      type="button"
                      className={`tt-cat-folder-tab tt-cat-folder-tab--excluded${isExcludedView ? ' active' : ''}`}
                      onClick={() => { setTherapyCategoryTab(EXCLUDED_THERAPIES_TAB); setEditingCell(null); setEditingFrequenzId(null); }}
                    >
                      {actionIcons.excluded}
                      Nicht angeboten
                    </button>
                  </div>
                </div>
                {/* The open folder: everything the active category tab labels. The
                    insurance rail keeps its own underline look and stays right-aligned in
                    here, so it reads as a filter INSIDE the folder, not a peer of the
                    categories. (Contents left at their original indentation.) */}
                <div className="tt-cat-panel">
                {isExcludedView ? renderExcludedView() : (
                  <>
                {/* Sub-view switcher (left) + Insurance tab selector & Defaults-Matrix toggle (right) */}
                <div className="tt-table-tabs" style={{ marginBottom: 16, alignItems: 'center', gap: 8 }}>
                  {supplementaryRows.length > 0 && (
                    <div className="tt-subview-pills" role="tablist" aria-label="Ansicht innerhalb der Heilmittelgruppe">
                      {[
                        { key: 'main', label: 'Behandlungen', count: mainTherapyRows.length, active: !isSupplementaryView },
                        { key: 'supplementary', label: 'Ergänzende Heilmittel', icon: actionIcons.supplementary, count: supplementaryRows.length, active: isSupplementaryView },
                      ].map(v => (
                        <button
                          key={v.key}
                          type="button"
                          role="tab"
                          aria-selected={v.active}
                          className={`tt-subview-pill${v.active ? ' is-active' : ''}`}
                          onClick={() => { setTherapySubView(v.key); setEditingCell(null); setEditingFrequenzId(null); }}
                        >
                          {v.icon && <span className="tt-subview-icon">{v.icon}</span>}
                          <span>{v.label}</span>
                          <span className="tt-subview-count">{v.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="tt-insurance-tabs">
                    <IconTooltipButton
                      tooltip="Einstellungen der Verordnungstypen"
                      className="icon-button tt-action-btn"
                      // Open on the group whose folder the gear was clicked in — with
                      // per-group editing on, that is the one the user is looking at.
                      onClick={() => { setMatrixCategoryTab(therapyCategoryTab); setDefaultsMatrixOpen(true); }}
                    >
                      {actionIcons.settings}
                    </IconTooltipButton>
                    {['private', 'public', 'selbstzahler', 'bg', 'entlassmanagement'].map(tab => (
                      <div
                        key={tab}
                        className={`tt-table-tab${therapyInsuranceTab === tab ? ' active' : ''}`}
                        onClick={() => { setTherapyInsuranceTab(tab); setEditingCell(null); setEditingFrequenzId(null); }}
                      >
                        {insuranceLabel(tab)}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Defaults-Matrix Modal — same settings as the per-tab modal
                    but for ALL 5 insurance types side-by-side. */}
                <Modal
                  isOpen={defaultsMatrixOpen}
                  onRequestClose={handleCloseDefaultsMatrix}
                  style={{
                    ...customModalStyles,
                    content: {
                      ...customModalStyles.content,
                      width: 'fit-content',
                      minWidth: 'min(92vw, 1080px)',
                      maxWidth: 'min(96vw, 1380px)',
                      maxHeight: '90vh',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      padding: '24px 28px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      margin: 'auto',
                    },
                  }}
                  contentLabel={matrixTitle}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: showCategoryScopeSwitch ? '14px' : '20px' }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>{matrixTitle}</h2>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Fristen, Behandlungszeiträume und Patienten-Routing nach Versicherungsart.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseDefaultsMatrix}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: 'var(--text-secondary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                      }}
                      aria-label="Schließen"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  {/* Master switch — one rule set for every Heilmittelgruppe, or one per
                      group. Same control as the therapy modal's PVS-Bezeichnung toggle.
                      The bottom margin is constant: it used to be 16 or 20 depending on the
                      mode, which was the last 4px of the jump the switch caused. */}
                  {showCategoryScopeSwitch && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                      <label className="ios-switch" aria-label={perCategoryMode ? 'Einstellungen für alle Heilmittelgruppen aktivieren' : 'Einstellungen je Heilmittelgruppe aktivieren'}>
                        <input
                          type="checkbox"
                          checked={!perCategoryMode}
                          onChange={e => handleToggleCategoryScope(e.target.checked)}
                        />
                        <span className="ios-switch-slider" />
                      </label>
                      <label className="form-label" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        Für alle Heilmittelgruppen
                        <InfoTooltip text="An: eine Regel für alle Heilmittelgruppen. Aus: jede Gruppe kann eigene Regeln bekommen. Eine Gruppe folgt weiter der gemeinsamen Regel, bis sie zum ersten Mal geändert wird - dann übernimmt sie die gemeinsamen Werte und ist ab da unabhängig." />
                      </label>
                    </div>
                  )}
                  {/* Folder tabs, same look as the Behandlungsarten rail: the grid below
                      is the open folder and edits exactly the selected group.
                      Rendered whenever the switch is, and merely faded to an inert state
                      while it is on, rather than unmounted: mounting the strip moved the
                      grid and the whole (vertically centred) dialog every time the switch
                      was flipped. Now only the strip itself changes. */}
                  {showCategoryScopeSwitch && (
                    <div className={`tt-cat-rail vt-cat-rail${perCategoryMode ? '' : ' is-inert'}`} aria-hidden={!perCategoryMode}>
                      {therapyCategories.map(key => {
                        // Read straight off the stored map, never a local flag: the
                        // marker then appears the moment the first edit materialises a
                        // group and disappears again if the backend ever prunes it away.
                        const customised = isCategoryCustomised(key);
                        return (
                          // has-swap is the reserved-slot class, not a promise of a swap
                          // control: it is what holds the 36px the overlaid icon needs.
                          // Carried by EVERY tab, customised or not, so the first edit
                          // materialising a group cannot widen its tab and reflow the strip.
                          <div key={key} className="tt-cat-tab has-swap">
                            <button
                              type="button"
                              className={`tt-cat-folder-tab${matrixCategory === key ? ' active' : ''}`}
                              onClick={() => setMatrixCategoryTab(key)}
                              // Inert while one rule governs every group: greyed out is a
                              // statement, not a trap, so the strip must not be clickable
                              // or reachable by keyboard in that state.
                              disabled={!perCategoryMode}
                              title={customised ? 'Eigene Regeln' : undefined}
                            >
                              {/* Rendered on every tab, tinted only on a customised one, so
                                  a group getting its own set never resizes its tab. */}
                              <span className={`vt-cat-dot${customised ? ' is-on' : ''}`} aria-hidden="true" />
                              {categoryLabel(key)}
                            </button>
                            {/* Hand THIS group back to the shared rule, on the tab it acts on
                                and only while that tab is hovered or focused. A sibling of the
                                label button (never nested), exactly like the Behandlungsarten
                                rail's edit control, so the tab stays one hover surface.
                                Rendered only for a group that has its own rules - a group
                                already following the shared set has nothing to hand back. */}
                            {customised && (
                              <IconTooltipButton
                                className="vt-cat-reset"
                                // One click, no confirm: the group simply follows the shared
                                // rules again, and clicking it by accident costs nothing that
                                // re-editing does not undo.
                                tooltip="Zurück zu den allgemeinen Regeln"
                                onClick={() => handleResetCategorySetting(key)}
                              >
                                {actionIcons.revert}
                              </IconTooltipButton>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* The folder body. Its rule and padding are unconditional whenever the
                      switch exists: making them follow the mode moved the grid by 19px on
                      every flip, which is the same jump the rail used to cause. */}
                  <div className={showCategoryScopeSwitch ? 'vt-cat-body' : undefined}>
                  {(() => {
                  const anySendLink = usesFormLink(matrixSettings)
                    || usesFormLink(insuranceTypeSettings)
                    || (perCategoryMode && Object.values(therapyCategorySettings.by_category || {}).some(usesFormLink));

                  const patchCell = (insurance, patch) => (perCategoryMode
                    ? handlePatchCategorySetting(matrixCategory, insurance, patch)
                    : handlePatchInsuranceSetting(insurance, patch));

                  // Unified link source: 'doc_iq' if any insurance block uses DOC-IQ, else 'general'
                  const currentUnifiedLinkSource = INSURANCE_KEYS.some(k =>
                    matrixSettings[k]?.new_patient_form_link_source === 'doc_iq' ||
                    matrixSettings[k]?.existing_patient_form_link_source === 'doc_iq'
                  ) ? 'doc_iq' : 'general';

                  const handleUpdateUnifiedLinkSource = (source) => {
                    const patchSources = (block) => ({
                      ...pickInsuranceFields(block),
                      new_patient_form_link_source: source,
                      existing_patient_form_link_source: source,
                    });

                    if (perCategoryMode) {
                      const stored = therapyCategorySettings.by_category || {};
                      const base = stored[matrixCategory] || Object.fromEntries(
                        INSURANCE_KEYS.map(k => [k, pickInsuranceFields(insuranceTypeSettings[k])])
                      );
                      const updatedCategory = Object.fromEntries(
                        INSURANCE_KEYS.map(k => [k, patchSources(base[k])])
                      );
                      putCategorySettings({
                        scope: VT_SCOPE_PER_CATEGORY,
                        by_category: {
                          ...stored,
                          [matrixCategory]: updatedCategory,
                        },
                      });
                    } else {
                      const merged = { ...insuranceTypeSettings };
                      INSURANCE_KEYS.forEach(k => {
                        merged[k] = patchSources(merged[k]);
                      });
                      setInsuranceTypeSettings(merged);
                      if (practiceId) {
                        queueMatrixWrite(() => axios.put(`${API_BASE_URL}/practices/${practiceId}/insurance-type-settings`, merged));
                      }
                    }
                  };

                  const numberCellStyle = {
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #d8dde5',
                    borderRadius: 6,
                    background: '#fff',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit',
                    color: 'var(--text-primary)',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                  };

                  return (
                    <div>
                      {/* CARD 1: Fristen & Behandlungszeiträume */}
                      <div style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 10,
                        background: '#ffffff',
                        padding: '16px 20px',
                        marginBottom: 16,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              Fristen & Behandlungszeiträume (Tage)
                            </span>
                            <InfoTooltip text="Fristen und Abstände in Tagen je nach Versicherungsart. Leer lassen, falls keine Begrenzung gilt." />
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Werte in Tagen (leer = unbegrenzt)
                          </span>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '160px repeat(5, minmax(150px, 1fr))',
                          gap: '10px 12px',
                          alignItems: 'center',
                        }}>
                          {/* Header row */}
                          <div />
                          {INSURANCE_KEYS.map(k => (
                            <div key={`h1-${k}`} style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', textAlign: 'center' }}>
                              {insuranceLabel(k)}
                            </div>
                          ))}

                          {/* Verordnungsfrist */}
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                            Verordnungsfrist
                          </div>
                          {INSURANCE_KEYS.map(k => (
                            <input
                              key={`vf-${k}`}
                              type="number"
                              min="0"
                              placeholder="-"
                              value={matrixSettings[k]?.verordnungsfrist_days ?? ''}
                              onChange={e => patchCell(k, { verordnungsfrist_days: parseDays(e.target.value) })}
                              style={numberCellStyle}
                            />
                          ))}

                          {/* Max. Terminabstand */}
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                            Max. Terminabstand
                          </div>
                          {INSURANCE_KEYS.map(k => (
                            <input
                              key={`mt-${k}`}
                              type="number"
                              min="0"
                              placeholder="-"
                              value={matrixSettings[k]?.max_terminabstand_days ?? ''}
                              onChange={e => patchCell(k, { max_terminabstand_days: parseDays(e.target.value) })}
                              style={numberCellStyle}
                            />
                          ))}

                          {/* Behandlungszeitraum Divider */}
                          <div style={{ gridColumn: '1 / -1', paddingTop: 8, paddingBottom: 4, borderTop: '1px solid #f1f5f9', marginTop: 4 }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Behandlungszeitraum (ab 1. Termin)
                            </span>
                          </div>

                          {/* bis 6 Termine & ab 7 Termine */}
                          {[['behandlungszeitraum_bis6_days', 'bis 6 Termine'], ['behandlungszeitraum_ab7_days', 'ab 7 Termine']].map(([field, label]) => (
                            <React.Fragment key={field}>
                              <div style={{ fontSize: '0.84rem', color: '#64748b', paddingLeft: 6 }}>{label}</div>
                              {INSURANCE_KEYS.map(k => (
                                <input
                                  key={`${field}-${k}`}
                                  type="number"
                                  min="0"
                                  placeholder="-"
                                  value={matrixSettings[k]?.[field] ?? ''}
                                  onChange={e => patchCell(k, { [field]: parseDays(e.target.value) })}
                                  style={numberCellStyle}
                                />
                              ))}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {/* CARD 2: Patientenbehandlung & Routing */}
                      <div style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 10,
                        background: '#ffffff',
                        padding: '16px 20px',
                        marginBottom: 16,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              Patientenbehandlung & Routing
                            </span>
                            <InfoTooltip text="Festlegung, wie Neu- und Bestandspatienten für die jeweilige Versicherungsart behandelt werden (z. B. direkt buchen, Warteliste oder Aufnahmeformular senden)." />
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Aktion bei Anruf je Versicherungsart
                          </span>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '160px repeat(5, minmax(150px, 1fr))',
                          gap: '10px 12px',
                          alignItems: 'center',
                        }}>
                          {/* Header row */}
                          <div />
                          {INSURANCE_KEYS.map(k => (
                            <div key={`h2-${k}`} style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', textAlign: 'center' }}>
                              {insuranceLabel(k)}
                            </div>
                          ))}

                          {/* Neupatienten */}
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                            Neupatienten
                          </div>
                          {INSURANCE_KEYS.map(k => (
                            <div key={`np-${k}`}>
                              <CustomDropdown
                                size="sm"
                                minWidth="100%"
                                value={handlingSelectValue(matrixSettings[k], 'new')}
                                onChange={(v) => patchCell(k, handlingPatch(v, 'new'))}
                                options={[
                                  { value: 'normal', label: 'Normal' },
                                  { value: 'smartab', label: 'SmartAB' },
                                  { value: 'waiting_list', label: 'Warteliste' },
                                  { value: 'send_link', label: 'Link senden' },
                                  { value: 'normal_send_link', label: 'Buchen + Link senden' },
                                ]}
                              />
                            </div>
                          ))}

                          {/* Bestandspatienten */}
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                            Bestandspatienten
                          </div>
                          {INSURANCE_KEYS.map(k => (
                            <div key={`ep-${k}`}>
                              <CustomDropdown
                                size="sm"
                                minWidth="100%"
                                value={handlingSelectValue(matrixSettings[k], 'existing')}
                                onChange={(v) => patchCell(k, handlingPatch(v, 'existing'))}
                                options={[
                                  { value: 'normal', label: 'Normal' },
                                  { value: 'smartab', label: 'SmartAB' },
                                  { value: 'normal_send_link', label: 'Buchen + Link senden' },
                                ]}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CARD 3: Aufnahmeformular & Link-Integration (SMS-Versand) */}
                      {anySendLink ? (
                        <div style={{
                          border: '1px solid #bfdbfe',
                          borderRadius: 10,
                          background: '#f8fafc',
                          padding: '16px 20px',
                          marginBottom: 8,
                          boxShadow: '0 1px 3px rgba(37,99,235,0.05)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e3a8a' }}>
                                Aufnahmeformular & Link-Optionen
                              </span>
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#2563eb',
                                background: '#eff6ff',
                                border: '1px solid #dbeafe',
                                padding: '2px 8px',
                                borderRadius: 12,
                                fontWeight: 500,
                              }}>
                                SMS-Versand aktiv
                              </span>
                            </div>
                            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                              Einheitlich für alle Versicherungsarten
                            </span>
                          </div>

                          <p style={{ margin: '0 0 14px 0', fontSize: '0.83rem', color: '#475569', lineHeight: 1.45 }}>
                            Für Versicherungsarten mit „Link senden“ oder „Buchen + Link senden“ erhält der Anrufer automatisch einen Link zum digitalen Aufnahmefragebogen per SMS.
                          </p>

                          {/* Unified Link Type Selection */}
                          <div>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: 10 }}>
                              Welcher Link soll versendet werden?
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, alignItems: 'stretch' }}>
                              {/* Option 1: DOC-IQ */}
                              <label style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 10,
                                padding: '14px 16px',
                                borderRadius: 10,
                                border: currentUnifiedLinkSource === 'doc_iq' ? '2px solid var(--color-secondary)' : '1px solid #cbd5e1',
                                background: currentUnifiedLinkSource === 'doc_iq' ? '#ffffff' : '#f8fafc',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: currentUnifiedLinkSource === 'doc_iq' ? '0 2px 8px rgba(47,107,215,0.08)' : 'none',
                              }}>
                                <input
                                  type="radio"
                                  name="unified-form-link-source"
                                  checked={currentUnifiedLinkSource === 'doc_iq'}
                                  onChange={() => handleUpdateUnifiedLinkSource('doc_iq')}
                                  style={{ marginTop: 3, accentColor: 'var(--color-secondary)', cursor: 'pointer' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                      DOC-IQ-Link
                                    </span>
                                    <span style={{ fontSize: '0.72rem', color: '#047857', background: '#d1fae5', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>
                                      Persönlich vorausgefüllt
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.45, marginBottom: 8 }}>
                                    Erstellt via DOC-IQ einen individuellen Fragebogen mit den Daten des Anrufers.
                                  </div>
                                  <div style={{ fontSize: '0.76rem', color: '#64748b', background: '#f1f5f9', padding: '6px 10px', borderRadius: 6, lineHeight: 1.4 }}>
                                    ℹ️ Falls DOC-IQ nicht erreichbar oder eingerichtet ist, dient die <strong>Formular-URL (rechts)</strong> als automatischer Fallback.
                                  </div>
                                  {currentUnifiedLinkSource === 'doc_iq' && (
                                    <div style={{ marginTop: 8 }}>
                                      <DocIqStatusNote status={docIqStatus} />
                                    </div>
                                  )}
                                </div>
                              </label>

                              {/* Option 2: General / Formular-URL */}
                              <label style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 10,
                                padding: '14px 16px',
                                borderRadius: 10,
                                border: currentUnifiedLinkSource === 'general' ? '2px solid var(--color-secondary)' : '1px solid #cbd5e1',
                                background: currentUnifiedLinkSource === 'general' ? '#ffffff' : '#f8fafc',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: currentUnifiedLinkSource === 'general' ? '0 2px 8px rgba(47,107,215,0.08)' : 'none',
                              }}>
                                <input
                                  type="radio"
                                  name="unified-form-link-source"
                                  checked={currentUnifiedLinkSource === 'general'}
                                  onChange={() => handleUpdateUnifiedLinkSource('general')}
                                  style={{ marginTop: 3, accentColor: 'var(--color-secondary)', cursor: 'pointer' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                      Allgemeiner Link
                                    </span>
                                    <span style={{ fontSize: '0.72rem', color: '#1e40af', background: '#dbeafe', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                                      Formular-URL
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.45, marginBottom: 6 }}>
                                    Sendet stets den festen Link zu Ihrem praxiseigenen Aufnahmeformular (dient auch als DOC-IQ Fallback):
                                  </div>
                                  <div onClick={e => e.stopPropagation()}>
                                    <input
                                      type="url"
                                      placeholder="https://..."
                                      value={matrixFormUrlDraft !== null ? matrixFormUrlDraft : (insuranceTypeSettings.new_patient_form_url || '')}
                                      onChange={e => setMatrixFormUrlDraft(e.target.value)}
                                      onBlur={() => {
                                        if (matrixFormUrlDraft !== null && matrixFormUrlDraft !== (insuranceTypeSettings.new_patient_form_url || '')) {
                                          handlePatchInsuranceSetting('_root', { new_patient_form_url: matrixFormUrlDraft });
                                        }
                                        setMatrixFormUrlDraft(null);
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') e.currentTarget.blur();
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: 6,
                                        background: '#fff',
                                        fontSize: '0.85rem',
                                        fontFamily: 'inherit',
                                        color: 'var(--text-primary)',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                      }}
                                    />
                                    {((matrixFormUrlDraft !== null ? matrixFormUrlDraft : insuranceTypeSettings.new_patient_form_url) || '').trim() ? (
                                      <div style={{ marginTop: 5, fontSize: '0.75rem', color: '#059669', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span>✓ Ziel-URL für allgemeinen Link & DOC-IQ Fallback aktiv.</span>
                                      </div>
                                    ) : (
                                      <div style={{ marginTop: 5, fontSize: '0.75rem', color: 'var(--color-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span>⚠️ Ohne Formular-URL wird kein Link verschickt.</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          marginTop: 8,
                          padding: '12px 16px',
                          borderRadius: 8,
                          border: '1px dashed #cbd5e1',
                          background: '#f8fafc',
                          fontSize: '0.82rem',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}>
                          <span style={{ fontSize: '1rem' }}>💡</span>
                          <span>
                            <strong>Hinweis:</strong> Sobald Sie oben bei Neu- oder Bestandspatienten <em>„Link senden“</em> oder <em>„Buchen + Link senden“</em> auswählen, können Sie hier das digitale Aufnahmeformular (DOC-IQ oder eigene Formular-URL) konfigurieren.
                          </span>
                        </div>
                      )}</div>
                  );
                  })()}
                  </div>
                  {/* Every change is written the moment it is made; the button only waits
                      for the last write to land, so pressing it is a truthful "stored". */}
                  <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className="button"
                      onClick={handleCloseDefaultsMatrix}
                      aria-busy={matrixWritesPending > 0}
                    >
                      {matrixWritesPending > 0 ? 'Speichern…' : 'Speichern'}
                    </button>
                  </div>
                </Modal>

                {tableTherapyTypes.length > 0 ? (
                  <div className="tt-table-container">
                    <table className="tt-table">
                      <thead>
                        <tr>
                          <th className="tt-table-name-col">Name</th>
                          <th className="tt-table-desc-col">Beschreibung</th>
                          {isSupplementaryView ? (
                            <th colSpan={5} className="tt-table-supp-col">
                              <div className="tt-supp-col-head">
                                <span>Regel beim Verschieben</span>
                                <span className="tt-intake-hint">{SUPPLEMENTARY_NOTE}</span>
                              </div>
                            </th>
                          ) : (
                            <>
                              <th className="tt-table-accepted-col">
                                <div className="tt-accepted-header">
                                  <span
                                    className={`tt-accepted-toggle${(allAccepted || someAccepted) ? ' tt-accepted-toggle--active' : ' tt-accepted-toggle--inactive'}`}
                                    onClick={() => handleToggleAllAccepted(!allAccepted)}
                                    title={togglingAllAccepted ? 'Wird gespeichert…' : (allAccepted ? 'Alle deaktivieren' : 'Alle aktivieren')}
                                    style={togglingAllAccepted ? { opacity: 0.5, cursor: 'wait', pointerEvents: 'none' } : undefined}
                                  >
                                    Akzeptiert
                                  </span>
                                </div>
                              </th>
                              <th className="tt-table-dauer-col">Dauer<br/>(Min)</th>
                              <th className="tt-table-zuschlag-col">Erstzuschlag<br/>(Min)</th>
                              <th className="tt-table-sitzungen-col">
                                Sitzungen
                                <InfoTooltip text="Bei Verordnungen werden zuerst die Angaben der Verordnung übernommen. Steht auf der Verordnung eine höhere Anzahl oder wünscht ein Selbstzahler mehr Termine, schlägt Deskbot vor, zunächst die hier definierte Anzahl zu buchen - der Rest wird vor Ort mit der Praxis abgestimmt." />
                              </th>
                              <th className="tt-table-frequenz-col">
                                Frequenz
                                <InfoTooltip text="Bei Verordnungen wird zuerst die auf der Verordnung angegebene Frequenz verwendet. Bei Privatverordnungen ohne Frequenz-Angabe oder bei Selbstzahlern greift dieser Default. Zudem wird diese Frequenz verwendet, wenn die Person nicht weiß, wie häufig terminiert werden soll." />
                              </th>
                            </>
                          )}
                          <th className="tt-table-actions-col">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody key={`${therapyCategoryTab}-${isSupplementaryView ? 'supp' : 'main'}-${therapyInsuranceTab}`}>
                        {tableTherapyTypes.map(therapyType => {
                          const suffix = insuranceSuffix(therapyInsuranceTab);
                          const acceptedTypes = therapyType.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE;
                          // An Ergänzendes Heilmittel has no acceptance (the move reads its
                          // minutes for any insurance), so it never dims.
                          const isSupplementaryRow = !!therapyType.supplementary_mode;
                          const isAccepted = isSupplementaryRow || acceptedTypes[therapyInsuranceTab] !== false;
                          const allInsuranceDeactivated = !isSupplementaryRow && ['private', 'public', 'selbstzahler', 'bg', 'entlassmanagement']
                            .every(k => acceptedTypes[k] === false);
                          const rowDimClass = [
                            !isAccepted ? 'tt-row-right-dimmed' : '',
                            allInsuranceDeactivated ? 'tt-row-fully-dimmed' : '',
                          ].filter(Boolean).join(' ');
                          const currentDuration = therapyType[`duration${suffix}`] || 30;
                          const currentFirstSessionExtra = therapyType[`first_session_extra${suffix}`] || 0;
                          const currentSessions = therapyType[`sessions${suffix}`] || 1;
                          const currentFrequency = therapyType[`frequency${suffix}`] || 'Wöchentlich';
                          const freqOption = frequencyOptions.find(opt => opt.value === currentFrequency)
                            || frequencyOptions.find(opt => opt.value === 'Wöchentlich')
                            || frequencyOptions[0];
                          const freqClassName = FREQUENCY_CLASS_MAP[freqOption?.value] || '';

                          // Aufnahmegespräch: the row IS it (no session count, no frequency,
                          // pinned, no grip), or it books it first. The role tag only shows
                          // when the name does not already say "Aufnahmegespräch".
                          const isIntakeRow = therapyType.is_intake_appointment === true;
                          const showIntakeTag = isIntakeRow && !(therapyType.name || '').toLowerCase().includes('aufnahmegespräch');
                          const booksIntakeFirst = !isIntakeRow && !!therapyType.intake_therapy_type_id;
                          // Blankorezept: the group's one Blanko row. Same rules as the
                          // Aufnahmegespräch for grip, Duplizieren and the tag (only when the
                          // name does not say "Blanko"); edit and delete stay normal.
                          const isBlankoRow = therapyType.is_blanko === true;
                          const showBlankoTag = isBlankoRow && !(therapyType.name || '').toLowerCase().includes('blanko');
                          // Both presets share one look (.tt-row-preset) and stay in place
                          const isPresetRow = isIntakeRow || isBlankoRow;
                          // Ergänzendes Heilmittel: rendered in the group's supplementary sub-view
                          // (grip, Duplizieren and delete stay) whose one cell holds its phrase.
                          // The minutes shown are the insurance tab's: the same in every tab unless
                          // they were set per insurance, which the phrase then says.
                          const suppMinutesKey = `${therapyType.id}_supplementary_minutes`;
                          const suppMinutes = therapyType[`duration${suffix}`] ?? SUPPLEMENTARY_NEW_MINUTES;
                          // The Aufnahmegespräch lists what it is linked to in this group: the
                          // same therapies its dialog ticks.
                          const intakeLinkedNames = isIntakeRow
                            ? visibleTherapyTypes.filter(t => t.intake_therapy_type_id === therapyType.id).map(t => t.name)
                            : [];
                          // The PVS name the bot sends for the insurance tab above, as
                          // _resolve_send_pvs_name picks it: the insurance's own name when the
                          // therapy keeps one per insurance and it is filled, else the base name.
                          const basePvsName = (therapyType.pvs_name || '').trim();
                          const shownPvsName = (therapyType.pvs_name_per_insurance && (therapyType[`pvs_name${suffix}`] || '').trim()) || basePvsName;
                          // Blue only when this insurance really sends another name: switching "je
                          // Versicherungsart" on copies the base name into every empty cell.
                          const pvsIsOwn = shownPvsName !== basePvsName;
                          // On the Aufnahmegespräch the bin removes it from THIS group only, so
                          // the armed state is scoped to the group as well.
                          const deleteKey = isIntakeRow ? `intake-${therapyType.id}-${therapyCategoryTab}` : `therapy-${therapyType.id}`;
                          const deleteAction = isIntakeRow
                            ? () => handleRemoveIntakeFromGroup(therapyType.id, therapyCategoryTab)
                            : () => handleDeleteTherapyType(therapyType.id);

                          const nameKey = `${therapyType.id}_name`;
                          const descriptionKey = `${therapyType.id}_description`;
                          const durationKey = `${therapyType.id}_duration`;
                          const firstSessionExtraKey = `${therapyType.id}_first_session_extra`;
                          const sessionsKey = `${therapyType.id}_sessions`;
                          // Any inline editor open on THIS row hides its drag grip
                          const rowInlineEditing = (editingCell || '').startsWith(`${therapyType.id}_`)
                            || editingFrequenzId === therapyType.id;

                          return (
                            <tr
                              key={therapyType.id}
                              className={[rowDimClass, isPresetRow ? 'tt-row-preset' : '', draggingTherapyId === therapyType.id ? 'tt-row-dragging' : ''].filter(Boolean).join(' ')}
                              onMouseEnter={() => setHoveredTherapyId(therapyType.id)}
                              onMouseLeave={() => setHoveredTherapyId(prev => prev === therapyType.id ? null : prev)}
                            >
                              {/* Name — click-to-edit */}
                              <td className="tt-table-name-cell">
                                {editingCell === nameKey ? (
                                  <input
                                    autoFocus
                                    type="text"
                                    className="tt-cell-input"
                                    value={cellEditValue}
                                    onChange={e => setCellEditValue(e.target.value)}
                                    onBlur={() => {
                                      const trimmed = cellEditValue.trim();
                                      if (trimmed && trimmed !== therapyType.name) {
                                        handleSaveCellEdit(therapyType, { name: trimmed });
                                      }
                                      setEditingCell(null);
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') e.target.blur();
                                      if (e.key === 'Escape') { setEditingCell(null); }
                                    }}
                                  />
                                ) : (
                                  <div className="tt-name-cell">
                                    {/* Drag handle — the only draggable zone; the ghost is the whole row.
                                        Suppressed with the rest of the category controls when the
                                        categories API is unavailable, so no drag can start that the
                                        backend would silently drop. */}
                                    {!rowInlineEditing && categoriesApiAvailable && !isPresetRow && (
                                      <span
                                        className="tt-drag-grip"
                                        draggable="true"
                                        title="In andere Kategorie ziehen"
                                        onDragStart={e => {
                                          draggedTherapyIdRef.current = therapyType.id;
                                          setDraggingTherapyId(therapyType.id);
                                          e.dataTransfer.effectAllowed = 'move';
                                          e.dataTransfer.setData('text/plain', String(therapyType.id));
                                          const row = e.currentTarget.closest('tr');
                                          if (row) e.dataTransfer.setDragImage(row, 16, 16);
                                        }}
                                        onDragEnd={() => {
                                          draggedTherapyIdRef.current = null;
                                          setDraggingTherapyId(null);
                                        }}
                                      >
                                        <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
                                          <circle cx="3" cy="3" r="1.2"/><circle cx="7" cy="3" r="1.2"/>
                                          <circle cx="3" cy="7" r="1.2"/><circle cx="7" cy="7" r="1.2"/>
                                          <circle cx="3" cy="11" r="1.2"/><circle cx="7" cy="11" r="1.2"/>
                                        </svg>
                                      </span>
                                    )}
                                    <div className={`tt-name-cell-main${(showIntakeTag || showBlankoTag || booksIntakeFirst) ? ' has-marker' : ''}`}>
                                      <span
                                        className="tt-cell-text tt-cell-text-name"
                                        onClick={!allInsuranceDeactivated ? () => { setEditingCell(nameKey); setCellEditValue(therapyType.name); } : undefined}
                                        title={!allInsuranceDeactivated ? "Klicken zum Bearbeiten" : undefined}
                                      >
                                        {therapyType.name}
                                      </span>
                                      {showIntakeTag && (
                                        <span className="tt-intake-tag" title="Neue Patienten bekommen für die verknüpften Behandlungen zuerst diesen Termin.">
                                          {actionIcons.intake}
                                          Aufnahmegespräch
                                        </span>
                                      )}
                                      {showBlankoTag && (
                                        <span className="tt-intake-tag" title="Behandlungsart für Blankoverordnungen">
                                          {actionIcons.blanko}
                                          Blankorezept
                                        </span>
                                      )}
                                      {booksIntakeFirst && (
                                        <span className="tt-intake-tag tt-intake-tag--linked" title="Neue Patienten: zuerst Aufnahmegespräch">
                                          {actionIcons.intake}
                                        </span>
                                      )}
                                      {(therapyType.force_sessions_enabled || therapyType.force_frequency_enabled) && (
                                        <span
                                          style={{ marginLeft: 6, fontSize: '0.75rem', opacity: 0.6, cursor: 'help' }}
                                          title={`Fest vorgegeben: ${[
                                            therapyType.force_sessions_enabled && 'Anzahl',
                                            therapyType.force_frequency_enabled && 'Frequenz',
                                          ].filter(Boolean).join(' und ')}, die anrufende Person wird nicht danach gefragt.`}
                                        >
                                          🔒
                                        </span>
                                      )}
                                      {therapyType.aliases?.length > 0 && (
                                        <div className="tt-cell-aliases">{therapyType.aliases.join(', ')}</div>
                                      )}
                                      {intakeLinkedNames.length > 0 && (
                                        <div className="tt-cell-aliases tt-cell-intake-links" title={`Behandlungen mit Aufnahmegespräch: ${intakeLinkedNames.join(', ')}`}>
                                          {actionIcons.intake}
                                          {intakeLinkedNames.join(', ')}
                                        </div>
                                      )}
                                      {therapyType.symptoms?.length > 0 && (
                                        <div className="tt-cell-symptoms" title={therapyType.symptoms.join(', ')}>
                                          <span className="tt-cell-symptoms-label">Sympt.</span>
                                          <span className="tt-cell-symptoms-list">
                                            {therapyType.symptoms.slice(0, SYMPTOM_PREVIEW).join(' · ')}
                                          </span>
                                          {therapyType.symptoms.length > SYMPTOM_PREVIEW && (
                                            <span className="tt-cell-symptoms-more">+{therapyType.symptoms.length - SYMPTOM_PREVIEW}</span>
                                          )}
                                        </div>
                                      )}
                                      {shownPvsName && (
                                        <div
                                          className={`tt-cell-pvs${pvsIsOwn ? ' is-own' : ''}`}
                                          title={pvsIsOwn
                                            ? `PVS-Bezeichnung (${insuranceLabel(therapyInsuranceTab)}): ${shownPvsName}`
                                            : `PVS-Bezeichnung: ${shownPvsName}`}
                                        >
                                          <span className="tt-cell-pvs-label">PVS</span>
                                          <span className="tt-cell-pvs-name">{shownPvsName}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>

                              {/* Beschreibung — click-to-edit */}
                              <td className="tt-table-desc-cell">
                                {editingCell === descriptionKey ? (
                                  <input
                                    autoFocus
                                    type="text"
                                    className="tt-cell-input"
                                    value={cellEditValue}
                                    onChange={e => setCellEditValue(e.target.value)}
                                    onBlur={() => {
                                      const trimmed = cellEditValue.trim();
                                      if (trimmed !== (therapyType.description || '')) {
                                        handleSaveCellEdit(therapyType, { description: trimmed });
                                      }
                                      setEditingCell(null);
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') e.target.blur();
                                      if (e.key === 'Escape') { setEditingCell(null); }
                                    }}
                                  />
                                ) : (
                                  <span
                                    className={`tt-cell-description${!allInsuranceDeactivated ? ' tt-cell-description--editable' : ''}`}
                                    title={!allInsuranceDeactivated ? (therapyType.description || 'Klicken zum Bearbeiten') : therapyType.description}
                                    onClick={!allInsuranceDeactivated ? () => { setEditingCell(descriptionKey); setCellEditValue(therapyType.description || ''); } : undefined}
                                  >
                                    {therapyType.description || (!allInsuranceDeactivated ? <span style={{ color: '#bbb', fontStyle: 'italic' }}>Klicken zum Bearbeiten</span> : '')}
                                  </span>
                                )}
                              </td>

                              {/* An Ergänzendes Heilmittel has none of the next five columns
                                  (Akzeptiert to Frequenz): one cell holds its phrase, saved
                                  straight from here (an option on click, the minutes on blur or
                                  Enter). Name, aliases and Beschreibung are those of every row. */}
                              {isSupplementaryRow ? (
                                <td colSpan={5} className="tt-supp-cell">
                                  <SupplementaryPhrase
                                    mode={therapyType.supplementary_mode}
                                    onToggle={(next) => saveSupplementaryRow(therapyType, { supplementary_mode: next })}
                                    note={sameMinutesEverywhere(therapyType) ? null : insuranceLabel(therapyInsuranceTab)}
                                    groupLabel={`Beim Verschieben: ${therapyType.name}`}
                                    minutesProps={{
                                      value: editingCell === suppMinutesKey ? cellEditValue : String(suppMinutes),
                                      'aria-label': `Minuten ${therapyType.name}`,
                                      onFocus: (e) => { setEditingCell(suppMinutesKey); setCellEditValue(String(suppMinutes)); e.target.select(); },
                                      onChange: (e) => { setEditingCell(suppMinutesKey); setCellEditValue(e.target.value.replace(/\D/g, '').slice(0, 3)); },
                                      onBlur: () => {
                                        if (editingCell === suppMinutesKey) commitSupplementaryMinutes(therapyType, cellEditValue, suffix);
                                        setEditingCell(null);
                                      },
                                      onKeyDown: (e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                        if (e.key === 'Escape') setEditingCell(null);
                                      },
                                    }}
                                  />
                                </td>
                              ) : (
                              <>
                              {/* Akzeptiert — iOS switcher (consistent with task list grouping toggle) */}
                              <td className="tt-table-accepted-cell">
                                <label className="ios-switch" aria-label="Akzeptiert">
                                <input
                                  type="checkbox"
                                  checked={isAccepted}
                                  onChange={e => {
                                    const newAccepted = {
                                      ...(therapyType.accepted_insurance_types || DEFAULT_ACCEPTED_INSURANCE),
                                      [therapyInsuranceTab]: e.target.checked,
                                    };
                                    handleSaveAccepted(therapyType, newAccepted);
                                  }}
                                />
                                  <span className="ios-switch-slider" />
                                </label>
                              </td>

                              {renderNumericCell(durationKey, currentDuration, 30, 'duration', therapyType, suffix, isAccepted)}
                              {renderNumericCell(firstSessionExtraKey, currentFirstSessionExtra, 0, 'first_session_extra', therapyType, suffix, isAccepted, isIntakeRow)}
                              {renderNumericCell(sessionsKey, currentSessions, 1, 'sessions', therapyType, suffix, isAccepted, isIntakeRow)}

                              {/* Frequenz — pill dropdown (rendered via portal so it isn't clipped).
                                  An Aufnahmegespräch is a single appointment, so it has no
                                  interval to pick: the cell reads "-" and stays inert. */}
                              <td>
                                {isIntakeRow ? (
                                  <span className="tt-cell-na" title={INTAKE_CELL_NA_TITLE}>-</span>
                                ) : (
                                <FrequencyCell
                                  therapyType={therapyType}
                                  isAccepted={isAccepted}
                                  isOpen={editingFrequenzId === therapyType.id}
                                  toggleOpen={() => setEditingFrequenzId(prev => prev === therapyType.id ? null : therapyType.id)}
                                  closeOpen={() => setEditingFrequenzId(null)}
                                  currentFrequency={currentFrequency}
                                  freqClassName={freqClassName}
                                  freqOption={freqOption}
                                  frequencyOptions={frequencyOptions}
                                  freqClassMap={FREQUENCY_CLASS_MAP}
                                  suffix={suffix}
                                  handleSaveCellEdit={handleSaveCellEdit}
                                />
                                )}
                              </td>
                              </>
                              )}

                              {/* Actions — edit, duplicate, delete (Wirklich overlays the row, right-aligned) */}
                              <td className="tt-table-actions-cell" style={{ position: 'relative' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  <IconTooltipButton tooltip="Bearbeiten" className="icon-button tt-action-btn" onClick={() => handleOpenTherapyTypeModal(therapyType)}>
                                    {actionIcons.settings}
                                  </IconTooltipButton>
                                  {/* No Duplizieren on a preset: a practice has exactly one
                                      Aufnahmegespräch and a group one Blankorezept (a second is
                                      a 409). */}
                                  {!isPresetRow && (
                                    <IconTooltipButton tooltip="Duplizieren" className="icon-button tt-action-btn" onClick={() => handleDuplicateTherapyType(therapyType)}>
                                      {actionIcons.duplicate}
                                    </IconTooltipButton>
                                  )}
                                  {confirmingDeleteKey === deleteKey ? (
                                    <button
                                      type="button"
                                      className="tt-action-btn--confirm-delete"
                                      onClick={() => requestDelete(deleteKey, deleteAction)}
                                      title={isIntakeRow ? 'Aus dieser Gruppe entfernen' : 'Klick zum Bestätigen'}
                                    >
                                      Wirklich?
                                    </button>
                                  ) : (
                                    <IconTooltipButton
                                      tooltip={isIntakeRow ? 'Aus dieser Gruppe entfernen' : 'Löschen'}
                                      className="icon-button tt-action-btn tt-action-btn-delete"
                                      onClick={() => requestDelete(deleteKey, deleteAction)}
                                    >
                                      {actionIcons.delete}
                                    </IconTooltipButton>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="empty-state">
                    {supplementaryRows.length > 0
                      ? 'Noch keine Hauptbehandlungen in dieser Kategorie.'
                      : therapyTypes.length > 0
                        ? 'Noch keine Behandlungsarten in dieser Kategorie.'
                        : 'Noch keine Behandlungsarten definiert.'}
                  </p>
                )}
                  </>
                )}
                </div>{/* /.tt-cat-panel */}
                {/* The "+" (in the excluded folder it adds a nicht angebotene Behandlung) and,
                    for admins, the import group, which its own margin-left:auto pushes right. */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Create in the ACTIVE category — outside the scrollable table container.
                      The click no longer opens the modal straight away: a Behandlungsart can
                      be a normal one or an Aufnahmegespräch, and that fork is one small
                      popover instead of a switch buried in the full form. */}
                  <span ref={addPlusWrapRef} style={{ display: 'inline-flex' }}>
                    <button
                      type="button"
                      className="tt-footer-icon-btn"
                      disabled={formLoadedFor !== practiceId}
                      onClick={(e) => {
                        if (isExcludedView) { handleOpenExcludedForm(); return; }
                        if (addTherapyPopover) { setAddTherapyPopover(null); return; }
                        openAddPopover(e.currentTarget);
                      }}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      <span>Neue Behandlung hinzufügen</span>
                    </button>
                  </span>
                  {/* Anchored to the "+" through a portal: a button cannot contain a form
                      field, and the table's own scroll container would clip it. */}
                  {addTherapyPopover && ReactDOM.createPortal(
                    <div
                      ref={addPopoverRef}
                      className="tt-add-popover"
                      role="dialog"
                      aria-label="Behandlungsart hinzufügen"
                      style={
                        addTherapyPopover.openUp
                          ? { left: addTherapyPopover.left, bottom: window.innerHeight - addTherapyPopover.top + 8 }
                          : { left: addTherapyPopover.left, top: addTherapyPopover.bottom + 8 }
                      }
                    >
                      <div className="tt-add-popover-title">Welche Behandlungsart möchten Sie hinzufügen?</div>
                      <div className="tt-add-popover-row">
                        <input
                          autoFocus
                          type="text"
                          className="form-input"
                          placeholder="Name der Behandlungsart"
                          value={addTherapyName}
                          onChange={(e) => setAddTherapyName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (addTherapyName.trim()) handleOpenTherapyTypeModal(null, { name: addTherapyName.trim() });
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="button"
                          disabled={!addTherapyName.trim()}
                          onClick={() => handleOpenTherapyTypeModal(null, { name: addTherapyName.trim() })}
                        >
                          Weiter
                        </button>
                      </div>
                      <div className="tt-add-popover-or">oder</div>
                      {/* The presets, one per line. Each one's "Öffnen" state is its own. */}
                      <div className="tt-cat-chip-stack">
                      {/* The practice has ONE Aufnahmegespräch: created when there is none, opened
                          to add this group when it is elsewhere. */}
                      {renderPresetChip('intakeHere', intakeListsGroup(therapyCategoryTab) ? intakeRow : null, actionIcons.intake, 'Aufnahmegespräch',
                        () => handleOpenTherapyTypeModal(intakeRow, intakeRow ? null : { name: 'Aufnahmegespräch', is_intake_appointment: true }))}
                      {/* ONE Blankorezept per group, offered in every group. */}
                      {blankoApiAvailable && renderPresetChip('blankoHere', tabBlankoRow, actionIcons.blanko, 'Blankorezept',
                        () => handleOpenTherapyTypeModal(null, { is_blanko: true }))}
                      {/* Several per group, so no row to open: it always creates, and keeps a
                          name already typed above. A new one starts with "Termin verlängern". */}
                      {supplementaryApiAvailable && renderPresetChip(null, null, actionIcons.supplementary, 'Ergänzendes Heilmittel',
                        () => handleOpenTherapyTypeModal(null, { name: addTherapyName.trim(), supplementary_mode: 'add_time' }))}
                      </div>
                    </div>,
                    document.body
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".xlsx,.xls"
                    onChange={handleFileSelected}
                  />
                  {/* File / PVS bulk-import are administrative actions — hidden from non-admins. */}
                  {userIsAdmin && !isExcludedView && (
                    <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                      <button
                        className="button-secondary button-secondary--lg"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Aus Datei importieren
                      </button>
                      <button
                        className="button-secondary button-secondary--lg"
                        onClick={handlePvsImport}
                        disabled={!pvsEnabled || pvsImportLoading}
                        title={!pvsEnabled ? 'PVS-Integration ist nicht aktiviert' : ''}
                      >
                        {pvsImportLoading ? 'Importiere...' : 'Vom PVS importieren'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Category dialog — add / edit (rename, merge, delete) / dropNew in ONE modal.
                    The merge and the delete-with-move confirmations swap the body in place.
                    Width is the CONTENT's, not a defensive constant: max-content wraps the
                    modal around its widest line (the longest stacked pill, or the edit
                    title next to the ×) and the cap only wraps a long hint sentence or the
                    merge title. 340, not 320: „Heilmittelgruppe bearbeiten“ plus the × needs
                    ~282px, which 320 minus padding and border (278) wraps on some system
                    fonts. The customModalStyles minWidth of 560 has to be cleared for that. */}
                <Modal
                  isOpen={!!categoryDialog}
                  onRequestClose={() => setCategoryDialog(null)}
                  style={{
                    ...customModalStyles,
                    content: { ...customModalStyles.content, minWidth: 0, width: 'max-content', maxWidth: 'min(340px, 90vw)' },
                  }}
                  contentLabel="Kategorie"
                >
                  {categoryDialog && (() => {
                    const newOptions = categoryOptions.filter(o => !therapyCategories.includes(o.value));
                    const pickMoveTo = (v) => setCategoryDialog(prev => (prev ? { ...prev, moveTo: v } : prev));
                    // Mark every option that is not a tab yet, so the dashed pill carries
                    // the "will be created" signal in all four lists without a heading.
                    const asOption = (o) => ({ ...o, isNew: !therapyCategories.includes(o.value) });

                    let title = '';
                    let hint = '';
                    let body = null;
                    let primaryLabel = '';
                    let primaryAction = () => {};
                    let primaryDisabled = categoryBusy;
                    let deleteLink = null;

                    if (categoryDialog.mode === 'add' || categoryDialog.mode === 'dropNew') {
                      const isDrop = categoryDialog.mode === 'dropNew';
                      const dropTherapy = therapyTypes.find(tt => tt.id === categoryDialog.therapyId);
                      title = 'Kategorie hinzufügen';
                      hint = isDrop ? `„${dropTherapy?.name || ''}“ wird dorthin verschoben.` : '';
                      // One click IS the action, like the rename list: adding a category and
                      // moving one therapy into a new one are both cheap and both undone in
                      // the same dialog, so nothing is ever "picked" here and there is no
                      // confirm button to reach afterwards.
                      body = (
                        <CategoryOptionList
                          options={newOptions.map(asOption)}
                          value={null}
                          disabled={categoryBusy || (isDrop && !dropTherapy)}
                          onChange={(v) => (isDrop
                            ? handleMoveTherapyToCategory(dropTherapy, v)
                            : handleAddCategory(v))}
                        />
                      );
                    } else {
                      const key = categoryDialog.key;
                      const count = categoryCounts[key] ?? 0;
                      // A bubble that already IS a tab folds the two together, so its click
                      // writes `mergeTarget` and this body swaps to the confirmation instead
                      // of renaming. The backend 409 stays the contract and lands in the same
                      // body after a concurrent rename. Either way the rows that move are the
                      // SOURCE category's rows, which is `count`.
                      const mergeTarget = categoryDialog.mergeTarget || null;
                      // Every other existing group in rail order: the edit state's merge
                      // targets and the first destinations of delete-with-move.
                      const otherGroups = therapyCategories
                        .filter(k => k !== key)
                        .map(k => ({ value: k, display: categoryLabel(k) }));

                      title = 'Heilmittelgruppe bearbeiten';

                      if (categoryDialog.deleting) {
                        const moveTo = categoryDialog.moveTo
                          || otherGroups[0]?.value
                          || newOptions[0]?.value
                          || '';
                        title = 'Heilmittelgruppe löschen';
                        // Say where the rows come FROM and that they go INTO the pick.
                        hint = count === 1
                          ? `1 Behandlungsart aus „${categoryLabel(key)}“ verschieben nach:`
                          : `${count} Behandlungsarten aus „${categoryLabel(key)}“ verschieben nach:`;
                        // Existing categories first, then the ones that would be created —
                        // ordering plus the dashed pill replaces the old section heading.
                        body = (
                          <CategoryOptionList
                            options={[...otherGroups, ...newOptions].map(asOption)}
                            value={moveTo}
                            onChange={pickMoveTo}
                            disabled={categoryBusy}
                          />
                        );
                        primaryLabel = 'Verschieben und löschen';
                        primaryAction = () => handleDeleteCategory(key, moveTo);
                        primaryDisabled = categoryBusy || !moveTo;
                      } else if (mergeTarget) {
                        title = 'Heilmittelgruppen zusammenführen';
                        hint = count === 0
                          ? `„${categoryLabel(key)}“ ist leer und entfällt.`
                          : count === 1
                            ? `1 Behandlungsart wandert nach „${categoryLabel(mergeTarget)}“.`
                            : `${count} Behandlungsarten wandern nach „${categoryLabel(mergeTarget)}“.`;
                        primaryLabel = 'Zusammenführen';
                        primaryAction = () => handleRekeyCategory(key, mergeTarget, true);
                      } else {
                        // One click IS the action, no select-then-confirm: neither list
                        // takes a `value` and this state has no confirm button. Three
                        // labelled sections say what a click does. On top the group being
                        // edited, as a statement (not a button). Then the other tabs in
                        // rail order: a click there dissolves this group into that one, so
                        // it steps to the merge confirmation first. Then the names that are
                        // not a tab yet, in option order, which rename on the spot. Both
                        // lists share the one routing below, and a section with nothing to
                        // offer is left out (one group: no merge; all five: no rename).
                        const pickEditTarget = (v) => (therapyCategories.includes(v)
                          ? setCategoryDialog(prev => (prev ? { ...prev, mergeTarget: v } : prev))
                          : handleRekeyCategory(key, v));
                        // Not redundant: an optimistic rename/delete takes `key` out of
                        // therapyCategories while this body is still on screen.
                        const renameOptions = newOptions.filter(o => o.value !== key);
                        body = (
                          <>
                            <div className="tt-cat-dialog-section">
                              <span className="pf-group-label">Aktuell</span>
                              <div className="tt-cat-chip tt-cat-chip--block tt-cat-chip--current is-selected">
                                {categoryLabel(key)}
                              </div>
                            </div>
                            {otherGroups.length > 0 && (
                              <div className="tt-cat-dialog-section">
                                <span className="pf-group-label">Zusammenführen mit</span>
                                <CategoryOptionList
                                  options={otherGroups.map(asOption)}
                                  value={null}
                                  disabled={categoryBusy}
                                  onChange={pickEditTarget}
                                />
                              </div>
                            )}
                            {renameOptions.length > 0 && (
                              <div className="tt-cat-dialog-section">
                                <span className="pf-group-label">Umbenennen in</span>
                                <CategoryOptionList
                                  options={renameOptions.map(asOption)}
                                  value={null}
                                  disabled={categoryBusy}
                                  onChange={pickEditTarget}
                                />
                              </div>
                            )}
                          </>
                        );
                      }

                      // Never offer to delete the last category (the backend 409s on it anyway),
                      // and never while the merge confirmation is on screen: that step is a
                      // yes/no on folding two categories together, nothing else.
                      if (!categoryDialog.deleting && !mergeTarget && therapyCategories.length > 1) {
                        // Bin plus word. The two-click arming rewords the button itself
                        // into the question and turns it destructive-red (is-armed), so no
                        // tooltip has to carry the confirmation any more.
                        const confirming = confirmingDeleteKey === `category-${key}`;
                        const deleteLabel = confirming ? 'Wirklich löschen?' : 'Löschen';
                        deleteLink = (
                          <button
                            type="button"
                            className={`icon-button tt-action-btn tt-cat-dialog-danger${confirming ? ' is-armed' : ''}`}
                            disabled={categoryBusy}
                            onClick={() => {
                              // Rows present: pick a destination first. Empty: two-click confirm.
                              if (count > 0) {
                                setCategoryDialog(prev => (prev ? { ...prev, deleting: true, error: null } : prev));
                                return;
                              }
                              requestDelete(`category-${key}`, () => handleDeleteCategory(key));
                            }}
                          >
                            {actionIcons.trash}
                            {deleteLabel}
                          </button>
                        );
                      }
                    }

                    return (
                      <>
                        {/* Title left, close top right: a bare ×. */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                          <h2 style={{ margin: 0, fontSize: '1.1rem', lineHeight: 1.3 }}>{title}</h2>
                          <button
                            type="button"
                            onClick={() => setCategoryDialog(null)}
                            style={{ background: 'none', border: 'none', fontSize: '1.4rem', lineHeight: 1, cursor: 'pointer', color: 'var(--text-secondary)' }}
                            title="Schließen"
                            aria-label="Schließen"
                          >
                            ×
                          </button>
                        </div>
                        {hint && (
                          <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{hint}</p>
                        )}
                        {body}
                        {categoryDialog.error && (
                          <p style={{ margin: '14px 0 0 0', fontSize: '0.85rem', color: 'var(--color-tertiary)' }}>
                            {categoryDialog.error}
                          </p>
                        )}
                        {/* Footer only where something still has to be decided: the bin for
                            the edit state, and the check for the two states that undo
                            nothing on their own — merging two categories and deleting one
                            with its rows. Adding a category and moving a therapy into a new
                            one apply on the click and have no footer at all. */}
                        {(deleteLink || primaryLabel) && (
                          <div className="tt-cat-dialog-actions">
                            <div>{deleteLink}</div>
                            {primaryLabel && (
                              <button
                                type="button"
                                className="icon-button tt-action-btn tt-cat-dialog-confirm"
                                onClick={primaryAction}
                                disabled={primaryDisabled}
                              >
                                {actionIcons.check}
                                {primaryLabel}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </Modal>
              </div>
            )}

            {/* Zuordnungen — Therapeuten & Standorte in one tabbed card */}
            {practiceId && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'var(--h2-margin-bottom, 16px)' }}>
                  <h2 className="h2" style={{ margin: 0 }}>Therapeuten und Behandlungszuordnungen</h2>
                  <button
                    type="button"
                    className="shortcuts-help-btn"
                    data-tooltip="Tastenkürzel anzeigen (?)"
                    aria-label="Tastenkürzel anzeigen"
                    onClick={() => setShowShortcutHelp(true)}
                  >
                    ?
                  </button>
                  {zuordnungTab === 'therapists' && (
                    <div className="add-therapist-inline" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {/* PVS roster sync is an administrative action — hidden from non-admins. */}
                      {userIsAdmin && (
                        <>
                          {pvsImportResult && (
                            <span className="pvs-sync-result" role="status">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              {pvsImportResult}
                            </span>
                          )}
                          <button
                            type="button"
                            className={`pvs-sync-btn${importingPvsTherapists ? ' is-loading' : ''}`}
                            onClick={handleImportTherapistsFromPvs}
                            disabled={importingPvsTherapists}
                            title="Therapeutenliste aus dem angebundenen PVS übernehmen"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="23 4 23 10 17 10" />
                              <polyline points="1 20 1 14 7 14" />
                              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                            {importingPvsTherapists ? 'Lädt…' : 'Aus PVS laden'}
                          </button>
                          <span className="add-therapist-divider" aria-hidden="true" />
                        </>
                      )}
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Neuen Therapeuten hinzufügen..."
                        value={newTherapistName}
                        onChange={(e) => setNewTherapistName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleQuickAddTherapist()}
                        disabled={addingTherapist}
                        style={{ width: '260px', alignSelf: 'stretch' }}
                      />
                      <button
                        className="button"
                        onClick={handleQuickAddTherapist}
                        disabled={addingTherapist || !newTherapistName.trim()}
                        style={{ padding: '0 18px', fontSize: '0.95rem', lineHeight: 1, height: 'auto', alignSelf: 'stretch' }}
                      >
                        {addingTherapist ? '...' : '+'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="zuordnung-header-row">
                  <div className="zuordnung-legend">
                    {zuordnungTab === 'therapists' ? (
                      <>
                        <span className="zuordnung-legend-title">Priorität</span>
                        <span className="priority-chip priority-1">1</span>
                        <span className="zuordnung-legend-label">Hoch</span>
                        <span className="priority-chip priority-2">2</span>
                        <span className="zuordnung-legend-label">Mittel</span>
                        <span className="priority-chip priority-3">3</span>
                        <span className="zuordnung-legend-label">Niedrig</span>
                        <span className="priority-chip priority-0">–</span>
                        <span className="zuordnung-legend-label">Keine Zuordnung</span>
                        <span className="priority-chip priority-x">X</span>
                        <span className="zuordnung-legend-label">Nie buchen oder verschieben</span>
                      </>
                    ) : zuordnungTab === 'locations' ? (
                      <span className="zuordnung-legend-text">Ordnen Sie jedem Standort die dort angebotenen Behandlungsarten zu.</span>
                    ) : (
                      <span className="zuordnung-legend-text">Ordnen Sie jedem Therapeuten die Standorte zu, an denen er/sie arbeitet.</span>
                    )}
                  </div>
                  {enableMultiLocation && (
                    <div className="tt-table-tabs">
                      <button
                        className={`tt-table-tab${zuordnungTab === 'therapists' ? ' active' : ''}`}
                        onClick={() => { setZuordnungTab('therapists'); setEditingCell(null); }}
                      >
                        Therapeuten
                      </button>
                      <button
                        className={`tt-table-tab${zuordnungTab === 'locations' ? ' active' : ''}`}
                        onClick={() => { setZuordnungTab('locations'); setEditingCell(null); }}
                      >
                        Standorte
                      </button>
                      <button
                        className={`tt-table-tab${zuordnungTab === 'therapist-locations' ? ' active' : ''}`}
                        onClick={() => { setZuordnungTab('therapist-locations'); setEditingCell(null); }}
                      >
                        Therapeuten &amp; Standorte
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Therapeuten tab ── */}
                {zuordnungTab === 'therapists' && (
                  <>
                    {matrixLoading ? (
                      <p>Lade Matrix...</p>
                    ) : priorityMatrix && priorityMatrix.therapists.length > 0 ? (
                      <div className="tt-table-container">
                        <table className="tt-table tt-table--matrix">
                          <thead>
                            <tr>
                              <th className="tt-table-name-col">Behandlung</th>
                              {priorityMatrix.therapists.map(therapist => {
                                const nameKey = `therapist_${therapist.id}_name`;
                                const isEditingName = editingCell === nameKey;
                                const therapistPvsName = (therapist.pvs_name || '').trim();
                                return (
                                  <th key={therapist.id} className="tt-table-therapy-col">
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}>
                                      {isEditingName ? (
                                        <input
                                          type="text"
                                          className="tt-cell-input"
                                          style={{ width: 'auto', minWidth: 70, maxWidth: '100%', flex: '0 0 auto' }}
                                          value={cellEditValue}
                                          onChange={(e) => setCellEditValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveTherapistName(therapist.id, cellEditValue);
                                            if (e.key === 'Escape') { setEditingCell(null); setCellEditValue(''); }
                                          }}
                                          onBlur={() => handleSaveTherapistName(therapist.id, cellEditValue)}
                                          autoFocus
                                        />
                                      ) : (
                                        <span
                                          className="tt-cell-text tt-cell-text-name"
                                          style={{ whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'normal', textAlign: 'center', lineHeight: 1.2 }}
                                          onClick={() => { setEditingCell(nameKey); setCellEditValue(therapist.name); }}
                                        >
                                          {therapist.name}
                                        </span>
                                      )}
                                      {/* The Behandlungsarten table's PVS line, under the name. It stays
                                          while the name is being renamed, so only the name swaps for
                                          its input. */}
                                      {therapistPvsName && (
                                        <div className="tt-cell-pvs" title={`PVS-Bezeichnung: ${therapistPvsName}`}>
                                          <span className="tt-cell-pvs-label">PVS</span>
                                          <span className="tt-cell-pvs-name">{therapistPvsName}</span>
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', gap: '2px', flexShrink: 0, justifyContent: 'center' }}>
                                        <IconTooltipButton
                                          tooltip="Bearbeiten"
                                          className="icon-button tt-action-btn"
                                          onClick={() => handleOpenTherapistModal(therapist)}
                                        >
                                          {actionIcons.settings}
                                        </IconTooltipButton>
                                        {confirmingDeleteKey === `therapist-${therapist.id}` ? (
                                          <button
                                            type="button"
                                            className="tt-action-btn--confirm-delete"
                                            onClick={() => requestDelete(`therapist-${therapist.id}`, () => handleDeleteTherapistFromMatrix(therapist.id))}
                                            title="Klick zum Bestätigen"
                                          >
                                            Wirklich?
                                          </button>
                                        ) : (
                                          <IconTooltipButton
                                            tooltip="Löschen"
                                            className="icon-button tt-action-btn tt-action-btn-delete"
                                            onClick={() => requestDelete(`therapist-${therapist.id}`, () => handleDeleteTherapistFromMatrix(therapist.id))}
                                          >
                                            {actionIcons.delete}
                                          </IconTooltipButton>
                                        )}
                                      </div>
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {priorityMatrix.therapy_types.map(tt => (
                              <tr key={tt.id}>
                                <td className="tt-table-name-cell">
                                  <span className="tt-cell-text tt-cell-text-name" style={{ cursor: 'default' }}>{tt.name}</span>
                                </td>
                                {priorityMatrix.therapists.map(therapist => {
                                  const prio = priorityMatrix.matrix[therapist.id]?.[tt.id] || 0;
                                  const dropdownKey = `${therapist.id}-${tt.id}`;
                                  const isOpen = openPriorityDropdown === dropdownKey;
                                  return (
                                    <td key={therapist.id}>
                                      <PriorityCell
                                        prio={prio}
                                        isOpen={isOpen}
                                        toggleOpen={() => setOpenPriorityDropdown(isOpen ? null : dropdownKey)}
                                        closeOpen={() => setOpenPriorityDropdown(null)}
                                        onSelect={(val) => { handlePriorityChange(therapist.id, tt.id, val); setOpenPriorityDropdown(null); }}
                                        onMouseEnter={() => setHoveredPriorityCell({ therapistId: therapist.id, therapyTypeId: tt.id })}
                                        onMouseLeave={() => setHoveredPriorityCell(prev => (prev && prev.therapistId === therapist.id && prev.therapyTypeId === tt.id) ? null : prev)}
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div>
                        <p className="empty-state">Noch keine Therapeuten hinzugefügt.</p>
                        <div className="add-therapist-inline" style={{ marginTop: '16px' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Ersten Therapeuten hinzufügen..."
                            value={newTherapistName}
                            onChange={(e) => setNewTherapistName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleQuickAddTherapist()}
                            disabled={addingTherapist}
                            style={{ flex: 1, marginRight: '8px', maxWidth: '300px' }}
                          />
                          <button
                            className="button"
                            onClick={handleQuickAddTherapist}
                            disabled={addingTherapist || !newTherapistName.trim()}
                            style={{ padding: '8px 16px' }}
                          >
                            {addingTherapist ? '...' : '+'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ── Standorte tab ── */}
                {zuordnungTab === 'locations' && (
                  <>
                    {locationMatrixLoading ? (
                      <p>Lade Matrix...</p>
                    ) : locationMatrix && (locationMatrix.locations.length > 0 || locationMatrix.therapy_types.length > 0) ? (
                      <>
                        {locationMatrix.therapy_types.length === 0 && (
                          <p className="section-description" style={{ marginTop: 0, marginBottom: 12, color: '#666', fontStyle: 'italic' }}>
                            Tipp: Behandlungsarten anlegen, um Standorten zuzuordnen, welche Behandlungen dort angeboten werden.
                          </p>
                        )}
                      <div className="tt-table-container">
                        <table className="tt-table tt-table--matrix">
                          <thead>
                            <tr>
                              <th className="tt-table-name-col">Standort</th>
                              <th className="tt-table-actions-col">Aktionen</th>
                              {locationMatrix.therapy_types.map(tt => (
                                <th key={tt.id} className="tt-table-therapy-col">{tt.name}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {locationMatrix.locations.map(location => {
                              const nameKey = `location_${location.id}_name`;
                              const isEditingName = editingCell === nameKey;
                              return (
                                <tr key={location.id}>
                                  <td>
                                    <div>
                                      {isEditingName ? (
                                        <input
                                          type="text"
                                          className="tt-cell-input"
                                          value={cellEditValue}
                                          onChange={(e) => setCellEditValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveLocationField(location.id, { name: cellEditValue });
                                            if (e.key === 'Escape') { setEditingCell(null); setCellEditValue(''); }
                                          }}
                                          onBlur={() => handleSaveLocationField(location.id, { name: cellEditValue })}
                                          autoFocus
                                        />
                                      ) : (
                                        <span
                                          className="tt-cell-text tt-cell-text-name"
                                          onClick={() => { setEditingCell(nameKey); setCellEditValue(location.name); }}
                                        >
                                          {location.name}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                      <button
                                        className="icon-button tt-action-btn"
                                        onClick={() => handleOpenLocationModal(location)}
                                        title="Weitere Details bearbeiten"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                                      >
                                        {actionIcons.settings}
                                      </button>
                                      {confirmingDeleteKey === `location-${location.id}` ? (
                                        <button
                                          className="icon-button tt-action-btn tt-action-btn--confirm-delete"
                                          onClick={() => requestDelete(`location-${location.id}`, () => handleDeleteLocation(location.id))}
                                          title="Klick zum Bestätigen"
                                        >
                                          Wirklich?
                                        </button>
                                      ) : (
                                        <button
                                          className="icon-button"
                                          onClick={() => requestDelete(`location-${location.id}`, () => handleDeleteLocation(location.id))}
                                          title="Standort löschen"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                                        >
                                          {actionIcons.delete}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  {locationMatrix.therapy_types.map(tt => (
                                    <td key={tt.id}>
                                      <input
                                        type="checkbox"
                                        checked={locationMatrix.matrix[location.id]?.[tt.id] || false}
                                        onChange={(e) => handleLocationToggle(location.id, tt.id, e.target.checked)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                            <tr>
                              <td colSpan={locationMatrix.therapy_types.length + 2}>
                                <div className="add-therapist-inline">
                                  <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Neuen Standort hinzufügen..."
                                    value={newLocationName}
                                    onChange={(e) => setNewLocationName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleQuickAddLocation()}
                                    disabled={addingLocation}
                                    style={{ flex: 1, marginRight: '8px' }}
                                  />
                                  <button
                                    className="button"
                                    onClick={handleQuickAddLocation}
                                    disabled={addingLocation || !newLocationName.trim()}
                                    style={{ padding: '8px 16px' }}
                                  >
                                    {addingLocation ? '...' : '+'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      </>
                    ) : locationMatrix && locationMatrix.therapy_types.length === 0 ? (
                      <p className="empty-state">Bitte erstellen Sie zuerst Behandlungsarten, um Standorte zuordnen zu können.</p>
                    ) : (
                      <div>
                        <p className="empty-state">Noch keine Standorte hinzugefügt.</p>
                        <div className="add-therapist-inline" style={{ marginTop: '16px' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Ersten Standort hinzufügen..."
                            value={newLocationName}
                            onChange={(e) => setNewLocationName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleQuickAddLocation()}
                            disabled={addingLocation}
                            style={{ flex: 1, marginRight: '8px', maxWidth: '300px' }}
                          />
                          <button
                            className="button"
                            onClick={handleQuickAddLocation}
                            disabled={addingLocation || !newLocationName.trim()}
                            style={{ padding: '8px 16px' }}
                          >
                            {addingLocation ? '...' : '+'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ── Therapeuten & Standorte tab ── */}
                {zuordnungTab === 'therapist-locations' && (
                  <>
                    {therapistLocationMatrixLoading ? (
                      <p>Lade Matrix...</p>
                    ) : therapistLocationMatrix && therapistLocationMatrix.locations.length > 0 && therapistLocationMatrix.therapists.length > 0 ? (
                      <div className="tt-table-container">
                        <table className="tt-table tt-table--matrix">
                          <thead>
                            <tr>
                              <th className="tt-table-name-col">Therapeut</th>
                              {therapistLocationMatrix.locations.map(loc => (
                                <th key={loc.id} className="tt-table-therapy-col">{loc.name}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {therapistLocationMatrix.therapists.map(therapist => (
                              <tr key={therapist.id}>
                                <td>{therapist.name}</td>
                                {therapistLocationMatrix.locations.map(loc => (
                                  <td key={loc.id}>
                                    <input
                                      type="checkbox"
                                      checked={therapistLocationMatrix.matrix[therapist.id]?.[loc.id] || false}
                                      onChange={(e) => handleTherapistLocationToggle(therapist.id, loc.id, e.target.checked)}
                                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="empty-state">
                        {(!therapistLocationMatrix?.therapists?.length)
                          ? 'Bitte erstellen Sie zuerst Therapeuten im Tab "Therapeuten".'
                          : 'Bitte erstellen Sie zuerst Standorte im Tab "Standorte".'}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Therapy Type Modal — explicit style object (no inheritance from
                customModalStyles which has minWidth:560 + marginRight:-50% that
                broke the centering with a wide width). The width is not a number
                chosen here either: --tt-modal-w (App.css, "BEHANDLUNGSART BEARBEITEN")
                is the insurance matrix's own width plus this padding, so the dialog is
                exactly as wide as the table inside it has to be and no wider.

                Same language as the "Therapeut bearbeiten" dialog: one label/value grid
                per section, a short label on the left, the control on the right, and every
                explanation in the InfoTooltip beside its label instead of a sentence under
                the field. Three sections, in the order a practice thinks about a therapy:
                what it is (Stammdaten), what it costs in time and sessions per insurance
                type (the matrix), and how a caller can name it (Erkennung).

                The matrix stays a table because it IS tabular — five insurance types by
                four values. What went is the noise around it: five header switches became
                the same struck-through chips the therapist dialog uses for an excluded
                insurance type, and the three full-width switch strips that used to cut the
                table into chunks are gone. A boolean that modifies exactly ONE row now
                rides on that row's label ("fest"), a boolean about the whole therapy is a
                switch row of its own above the table. */}
            <Modal
              isOpen={therapyTypeModalOpen}
              onRequestClose={() => setTherapyTypeModalOpen(false)}
              style={{
                overlay: {
                  zIndex: 2000,
                  background: 'rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                content: {
                  position: 'static',
                  inset: 'auto',
                  width: '90vw',
                  // An Ergänzendes Heilmittel has no insurance matrix to make room for
                  maxWidth: isSupplementaryForm ? '640px' : 'var(--tt-modal-w)',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  padding: '24px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  margin: 0,
                  transform: 'none',
                },
              }}
              contentLabel={editingTherapyType ? "Behandlungsart bearbeiten" : "Behandlungsart hinzufügen"}
            >
              {/* The form is missing half its rows in intake mode, so the heading says
                  why. The flex style comes with the tag: without it the heading has to
                  render exactly as it always did. A Blankorezept gets the same kind of tag
                  (no Kategorie, no Doppeltermine, both "fest" chips locked). */}
              <h2 style={(isIntakeForm || isBlankoForm || isSupplementaryForm) ? { display: 'flex', alignItems: 'center', gap: 4 } : undefined}>
                {editingTherapyType ? "Behandlungsart bearbeiten" : "Behandlungsart hinzufügen"}
                {isIntakeForm && (
                  <span className="tt-intake-tag">{actionIcons.intake}Aufnahmegespräch</span>
                )}
                {isBlankoForm && (
                  <span className="tt-intake-tag">{actionIcons.blanko}Blankorezept</span>
                )}
                {isSupplementaryForm && (
                  <span className="tt-intake-tag">{actionIcons.supplementary}Ergänzendes Heilmittel</span>
                )}
              </h2>
              {/* One row in every group it is shown in: everything above the chip list
                  (name, PVS-Bezeichnung, Versicherungsarten, Dauer, Beschreibung) is shared. */}
              {isIntakeForm && (
                <p className="tt-intake-hint tt-intake-hint--lead">
                  Gilt in allen Gruppen, in denen das Aufnahmegespräch hinzugefügt ist.
                </p>
              )}
              {/* An Ergänzendes Heilmittel keeps its name, PVS-Bezeichnung, Beschreibung,
                  phrase and aliases. The subtitle says why booking does not offer it yet. */}
              {isSupplementaryForm && (
                <p className="tt-intake-hint tt-intake-hint--lead">{SUPPLEMENTARY_NOTE} berücksichtigt.</p>
              )}
              <div style={{ marginTop: '20px' }}>

                {/* ---- Stammdaten: what this therapy is ----
                    Two label/value pairs per row: a therapy name does not need the
                    width of the matrix below it, and Name / PVS-Bezeichnung sat side
                    by side here before this dialog was rebuilt. Same grid language,
                    twice per row. Only the Beschreibung keeps the full width. The far
                    shorter form of an Ergänzendes Heilmittel keeps one pair per row. */}
                <div className={`pf-grid ${isSupplementaryForm ? 'pf-grid--form tt-supp-form' : 'pf-grid--pairs'}`}>

                  <span className="pf-row-label">Name *</span>
                  <input
                    autoFocus
                    type="text"
                    className="form-input pf-input"
                    value={therapyTypeForm.name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTherapyTypeForm(prev => ({ ...prev, name: val }));
                    }}
                    required
                  />

                  {/* Umbrella category — saving with a changed value moves the therapy.
                      Hidden while the categories API is unavailable (old backend during a
                      deploy window): that backend drops the unknown field and answers 200,
                      so offering the control would silently discard the move. Also hidden
                      on a Blankorezept: it belongs to its group (the server keeps it there).
                      And on an Ergänzendes Heilmittel, whose short form has no group choice
                      (its row's grip still moves it to another group). */}
                  {categoriesApiAvailable && !isIntakeForm && !isBlankoForm && !isSupplementaryForm && (
                    <>
                      <span className="pf-row-label">Kategorie</span>
                      <div className="pf-field">
                        <CustomDropdown
                          size="sm"
                          minWidth="260px"
                          value={therapyTypeForm.category}
                          onChange={(v) => setTherapyTypeForm(prev => ({ ...prev, category: v }))}
                          options={therapyCategories.map(key => ({ value: key, label: categoryLabel(key) }))}
                        />
                      </div>
                    </>
                  )}

                  {!isIntakeForm && (
                    <>
                      <span className="pf-row-label pf-row-start">
                        Aliase
                        <InfoTooltip text="Weitere Bezeichnungen, unter denen die Anrufer diese Behandlung nennen könnten (z.B. 'MLD 30', 'Lymphdrainage 30 Minuten'). Mit Komma oder Enter bestätigen." />
                      </span>
                      <div className="pf-field pf-field--chips pf-value-wide">
                        <FwdChipBox
                          values={therapyTypeForm.aliases || []}
                          onChange={(next) => setTherapyTypeForm(prev => ({ ...prev, aliases: next }))}
                          draft={aliasDraft}
                          setDraft={setAliasDraft}
                          removeLabel="Alias entfernen"
                          addLabel="Alias eingeben"
                          placeholder="Alias eingeben"
                        />
                      </div>
                    </>
                  )}

                  {/* The per-insurance master toggle sits ON this row, beside the name it
                      overrides: switching it on adds a PVS-Bezeichnung row to the matrix
                      below and turns this field into the fallback, which the dimming says.
                      One PVS article per chip; the form keeps ONE string ("A, B, C"), so
                      payloads, imports and Duplizieren are unchanged. */}
                  <span className="pf-row-label pf-row-start">
                    PVS-Bezeichnung
                    {!isSupplementaryForm && (
                    <InfoTooltip text="Artikelbezeichnung im PVS, falls PVS angeschlossen wird. Mehrere Bezeichnungen mit Komma oder Enter bestätigen, Reihenfolge und Wiederholungen bleiben erhalten. Mit 'je Versicherungsart' bekommt jede Versicherungsart im Raster unten eine eigene Bezeichnung; leere Felder nutzen dann diese hier." />
                    )}
                  </span>
                  {/* An Ergänzendes Heilmittel has at most ONE PVS article (the server refuses
                      a second; none is allowed), and the move finds it by that name alone: one plain field
                      with an example. Its own names per insurance sit under "Je
                      Versicherungsart" below, so the chip next to this field is not shown. */}
                  {isSupplementaryForm ? (
                    <input
                      type="text"
                      className="form-input pf-input tt-supp-pvs"
                      aria-label="PVS-Bezeichnung"
                      placeholder="z.B. HR"
                      value={therapyTypeForm.pvs_name || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTherapyTypeForm(prev => ({ ...prev, pvs_name: val }));
                      }}
                    />
                  ) : (
                  <div
                    className="pf-field pf-field--chips"
                    style={{ opacity: therapyTypeForm.pvs_name_per_insurance ? 0.6 : 1 }}
                    title={therapyTypeForm.pvs_name_per_insurance ? 'Standard-Bezeichnung (Fallback für leere Felder)' : undefined}
                  >
                    <FwdChipBox
                      values={splitCommaList(therapyTypeForm.pvs_name)}
                      onChange={(chips) => setTherapyTypeForm(prev => ({ ...prev, pvs_name: chips.join(', ') }))}
                      draft={pvsNameDrafts.pvs_name || ''}
                      setDraft={(val) => setPvsNameDrafts(prev => ({ ...prev, pvs_name: val }))}
                      removeLabel="Bezeichnung entfernen"
                      addLabel="PVS-Bezeichnung eingeben"
                    />
                  </div>
                  )}
                  {/* The switch takes the place of the second pair on this row: it
                      belongs to the field on its left, and pairing it keeps the field
                      as wide as the Name above instead of stretching it. */}
                  {!isSupplementaryForm && (
                  <label className={`tt-cat-chip pf-toggle pf-flag pf-pair-b${therapyTypeForm.pvs_name_per_insurance ? ' is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={therapyTypeForm.pvs_name_per_insurance}
                      aria-label="PVS-Bezeichnung je Versicherungsart"
                      onChange={() => setTherapyTypeForm(prev => {
                        if (prev.pvs_name_per_insurance) {
                          // Turning OFF — keep the cell values untouched
                          return { ...prev, pvs_name_per_insurance: false };
                        }
                        // Turning ON — prefill ONLY empty cells from the base name
                        const fill = v => (v && v.trim() ? v : (prev.pvs_name || ''));
                        const filled = {};
                        INSURANCE_KEYS.forEach(k => {
                          filled[`pvs_name_${k}`] = fill(prev[`pvs_name_${k}`]);
                        });
                        return { ...prev, pvs_name_per_insurance: true, ...filled };
                      })}
                    />
                    <span className="pf-toggle-text">je Versicherungsart</span>
                  </label>
                  )}

                  <span className="pf-row-label pf-row-start">Beschreibung</span>
                  <textarea
                    className="form-input pf-input pf-value-wide"
                    rows={1}
                    style={{ resize: 'none', overflow: 'hidden' }}
                    value={therapyTypeForm.description}
                    ref={(el) => {
                      if (el) {
                        requestAnimationFrame(() => {
                          el.style.height = 'auto';
                          el.style.height = el.scrollHeight + 'px';
                        });
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTherapyTypeForm(prev => ({ ...prev, description: val }));
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />

                  {!isIntakeForm && !isSupplementaryForm && (
                    <>
                      <span className="pf-row-label pf-row-start">
                        Symptome
                        <InfoTooltip text="Beschwerden und Symptome, die Anrufer nennen könnten, wenn sie den Behandlungsnamen nicht kennen (z.B. 'Rückenschmerzen', 'Knie verdreht'). Mit Komma oder Enter bestätigen." />
                      </span>
                      <div className="pf-field pf-field--chips pf-value-wide">
                        <FwdChipBox
                          values={therapyTypeForm.symptoms || []}
                          onChange={(next) => setTherapyTypeForm(prev => ({ ...prev, symptoms: next }))}
                          draft={symptomDraft}
                          setDraft={setSymptomDraft}
                          removeLabel="Symptom entfernen"
                          addLabel="Symptom eingeben"
                          placeholder="Symptom eingeben"
                        />
                      </div>
                    </>
                  )}

                  {/* Booking switches. Both are about HOW this therapy is booked, so they
                      close the block; the first one's hairline is the separator, a second
                      one right under it would only draw the same line twice.
                      "Zuerst Aufnahmegespräch buchen" is offered only in a group the
                      Aufnahmegespräch is shown in. The practice has exactly one, so the
                      switch writes its id. Never on a Blankorezept: the two presets do not combine.
                      Neither switch on an Ergänzendes Heilmittel: booking never reaches it. */}
                  {!isIntakeForm && !isBlankoForm && !isSupplementaryForm && intakeListsGroup(formCategory) && (
                    <div className="pf-gate">
                      <label className="ios-switch" aria-label={therapyTypeForm.intake_therapy_type_id ? 'Aufnahmegespräch deaktivieren' : 'Aufnahmegespräch aktivieren'}>
                        <input
                          type="checkbox"
                          checked={!!therapyTypeForm.intake_therapy_type_id}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setTherapyTypeForm(prev => ({
                              ...prev,
                              intake_therapy_type_id: on ? intakeRow.id : null,
                            }));
                          }}
                        />
                        <span className="ios-switch-slider" />
                      </label>
                      <span className="pf-gate-label">
                        Zuerst Aufnahmegespräch buchen
                        <InfoTooltip text="Neue Patienten erhalten für diese Behandlung zuerst ein Aufnahmegespräch. Die weiteren Termine vereinbart die Praxis." />
                      </span>
                    </div>
                  )}

                  {/* No Doppeltermine on a Blankorezept: the server saves it off. */}
                  {!isIntakeForm && !isBlankoForm && !isSupplementaryForm && (
                    <div className={`pf-gate${intakeListsGroup(formCategory) ? ' pf-gate--flush' : ''}`}>
                      <label className="ios-switch" aria-label={therapyTypeForm.double_appointments_enabled ? 'Doppeltermine deaktivieren' : 'Doppeltermine aktivieren'}>
                        <input
                          type="checkbox"
                          checked={!!therapyTypeForm.double_appointments_enabled}
                          onChange={() => setTherapyTypeForm(prev => ({ ...prev, double_appointments_enabled: !prev.double_appointments_enabled }))}
                        />
                        <span className="ios-switch-slider" />
                      </label>
                      <span className="pf-gate-label">
                        Doppeltermine anbieten
                        <InfoTooltip text="Sobald die Anzahl der Sitzungen bekannt ist, bietet DeskBot an, die Behandlung als Doppeltermine zu buchen: halb so viele Termine mit doppelter Dauer. Nennt die anrufende Person Doppeltermine von sich aus, wird das auch ohne diesen Schalter übernommen." />
                      </span>
                    </div>
                  )}

                  {/* What the move does with an Ergänzendes Heilmittel, in the place of the
                      booking switches: the same phrase as in its table row, saved with
                      Speichern, and under it a small picture of the moved appointment.
                      "Je Versicherungsart" holds what differs per insurance; an empty field
                      there takes the value above. */}
                  {isSupplementaryForm && (
                    <>
                      <span className="pf-row-label">Beim Verschieben</span>
                      <div>
                        <SupplementaryPhrase
                          mode={therapyTypeForm.supplementary_mode}
                          onToggle={(next) => setTherapyTypeForm(prev => ({ ...prev, supplementary_mode: next }))}
                          minutesProps={{
                            value: suppDraft.minutes,
                            'aria-label': 'Minuten',
                            onChange: (e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                              setSuppDraft(prev => ({ ...prev, minutes: val }));
                            },
                          }}
                        />
                        <div className={`tt-supp-preview is-${therapyTypeForm.supplementary_mode}`} aria-hidden="true">
                          {therapyTypeForm.supplementary_mode === 'arrive_early' && (
                            <>
                              <span className="tt-supp-block tt-supp-block--early">{suppDraftMinutes} Min früher</span>
                              <span className="tt-supp-tick" />
                            </>
                          )}
                          <span className="tt-supp-block tt-supp-block--main">
                            Hauptbehandlung
                            {therapyTypeForm.supplementary_mode === SUPPLEMENTARY_INCLUDED && therapyTypeForm.name.trim()
                              ? ` inkl. ${therapyTypeForm.name.trim()}`
                              : ''}
                          </span>
                          {therapyTypeForm.supplementary_mode === 'add_time' && (
                            <span className="tt-supp-block tt-supp-block--extra">+{suppDraftMinutes} Min</span>
                          )}
                        </div>
                      </div>

                      <span aria-hidden="true" />
                      <div>
                        <button
                          type="button"
                          className="tt-supp-disclosure"
                          aria-expanded={suppDraft.perIns}
                          onClick={() => setSuppDraft(prev => ({ ...prev, perIns: !prev.perIns }))}
                        >
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                          Je Versicherungsart
                        </button>
                      </div>
                      {suppDraft.perIns && (
                        <>
                          <span aria-hidden="true" />
                          <div className="tt-supp-ins-grid tt-supp-ins-head">
                            {INSURANCE_KEYS.map(k => <span key={k}>{insuranceLabel(k)}</span>)}
                          </div>
                          <span className="pf-row-label">PVS</span>
                          <div className="tt-supp-ins-grid">
                            {INSURANCE_KEYS.map(k => (
                              <input
                                key={k}
                                type="text"
                                className="form-input pf-input"
                                aria-label={`PVS-Bezeichnung ${insuranceLabel(k)}`}
                                placeholder={therapyTypeForm.pvs_name || ''}
                                value={therapyTypeForm[`pvs_name_${k}`] || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setTherapyTypeForm(prev => ({ ...prev, [`pvs_name_${k}`]: val }));
                                }}
                              />
                            ))}
                          </div>
                          <span className="pf-row-label">Minuten</span>
                          <div className="tt-supp-ins-grid">
                            {INSURANCE_KEYS.map(k => (
                              <input
                                key={k}
                                type="text"
                                inputMode="numeric"
                                className="form-input pf-input"
                                aria-label={`Minuten ${insuranceLabel(k)}`}
                                placeholder={suppDraft.minutes}
                                value={suppDraft.minutesByIns[k] || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                                  setSuppDraft(prev => ({ ...prev, minutesByIns: { ...prev.minutesByIns, [k]: val } }));
                                }}
                              />
                            ))}
                          </div>
                          <span aria-hidden="true" />
                          <p className="tt-intake-hint tt-supp-hint">Leere Felder übernehmen die Angaben oben.</p>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* ---- Versicherungsarten: the one genuinely tabular part ----
                    Not on an Ergänzendes Heilmittel: its phrase above carries its minutes. */}
                {!isSupplementaryForm && (
                <div className="pf-section">
                  <h3 className="pf-title">
                    Versicherungsarten
                    <InfoTooltip text={isIntakeForm
                      ? "Für diese Versicherungsarten wird bei neuen Patienten zuerst ein Aufnahmegespräch gebucht."
                      : "Je Versicherungsart: ob diese Behandlung angeboten wird und mit welchen Vorgaben sie gebucht wird. Eine durchgestrichene Versicherungsart wird für diese Behandlung nicht angeboten."} />
                  </h3>

                  <div className="therapy-type-table-scroll">
                    <div className="therapy-type-table therapy-type-table--pf">
                      {/* Header row. The column name and its accept switch used to be two
                          things; now they are one chip, in the same struck-through language
                          the therapist dialog uses for an insurance type that excludes.
                          The corner stays empty — the row labels below say what each line is. */}
                      <div className="therapy-type-table-header" />
                      {INSURANCE_KEYS.map((key) => {
                        const isActive = therapyTypeForm.accepted_insurance_types?.[key] !== false;
                        return (
                          <div key={key} className="therapy-type-table-header tt-ins-head">
                            <label className={`tt-cat-chip pf-toggle tt-ins-pill${isActive ? ' is-selected' : ' is-off'}`}>
                              <input
                                type="checkbox"
                                checked={isActive}
                                aria-label={`${insuranceLabel(key)} ${isActive ? 'deaktivieren' : 'aktivieren'}`}
                                onChange={() => setTherapyTypeForm(prev => ({
                                  ...prev,
                                  accepted_insurance_types: { ...prev.accepted_insurance_types, [key]: !isActive },
                                }))}
                              />
                              <span className="pf-toggle-text">{insuranceLabel(key)}</span>
                            </label>
                          </div>
                        );
                      })}

                      {/* Per-insurance PVS article name row — only when the chip on the
                          PVS-Bezeichnung row above is on. The same chips as that field,
                          wrapping inside the cell; an empty cell shows the base name it
                          falls back to. */}
                      {therapyTypeForm.pvs_name_per_insurance && (
                        <>
                          <div className="therapy-type-table-label">PVS-Bezeichnung</div>
                          {INSURANCE_KEYS.map((key) => {
                            const field = `pvs_name_${key}`;
                            const chips = splitCommaList(therapyTypeForm[field]);
                            return (
                              <div key={key} className="therapy-type-table-cell" style={{ opacity: insuranceOpacity(therapyTypeForm.accepted_insurance_types, key) }}>
                                <FwdChipBox
                                  values={chips}
                                  onChange={(next) => setTherapyTypeForm(prev => ({ ...prev, [field]: next.join(', ') }))}
                                  draft={pvsNameDrafts[field] || ''}
                                  setDraft={(val) => setPvsNameDrafts(prev => ({ ...prev, [field]: val }))}
                                  removeLabel="Bezeichnung entfernen"
                                  addLabel={`PVS-Bezeichnung ${insuranceLabel(key)}`}
                                  placeholder={chips.length ? '' : (therapyTypeForm.pvs_name || '-')}
                                />
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Dauer row */}
                      <div className="therapy-type-table-label">Dauer (Min) *</div>
                      {INSURANCE_KEYS.map((key) => (
                        <div key={key} className="therapy-type-table-cell" style={{ opacity: insuranceOpacity(therapyTypeForm.accepted_insurance_types, key) }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="form-input"
                            value={therapyTypeForm[`duration_${key}`]}
                            aria-label={`Dauer ${insuranceLabel(key)}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              setTherapyTypeForm(prev => ({ ...prev, [`duration_${key}`]: val === '' ? '' : parseInt(val) }));
                            }}
                            required
                          />
                        </div>
                      ))}

                      {/* An Aufnahmegespräch is one appointment with no prescription of its
                          own: no Ersttermin bonus, no session count, no interval, no fixed
                          values and no Doppeltermine. The API forces all of them off in the
                          database too, so hiding them here hides nothing that still applies. */}
                      {!isIntakeForm && (
                      <>
                      {/* Erstterminzuschlag (Min) row */}
                      <div className="therapy-type-table-label">Erstzuschlag (Min)</div>
                      {INSURANCE_KEYS.map((key) => (
                        <div key={key} className="therapy-type-table-cell" style={{ opacity: insuranceOpacity(therapyTypeForm.accepted_insurance_types, key) }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="form-input"
                            value={therapyTypeForm[`first_session_extra_${key}`]}
                            aria-label={`Erstzuschlag ${insuranceLabel(key)}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              setTherapyTypeForm(prev => ({ ...prev, [`first_session_extra_${key}`]: val === '' ? '' : parseInt(val) }));
                            }}
                          />
                        </div>
                      ))}

                      {/* Sitzungen row. "fest" is the old full-width switch strip: it governs
                          exactly this row, so it rides on this row's label instead of cutting
                          the table in half above it. On a Blankorezept both "fest" chips are
                          always on and locked (the server saves them on); the VALUES below
                          stay editable. */}
                      <div className="therapy-type-table-label">
                        Sitzungen *
                        <InfoTooltip text="Bei Verordnungen werden zuerst die Angaben der Verordnung übernommen. Steht auf der Verordnung eine höhere Anzahl oder wünscht ein Selbstzahler mehr Termine, schlägt Deskbot vor, zunächst die hier definierte Anzahl zu buchen - der Rest wird vor Ort mit der Praxis abgestimmt. Mit 'fest' wird die anrufende Person gar nicht erst gefragt und immer diese Anzahl verwendet." />
                        <label
                          className={`tt-cat-chip pf-toggle pf-flag${(isBlankoForm || therapyTypeForm.force_sessions_enabled) ? ' is-selected' : ''}${isBlankoForm ? ' is-disabled' : ''}`}
                          title={isBlankoForm ? BLANKO_FIXED_TITLE : 'Anzahl fest vorgeben'}
                        >
                          <input
                            type="checkbox"
                            checked={isBlankoForm || !!therapyTypeForm.force_sessions_enabled}
                            disabled={isBlankoForm}
                            aria-label="Anzahl fest vorgeben"
                            onChange={() => setTherapyTypeForm(prev => ({ ...prev, force_sessions_enabled: !prev.force_sessions_enabled }))}
                          />
                          <span className="pf-toggle-text">fest</span>
                        </label>
                      </div>
                      {INSURANCE_KEYS.map((key) => (
                        <div key={key} className="therapy-type-table-cell" style={{ opacity: insuranceOpacity(therapyTypeForm.accepted_insurance_types, key) }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="form-input"
                            value={therapyTypeForm[`sessions_${key}`]}
                            aria-label={`Sitzungen ${insuranceLabel(key)}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              setTherapyTypeForm(prev => ({ ...prev, [`sessions_${key}`]: val === '' ? '' : parseInt(val) }));
                            }}
                            required
                          />
                        </div>
                      ))}

                      {/* Frequenz row */}
                      <div className="therapy-type-table-label">
                        Frequenz *
                        <InfoTooltip text="Bei Verordnungen wird zuerst die auf der Verordnung angegebene Frequenz verwendet. Bei Privatverordnungen ohne Frequenz-Angabe oder bei Selbstzahlern greift dieser Default. Zudem wird diese Frequenz verwendet, wenn die Person nicht weiß, wie häufig terminiert werden soll. Mit 'fest' wird die anrufende Person gar nicht erst gefragt und immer diese Frequenz verwendet." />
                        <label
                          className={`tt-cat-chip pf-toggle pf-flag${(isBlankoForm || therapyTypeForm.force_frequency_enabled) ? ' is-selected' : ''}${isBlankoForm ? ' is-disabled' : ''}`}
                          title={isBlankoForm ? BLANKO_FIXED_TITLE : 'Frequenz fest vorgeben'}
                        >
                          <input
                            type="checkbox"
                            checked={isBlankoForm || !!therapyTypeForm.force_frequency_enabled}
                            disabled={isBlankoForm}
                            aria-label="Frequenz fest vorgeben"
                            onChange={() => setTherapyTypeForm(prev => ({ ...prev, force_frequency_enabled: !prev.force_frequency_enabled }))}
                          />
                          <span className="pf-toggle-text">fest</span>
                        </label>
                      </div>
                      {INSURANCE_KEYS.map((key) => (
                        <div key={key} className="therapy-type-table-cell" style={{ opacity: insuranceOpacity(therapyTypeForm.accepted_insurance_types, key) }}>
                          <select
                            className="form-input"
                            value={therapyTypeForm[`frequency_${key}`]}
                            aria-label={`Frequenz ${insuranceLabel(key)}`}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTherapyTypeForm(prev => ({ ...prev, [`frequency_${key}`]: val }));
                            }}
                            required
                          >
                            {frequencyOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.display}</option>))}
                          </select>
                        </div>
                      ))}
                      </>
                      )}
                    </div>
                  </div>
                </div>
                )}

                {/* The other half of the link, seen from the Aufnahmegespräch: which of THIS
                    group's therapies send their new patients through it. Saved after the row
                    itself, through the group route, as the complete set for this group.
                    Therapists are seeded from the group only when the row is CREATED, so the
                    second sentence of the tooltip is only true while creating it. */}
                {isIntakeForm && (
                  <div className="pf-section">
                    <h3 className="pf-title">
                      Behandlungen mit Aufnahmegespräch
                      <InfoTooltip text={`Welche Behandlungen dieser Gruppe neue Patienten zuerst über das Aufnahmegespräch führen. Therapeuten und Standorte ordnen Sie wie bei jeder Behandlungsart zu.${editingTherapyType ? '' : ' Die Therapeuten der Gruppe werden beim Speichern vorbelegt.'}`} />
                    </h3>
                    {editingTherapyType && !intakeListsGroup(formCategory) && (
                      <p className="tt-intake-hint">Das Aufnahmegespräch wird dieser Gruppe hinzugefügt.</p>
                    )}
                    {linkableRowsInFormCategory.length > 0 ? (
                      <div
                        ref={measureLinkListFade}
                        className={`tt-intake-link-list${linkListFade.top ? ' is-fade-top' : ''}${linkListFade.bottom ? ' is-fade-bottom' : ''}`}
                        onScroll={(e) => measureLinkListFade(e.currentTarget)}
                      >
                        {linkableRowsInFormCategory.map(tt => {
                          const checked = intakeLinkedIds.includes(tt.id);
                          const inactive = tt.is_active === false;
                          return (
                            <label
                              key={tt.id}
                              className={`tt-cat-chip tt-intake-link-chip${checked ? ' is-selected' : ''}${inactive ? ' tt-intake-link-chip--inactive' : ''}`}
                              title={inactive ? 'inaktiv' : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setIntakeLinkedIds(prev => (
                                  prev.includes(tt.id) ? prev.filter(id => id !== tt.id) : [...prev, tt.id]
                                ))}
                              />
                              {tt.name}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="tt-intake-hint">
                        In dieser Gruppe gibt es noch keine Behandlungsarten.
                      </p>
                    )}
                  </div>
                )}

                {/* Save / Cancel — pinned to bottom-right of the modal */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <button className="button-secondary" onClick={() => setTherapyTypeModalOpen(false)}>
                    Abbrechen
                  </button>
                  <button className="button" onClick={handleSaveTherapyType}>
                    Speichern
                  </button>
                </div>
              </div>
            </Modal>

            {/* Add / Edit Excluded Therapy Modal — the Therapeut dialog's label/value grid */}
            <Modal
              isOpen={excludedModalOpen}
              onRequestClose={handleCancelExcludedForm}
              style={{ ...customModalStyles, content: { ...customModalStyles.content, padding: '24px', borderRadius: '12px' } }}
              contentLabel="Nicht angebotene Behandlung"
            >
              <h2>{excludedEditingId ? 'Nicht angebotene Behandlung bearbeiten' : 'Nicht angebotene Behandlung hinzufügen'}</h2>
              <div className="pf-grid pf-grid--form">
                <span className="pf-row-label">Name *</span>
                <input
                  autoFocus
                  type="text"
                  className="form-input pf-input"
                  value={excludedForm.name}
                  onChange={(e) => setExcludedForm({ ...excludedForm, name: e.target.value })}
                  required
                />

                {/* Empty = the bot's default sentence (the table shows it with the row's
                    name). Fixed wording: a long name would wrap the placeholder, and an
                    empty textarea does not grow for it. One line at rest, growing when the
                    text wraps; minHeight beats the component's own minHeight 0 (34px row). */}
                <span className="pf-row-label">
                  Nachricht
                  <InfoTooltip text="Wird dem Anrufer vorgelesen, danach folgt die Verabschiedung." />
                </span>
                <AutoGrowTextarea
                  className="form-input pf-input"
                  style={{ minHeight: 34 }}
                  placeholder="Wir bieten diese Behandlung leider nicht an."
                  value={excludedForm.reject_reason}
                  onChange={(e) => setExcludedForm({ ...excludedForm, reject_reason: e.target.value })}
                />

                <span className="pf-row-label">
                  Aliase
                  <InfoTooltip text="Weitere Bezeichnungen, unter denen Anrufer diese Behandlung nennen könnten. Mit Komma oder Enter bestätigen." />
                </span>
                <div className="pf-field pf-field--chips">
                  <FwdChipBox
                    values={excludedForm.aliases || []}
                    onChange={(next) => setExcludedForm(prev => ({ ...prev, aliases: next }))}
                    draft={excludedAliasDraft}
                    setDraft={setExcludedAliasDraft}
                    removeLabel="Alias entfernen"
                    addLabel="Alias eingeben"
                    placeholder="Alias eingeben"
                  />
                </div>
              </div>

              {excludedError && (
                <p style={{ margin: '14px 0 0 0', fontSize: '0.85rem', color: 'var(--color-tertiary)' }}>{excludedError}</p>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button className="button-secondary" style={{ margin: 0 }} onClick={handleCancelExcludedForm} disabled={excludedSaving}>
                  Abbrechen
                </button>
                <button className="button" style={{ margin: 0 }} onClick={handleSaveExcluded} disabled={excludedSaving}>
                  {excludedSaving ? 'Wird gespeichert…' : 'Speichern'}
                </button>
              </div>
            </Modal>

            {/* Location Detail Modal */}
            <Modal
              isOpen={locationModalOpen}
              onRequestClose={() => setLocationModalOpen(false)}
              style={{
                ...customModalStyles,
                content: {
                  ...customModalStyles.content,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                },
              }}
              contentLabel="Standort bearbeiten"
            >
              <h2>Weitere Details bearbeiten</h2>
              <div style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label className="form-label">FAQ</label>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px', marginTop: '0' }}>
                    Haeufig gestellte Fragen zu diesem Standort (z.B. Anfahrt, Parken, Barrierefreiheit)
                  </p>
                  <textarea
                    className="form-input"
                    value={locationForm.faq}
                    onChange={(e) => setLocationForm({ ...locationForm, faq: e.target.value })}
                    rows="5"
                  />
                </div>

                {pvsEnabled && enableMultiLocation && (
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label">PVS-Bezeichnung</label>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px', marginTop: '0' }}>
                      Name dieses Standorts im PVS-System
                    </p>
                    <input
                      type="text"
                      className="form-input"
                      value={locationForm.pvs_name}
                      onChange={(e) => setLocationForm({ ...locationForm, pvs_name: e.target.value })}
                      placeholder="z.B. Standort Mitte"
                    />
                  </div>
                )}

                <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                  <button className="button" onClick={handleSaveLocationDetails}>
                    Speichern
                  </button>
                  <button className="button-secondary" onClick={() => setLocationModalOpen(false)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </Modal>

            {/* Therapist Detail Modal */}
            <Modal
              isOpen={therapistModalOpen}
              onRequestClose={() => setTherapistModalOpen(false)}
              style={{
                ...customModalStyles,
                content: {
                  ...customModalStyles.content,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                },
              }}
              contentLabel="Therapeut bearbeiten"
            >
              <h2>Therapeut bearbeiten</h2>
              <div style={{ marginTop: '20px' }}>
                {/* Same label/value grid as the Patientenfilter below: what it is on the
                    left, the value on the right. Name, PVS-Bezeichnung and Telefonnummer
                    belong to the therapist, not to the notification (the whitelist on the
                    Anrufablauf tab adopts the number too), so the switch's hairline
                    separates the two halves.

                    The switch is the gate: channel and address are meaningless without it
                    and only appear once it is on. Hidden means HIDDEN ONLY — slack_user_id,
                    email and notify_channel stay in the form and keep being saved
                    (handleSaveTherapistDetails sends everything except the name and the
                    pf_* keys), so switching off and on again loses nothing.

                    Short-notice notification is visible to every practice member, matching
                    what the backend already allows: PUT /therapists/{id}/details checks
                    membership only, and the matrix endpoint that fills this form already
                    ships phone + toggle to members. */}
                <div className="pf-grid pf-grid--form">

                  {/* The same name the matrix header edits inline; same label as the
                      therapy dialog's required name. */}
                  <span className="pf-row-label">Name *</span>
                  <input
                    type="text"
                    className="form-input pf-input"
                    value={therapistForm.name}
                    onChange={(e) => setTherapistForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />

                  <span className="pf-row-label">
                    PVS-Bezeichnung
                    {!pvsEnabled && <InfoTooltip text="Optional, falls PVS später angeschlossen wird" />}
                  </span>
                  <input
                    type="text"
                    className="form-input pf-input"
                    value={therapistForm.pvs_name}
                    onChange={(e) => setTherapistForm(prev => ({ ...prev, pvs_name: e.target.value }))}
                    placeholder="Name im PVS-System, z.B. G. Evans"
                  />

                  <span className="pf-row-label">
                    Telefonnummer
                    <InfoTooltip text="Für Benachrichtigungen per SMS und zum Übernehmen in die Whitelist der Direktweiterleitung (Tab Anrufablauf-Konfiguration). Die Whitelist speichert eine Kopie: eine spätere Änderung hier ändert sie nicht." />
                  </span>
                  <input
                    type="tel"
                    className="form-input pf-input"
                    value={therapistForm.phone}
                    onChange={(e) => setTherapistForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="z.B. 0151 23456789"
                  />

                  <div className="pf-gate">
                    <label className="ios-switch" aria-label={therapistForm.notify_short_notice_changes ? 'Benachrichtigung bei kurzfristigen Änderungen deaktivieren' : 'Benachrichtigung bei kurzfristigen Änderungen aktivieren'}>
                      <input
                        type="checkbox"
                        checked={therapistForm.notify_short_notice_changes}
                        onChange={() => setTherapistForm(prev => ({ ...prev, notify_short_notice_changes: !prev.notify_short_notice_changes }))}
                      />
                      <span className="ios-switch-slider" />
                    </label>
                    <span className="pf-gate-label">
                      Therapeut über Änderungen innerhalb von 24 Stunden informieren
                      <InfoTooltip text="Sendet eine Nachricht über den gewählten Kanal, wenn ein Anruf einen Termin dieses Therapeuten in den nächsten 24 Stunden bucht, storniert oder verschiebt." />
                    </span>
                  </div>

                  {therapistForm.notify_short_notice_changes && (
                    <>
                      {/* Single-select like Geschlecht in the Patientenfilter: three options
                          fit on one line and need no menu that opens over the dialog. */}
                      <span className="pf-row-label">Kanal</span>
                      <div className="pf-chips" role="group" aria-label="Benachrichtigungskanal">
                        {[
                          { value: 'sms', label: 'SMS' },
                          { value: 'slack', label: 'Slack' },
                          { value: 'email', label: 'E-Mail' },
                        ].map((opt) => {
                          const active = therapistForm.notify_channel === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`tt-cat-chip${active ? ' is-selected' : ''}`}
                              aria-pressed={active}
                              onClick={() => setTherapistForm(prev => ({ ...prev, notify_channel: opt.value }))}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* One field, two channels: slack_user_id and email stay separate
                          columns, so switching between them never loses the other. The
                          channel picks which one the field is bound to; label, type,
                          placeholder and tooltip all come from THERAPIST_CHANNEL_FIELD so the
                          variants exist exactly once. SMS has no field here: it goes to the
                          Telefonnummer above. */}
                      {therapistChannelField ? (
                        <>
                          <span className="pf-row-label">
                            {therapistChannelField.label}
                            <InfoTooltip text={therapistChannelField.tooltip} />
                          </span>
                          <input
                            type={therapistChannelField.type}
                            className="form-input pf-input"
                            value={therapistForm[therapistChannelField.key]}
                            onChange={(e) => setTherapistForm(prev => ({
                              ...prev,
                              [therapistChannelField.key]: e.target.value,
                            }))}
                            placeholder={therapistChannelField.placeholder}
                          />
                        </>
                      ) : (
                        <>
                          <span aria-hidden="true" />
                          <p className="tt-intake-hint tt-supp-hint">Die SMS geht an die Telefonnummer oben (Mobilnummer).</p>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Exklusive Spezialisierung. Zwei Gruppen: passt ein Patient zu EINER
                    gesetzten Zeile (jede Zeile gilt fuer sich), bekommt er nur Therapeuten mit dieser
                    Spezialisierung; fuer alle anderen bleibt dieser Therapeut normal
                    buchbar. Die Versicherungsart ist etwas anderes (sie schliesst diesen
                    Therapeuten aus) und traegt deshalb ihre eigene Versalzeile. Die Regel
                    steht sichtbar in der Unterzeile, die Folge fuer diesen Therapeuten im
                    Live-Satz unter den Zeilen; die Tooltips erklaeren nur die Eingabe. */}
                <div className="pf-section">
                  <h3 className="pf-title">
                    Exklusive Spezialisierung
                    <InfoTooltip text="Nennt der Anrufer selbst einen Therapeuten, gilt sein Wunsch." />
                  </h3>
                  <p className="tt-intake-hint pf-caption">
                    Passt ein Patient zu einer der Angaben (Alter, Geschlecht oder Symptom), bekommt er nur Termine bei Therapeuten mit dieser Spezialisierung, auch wenn andere früher frei sind. Für alle anderen Patienten bleibt dieser Therapeut normal buchbar.
                  </p>

                  <div className="pf-group">
                    <div className="pf-grid">

                      {/* Symptome: dieselbe Chip-Eingabe wie die Weiterleitungs-Stichwörter */}
                      <span className="pf-row-label">
                        Symptome
                        <InfoTooltip text="Zählt, wenn der Anrufer eines dieser Symptome nennt. Mit Komma oder Enter bestätigen." />
                      </span>
                      <div className="pf-field">
                        <div
                          className="fwd-chip-box"
                          onClick={(e) => {
                            const input = e.currentTarget.querySelector('input');
                            if (input) input.focus();
                          }}
                        >
                          {(therapistForm.pf_symptoms || []).map((symptom, index) => (
                            <span key={index} className="fwd-chip">
                              {symptom}
                              <button
                                type="button"
                                className="fwd-chip-remove"
                                onClick={() => setTherapistForm(prev => ({
                                  ...prev,
                                  pf_symptoms: (prev.pf_symptoms || []).filter((_, i) => i !== index),
                                }))}
                                title="Symptom entfernen"
                                aria-label="Symptom entfernen"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            value={therapistSymptomDraft}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.includes(',')) {
                                const parts = val.split(',').map(p => p.trim()).filter(Boolean);
                                if (parts.length > 0) {
                                  setTherapistForm(prev => ({
                                    ...prev,
                                    pf_symptoms: [...(prev.pf_symptoms || []), ...parts],
                                  }));
                                }
                                setTherapistSymptomDraft('');
                              } else {
                                setTherapistSymptomDraft(val);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const trimmed = therapistSymptomDraft.trim();
                                if (trimmed) {
                                  setTherapistForm(prev => ({
                                    ...prev,
                                    pf_symptoms: [...(prev.pf_symptoms || []), trimmed],
                                  }));
                                  setTherapistSymptomDraft('');
                                }
                              } else if (e.key === 'Backspace' && therapistSymptomDraft === '' && (therapistForm.pf_symptoms || []).length > 0) {
                                setTherapistForm(prev => ({
                                  ...prev,
                                  pf_symptoms: (prev.pf_symptoms || []).slice(0, -1),
                                }));
                              }
                            }}
                            onBlur={() => {
                              const trimmed = therapistSymptomDraft.trim();
                              if (trimmed) {
                                setTherapistForm(prev => ({
                                  ...prev,
                                  pf_symptoms: [...(prev.pf_symptoms || []), trimmed],
                                }));
                                setTherapistSymptomDraft('');
                              }
                            }}
                            placeholder="Symptom eingeben"
                          />
                        </div>
                        {/* Was die Praxis auf ihren Behandlungsarten schon gepflegt hat, als
                            Vorschlag. Die Entwurfseingabe filtert mit, damit aus einer langen
                            Liste eine Suche wird statt einer Wand aus Knöpfen. */}
                        {(() => {
                          const draft = therapistSymptomDraft.trim().toLowerCase();
                          const pool = Array.from(new Set(
                            (therapyTypes || []).flatMap(tt => tt.symptoms || [])
                          ))
                            .filter(x => !(therapistForm.pf_symptoms || []).includes(x)
                                      && (!draft || x.toLowerCase().includes(draft)));
                          if (!pool.length) return null;
                          const shown = pool.slice(0, 8);
                          return (
                            <div className="pf-suggest-row">
                              {shown.map((symptom) => (
                                <button
                                  key={symptom}
                                  type="button"
                                  className="tt-cat-chip tt-cat-chip--new pf-suggest"
                                  onClick={() => setTherapistForm(prev => ({
                                    ...prev,
                                    pf_symptoms: [...(prev.pf_symptoms || []), symptom],
                                  }))}
                                  title="Übernehmen"
                                >
                                  + {symptom}
                                </button>
                              ))}
                              {pool.length > shown.length && (
                                <span className="pf-suggest-more" title="Eingeben, um weitere zu finden">
                                  +{pool.length - shown.length}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Altersgruppen: eine Spanne = eine Pille mit zwei Feldern,
                          statt einer umrandeten Karte pro Gruppe. */}
                      <span className="pf-row-label">
                        Alter
                        <InfoTooltip text="Zählt, wenn der Patient in eine der Altersgruppen fällt. Angaben in Jahren, die Grenzen zählen mit ('bis 30' schließt 30 ein), ein leeres Bis bleibt nach oben offen. Setzt voraus, dass das Geburtsdatum im Anruf erfragt wird." />
                      </span>
                      <div className="pf-chips" role="group" aria-label="Altersgruppen">
                        {(therapistForm.pf_age_groups || []).map((group, index) => (
                          <span key={index} className="pf-age">
                            <input
                              type="number"
                              min="0"
                              value={group.min}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTherapistForm(prev => {
                                  const updated = [...(prev.pf_age_groups || [])];
                                  updated[index] = { ...updated[index], min: val };
                                  return { ...prev, pf_age_groups: updated };
                                });
                              }}
                              placeholder="0"
                              aria-label={`Alter von, Gruppe ${index + 1}`}
                            />
                            <span>bis</span>
                            <input
                              type="number"
                              min="0"
                              value={group.max}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTherapistForm(prev => {
                                  const updated = [...(prev.pf_age_groups || [])];
                                  updated[index] = { ...updated[index], max: val };
                                  return { ...prev, pf_age_groups: updated };
                                });
                              }}
                              placeholder="∞"
                              aria-label={`Alter bis, Gruppe ${index + 1}`}
                            />
                            <button
                              type="button"
                              className="fwd-chip-remove"
                              onClick={() => setTherapistForm(prev => ({
                                ...prev,
                                pf_age_groups: (prev.pf_age_groups || []).filter((_, i) => i !== index),
                              }))}
                              title="Altersgruppe entfernen"
                              aria-label="Altersgruppe entfernen"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <button
                          type="button"
                          className="tt-cat-chip tt-cat-chip--new pf-suggest pf-age-add"
                          onClick={() => setTherapistForm(prev => ({
                            ...prev,
                            pf_age_groups: [...(prev.pf_age_groups || []), { min: '', max: '' }],
                          }))}
                          title="Altersgruppe hinzufügen"
                          aria-label="Altersgruppe hinzufügen"
                        >
                          +
                        </button>
                      </div>

                      {/* Geschlecht: kein Filter, sondern eine Markierung. Genau ein
                          markierter Chip ist die Spezialisierung, keiner und beide bedeuten
                          dasselbe (der Save-Handler schickt beides gar nicht mit) - also
                          nimmt ein Klick auf den markierten Chip die Markierung zurueck und
                          die Zeile braucht kein sichtbares "Alle" mehr, das sie ueberhaupt
                          erst wie eine Auswahl unter Ausschluss der anderen aussehen liess.
                          Der Stern markiert nur den Zustand: hohl heisst "kann markiert
                          werden", gefuellt heisst "gesetzt, Teil der Spezialisierung". Die
                          Bedeutung steht im Live-Satz unter den Zeilen. */}
                      <span className="pf-row-label">
                        Geschlecht
                        <InfoTooltip text="Gemeint ist das Geschlecht des Patienten, nicht das des Therapeuten. Es wird nicht erfragt, sondern aus dem Vornamen abgeleitet." />
                      </span>
                      <div className="pf-chips" role="group" aria-label="Geschlecht">
                        {PF_GENDER_KEYS.map((key) => {
                          const picked = therapistForm.pf_genders || [];
                          const active = picked.length === 1 && picked[0] === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`tt-cat-chip pf-gender${active ? ' is-selected' : ''}`}
                              aria-pressed={active}
                              onClick={() => setTherapistForm(prev => {
                                const cur = prev.pf_genders || [];
                                const marked = cur.length === 1 && cur[0] === key;
                                // Beide Schluessel zurueck heisst "keine Vorgabe" - genau der
                                // Wert, den der Ladepfad fuer einen Therapeuten ohne
                                // Geschlechtsvorgabe setzt, und der Save-Handler laesst ihn weg.
                                return { ...prev, pf_genders: marked ? [...PF_GENDER_KEYS] : [key] };
                              })}
                            >
                              {/* fill/stroke stehen im CSS, weil sie den Zustand tragen. */}
                              <svg className="pf-star" width="13" height="13" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                              {PF_GENDER_LABEL[key]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* Live: was die gesetzten Zeilen fuer diesen Therapeuten bedeuten.
                        Nichts gesetzt = kein Satz. Volle Breite wie die Unterzeile. */}
                    {(() => {
                      const who = pfSpecializationWho(therapistForm);
                      if (!who) return null;
                      return (
                        <p className="tt-intake-hint pf-summary">
                          Termine für {who.map((part, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && (i === who.length - 1 ? ' oder ' : ', ')}
                              <strong>{part}</strong>
                            </React.Fragment>
                          ))} werden nur {therapistForm.name.trim() || 'diesem Therapeuten'} zugewiesen.
                        </p>
                      );
                    })()}
                  </div>

                  {/* Die Versicherungsart: ein nicht angehakter Typ schließt diesen
                      Therapeuten aus. Der Durchstrich sagt das ohne Satz. */}
                  <div className="pf-group pf-group--limit">
                    <span className="pf-group-label">Einschränkung</span>
                    <div className="pf-grid">
                      <span className="pf-row-label">
                        Versicherung
                        <InfoTooltip text="Nicht angehakte Versicherungsarten schließen diesen Therapeuten für diese Patienten aus. Alle Haken gesetzt: keine Einschränkung." />
                      </span>
                      <div className="pf-chips" role="group" aria-label="Versicherungsarten">
                        {INSURANCE_KEYS.map((key) => {
                          const on = (therapistForm.pf_insurance_types || []).includes(key);
                          return (
                            <label key={key} className={`tt-cat-chip pf-toggle${on ? ' is-selected' : ' is-off'}`}>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => setTherapistForm(prev => {
                                  const cur = prev.pf_insurance_types || [];
                                  return {
                                    ...prev,
                                    pf_insurance_types: cur.includes(key)
                                      ? cur.filter(x => x !== key)
                                      : [...cur, key],
                                  };
                                })}
                              />
                              <span className="pf-toggle-text">{insuranceLabel(key)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {therapistFormError && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-tertiary)', marginTop: '14px' }}>
                    {therapistFormError}
                  </div>
                )}

                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button className="button" onClick={handleSaveTherapistDetails}>
                    Speichern
                  </button>
                  <button className="button-secondary" onClick={() => setTherapistModalOpen(false)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </Modal>

            {/* Insurance Type Settings Modal */}
            <Modal
              isOpen={insuranceSettingsModalOpen}
              onRequestClose={() => setInsuranceSettingsModalOpen(false)}
              style={{
                ...customModalStyles,
                content: {
                  ...customModalStyles.content,
                  minWidth: '380px',
                  maxWidth: '420px',
                },
              }}
              contentLabel="Versicherungsart Einstellungen"
            >
              <h2 style={{ marginBottom: '20px', fontSize: '1.1rem' }}>Einstellungen: {insuranceLabel(insuranceSettingsModalTab)}</h2>
              <div className="form-group">
                <label className="form-label">Verordnungsfrist</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    style={{ width: '100px' }}
                    value={insuranceSettingsForm.verordnungsfrist_days}
                    onChange={(e) => setInsuranceSettingsForm({ ...insuranceSettingsForm, verordnungsfrist_days: e.target.value })}
                    placeholder="-"
                  />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Tage</span>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Maximaler Terminabstand</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    style={{ width: '100px' }}
                    value={insuranceSettingsForm.max_terminabstand_days}
                    onChange={(e) => setInsuranceSettingsForm({ ...insuranceSettingsForm, max_terminabstand_days: e.target.value })}
                    placeholder="-"
                  />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Tage</span>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Neupatientenbehandlung</label>
                <select
                  className="form-input"
                  value={handlingSelectValue(insuranceSettingsForm, 'new')}
                  onChange={(e) => setInsuranceSettingsForm({ ...insuranceSettingsForm, ...handlingPatch(e.target.value, 'new') })}
                >
                  <option value="normal">Normal</option>
                  <option value="smartab">SmartAB</option>
                  <option value="send_link">Link senden</option>
                  <option value="normal_send_link">Buchen + Link senden</option>
                </select>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Bestandspatientenbehandlung</label>
                <select
                  className="form-input"
                  value={handlingSelectValue(insuranceSettingsForm, 'existing')}
                  onChange={(e) => setInsuranceSettingsForm({ ...insuranceSettingsForm, ...handlingPatch(e.target.value, 'existing') })}
                >
                  <option value="normal">Normal</option>
                  <option value="smartab">SmartAB</option>
                  <option value="normal_send_link">Buchen + Link senden</option>
                </select>
              </div>
              {(insuranceSettingsForm.new_patient_handling === 'send_link'
                || insuranceSettingsForm.new_patient_send_form_link === true
                || insuranceSettingsForm.existing_patient_send_form_link === true) && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label className="form-label">Formular-URL</label>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://..."
                    value={insuranceSettingsForm.new_patient_form_url || ''}
                    onChange={(e) => setInsuranceSettingsForm({ ...insuranceSettingsForm, new_patient_form_url: e.target.value })}
                  />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Dieser Link wird per SMS und per E-Mail mit der Terminbestätigung gesendet.
                  </span>
                </div>
              )}
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <button className="button" onClick={handleSaveInsuranceSettings}>
                  Speichern
                </button>
                <button className="button-secondary" onClick={() => setInsuranceSettingsModalOpen(false)}>
                  Abbrechen
                </button>
              </div>
            </Modal>

            {/* Import Preview Modal */}
            {importPreview && (
              <div className="import-modal-overlay" onClick={() => !importLoading && setImportPreview(null)}>
                <div className="import-modal" onClick={e => e.stopPropagation()}>
                  <h2>Import-Vorschau</h2>

                  <div className="import-modal-section">
                    <h3>Behandlungsarten ({importPreview.newTherapies.length} neu{importPreview.existingTherapies.length > 0 && `, ${importPreview.existingTherapies.length} ${importPreview.xlsx ? 'aktualisiert' : 'vorhanden'}`}{importPreview.deletedTherapies.length > 0 && `, ${importPreview.deletedTherapies.length} gelöscht`})</h3>
                    <ul className="import-modal-list">
                      {importPreview.newTherapies.map(t => (
                        <li key={t.name}>{t.name} <span className="import-modal-detail">({t.duration} Min)</span></li>
                      ))}
                      {importPreview.existingTherapies.map(t => (
                        <li key={t.name} className="import-modal-existing">{t.name} ({importPreview.xlsx ? 'aktualisiert' : 'vorhanden'})</li>
                      ))}
                      {importPreview.deletedTherapies.map(t => (
                        <li key={t.id} className="import-modal-deleted">{t.name} (wird gelöscht)</li>
                      ))}
                    </ul>
                  </div>

                  {!importPreview.xlsx && (
                  <div className="import-modal-section">
                    <h3>Standorte ({importPreview.newLocations.length} neu{importPreview.existingLocations.length > 0 && `, ${importPreview.existingLocations.length} vorhanden`}{importPreview.deletedLocations.length > 0 && `, ${importPreview.deletedLocations.length} gelöscht`})</h3>
                    <ul className="import-modal-list">
                      {importPreview.newLocations.map(n => (
                        <li key={n}>{n}</li>
                      ))}
                      {importPreview.existingLocations.map(n => (
                        <li key={n} className="import-modal-existing">{n} (vorhanden)</li>
                      ))}
                      {importPreview.deletedLocations.map(l => (
                        <li key={l.id} className="import-modal-deleted">{l.name} (wird gelöscht)</li>
                      ))}
                    </ul>
                  </div>
                  )}

                  {!importPreview.xlsx && (
                  <div className="import-modal-section">
                    <h3>Therapeuten ({importPreview.newTherapists.length} neu{importPreview.existingTherapists.length > 0 && `, ${importPreview.existingTherapists.length} vorhanden`}{importPreview.deletedTherapists.length > 0 && `, ${importPreview.deletedTherapists.length} gelöscht`})</h3>
                    <ul className="import-modal-list">
                      {importPreview.newTherapists.map(n => (
                        <li key={n}>{n}</li>
                      ))}
                      {importPreview.existingTherapists.map(n => (
                        <li key={n} className="import-modal-existing">{n} (vorhanden)</li>
                      ))}
                      {importPreview.deletedTherapists.map(t => (
                        <li key={t.id} className="import-modal-deleted">{t.name} (wird gelöscht)</li>
                      ))}
                    </ul>
                  </div>
                  )}

                  {importPreview.skippedCount > 0 && (
                    <p className="import-modal-skipped">
                      {importPreview.skippedCount} {importPreview.skippedNote || 'Einträge ohne Standort-Zuordnung übersprungen.'}
                    </p>
                  )}

                  <div className="import-modal-actions">
                    <button
                      className="button"
                      onClick={handleExecuteImport}
                      disabled={importLoading}
                    >
                      {importLoading ? 'Importiere...' : 'Importieren'}
                    </button>
                    <button
                      className="button-secondary button-secondary--lg"
                      onClick={() => setImportPreview(null)}
                      disabled={importLoading}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {showShortcutHelp && (
          <div
            className="shortcut-help-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) setShowShortcutHelp(false); }}
          >
            <div className="shortcut-help-modal" onClick={(e) => e.stopPropagation()}>
              <div className="shortcut-help-header">
                <h3 style={{ margin: 0 }}>Tastenkürzel</h3>
                <button
                  type="button"
                  className="shortcut-help-close"
                  onClick={() => setShowShortcutHelp(false)}
                  aria-label="Schließen"
                >×</button>
              </div>
              <div className="shortcut-help-body">
                <div className="shortcut-help-row" style={{ marginBottom: 12 }}><span className="keys"><kbd>?</kbd></span><span>Diese Übersicht ein-/ausblenden</span></div>
                <div className="shortcut-help-columns">
                  <div>
                    <div className="shortcut-help-column-title">Behandlungsarten</div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>N</kbd></span><span>Neue Behandlungsart (in der aktuellen Kategorie)</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>1</kbd></span><span>Privat</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>2</kbd></span><span>Gesetzlich</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>3</kbd></span><span>Selbstzahler</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>4</kbd></span><span>BG</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>5</kbd></span><span>Entlassmgmt</span></div>
                    <div className="shortcut-help-section-title">Mit Maus auf einer Zeile</div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>E</kbd></span><span>Bearbeiten</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>D</kbd></span><span>Aktivieren / deaktivieren</span></div>
                  </div>
                  <div>
                    <div className="shortcut-help-column-title">Therapeuten-Matrix</div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>0</kbd></span><span>Keine Zuordnung</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>1</kbd></span><span>Priorität hoch</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>2</kbd></span><span>Priorität mittel</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>3</kbd></span><span>Priorität niedrig</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>X</kbd></span><span>Nie buchen oder verschieben</span></div>
                    <div className="shortcut-help-section-title" style={{ color: '#aaa' }}>Hinweis</div>
                    <div style={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.4 }}>
                      Die Tasten <kbd>0</kbd>–<kbd>3</kbd> und <kbd>X</kbd> wirken nur, wenn die Maus über einer Prioritäts-Zelle steht.
                      Sonst schalten <kbd>1</kbd>–<kbd>5</kbd> den Verordnungstyp um.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'callflow' && (
          <div className="tab-content" style={{ position: 'relative' }}>
            <LoadingShroud loading={formLoadedFor !== practiceId || callflowLoadedFor !== practiceId || messagesLoadedFor !== practiceId} />
            {/* No Speichern button here on purpose. Everything on this tab persists
                itself: the switches and short values through the debounced per-practice
                config auto-save, the keyword chips / forwarding number / recording
                toggle through the practice-row auto-save, and each spoken message
                through its own save-and-generate button. The status slot stays —
                it is where a failed per-message save reports itself. (The KI-
                Konfiguration tab keeps its Speichern, which still runs the whole
                handleLlmConfigSave chain including the global filler sweep.) */}
            <div style={{ position: 'absolute', top: -20, right: 0, zIndex: 3, pointerEvents: 'none' }}>
              <SaveStatusSlot saving={llmSaving} status={llmSaveStatus} />
            </div>

            <form id="callflow-form" onSubmit={handleLlmConfigSave}>
              {/* Two-column layout: Functionality (left) | Messages (right) */}
              <div className="config-cards-grid" style={{ alignItems: 'start' }}>
                {/* LEFT COLUMN: Functionality Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* 1. Betriebsmodus (admin only) */}
                  {userIsAdmin && (
                  <CollapsibleSubCard
                    title="Betriebsmodus"
                    badge={kiMode === 'langgraph' ? 'KI-Praxisassistent' : kiMode === 'smart_ab' ? 'Smart-AB' : 'Digital-AB'}
                  >
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">KI-Modus</label>
                      <CustomDropdown
                        value={kiMode}
                        onChange={setKiMode}
                        options={[
                          { value: 'langgraph', label: 'KI-Praxisassistent' },
                          { value: 'smart_ab', label: 'Smart-AB' },
                          { value: 'digital_ab', label: 'Digital-AB' },
                        ]}
                      />
                    </div>

                    {/* LangGraph flow toggles (checkboxes only, no sub-configs) */}
                    <SwitchCollapse open={kiMode === 'langgraph'}>
                      <div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="as-switch"
                              checked={langgraphBookEnabled}
                              onChange={e => setLanggraphBookEnabled(e.target.checked)}
                            />
                            <span style={{ fontSize: '0.9rem' }}>Terminbuchung</span>
                          </label>
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="as-switch"
                              checked={langgraphCancelEnabled}
                              onChange={e => setLanggraphCancelEnabled(e.target.checked)}
                            />
                            <span style={{ fontSize: '0.9rem' }}>Termin-Stornierung</span>
                          </label>
                        </div>
                        <div style={{ marginBottom: '0' }}>
                          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="as-switch"
                              checked={langgraphMoveEnabled}
                              onChange={e => setLanggraphMoveEnabled(e.target.checked)}
                            />
                            <span style={{ fontSize: '0.9rem' }}>Terminverschiebung</span>
                          </label>
                        </div>
                      </div>
                    </SwitchCollapse>
                  </CollapsibleSubCard>
                  )}

                  {/* 1. Einstellungen für Terminanfragen — primary settings card */}
                  <CollapsibleSubCard
                    title="Einstellungen für Terminanfragen"
                    badge={`${[
                      requireBirthdate,
                      askSymptomsEnabled,
                      requireAddress,
                      userIsAdmin && requirePhone,
                      requireVerordnung,
                      kiMode === 'langgraph' && allowMultiTherapistSeries,
                      kiMode === 'langgraph' && requireInsuranceCompany,
                      kiMode === 'langgraph' && userIsSuperAdmin && enableMultiLocation,
                      (kiMode === 'langgraph' || kiMode === 'smart_ab') && requireWhatsappOptin,
                      kiMode === 'langgraph' && slotRasterEnabled,
                      kiMode === 'langgraph' && sameDayCutoffEnabled,
                    ].filter(Boolean).length} aktiv`}
                  >
                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={requireBirthdate} onChange={e => setRequireBirthdate(e.target.checked)} />
                        <span className="checkbox-label">Geburtsdatum anfordern</span>
                      </label>
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={askSymptomsEnabled} onChange={e => setAskSymptomsEnabled(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Beschwerden abfragen
                          <InfoTooltip text="Nach der Behandlungsart fragt die KI nach den Beschwerden. Haben Therapeuten Symptome hinterlegt, sucht die Terminvergabe bei passender Beschwerde nur bei diesen Therapeuten. Möchte der Anrufer keine Beschwerden nennen, geht die Buchung ohne sie weiter." />
                        </span>
                      </label>
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={requireAddress} onChange={e => setRequireAddress(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Adresse anfordern (neue Patienten)
                          <InfoTooltip text="Die KI fragt neue Patienten während der Terminbuchung nach ihrer vollständigen Adresse (Straße, Hausnummer, PLZ und Ort). Verweigert der Anrufer die Angabe, wird kein Termin gebucht – die Anfrage geht stattdessen als Aufgabe an das Praxisteam." />
                        </span>
                      </label>
                      {/* Admin-only: clients must not be able to disable phone collection */}
                      {userIsAdmin && (
                      <div>
                        <label className="checkbox-item">
                          <input type="checkbox" className="as-switch" checked={requirePhone} onChange={e => setRequirePhone(e.target.checked)} />
                          <span className="checkbox-label">Telefonnummer anfordern</span>
                        </label>
                        <SwitchCollapse open={requirePhone} compact>
                          <div style={{ marginLeft: '54px' }}>
                            <label className="checkbox-item">
                              <input type="checkbox" className="as-switch" checked={trustCallerId} onChange={e => setTrustCallerId(e.target.checked)} />
                              <span className="checkbox-label">Eingehende Telefonnummer nutzen</span>
                            </label>
                            {/* Per-number carve-out: numbers that are never trusted even
                                though caller IDs are used. Only meaningful when the caller
                                ID is trusted (either/or with the toggle above). */}
                            <SwitchCollapse open={trustCallerId} compact>
                              <div style={{ maxWidth: 420 }}>
                                <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                  Diese Nummern nie übernehmen
                                  <InfoTooltip text="Rufnummern, die nie als Anrufer-ID übernommen werden – z. B. wenn eine Praxis ihre eigene Nummer zu uns weiterleitet. Bei diesen Nummern fragt die KI immer nach der Telefonnummer, und die Nummer erscheint nicht in der Aufgabe (wird als unbekannt behandelt). Format egal: +49, 0049 oder 0 werden gleich behandelt." />
                                </span>
                                <FwdChipBox
                                  values={rejectCallerIds}
                                  onChange={(next) => setRejectCallerIds([...new Set(next)])}
                                  draft={rejectDraft}
                                  setDraft={setRejectDraft}
                                  removeLabel="Nummer entfernen"
                                  addLabel="Nummer hinzufügen"
                                  placeholder={rejectCallerIds.length === 0 ? 'z.B. +49 821 1234567' : ''}
                                />
                              </div>
                            </SwitchCollapse>
                          </div>
                        </SwitchCollapse>
                      </div>
                      )}
                      <label className="checkbox-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="checkbox" className="as-switch" checked={requireVerordnung} onChange={e => setRequireVerordnung(e.target.checked)} />
                          <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Verordnungsinformationen anfordern
                            <InfoTooltip text="Fragt, ob der Patient eine Verordnung hat - und falls ja, welche Behandlung, wie viele Sitzungen und welche Frequenz darauf vermerkt sind, sowie ob es sich um einen Erst- oder Folgetermin und ggf. um eine Hausbesuchs-Verordnung handelt." />
                          </span>
                        </div>
                      </label>
                      {kiMode === 'langgraph' && (
                      /* Toggle + count stay on ONE row. The dropdown must remain
                         OUTSIDE the <label>, otherwise clicking it bubbles to the
                         label and flips the switch. */
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <label className="checkbox-item">
                          <input type="checkbox" className="as-switch" checked={allowMultiTherapistSeries} onChange={e => setAllowMultiTherapistSeries(e.target.checked)} />
                          <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Mehrere Therapeuten in einer Serie erlauben
                            <InfoTooltip text="Aus: Alle Termine einer Buchung bleiben beim Therapeuten des ersten Termins, auch bei laufenden Verordnungen. Gewechselt wird nur, wenn der Anrufer ausdrücklich einen anderen Therapeuten wünscht. An: Hat der Haupttherapeut für einen Termin nichts Passendes frei, übernimmt ein weiterer Therapeut diesen Termin, höchstens so viele wie eingestellt; danach geht es beim Haupttherapeuten weiter." />
                          </span>
                        </label>
                        {allowMultiTherapistSeries && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>max.</span>
                            <CustomDropdown
                              value={maxTherapistsPerSeries}
                              onChange={v => setMaxTherapistsPerSeries(Number(v))}
                              size="sm"
                              options={maxTherapistOptions}
                            />
                            <span style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>Therapeuten</span>
                          </div>
                        )}
                      </div>
                      )}
                      {kiMode === 'langgraph' && (
                      <label className="checkbox-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input type="checkbox" className="as-switch" checked={requireInsuranceCompany} onChange={e => setRequireInsuranceCompany(e.target.checked)} />
                          <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Krankenkasse erfragen
                            <InfoTooltip text="Fragt bei welcher Krankenkasse der Patient versichert ist" />
                          </span>
                        </div>
                      </label>
                      )}
                      {/* Super-admin only: switched on without a location PVS name, it
                          silently disables the PVS slot prefetch for the practice. */}
                      {kiMode === 'langgraph' && userIsSuperAdmin && (
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={enableMultiLocation} onChange={e => setEnableMultiLocation(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Mehrere Standorte
                          <InfoTooltip text="Standortauswahl im Buchungsflow, wenn die Behandlung an mehreren Standorten angeboten wird" />
                        </span>
                      </label>
                      )}
                      {(kiMode === 'langgraph' || kiMode === 'smart_ab') && (
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={requireWhatsappOptin} onChange={e => setRequireWhatsappOptin(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          WhatsApp-Kontakt anfragen
                          <InfoTooltip text="Fragt nach der Buchung, ob der Patient per WhatsApp kontaktiert werden darf" />
                        </span>
                      </label>
                      )}
                      {kiMode === 'langgraph' && (
                      <div>
                        <label className="checkbox-item">
                          <input type="checkbox" className="as-switch" checked={slotRasterEnabled} onChange={e => setSlotRasterEnabled(e.target.checked)} />
                          <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Terminraster
                            <InfoTooltip text="Vorgeschlagene Termine beginnen nur zu vollen Rasterzeiten, auch wenn dadurch Lücken im Kalender entstehen. Vom Anrufer ausdrücklich gewünschte Uhrzeiten bleiben buchbar." />
                          </span>
                        </label>
                        <SwitchCollapse open={slotRasterEnabled} compact>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '0.5rem' }}>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>Raster:</span>
                            <CustomDropdown
                              value={slotRasterMinutes}
                              onChange={v => setSlotRasterMinutes(Number(v))}
                              size="sm"
                              options={[
                                { value: 15, label: 'Alle 15 Minuten (:00/:15/:30/:45)' },
                                { value: 20, label: 'Alle 20 Minuten (:00/:20/:40)' },
                                { value: 30, label: 'Zur vollen halben Stunde (:00/:30)' },
                                { value: 60, label: 'Zur vollen Stunde (:00)' },
                              ]}
                            />
                          </div>
                        </SwitchCollapse>
                      </div>
                      )}
                      {kiMode === 'langgraph' && (
                      /* One sentence on one row: label + Uhrzeit-Dropdown read as
                         "Keine Termine am Anruftag, wenn der Anruf nach 12:00 Uhr eingeht"
                         (all-day: "..., egal wann der Anruf eingeht" + "Ganztägig").
                         Dropdown and Info-Icon sit OUTSIDE the <label> on purpose:
                         inside it, every click on them would toggle the switch. */
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <label className="checkbox-item" style={{ alignItems: 'center' }}>
                          <input type="checkbox" className="as-switch" checked={sameDayCutoffEnabled} onChange={e => setSameDayCutoffEnabled(e.target.checked)} />
                          <span className="checkbox-label">
                            Keine Termine am Anruftag{!sameDayCutoffEnabled ? ''
                              : sameDayCutoffTime === SAME_DAY_CUTOFF_ALL_DAY ? ', egal wann der Anruf eingeht'
                              : ', wenn der Anruf nach'}
                          </span>
                        </label>
                        {sameDayCutoffEnabled && (
                          <CustomDropdown
                            value={sameDayCutoffTime}
                            onChange={v => setSameDayCutoffTime(String(v))}
                            size="sm"
                            fontSize="inherit"
                            fontWeight={500}
                            options={SAME_DAY_CUTOFF_OPTIONS}
                          />
                        )}
                        {sameDayCutoffEnabled && sameDayCutoffTime !== SAME_DAY_CUTOFF_ALL_DAY && (
                          /* .checkbox-label only styles DESCENDANTS of .checkbox-item, and this
                             tail sits outside the <label>, so it carries the weight/color itself. */
                          <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>eingeht</span>
                        )}
                        <InfoTooltip text="Ruft ein Patient ab der eingestellten Uhrzeit an, schlägt DeskBot für den laufenden Tag keine Termine mehr vor. Mit 'Ganztägig' werden nie Termine für den laufenden Tag angeboten, unabhängig von der Uhrzeit des Anrufs. Gilt auch für Verschiebungen." />
                      </div>
                      )}
                    </div>
                  </CollapsibleSubCard>

                  {/* 1b. Terminabsage und -verschiebung (visible to all users, only when langgraph mode) */}
                  {kiMode === 'langgraph' && (
                  <CollapsibleSubCard
                    title="Einstellungen für Terminabsagen und Verschiebungen"
                    badge={
                      !langgraphCancelEnabled ? 'Nur Verschiebung'
                        : cancellationFeePeriod === 0 ? 'Keine Stornofrist'
                        : cancellationFeePeriod <= -100 ? `Frist: Am Termintag (${-cancellationFeePeriod - 100}:00 Uhr)`
                        : cancellationFeePeriod < 0 ? `Frist: Vortag (${-cancellationFeePeriod}:00 Uhr)`
                        : `Stornofrist: ${cancellationFeePeriod} Std.`
                    }
                  >

                    {/* Cancellation sub-configs (only when Termin-Stornierung is enabled) */}
                    <SwitchCollapse open={langgraphCancelEnabled} compact>
                      {/* Single column on purpose: the control row changes width a lot between
                          "24 Stunden" and "Letzter Werktag vor dem Termin" (+ Uhrzeit). With an
                          `auto` first column the controls ate the row and squeezed the Ansage
                          into a ragged strip. Children below still declare gridColumn '1 / -1',
                          which stays valid in a one-column grid. */}
                      <div style={{ marginBottom: '0.75rem', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.75rem' }}>
                        {/* Stornofrist label + dropdown(s) */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <label className="form-label" style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 0, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Stornofrist:
                            <InfoTooltip text="Bis wann ein Termin kostenfrei abgesagt oder verschoben werden kann. 'X Stunden' rechnet ab dem Terminbeginn zurück. Im Modus 'Letzter Werktag vor dem Termin' endet die Frist zur eingestellten Uhrzeit am letzten Tag vor dem Termin, an dem die Praxis geöffnet ist, abgeleitet aus den konfigurierten Öffnungszeiten und den eingetragenen Feiertagen und Schließtagen. Beispiel: Termin am Montag, Praxis samstags und sonntags geschlossen, Uhrzeit 12:00 Uhr → die Frist endet am Freitag um 12:00 Uhr. Im Modus 'Am Tag des Termines' endet die Frist zur eingestellten Uhrzeit am Tag des Termins selbst; Termine an späteren Tagen sind damit immer kostenfrei absagbar, bis zu dieser Uhrzeit am Tag des Termines." />
                          </label>
                          {/* Band-encoded: <= -100 = same-day mode (hour = -value - 100), other negatives = last-working-day mode.
                              -108 / -12 are the representative options (the hour lives in the second dropdown) */}
                          <CustomDropdown
                            value={cancellationFeePeriod <= -100 ? -108 : cancellationFeePeriod < 0 ? -12 : cancellationFeePeriod}
                            onChange={v => setCancellationFeePeriod(Number(v))}
                            size="sm"
                            options={[
                              { value: 0, label: 'Keine' },
                              { value: 12, label: '12 Stunden' },
                              { value: 24, label: '24 Stunden' },
                              { value: -12, label: 'Letzter Werktag vor dem Termin' },
                              { value: -108, label: 'Am Tag des Termines' },
                            ]}
                          />
                          {cancellationFeePeriod < 0 && (
                            <>
                              <span style={{ fontSize: '0.85rem', color: '#64748b', whiteSpace: 'nowrap' }}>um</span>
                              <CustomDropdown
                                value={cancellationFeePeriod}
                                onChange={v => setCancellationFeePeriod(Number(v))}
                                size="sm"
                                options={cancellationFeePeriod <= -100
                                  ? [7, 8, 9].map(h => ({ value: -(h + 100), label: `${h}:00 Uhr` }))
                                  : Array.from({ length: 13 }, (_, i) => i + 7).map(h => ({ value: -h, label: `${h}:00 Uhr` }))}
                              />
                            </>
                          )}
                        </div>
                        {cancellationFeePeriod !== 0 && (() => {
                          const isEditing = editingFeeNotice;
                          return (
                            <div>
                              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0, marginBottom: '0.5rem', fontWeight: '500' }}>
                                Ansage, wenn nicht fristgerecht:
                              </p>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', lineHeight: 1.7, maxWidth: 720 }}>
                              <span style={{ color: '#64748b' }}>"Die kostenfreie Stornierungsfrist für diesen Termin ist bereits abgelaufen. </span>
                              {isEditing ? (
                                <input
                                  type="text"
                                  autoFocus
                                  value={cancellationFeeNotice}
                                  onChange={e => setCancellationFeeNotice(e.target.value)}
                                  onBlur={() => setEditingFeeNotice(false)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === 'Escape') {
                                      e.preventDefault();
                                      setEditingFeeNotice(false);
                                    }
                                  }}
                                  maxLength={500}
                                  style={{
                                    display: 'inline-block',
                                    padding: '1px 6px',
                                    border: '1px solid var(--color-secondary)',
                                    borderRadius: 4,
                                    background: '#fff',
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    color: 'var(--color-main)',
                                    fontWeight: 600,
                                    outline: 'none',
                                    minWidth: Math.max(120, (cancellationFeeNotice.length || 20) * 7) + 'px',
                                  }}
                                />
                              ) : (
                                <span
                                  onClick={() => setEditingFeeNotice(true)}
                                  title="Klicken zum Bearbeiten"
                                  style={{
                                    display: 'inline-block',
                                    padding: '1px 6px',
                                    border: '1px solid #d8dde5',
                                    borderRadius: 4,
                                    background: '#fbfcfd',
                                    fontWeight: 600,
                                    color: 'var(--color-main)',
                                    cursor: 'text',
                                    transition: 'border-color 0.15s ease, background 0.15s ease',
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.borderColor = 'var(--color-secondary)';
                                    e.currentTarget.style.background = 'color-mix(in srgb, var(--color-secondary) 5%, #fff)';
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.borderColor = '#d8dde5';
                                    e.currentTarget.style.background = '#fbfcfd';
                                  }}
                                >
                                  {cancellationFeeNotice || 'Es kann eine Ausfallgebühr anfallen'}
                                </span>
                              )}
                              <span style={{ color: '#64748b' }}>. Möchten Sie den Termin stornieren?"</span>
                              </div>
                            </div>
                          );
                        })()}
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label className="checkbox-item">
                            <input type="checkbox" className="as-switch" checked={acceptableCancellationReasonsEnabled} onChange={e => setAcceptableCancellationReasonsEnabled(e.target.checked)} />
                            <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              Akzeptable Stornierungsgründe
                              <InfoTooltip text="Nennt der Anrufer einen dieser Gründe für die Absage, wird der Termin sofort kulant storniert: keine Ausfallgebühr, keine Nachfrage zur Stornierungsfrist. Der Hinweis 'Die kostenfreie Stornierungsfrist für diesen Termin ist bereits abgelaufen…' wird in diesen Fällen übersprungen." />
                            </span>
                          </label>
                          <SwitchCollapse open={acceptableCancellationReasonsEnabled} compact>
                            <FwdChipBox
                              values={splitCommaList(acceptableCancellationReasons)}
                              onChange={(next) => setAcceptableCancellationReasons(next.join(', '))}
                              draft={reasonsDraft}
                              setDraft={setReasonsDraft}
                              removeLabel="Grund entfernen"
                              addLabel="Grund hinzufügen"
                            />
                          </SwitchCollapse>
                        </div>
                      </div>
                    </SwitchCollapse>

                    {/* Grund aktiv erfragen — gilt für Absage UND Verschiebung, daher außerhalb des Storno-Gates */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={askCancelMoveReasonEnabled} onChange={e => setAskCancelMoveReasonEnabled(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Grund für Absage oder Verschiebung aktiv erfragen
                          <InfoTooltip text="DeskBot fragt den Anrufer höflich nach dem Grund für die Absage bzw. Verschiebung. Das Gespräch läuft sofort weiter - egal was der Anrufer antwortet oder ob er die Antwort verweigert; es wird nie nachgehakt. Ist zusätzlich 'Akzeptable Stornierungsgründe' aktiv und passt der genannte Grund, entfällt die Nachfrage zur Ausfallgebühr. Bei aktiver Praxisprogramm-Anbindung wird der Grund mit der Absage übergeben. Ohne diese Option erfasst DeskBot einen Grund nur, wenn der Anrufer ihn von sich aus nennt." />
                        </span>
                      </label>
                    </div>

                    {/* Therapeuten-Erwähnung bei Storno/Verschiebung */}
                    <div>
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={mentionTherapistInCancelMove} onChange={e => setMentionTherapistInCancelMove(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Proaktiv Therapeutennamen bei Stornierung, Verschiebung oder Terminauskunft nennen
                          <InfoTooltip text="Greift, wenn der Bot dem Anrufer bestehende Termine vorliest - bei Terminauskunft, Stornierung oder Verschiebung. Aktiviert: Bot nennt den Therapeutennamen direkt mit - z.B. 'Sie haben am Dienstag um 14 Uhr einen Termin bei Frau Müller.'. Deaktiviert: Bot bleibt zunächst anonym ('Sie haben am Dienstag um 14 Uhr einen Termin.') und nennt den Therapeuten erst auf Nachfrage." />
                        </span>
                      </label>
                    </div>

                  </CollapsibleSubCard>
                  )}

                  {/* Einstellungen für Anruferauthentifizierung (booking-flow only).
                      Super-admin only: this is a practice-setup decision (default birthdate),
                      not something practice users should change — hidden for owner/member. */}
                  {kiMode === 'langgraph' && userIsSuperAdmin && (
                  <CollapsibleSubCard
                    title="Anrufererkennung"
                    badge={patientIdentificationMethod === 'birthdate' ? 'Geburtsdatum' : 'Telefonnummer'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label className="form-label" style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 0, whiteSpace: 'nowrap' }}>
                        Patientenerkennung über:
                      </label>
                      <CustomDropdown
                        value={patientIdentificationMethod}
                        onChange={setPatientIdentificationMethod}
                        size="sm"
                        options={[
                          { value: 'phone', label: 'Telefonnummer' },
                          { value: 'birthdate', label: 'Geburtsdatum' },
                        ]}
                      />
                      <InfoTooltip text="Welches Merkmal wird primär abgefragt, wenn der Anrufer nicht bereits über die Telefonnummer eindeutig erkannt werden konnte? Die Zuordnung erfolgt nur, wenn zusätzlich der korrekte Name übereinstimmt." />
                    </div>
                    {/* Nicht zugeordnete Bestandspatienten: statt Weitergabe ans Praxisteam als Neupatient buchen */}
                    <label className="checkbox-item" style={{ marginTop: '0.75rem' }}>
                      <input type="checkbox" className="as-switch" checked={bookUnidentifiedReturningAsNew} onChange={e => setBookUnidentifiedReturningAsNew(e.target.checked)} />
                      <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        Nicht zugeordnete Bestandspatienten als Neupatienten buchen
                        <InfoTooltip text="Gibt ein Anrufer an, bereits Patient zu sein, kann aber im Praxissystem nicht zugeordnet werden, läuft die Terminbuchung wie bei einem Neupatienten weiter und der Patient wird neu angelegt. Ausgeschaltet: Die Anfrage geht als Aufgabe an das Praxisteam, damit keine doppelten Patienten entstehen." />
                      </span>
                    </label>
                  </CollapsibleSubCard>
                  )}

                  {/* Benachrichtigungen und Bestätigungen — Anrufer-Bestätigung (SMS/E-Mail)
                      und Praxis-Benachrichtigung in einer gemeinsamen Karte. */}
                  {(kiMode === 'langgraph' || kiMode === 'smart_ab') && (
                  <CollapsibleSubCard
                    title="Benachrichtigungen und Bestätigungen"
                    badge={[
                      kiMode === 'langgraph' && smsToCallerEnabled && 'SMS',
                      kiMode === 'langgraph' && emailConfirmationEnabled && 'E-Mail',
                      smsToNumberEnabled && 'Praxis-SMS',
                    ].filter(Boolean).join(' · ') || 'Aus'}
                  >

                    {/* Terminbestätigung an den Anrufer — booking confirmations only
                        exist in the KI-Praxisassistent flow; Smart-AB doesn't book,
                        so only the Praxis-Benachrichtigung below applies there. */}
                    {kiMode === 'langgraph' && (
                    <>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Terminbestätigung an Patient:innen bei Buchung, Stornierung oder Änderung:
                    </p>
                    <label className="checkbox-item">
                      <input type="checkbox" className="as-switch" checked={smsToCallerEnabled} onChange={e => setSmsToCallerEnabled(e.target.checked)} />
                      <span className="checkbox-label">SMS an Anrufer</span>
                    </label>
                    <label className="checkbox-item">
                      <input type="checkbox" className="as-switch" checked={emailConfirmationEnabled} onChange={e => setEmailConfirmationEnabled(e.target.checked)} />
                      <span className="checkbox-label">E-Mail-Bestätigung</span>
                    </label>
                    </>
                    )}

                    {/* Praxis-Benachrichtigung */}
                    <div style={{ marginTop: '0.75rem' }}>
                      <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '500' }}>
                        Praxis-Benachrichtigung:
                      </p>
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={smsToNumberEnabled} onChange={e => setSmsToNumberEnabled(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          SMS an Nummer
                          <InfoTooltip text="Sendet eine SMS an eine feste Nummer (z.B. Empfangsmitarbeiter) bei jeder neuen Aufgabe." />
                        </span>
                      </label>
                      <SwitchCollapse open={smsToNumberEnabled} compact>
                        <div style={{ paddingLeft: '0.5rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#334155' }}>
                            Empfänger-Telefonnummer:
                            <input
                              type="text"
                              value={smsNotificationPhoneNumber}
                              onChange={e => setSmsNotificationPhoneNumber(e.target.value)}
                              placeholder="+4915123456789"
                              style={{ display: 'block', marginTop: '0.25rem', padding: '0.35rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', width: '220px' }}
                            />
                          </label>
                        </div>
                      </SwitchCollapse>
                    </div>

                    {/* Displayed sender — a per-practice setting, so every role that can
                        open this tab may edit it. The gateway selection and the seven.io
                        token moved to the KI-Konfiguration tab: both are global.
                        Rendered unconditionally: the gateway is no longer a per-practice
                        value, so hiding this behind it would let a platform-wide switch
                        hide a practice's own setting. It applies whenever seven.io carries
                        the message — as primary OR as fallback — which is why the help
                        text explains the AWS case instead of hiding the field. */}
                    <div style={{ marginTop: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#334155' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          SMS-Absender
                          <InfoTooltip text="Name, der beim Empfänger als Absender erscheint. Maximal 11 Zeichen, nur Buchstaben und Ziffern, mindestens ein Buchstabe – so schreibt es der SMS-Standard vor. Leer lassen für 'DeskBot'. Auf eine SMS mit Namen als Absender kann nicht geantwortet werden. Wird nur über seven.io angezeigt; über AWS SNS erscheint immer 'DeskBot'." />
                        </span>
                        <input
                          type="text"
                          value={smsSenderId}
                          onChange={e => setSmsSenderId(e.target.value)}
                          maxLength={11}
                          placeholder="DeskBot"
                          style={{ display: 'block', marginTop: '0.25rem', padding: '0.35rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', width: '220px' }}
                        />
                      </label>
                    </div>
                  </CollapsibleSubCard>
                  )}

                  {/* 3. Anrufweiterleitung
                      Vier Geltungsbereiche, jeder mit seinem eigenen Schalter als Kopf:
                      allgemeine Weiterleitung (Standardnummer + Ansage),
                      schlüsselwortbasierte Weiterleitung (Regelkarten), die
                      Direktweiterleitung (Whitelist) und der Digital-AB,
                      wenn niemand abnimmt. */}
                  {(() => {
                    const getForwardingPhoneError = (raw) => {
                      const val = (raw || '').trim();
                      if (!val) return 'Bitte eine Rufnummer eingeben.';
                      // Same rule as the backend's sanitize_phone_number (ai_config_store.py),
                      // so a field shown as valid is a number that actually gets dialled.
                      const compact = val.replace(/[\s\-()/.]/g, '');
                      if (!/^\+?[0-9]{3,20}$/.test(compact)) {
                        return 'Ungültiges Format: Nur Ziffern und ein optionales führendes + sind erlaubt.';
                      }
                      return null;
                    };

                    return (
                  <CollapsibleSubCard
                    title="Anrufweiterleitung"
                    badge={[
                      enableForwarding && 'Allgemein',
                      (kiMode === 'langgraph' || kiMode === 'smart_ab') && enableKeywordForwarding && `${(triggerKeywords.forwarding_rules || []).length} Regeln`,
                      enableDirectForwarding && 'Sofort',
                      enableWhitelistForwarding && !enableDirectForwarding && 'Whitelist',
                    ].filter(Boolean).join(' · ') || 'Aus'}
                  >

                    {/* Geltungsbereich 1: allgemeine Weiterleitung */}
                    <div className="fwd-group">
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={enableForwarding} onChange={e => setEnableForwarding(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Allgemeine Weiterleitung aktivieren
                          <InfoTooltip text="Erlaubt der KI, Anrufe an die Standard-Weiterleitungsnummer zu übergeben, wenn der Anrufer allgemein eine Person sprechen möchte. Unabhängig von der schlüsselwortbasierten Weiterleitung." />
                        </span>
                      </label>

                      <SwitchCollapse open={enableForwarding}>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Standard-Weiterleitungsnummer
                            <InfoTooltip text="Wird verwendet, wenn kein Schlüsselwort eine eigene Nummer bestimmt." />
                          </label>
                          {(() => {
                            const err = enableForwarding ? getForwardingPhoneError(practiceInfo.forwarding_phone_number) : null;
                            return (
                              <>
                                <input
                                  type="text"
                                  name="forwarding_phone_number"
                                  className="form-input"
                                  placeholder="z.B. +49 123 456789"
                                  value={practiceInfo.forwarding_phone_number || ''}
                                  onChange={handlePracticeInfoChange}
                                  style={err ? { borderColor: '#C2410C' } : undefined}
                                />
                                {err && (
                                  <span className="fwd-field-help fwd-field-help--error">{err}</span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </SwitchCollapse>
                      {/* Keyword rules without their own sentence speak this one too, so it
                          stays reachable with only keyword forwarding on (2px + 12px of the
                          two collapse bodies = the 14px gap it had inside one). */}
                      <SwitchCollapse open={enableForwarding || enableKeywordForwarding}>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Ansage bei Weiterleitung
                            <InfoTooltip text="Wird dem Anrufer vorgelesen, kurz bevor er weitergeleitet wird. Leer lassen für die Standard-Ansage. Gilt auch für Schlüsselwort-Regeln ohne eigene Ansage." />
                            {msgPreviewBtn('forwarding_message')}
                            {msgSaveBtn('forwarding_message')}
                          </label>
                          <textarea
                            className="form-input"
                            rows={2}
                            value={forwardingMessage}
                            onChange={e => setForwardingMessage(e.target.value)}
                            placeholder="Standard: Einen Moment bitte, ich stelle Sie durch."
                          />
                        </div>
                      </SwitchCollapse>
                    </div>

                    {/* Geltungsbereich 2: schlüsselwortbasierte Weiterleitung */}
                    {(kiMode === 'langgraph' || kiMode === 'smart_ab') && (
                    <div className="fwd-group">
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={enableKeywordForwarding} onChange={e => setEnableKeywordForwarding(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Schlüsselwortbasierte Weiterleitung aktivieren
                          <InfoTooltip text="Erlaubt der KI, Anrufe an die Nummer einer Weiterleitungsregel zu übergeben, wenn der Anrufer eines ihrer Schlüsselwörter nennt. Unabhängig von der allgemeinen Weiterleitung – die Regeln bleiben beim Ausschalten erhalten." />
                        </span>
                      </label>

                      <SwitchCollapse open={enableKeywordForwarding}>
                        <div className="fwd-rules">
                          {(triggerKeywords.forwarding_rules || []).map((rule, index) => {
                            const keywordList = (rule.keywords || '')
                              .split(',')
                              .map(s => s.trim())
                              .filter(Boolean);
                            const draft = keywordDrafts[index] || '';
                            const setDraft = (val) => setKeywordDrafts({ ...keywordDrafts, [index]: val });
                            const commitKeywords = (next) => {
                              const updated = [...triggerKeywords.forwarding_rules];
                              updated[index] = { ...updated[index], keywords: next.join(', ') };
                              setTriggerKeywords({ ...triggerKeywords, forwarding_rules: updated });
                            };
                            return (
                            <div key={index} className="fwd-rule">
                              <div className="fwd-rule-head">
                                <span className="fwd-rule-title">Regel {index + 1}</span>
                                <button
                                  type="button"
                                  className="fwd-rule-remove"
                                  onClick={() => {
                                    const updated = triggerKeywords.forwarding_rules.filter((_, i) => i !== index);
                                    setTriggerKeywords({ ...triggerKeywords, forwarding_rules: updated });
                                  }}
                                  title="Regel entfernen"
                                >
                                  Entfernen
                                </button>
                              </div>

                              <div className="fwd-rule-fields">
                                <div className="fwd-field">
                                  <span className="fwd-field-label">Schlüsselwörter</span>
                                  <FwdChipBox
                                    values={keywordList}
                                    onChange={commitKeywords}
                                    draft={draft}
                                    setDraft={setDraft}
                                    removeLabel="Schlüsselwort entfernen"
                                    addLabel="Schlüsselwort hinzufügen"
                                  />
                                </div>

                                <div className="fwd-rule-grid">
                                  <div className="fwd-field">
                                    <span className="fwd-field-label">Weiterleitungsnummer</span>
                                    {(() => {
                                      const err = enableKeywordForwarding ? getForwardingPhoneError(rule.phone_number) : null;
                                      return (
                                        <>
                                          <input
                                            type="text"
                                            className="form-input"
                                            value={rule.phone_number || ''}
                                            onChange={(e) => {
                                              const updated = [...triggerKeywords.forwarding_rules];
                                              updated[index] = { ...updated[index], phone_number: e.target.value };
                                              setTriggerKeywords({ ...triggerKeywords, forwarding_rules: updated });
                                            }}
                                            placeholder="z.B. +49 123 456789"
                                            aria-label="Weiterleitungsnummer"
                                            style={err ? { borderColor: '#C2410C' } : undefined}
                                          />
                                          {err && (
                                            <span className="fwd-field-help fwd-field-help--error">{err}</span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <div className="fwd-field">
                                    {/* The audio buttons key off the rule's id, so they
                                        only appear once it has one (a rule added in this
                                        session has it immediately; a legacy stored rule
                                        gets one on its next save). */}
                                    <span className="fwd-field-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                      Eigene Ansage
                                      {rule.id && msgPreviewBtn(`forwarding_rule_${rule.id}`)}
                                      {rule.id && msgSaveBtn(`forwarding_rule_${rule.id}`)}
                                    </span>
                                    <input
                                      type="text"
                                      className="form-input"
                                      value={rule.message || ''}
                                      onChange={(e) => {
                                        const updated = [...triggerKeywords.forwarding_rules];
                                        updated[index] = { ...updated[index], message: e.target.value };
                                        setTriggerKeywords({ ...triggerKeywords, forwarding_rules: updated });
                                      }}
                                      placeholder="Optional, sonst wird die allgemeine Ansage verwendet"
                                      aria-label="Eigene Ansage"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          className="fwd-add-rule"
                          onClick={() => setTriggerKeywords({
                            ...triggerKeywords,
                            forwarding_rules: [
                              ...(triggerKeywords.forwarding_rules || []),
                              // Minted here so the new rule has a key for its Ansage
                              // buttons immediately, before any round-trip. The backend
                              // regenerating one it rejects is the safety net, not this.
                              { id: newForwardingRuleId(), keywords: '', phone_number: practiceInfo.forwarding_phone_number || '', message: '' }
                            ]
                          })}
                        >
                          + Regel hinzufügen
                        </button>
                      </SwitchCollapse>
                    </div>
                    )}

                    {/* Geltungsbereich 3 & 4: Sofortige Weiterleitung (alle Anrufe) und
                        Whitelist-Weiterleitung (bestimmte Anrufer). Beide leiten vor der
                        Annahme an die Weiterleitungsnummer weiter (ari_worker pre-answer). */}
                    {(() => {
                      const getPhoneError = getForwardingPhoneError;

                      const isValidPhone = (num) => {
                        const val = (num || '').trim();
                        return !!val && getPhoneError(val) === null;
                      };

                      const validDirectNumber = isValidPhone(directForwardingNumber);
                      const validWhitelistNumber = isValidPhone(whitelistForwardingNumber);
                      const whitelistCount = whitelistEntriesToConfig(whitelistEntries).whitelist_caller_ids.length;

                      const directMode = !enableDirectForwarding ? 'off' : !validDirectNumber ? 'missing' : 'all';
                      const directPill = {
                        all: 'Alle Anrufe',
                        missing: 'Nummer fehlt',
                      }[directMode];

                      const whitelistMode = !enableWhitelistForwarding ? 'off'
                        : enableDirectForwarding && validDirectNumber ? 'overruled'
                        : !validWhitelistNumber ? 'missing'
                        : whitelistCount === 0 ? 'missing'
                        : 'list';
                      const whitelistPill = {
                        overruled: 'Durch Direktweiterleitung überschrieben',
                        list: `${whitelistCount} ${whitelistCount === 1 ? 'Rufnummer' : 'Rufnummern'}`,
                        missing: !validWhitelistNumber ? 'Nummer fehlt' : 'Keine Rufnummer',
                      }[whitelistMode];

                      const matrixTherapists = priorityMatrix?.therapists || [];
                      const normName = (s) => (s || '').trim().toLowerCase();
                      const findMatchingTherapist = (entry) => {
                        const eName = normName(entry.name);
                        const ePhone = comparablePhoneNumber(entry.number);
                        return matrixTherapists.find(t =>
                          (ePhone && comparablePhoneNumber(t.phone) === ePhone) ||
                          (eName && normName(t.name) === eName)
                        );
                      };
                      const therapistsToAdd = matrixTherapists.filter(t => !whitelistEntries.some(e => findMatchingTherapist(e)?.id === t.id));

                      const addTherapists = (list) => setWhitelistEntries(prev => {
                        const seenPhones = new Set(prev.map(e => comparablePhoneNumber(e.number)).filter(Boolean));
                        const seenNames = new Set(prev.map(e => normName(e.name)).filter(Boolean));
                        const added = list.filter((t) => {
                          const phoneKey = comparablePhoneNumber(t.phone);
                          const nameKey = normName(t.name);
                          if ((phoneKey && seenPhones.has(phoneKey)) || (nameKey && seenNames.has(nameKey))) return false;
                          if (phoneKey) seenPhones.add(phoneKey);
                          if (nameKey) seenNames.add(nameKey);
                          return true;
                        });
                        return [...prev, ...added.map(t => ({ number: (t.phone || '').trim(), name: t.name, type: 'Therapeut' }))];
                      });

                      const syncTherapistPhoneIfNeeded = async (entry) => {
                        const trimmedPhone = (entry.number || '').trim();
                        if (!comparablePhoneNumber(trimmedPhone)) return;
                        const matched = matrixTherapists.find(t => normName(t.name) === normName(entry.name));
                        if (!matched || comparablePhoneNumber(matched.phone) === comparablePhoneNumber(trimmedPhone)) return;
                        try {
                          await axios.put(`${API_BASE_URL}/therapists/${matched.id}/details`, {
                            pvs_name: matched.pvs_name || null,
                            phone: trimmedPhone,
                          });
                          setPriorityMatrix(prev => prev ? {
                            ...prev,
                            therapists: (prev.therapists || []).map(t => t.id === matched.id ? { ...t, phone: trimmedPhone } : t),
                          } : prev);
                        } catch (err) {
                          console.error('Failed to sync therapist phone from whitelist:', err);
                        }
                      };

                      const renderTargetNumberField = (value, onChange) => {
                        const errorMsg = getPhoneError(value);
                        return (
                          <div className="fwd-field">
                            <span className="fwd-field-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              Weiterleitungsnummer
                              <InfoTooltip text="Eine Nummer, die nicht auf DeskBot umgeleitet ist. Sonst kommt der Anruf wieder bei DeskBot an." />
                            </span>
                            <input
                              type="text"
                              className="form-input"
                              value={value}
                              onChange={e => onChange(e.target.value)}
                              placeholder="z.B. +49 123 456789"
                              aria-label="Weiterleitungsnummer"
                              style={errorMsg ? { borderColor: '#C2410C' } : undefined}
                            />
                            {errorMsg && (
                              <span className="fwd-field-help fwd-field-help--error">{errorMsg}</span>
                            )}
                          </div>
                        );
                      };

                      return (
                        <>
                          {/* 3. Sofortige Weiterleitung (alle Anrufe) */}
                          <div className="fwd-group">
                            <div className="fwd-group-head">
                              <label className="checkbox-item">
                                <input type="checkbox" className="as-switch" checked={enableDirectForwarding} onChange={e => setEnableDirectForwarding(e.target.checked)} />
                                <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  Direktweiterleitung aktivieren (alle Anrufe)
                                  <InfoTooltip text="Alle eingehenden Anrufe werden sofort an die Weiterleitungsnummer durchgestellt, ohne dass die KI abnimmt." />
                                </span>
                              </label>
                              {directPill && <span className={`fwd-mode-pill fwd-mode-pill--${directMode}`}>{directPill}</span>}
                            </div>

                            <SwitchCollapse open={enableDirectForwarding}>
                              <div className="fwd-rule">
                                <div className="fwd-rule-fields">
                                  {renderTargetNumberField(directForwardingNumber, setDirectForwardingNumber)}
                                </div>
                              </div>
                            </SwitchCollapse>
                          </div>

                          {/* 4. Whitelist-Weiterleitung (bestimmte Anrufer) */}
                          <div className="fwd-group">
                            <div className="fwd-group-head">
                              <label className="checkbox-item">
                                <input type="checkbox" className="as-switch" checked={enableWhitelistForwarding} onChange={e => setEnableWhitelistForwarding(e.target.checked)} />
                                <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  Whitelist-Weiterleitung aktivieren
                                  <InfoTooltip text="Nur Anrufer auf dieser Liste (z.B. Therapeuten oder Ärzte) werden sofort an die Weiterleitungsnummer durchgestellt, ohne dass die KI abnimmt. Alle anderen Anrufer sprechen wie gewohnt mit der KI." />
                                </span>
                              </label>
                              {whitelistPill && (
                                <span className={`fwd-mode-pill fwd-mode-pill--${whitelistMode === 'overruled' ? 'all' : whitelistMode}`}>
                                  {whitelistPill}
                                </span>
                              )}
                            </div>

                            <SwitchCollapse open={enableWhitelistForwarding}>
                              <div className="fwd-rule">
                                <div className="fwd-rule-fields">
                                  {enableDirectForwarding && validDirectNumber && (
                                    <span className="fwd-field-help">
                                      Hinweis: Da die Direktweiterleitung für alle Anrufe aktiv ist, werden derzeit alle Anrufe an die Nummer der Direktweiterleitung weitergeleitet.
                                    </span>
                                  )}
                                  {renderTargetNumberField(whitelistForwardingNumber, setWhitelistForwardingNumber)}

                                  <div className="fwd-field">
                                    <span className="fwd-field-label">Weiterzuleitende Kontakte (Whitelist)</span>
                                    {matrixTherapists.length > 0 && (
                                      <div className="fwd-wl-therapists">
                                        {therapistsToAdd.length > 0 ? (
                                          <div className="pf-chips">
                                            <span className="pf-row-label">Therapeuten auswählen:</span>
                                            {therapistsToAdd.length > 1 && (
                                              <button
                                                type="button"
                                                className="tt-cat-chip"
                                                onClick={() => addTherapists(therapistsToAdd)}
                                              >
                                                + Alle Therapeuten ({therapistsToAdd.length})
                                              </button>
                                            )}
                                            {therapistsToAdd.map(t => (
                                              <button
                                                key={t.id}
                                                type="button"
                                                className="tt-cat-chip"
                                                onClick={() => addTherapists([t])}
                                                title={t.phone ? t.phone : 'Noch keine Rufnummer hinterlegt — nach dem Auswählen eintragen'}
                                              >
                                                + {t.name}
                                              </button>
                                            ))}
                                          </div>
                                        ) : (
                                          <span className="fwd-field-help">Alle Therapeuten stehen auf der Whitelist.</span>
                                        )}
                                      </div>
                                    )}
                                    {whitelistEntries.length > 0 && (
                                      <div className="fwd-wl-list">
                                        <div className="fwd-wl-row fwd-wl-head fwd-field-label" aria-hidden="true">
                                          <span>Name</span>
                                          <span>Rufnummer</span>
                                          <span>Typ</span>
                                        </div>
                                        {whitelistEntries.map((entry, entryIndex) => {
                                          const isTherapistRow = matrixTherapists.some(t => normName(t.name) === normName(entry.name));
                                          const missingRowPhone = !comparablePhoneNumber(entry.number);
                                          return (
                                            <div key={entryIndex} className="fwd-wl-row">
                                              {[
                                                { field: 'name', label: 'Name', placeholder: 'z.B. Dr. Weber' },
                                                {
                                                  field: 'number',
                                                  label: 'Rufnummer',
                                                  placeholder: isTherapistRow ? 'Rufnummer eintragen (speichert im Profil)' : 'z.B. 0151 23456789',
                                                },
                                                { field: 'type', label: 'Typ', placeholder: 'z.B. Arzt' },
                                              ].map(({ field, label, placeholder }) => (
                                                <input
                                                  key={field}
                                                  type={field === 'number' ? 'tel' : 'text'}
                                                  className="form-input"
                                                  value={entry[field]}
                                                  style={field === 'number' && missingRowPhone ? { borderColor: '#F59E0B' } : undefined}
                                                  onChange={e => {
                                                    const value = e.target.value;
                                                    setWhitelistEntries(prev => prev.map((row, i) => (i === entryIndex ? { ...row, [field]: value } : row)));
                                                  }}
                                                  onBlur={() => {
                                                    if (field === 'number') {
                                                      syncTherapistPhoneIfNeeded(entry);
                                                    }
                                                  }}
                                                  placeholder={placeholder}
                                                  aria-label={label}
                                                />
                                              ))}
                                              <button
                                                type="button"
                                                className="fwd-rule-remove"
                                                onClick={() => setWhitelistEntries(prev => prev.filter((_, i) => i !== entryIndex))}
                                                title="Eintrag entfernen"
                                                aria-label="Eintrag entfernen"
                                              >
                                                ×
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className="fwd-add-rule"
                                      onClick={() => setWhitelistEntries(prev => [...prev, { number: '', name: '', type: '' }])}
                                    >
                                      + Kontakt hinzufügen
                                    </button>
                                    <span className="fwd-field-help">
                                      Format egal: +49, 0049 oder 0 werden gleich behandelt. Bei Therapeuten eingetragene Rufnummern werden automatisch im Therapeuten-Profil gespeichert.
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </SwitchCollapse>
                          </div>
                        </>
                      );
                    })()}

                    {/* Geltungsbereich 4: Digital-AB, wenn niemand abnimmt. Gilt für
                        jede Weiterleitung (allgemein, Schlüsselwort, Whitelist, Übernahme im
                        Call-Monitor). */}
                    <div className="fwd-group">
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={forwardNoanswerVoicemail} onChange={e => setForwardNoanswerVoicemail(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Digital-AB, wenn nach
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            value={forwardNoanswerRingSeconds}
                            onChange={e => { const v = e.target.value.replace(/\D/g, ''); setForwardNoanswerRingSeconds(v === '' ? '' : Number(v)); }}
                            onBlur={() => { if (forwardNoanswerRingSeconds === '') setForwardNoanswerRingSeconds(10); }}
                            style={{ width: '2.2em', padding: '0 2px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                          />
                          Sekunden niemand abnimmt
                          <InfoTooltip text="Nimmt nach der Weiterleitung niemand ab, landet der Anrufer auf dem Digital-AB. Die Nachricht erscheint in der Aufgabe." />
                        </span>
                      </label>
                    </div>
                  </CollapsibleSubCard>
                    );
                  })()}

                  {/* Smart-AB Aktivierung im Gespräch — eigene Karte, da inhaltlich
                      vom Weiterleitungs-Block getrennt (wechselt in den AB-Modus statt
                      an eine Person zu verbinden). */}
                  <CollapsibleSubCard
                    title="Smart-AB Aktivierung im Gespräch"
                    badge={`${splitCommaList(triggerKeywords.smartab_keywords).length} Wörter`}
                  >
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        Schlüsselwort-Regeln
                        <InfoTooltip text="Bei diesen Wörtern wechselt das System in den SmartAB-Modus. 'Rückruf' wirkt hier nicht: Bei einem Rückrufwunsch bietet der Assistent zuerst seine Hilfe an." />
                      </label>
                      <FwdChipBox
                        values={splitCommaList(triggerKeywords.smartab_keywords)}
                        onChange={(next) => setTriggerKeywords({ ...triggerKeywords, smartab_keywords: next.join(', ') })}
                        draft={smartabDraft}
                        setDraft={setSmartabDraft}
                        removeLabel="Schlüsselwort entfernen"
                        addLabel="Schlüsselwort hinzufügen"
                      />
                    </div>
                  </CollapsibleSubCard>

                </div>

                {/* RIGHT COLUMN: Message Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* 0. Datenschutzmaßnahmen: Anrufaufzeichnung, Einwilligungsfrage and the data
                      retention switch share one card (the consent question is what makes
                      recording lawful). */}
                  <CollapsibleSubCard
                    title="Datenschutzmaßnahmen"
                    badge={[
                      practiceInfo.recording_enabled ? 'Aufzeichnung an' : 'Aufzeichnung aus',
                      dataRetentionEnabled && `Löschung ${dataRetentionDays || 30}d`,
                    ].filter(Boolean).join(' · ')}
                  >
                    <SwitchCollapse open={Boolean(practiceInfo.recording_enabled)} compact>
                      <div style={{ marginBottom: '18px' }}>
                        <h4 style={{ marginTop: 0 }}>
                          Einwilligungsfrage
                          {msgPreviewBtn('consent')}
                          {msgSaveBtn('consent')}
                        </h4>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <InlineEditableTextarea
                            value={consentQuestion}
                            onChange={e => setConsentQuestion(e.target.value)}
                            maxLength={500}
                          />
                        </div>
                      </div>
                    </SwitchCollapse>
                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          className="as-switch"
                          checked={practiceInfo.recording_enabled}
                          onChange={(e) => {
                            // Persisted on the practice row → a remote reload would
                            // revert it, so it counts as an unsaved practice edit.
                            setPracticeTabDirty(true);
                            setPracticeInfo({ ...practiceInfo, recording_enabled: e.target.checked });
                          }}
                        />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Gesprächsaufzeichnung nach Einwilligung aktivieren
                          <InfoTooltip text="Sprachaufnahmen werden während KI-Gesprächen gespeichert, wenn die Person die Einwilligung gibt. Die Einwilligung ist nach § 201 StGB erforderlich." />
                        </span>
                      </label>
                      {/* Upfront consent: KI-Praxisassistent only (the call reads use_langgraph too). */}
                      {kiMode === 'langgraph' && (
                      <SwitchCollapse open={Boolean(practiceInfo.recording_enabled)} compact>
                        <label className="checkbox-item">
                          <input type="checkbox" className="as-switch" checked={consentUpfront} onChange={e => setConsentUpfront(e.target.checked)} />
                          <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Einwilligungsfrage direkt nach der Begrüßung stellen
                            <InfoTooltip text="Die Einwilligungsfrage wird direkt an die Begrüßung angehängt. Die Begrüßungstexte sollten dann nicht mit einer Frage enden." />
                          </span>
                        </label>
                      </SwitchCollapse>
                      )}
                      {/* Data retention: every KI mode creates tasks/voicemails, so shown for all. */}
                      <label className="checkbox-item">
                        <input type="checkbox" className="as-switch" checked={dataRetentionEnabled} onChange={e => setDataRetentionEnabled(e.target.checked)} />
                        <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Personenbezogene Daten nach
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={3}
                            value={dataRetentionDays}
                            onChange={e => { const v = e.target.value.replace(/\D/g, ''); setDataRetentionDays(v === '' ? '' : Number(v)); }}
                            onBlur={() => { if (dataRetentionDays === '') setDataRetentionDays(30); else if (dataRetentionDays < 7) setDataRetentionDays(7); }}
                            style={{ width: '2.8em', padding: '0 2px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                          />
                          Tagen löschen
                          <InfoTooltip text="Jede Nacht werden Aufgaben, Gesprächsverläufe, Aufnahmen, Sprachnachrichten und DOC-IQ-Formulare gelöscht, deren Anruf länger als die eingestellte Zahl an Tagen zurückliegt, außerdem Termine im Kalender, die so lange vorbei sind. Die Anrufstatistik bleibt ohne Namen, Nummern und Geburtsdaten erhalten. Gelöschte Daten lassen sich nicht wiederherstellen." />
                        </span>
                      </label>
                    </div>
                  </CollapsibleSubCard>

                  {/* 1. Begrüßungs- und Abschiedsnachrichten (unified card) */}
                  <CollapsibleSubCard
                    title="Begrüßungs- und Abschiedsnachrichten"
                    badge={`${2 + [
                      pvsEnabled && enableGreetingPvs,
                      enableGreetingAway,
                      pvsEnabled && enableGreetingAway && enableGreetingPvs && Boolean(greetingMessagePvsAway?.trim()),
                      (kiMode === 'agentic' || kiMode === 'langgraph') && enableFarewellAppointment,
                    ].filter(Boolean).length} aktiv`}
                    preview={greetingMessage}
                  >
                    {/* Category 1: Begrüßungsnachricht */}
                    <div className="fwd-group">
                      <h4 style={{ marginTop: 0 }}>
                        Allgemeine Begrüßungsnachricht
                        {msgPreviewBtn('greeting')}
                        {msgSaveBtn('greeting')}
                      </h4>
                      <div className="form-group" style={{ marginBottom: '20px' }}>
                        <InlineEditableTextarea
                          value={greetingMessage}
                          onChange={e => setGreetingMessage(e.target.value)}
                          maxLength={500}
                        />
                      </div>

                      <div className="checkbox-grid" style={{ gap: '20px' }}>
                        {/* Switch 1a: Bekannte Patienten (PVS) — shown first as primary focus */}
                        {pvsEnabled && (
                          <div>
                            <label className="checkbox-item">
                              <input
                                type="checkbox"
                                className="as-switch"
                                checked={enableGreetingPvs}
                                onChange={e => setEnableGreetingPvs(e.target.checked)}
                              />
                              <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                Eigene Begrüßung für bekannte Patienten
                                <InfoTooltip text='Wird als "Hallo [Name], [diese Nachricht]" abgespielt, wenn der Anrufer im PVS erkannt wird' />
                                {enableGreetingPvs && msgPreviewBtn('greeting_pvs_demo')}
                                {enableGreetingPvs && msgSaveBtn('greeting_pvs_demo')}
                              </span>
                            </label>
                            <SwitchCollapse open={enableGreetingPvs} compact>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <InlineEditableTextarea
                                  value={greetingMessagePvs}
                                  onChange={e => setGreetingMessagePvs(e.target.value)}
                                  maxLength={500}
                                />
                              </div>
                            </SwitchCollapse>
                          </div>
                        )}

                        {/* Switch 1b: Empfang abwesend */}
                        <div>
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              className="as-switch"
                              checked={enableGreetingAway}
                              onChange={e => setEnableGreetingAway(e.target.checked)}
                            />
                            <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              Eigene Begrüßung bei abwesendem Empfang
                              <InfoTooltip text="Optional. Wird statt der normalen Begrüßung abgespielt, wenn der Empfang nicht besetzt ist (Abwesenheit, Feiertag oder außerhalb der Empfangszeiten). Leer = normale Begrüßung." />
                              {enableGreetingAway && msgPreviewBtn('greeting_away')}
                              {enableGreetingAway && msgSaveBtn('greeting_away')}
                            </span>
                          </label>
                          <SwitchCollapse open={enableGreetingAway} compact>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <InlineEditableTextarea
                                value={greetingMessageAway}
                                onChange={e => setGreetingMessageAway(e.target.value)}
                                placeholder="z.B. Praxis Mustermann, was kann ich für Sie tun? Unser Empfang ist gerade nicht besetzt."
                                maxLength={500}
                              />
                            </div>
                          </SwitchCollapse>
                        </div>

                        {/* Combined: Both checked -> Bekannte Patienten + Empfang abwesend */}
                        {pvsEnabled && (
                          <SwitchCollapse open={enableGreetingAway && enableGreetingPvs} compact>
                            <div style={{ paddingLeft: '14px', borderLeft: '2px solid var(--border-light)', marginTop: '2px' }}>
                              <label className="form-label" style={{ fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '8px' }}>
                                Begrüßung für bekannte Patienten bei abwesendem Empfang (optional)
                                <InfoTooltip text="Optional. Variante für erkannte Patienten, wenn der Empfang nicht besetzt ist. Leer = normale Begrüßung." />
                                {msgPreviewBtn('greeting_pvs_away_demo')}
                                {msgSaveBtn('greeting_pvs_away_demo')}
                              </label>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <InlineEditableTextarea
                                  value={greetingMessagePvsAway}
                                  onChange={e => setGreetingMessagePvsAway(e.target.value)}
                                  placeholder="z.B. schön dass Sie wieder anrufen. Unser Empfang ist gerade nicht besetzt."
                                  maxLength={500}
                                />
                              </div>
                            </div>
                          </SwitchCollapse>
                        )}
                      </div>
                    </div>

                    {/* Category 2: Abschiedsnachricht */}
                    <div className="fwd-group" style={{ marginTop: '24px', paddingTop: '24px' }}>
                      <h4 style={{ marginTop: 0 }}>
                        Allgemeine Abschiedsnachricht
                        {msgPreviewBtn('farewell_general')}
                        {msgSaveBtn('farewell_general')}
                      </h4>
                      <div className="form-group" style={{ marginBottom: (kiMode === 'agentic' || kiMode === 'langgraph') ? '20px' : 0 }}>
                        <InlineEditableTextarea
                          value={farewellGeneral}
                          onChange={e => setFarewellGeneral(e.target.value)}
                          maxLength={500}
                        />
                      </div>

                      {(kiMode === 'agentic' || kiMode === 'langgraph') && (
                        <div>
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              className="as-switch"
                              checked={enableFarewellAppointment}
                              onChange={e => setEnableFarewellAppointment(e.target.checked)}
                            />
                            <span className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              Eigene Abschiedsnachricht nach Terminbuchung
                              <InfoTooltip text="Wird nach erfolgreicher Terminbuchung oder -änderung abgespielt" />
                              {enableFarewellAppointment && msgPreviewBtn('farewell_appointment')}
                              {enableFarewellAppointment && msgSaveBtn('farewell_appointment')}
                            </span>
                          </label>
                          <SwitchCollapse open={enableFarewellAppointment} compact>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <InlineEditableTextarea
                                value={farewellAppointment}
                                onChange={e => setFarewellAppointment(e.target.value)}
                                maxLength={500}
                              />
                            </div>
                          </SwitchCollapse>
                        </div>
                      )}
                    </div>
                  </CollapsibleSubCard>

                  {/* 4. Digital-AB */}
                  <CollapsibleSubCard
                    title="Digital-AB"
                    tooltip="Wird abgespielt, wenn bereits ein Anruf läuft oder niemand eine Weiterleitung annimmt."
                    preview={voicemailMessage}
                  >
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '0.5rem' }}>
                        Ansage wenn KI-Assistent beschäftigt ist
                        {msgPreviewBtn('voicemail_announcement')}
                        {msgSaveBtn('voicemail_announcement')}
                      </label>
                      <InlineEditableTextarea
                        value={voicemailMessage}
                        onChange={e => setVoicemailMessage(e.target.value)}
                        maxLength={500}
                      />
                    </div>
                  </CollapsibleSubCard>

                  {/* Warteschleife — only when SIP mode is 'hold' (configured under SIP) */}
                  {sipConfig.parallel_call_mode === 'hold' && (
                  <CollapsibleSubCard
                    title="Warteschleife"
                    tooltip="Wird dem Anrufer vorgespielt, während er in der Warteschleife wartet. Optional kann zusätzlich Musik hochgeladen werden, die in Dauerschleife läuft."
                    badge={holdMusicExists ? 'Mit Musik' : 'Nur Ansage'}
                  >
                    <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                      <label className="form-label" style={{ fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '0.5rem' }}>
                        Ansage in der Warteschleife
                        {msgPreviewBtn('hold_message')}
                        {msgSaveBtn('hold_message')}
                      </label>
                      <InlineEditableTextarea
                        value={holdMessage}
                        onChange={e => setHoldMessage(e.target.value)}
                        maxLength={500}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.9rem' }}>
                        Wartemusik
                      </label>
                      <input
                        type="file"
                        accept=".wav,.mp3,audio/wav,audio/mpeg"
                        ref={holdMusicInputRef}
                        onChange={handleHoldMusicUpload}
                        style={{ display: 'none' }}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          type="button"
                          disabled={holdMusicUploading || !practiceId}
                          onClick={() => holdMusicInputRef.current && holdMusicInputRef.current.click()}
                          title={holdMusicExists ? 'Musik ersetzen' : 'Musik hochladen'}
                          style={iconBtnStyle(holdMusicUploading || !practiceId)}
                        >
                          {holdMusicUploading ? '…' : (
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }} aria-hidden="true">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                          )}
                        </button>
                        {holdMusicExists && (
                          <>
                            <button
                              type="button"
                              disabled={holdMusicPreviewLoading || holdMusicUploading || !practiceId}
                              onClick={toggleHoldMusicPreview}
                              title={holdMusicPlaying ? 'Stopp' : 'Abspielen'}
                              style={iconBtnStyle(holdMusicPreviewLoading || holdMusicUploading || !practiceId)}
                            >
                              {holdMusicPreviewLoading ? '…' : holdMusicPlaying ? (
                                <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }} aria-hidden="true">
                                  <rect x="6" y="6" width="12" height="12" rx="1.5" />
                                </svg>
                              ) : (
                                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }} aria-hidden="true">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              )}
                            </button>
                            <button
                              type="button"
                              disabled={holdMusicUploading || !practiceId}
                              onClick={removeHoldMusic}
                              title="Entfernen"
                              style={iconBtnStyle(holdMusicUploading || !practiceId, true)}
                            >
                              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }} aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                      {holdMusicError && (
                        <p className="form-help-text" style={{ margin: '0.5rem 0 0', color: 'var(--destructive)' }}>{holdMusicError}</p>
                      )}
                    </div>
                  </CollapsibleSubCard>
                  )}

                  {/* 5. Hintergrund-Ambiente */}
                  {(kiMode === 'langgraph' || kiMode === 'smart_ab') && (
                  <CollapsibleSubCard
                    title="Hintergrund-Ambiente"
                    tooltip="Spielt dezente Büro-Geräusche im Hintergrund des Anrufs für eine natürlichere Atmosphäre. Wird leiser, während der Assistent spricht."
                    badge={callAmbienceEnabled ? (callAmbienceSound === 'office2' ? 'Büro 2' : 'Büro 1') : 'Aus'}
                  >
                    <label className="checkbox-item">
                      <input type="checkbox" className="as-switch" checked={callAmbienceEnabled} onChange={e => setCallAmbienceEnabled(e.target.checked)} />
                      <span className="checkbox-label">Aktiviert</span>
                    </label>
                    <SwitchCollapse open={callAmbienceEnabled}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Hintergrundgeräusch</label>
                      <select
                        value={callAmbienceSound}
                        onChange={e => setCallAmbienceSound(e.target.value)}
                        className="form-input"
                      >
                        <option value="office1">Büro 1</option>
                        <option value="office2">Büro 2</option>
                      </select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={toggleAmbiencePreview}
                          disabled={ambiencePreviewLoading}
                          title={ambienceIsPlaying ? 'Pause' : 'Abspielen'}
                          aria-label={ambienceIsPlaying ? 'Pause' : 'Abspielen'}
                          style={{
                            flex: '0 0 auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 34,
                            height: 34,
                            padding: 0,
                            border: 'none',
                            borderRadius: '50%',
                            background: 'var(--color-secondary)',
                            color: '#ffffff',
                            cursor: ambiencePreviewLoading ? 'default' : 'pointer',
                          }}
                        >
                          {ambiencePreviewLoading ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <circle cx="5" cy="12" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="19" cy="12" r="2" />
                            </svg>
                          ) : ambienceIsPlaying ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <rect x="6" y="5" width="4" height="14" rx="1" />
                              <rect x="14" y="5" width="4" height="14" rx="1" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
                            </svg>
                          )}
                        </button>
                        <input
                          type="range"
                          min={0}
                          max={ambienceDuration || 0}
                          step={0.1}
                          value={Math.min(ambienceCurrentTime, ambienceDuration || 0)}
                          onChange={e => seekAmbiencePreview(parseFloat(e.target.value))}
                          disabled={!ambienceDuration}
                          aria-label="Wiedergabeposition"
                          style={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            accentColor: 'var(--color-secondary)',
                            cursor: !ambienceDuration ? 'default' : 'pointer',
                          }}
                        />
                        <span style={{ flex: '0 0 auto', minWidth: 78, textAlign: 'right', fontSize: '0.8rem', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                          {formatAmbienceTime(ambienceCurrentTime)} / {formatAmbienceTime(ambienceDuration)}
                        </span>
                      </div>
                      {ambiencePreviewError && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--color-tertiary)', margin: '6px 0 0' }}>
                          Vorschau nicht verfügbar
                        </p>
                      )}
                    </div>
                    </SwitchCollapse>
                  </CollapsibleSubCard>
                  )}
                </div>
              </div>

            </form>
          </div>
        )}
        {userIsAdmin && activeTab === 'sip' && (
          <div className="tab-content" style={{ position: 'relative' }}>
            <LoadingShroud loading={sipLoadedFor !== practiceId} />
            <div className="card sip-config-form">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
                  <SaveStatusSlot saving={sipSaving} status={saveStatus.sip} />
                  <button
                    type="submit"
                    form="sip-form"
                    className="save-btn"
                    title={formLoadedFor !== practiceId ? 'Praxisdaten werden noch geladen…' : 'SIP-Konfiguration speichern'}
                    disabled={sipSaving || formLoadedFor !== practiceId}
                    aria-busy={sipSaving}
                  >
                    Speichern
                  </button>
                </div>
              </div>
              <p>Konfigurieren Sie Ihre SIP-Einstellungen, um die Anrufbehandlung zu aktivieren.</p>
              <form id="sip-form" onSubmit={handleSaveSipConfig}>
                <div className="form-group">
                  <label className="form-label">Server-Adresse</label>
                  <input type="text" name="server_address" className="form-input" value={sipConfig.server_address} onChange={handleSipConfigChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Benutzername</label>
                    <input type="text" name="username" className="form-input" value={sipConfig.username} onChange={handleSipConfigChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Passwort</label>
                    <input type="password" name="password" className="form-input" value={sipConfig.password} onChange={handleSipConfigChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Port</label>
                    <input type="text" name="port" className="form-input" value={sipConfig.port} onChange={handleSipConfigChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Aktiviert</label>
                    <div className="checkbox-wrapper">
                      <input type="checkbox" name="enabled" id="sip-enabled" checked={sipConfig.enabled} onChange={handleSipConfigChange} />
                      <label htmlFor="sip-enabled">SIP aktivieren</label>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Anrufverhalten bei Mehrfachanrufen</label>
                  <select
                    name="parallel_call_mode"
                    className="form-input"
                    value={sipConfig.parallel_call_mode || 'voicemail'}
                    onChange={handleSipConfigChange}
                  >
                    <option value="voicemail">Anrufe an Voicemail weiterleiten</option>
                    <option value="parallel">Parallele Anrufe annehmen (bis zu 4)</option>
                    <option value="hold">In Warteschleife halten</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Proxy-URL</label>
                  <input type="text" name="proxy_url" className="form-input" placeholder="SIP-Proxy-URL eingeben" value={sipConfig.proxy_url || ''} onChange={handleSipConfigChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">SIP-Telefonnummer</label>
                  <input type="text" name="sip_phone_number" className="form-input" placeholder="z.B. 0221123456789" value={sipConfig.sip_phone_number || ''} onChange={handleSipConfigChange} required />
                </div>
                <div className="form-actions">
                  <button type="button" className="button test-button" onClick={handleTestSipConnection} disabled={testStatus.testing}>{testStatus.testing ? 'Teste...' : 'Verbindung testen'}</button>
                  <button type="button" className="button delete-button" onClick={handleDeleteSipConfig} disabled={!sipConfigId}>SIP-Konfiguration löschen</button>
                  <button type="button" className="button test-phone-call-button" onClick={handleTestSipPhoneCall} disabled={testStatus.testing || !sipConfigId}>Testanruf</button>
                </div>
                {testStatus.message && (
                  <div className={`test-status ${testStatus.success ? 'success' : 'error'}`}>{testStatus.message}</div>
                )}
              </form>
            </div>
          </div>
        )}
        {userIsSuperAdmin && activeTab === 'ai' && (
          <div className="tab-content" style={{ position: 'relative' }}>
            <LoadingShroud loading={callflowLoadedFor !== practiceId} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem' }}>
              <SaveStatusSlot saving={llmSaving} status={llmSaveStatus} />
              <button
                type="submit"
                form="ai-form"
                className="save-btn"
                title={formLoadedFor !== practiceId ? 'Praxisdaten werden noch geladen…' : 'KI-Konfiguration speichern'}
                disabled={llmSaving || formLoadedFor !== practiceId}
                aria-busy={llmSaving}
              >
                Speichern
              </button>
            </div>
            <p>Konfigurieren Sie Ihre KI-Einstellungen für die Anrufbearbeitung.</p>

            <form id="ai-form" onSubmit={handleLlmConfigSave}>
              {/* Provider Selection Box */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="h3">LLM Provider Auswahl</h3>
                <div className="form-group">
                  <label className="form-label">Provider</label>
                  <select
                    value={provider}
                    onChange={e => setProvider(e.target.value)}
                    className="form-input"
                  >
                    <option value="bedrock">AWS Bedrock</option>
                    <option value="azure">Azure OpenAI</option>
                    <option value="gemini">Google Gemini</option>
                  </select>
                </div>
              </div>

              {/* Dual-VAD Toggle */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="h3">VAD Konfiguration</h3>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={enableDualVad}
                      onChange={e => setEnableDualVad(e.target.checked)}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span>Doppel-VAD Aktivieren</span>
                  </label>
                  <p className="form-help-text" style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                    Wenn aktiviert, müssen sowohl Asterisk als auch Silero VAD übereinstimmen, bevor ein Sprecherwechsel ausgelöst wird. Dies reduziert Fehlauslösungen bei Sprechpausen.
                  </p>
                </div>
              </div>

              {/* SMS-Gateway — GLOBAL, and on this tab because of it: which gateway
                  carries the platform's SMS is one decision, like the LLM provider
                  above, not a per-practice one. The unselected gateway becomes the
                  automatic fallback, so this picks the ORDER of the pair, not who is
                  used. The per-practice bits stay on the Anrufablauf tab: whether a
                  practice sends SMS at all, and the displayed sender (SMS-Absender). */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="h3">SMS-Gateway</h3>
                <div className="form-group">
                  <label className="form-label">Primärer Anbieter</label>
                  <select
                    value={smsProvider}
                    onChange={e => setSmsProvider(e.target.value)}
                    className="form-input"
                  >
                    <option value="seven">seven.io</option>
                    <option value="aws">AWS SNS</option>
                  </select>
                  <p className="form-help-text" style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                    Gilt für ALLE Praxen. Schlägt der Versand über den primären Anbieter
                    fehl, wird die SMS automatisch einmalig über den jeweils anderen
                    Anbieter gesendet ({smsProvider === 'seven' ? 'AWS SNS' : 'seven.io'});
                    erst wenn auch das fehlschlägt, wird die Aufgabe als fehlgeschlagen
                    markiert. Empfohlen ist seven.io als primärer Anbieter: seven.io meldet
                    Fehler sofort, AWS SNS erst einige Minuten später.
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">seven.io API-Key</label>
                  <input
                    type="password"
                    value={sevenApiKey}
                    onChange={e => setSevenApiKey(e.target.value)}
                    placeholder="X-Api-Key"
                    className="form-input"
                  />
                  <p className="form-help-text" style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                    Ein seven.io-Konto für alle Praxen. Wird auch benötigt, wenn seven.io
                    nur der Fallback ist. Wird nur beim expliziten Speichern übernommen.
                  </p>
                </div>
              </div>

              {/* Conditional Configuration based on Provider */}
              {provider === 'azure' ? (
                /* Azure OpenAI Configuration - Connections + Use-Case Mapping */
                <div>
                  {/* Section A: Verbindungen */}
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                      <div>
                        <h3 className="h3" style={{ marginBottom: '0.25rem' }}>Verbindungen</h3>
                        <p className="form-help-text" style={{ color: '#64748b', margin: 0 }}>
                          Azure OpenAI Projektverbindungen konfigurieren
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAzureConnections(prev => [...prev, {
                          id: `conn_${Date.now().toString(36)}`,
                          label: '',
                          endpoint: '',
                          api_key: '',
                          deployment: '',
                          api_version: ''
                        }])}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          padding: '0.5rem 1rem',
                          background: 'var(--color-secondary)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Verbindung hinzufügen
                      </button>
                    </div>

                    {azureConnections.length === 0 && (
                      <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#94a3b8',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '0.5rem',
                        fontSize: '0.9rem'
                      }}>
                        Noch keine Verbindungen konfiguriert. Klicken Sie auf "Verbindung hinzufügen".
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                      {azureConnections.map((conn, idx) => (
                        <div
                          key={conn.id}
                          onDragOver={e => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDragEnter={e => {
                            e.preventDefault();
                            const from = azureDragIndexRef.current;
                            // Only reorder when entering a different card
                            if (from === null || from === idx) return;
                            setAzureConnections(prev => {
                              const next = [...prev];
                              const [moved] = next.splice(from, 1);
                              next.splice(idx, 0, moved);
                              azureDragIndexRef.current = idx;
                              return next;
                            });
                            setAzureDraggingIndex(idx);
                          }}
                          onDrop={e => {
                            // Reorder already happened live on onDragEnter — just prevent default
                            e.preventDefault();
                          }}
                          style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.5rem',
                            background: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            opacity: azureDraggingIndex === idx ? 0.45 : 1,
                            transition: 'box-shadow 0.15s ease, opacity 0.15s ease',
                            display: 'flex',
                            overflow: 'hidden'
                          }}>
                          {/* Drag handle — the only draggable zone */}
                          <div
                            draggable="true"
                            onDragStart={e => {
                              azureDragIndexRef.current = idx;
                              setAzureDraggingIndex(idx);
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', String(idx));
                            }}
                            onDragEnd={() => {
                              azureDragIndexRef.current = null;
                              setAzureDraggingIndex(null);
                              // Persist order to localStorage
                              setAzureConnections(curr => {
                                localStorage.setItem('azureConnectionOrder', JSON.stringify(curr.map(c => c.id)));
                                return curr;
                              });
                            }}
                            title="Ziehen zum Sortieren"
                            style={{
                              width: '20px',
                              flexShrink: 0,
                              background: 'var(--color-secondary)',
                              cursor: 'grab',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'rgba(255,255,255,0.7)',
                              fontSize: '12px',
                              letterSpacing: '0.5px',
                              userSelect: 'none'
                            }}
                          >
                            &#8942;
                          </div>
                          {/* Card content */}
                          <div style={{ flex: 1, padding: '1rem' }}>
                          {/* Card header: label input + delete */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
                            <input
                              type="text"
                              value={conn.label}
                              onChange={e => setAzureConnections(prev => prev.map((c, i) => i === idx ? { ...c, label: e.target.value } : c))}
                              placeholder="Bezeichnung (z.B. GPT-4.1 Smart)"
                              style={{
                                flex: 1,
                                padding: '0.45rem 0.65rem',
                                borderRadius: '0.375rem',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#1e293b',
                                cursor: 'auto'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const duplicate = { ...conn, id: `conn_${Date.now().toString(36)}` };
                                setAzureConnections(prev => {
                                  const next = [...prev];
                                  next.splice(idx + 1, 0, duplicate);
                                  return next;
                                });
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                background: 'none',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                padding: 0,
                                color: '#64748b'
                              }}
                              title="Verbindung duplizieren"
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              disabled={azureConnections.length === 1}
                              onClick={() => {
                                const removedId = azureConnections[idx].id;
                                setAzureConnections(prev => prev.filter((_, i) => i !== idx));
                                // Clean orphaned mapping references
                                setAzureUseCaseMapping(prev => {
                                  const cleaned = { ...prev };
                                  for (const key in cleaned) {
                                    if (cleaned[key] === removedId) cleaned[key] = '';
                                  }
                                  return cleaned;
                                });
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                background: 'none',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.375rem',
                                cursor: azureConnections.length === 1 ? 'not-allowed' : 'pointer',
                                opacity: azureConnections.length === 1 ? 0.4 : 1,
                                padding: 0
                              }}
                              title="Verbindung entfernen"
                            >
                              {actionIcons.delete}
                            </button>
                          </div>

                          {/* Card fields grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div>
                              <label style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Endpoint</label>
                              <input
                                type="text"
                                value={conn.endpoint}
                                onChange={e => setAzureConnections(prev => prev.map((c, i) => i === idx ? { ...c, endpoint: e.target.value } : c))}
                                placeholder="https://ai-services.cognitiveservices.azure.com"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 0.65rem',
                                  borderRadius: '0.375rem',
                                  border: '1px solid #e2e8f0',
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  cursor: 'auto'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>API Key</label>
                              <div style={{ position: 'relative' }}>
                                <input
                                  type={azureKeyVisible[conn.id] ? 'text' : 'password'}
                                  value={conn.api_key}
                                  onChange={e => setAzureConnections(prev => prev.map((c, i) => i === idx ? { ...c, api_key: e.target.value } : c))}
                                  placeholder="Azure API Key"
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem 2rem 0.5rem 0.65rem',
                                    borderRadius: '0.375rem',
                                    border: '1px solid #e2e8f0',
                                    fontSize: '0.8rem',
                                    boxSizing: 'border-box',
                                    cursor: 'auto'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setAzureKeyVisible(prev => ({ ...prev, [conn.id]: !prev[conn.id] }))}
                                  style={{
                                    position: 'absolute',
                                    right: '0.4rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.1rem',
                                    color: '#94a3b8',
                                    lineHeight: 1
                                  }}
                                  title={azureKeyVisible[conn.id] ? 'Verbergen' : 'Anzeigen'}
                                >
                                  {azureKeyVisible[conn.id] ? (
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                      <line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                  ) : (
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                      <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div>
                              <label style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Deployment</label>
                              <input
                                type="text"
                                value={conn.deployment}
                                onChange={e => setAzureConnections(prev => prev.map((c, i) => i === idx ? { ...c, deployment: e.target.value } : c))}
                                placeholder="gpt-4-1"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 0.65rem',
                                  borderRadius: '0.375rem',
                                  border: '1px solid #e2e8f0',
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  cursor: 'auto'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>API Version</label>
                              <input
                                type="text"
                                value={conn.api_version}
                                onChange={e => setAzureConnections(prev => prev.map((c, i) => i === idx ? { ...c, api_version: e.target.value } : c))}
                                placeholder="2025-01-01-preview"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 0.65rem',
                                  borderRadius: '0.375rem',
                                  border: '1px solid #e2e8f0',
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  cursor: 'auto'
                                }}
                              />
                            </div>
                          </div>
                          </div>{/* end card content */}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section B: Modell-Zuordnung */}
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <h3 className="h3" style={{ marginBottom: '0.25rem' }}>Modell-Zuordnung</h3>
                    <p className="form-help-text" style={{ color: '#64748b', marginBottom: '1.25rem' }}>
                      Welche Verbindung wird fur welchen Anwendungsfall verwendet?
                    </p>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', width: '55%' }}>Anwendungsfall</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Verbindung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {AZURE_USE_CASE_CONFIG.map(({ key, label, bold }) => (
                              <tr key={key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.875rem', color: '#334155', fontWeight: bold ? '600' : '400' }}>
                                  {label}
                                </td>
                                <td style={{ padding: '0.6rem 0.75rem' }}>
                                  <select
                                    value={azureUseCaseMapping[key] || ''}
                                    onChange={e => setAzureUseCaseMapping(prev => ({ ...prev, [key]: e.target.value }))}
                                    style={{
                                      width: '100%',
                                      padding: '0.4rem 0.6rem',
                                      borderRadius: '0.375rem',
                                      border: '1px solid #e2e8f0',
                                      fontSize: '0.85rem',
                                      color: '#334155',
                                      background: '#fff',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <option value="">— nicht zugewiesen —</option>
                                    {azureConnections.map(c => (
                                      <option key={c.id} value={c.id}>
                                        {c.label || '(ohne Bezeichnung)'}{c.deployment ? `, ${c.deployment}` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : provider === 'gemini' ? (
                /* Google Gemini Configuration Box - Vertex AI EU */
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 className="h3" style={{ marginBottom: '0.5rem' }}>Google Gemini (Vertex AI)</h3>
                  <p className="form-help-text" style={{ marginBottom: '1.5rem', color: '#64748b' }}>
                    EU-Region (GDPR-konform) mit 4-Modell-Architektur
                  </p>

                  {/* Vertex AI Settings Header - Google colors */}
                  <div style={{
                    background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)',
                    padding: '1.25rem',
                    borderRadius: '0.75rem',
                    marginBottom: '1.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>&#x2601;</span>
                      <span style={{ color: 'white', fontWeight: '600', fontSize: '0.95rem' }}>Vertex AI Einstellungen</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.25rem', display: 'block' }}>PROJECT ID</label>
                        <input
                          type="text"
                          value={geminiProjectId}
                          onChange={e => setGeminiProjectId(e.target.value)}
                          placeholder="your-gcp-project-id"
                          style={{
                            width: '100%',
                            padding: '0.6rem 0.75rem',
                            borderRadius: '0.5rem',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(255,255,255,0.95)',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem'
                          }}
                          required
                        />
                      </div>
                      <div>
                        <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.25rem', display: 'block' }}>REGION (EU)</label>
                        <select
                          value={geminiRegion}
                          onChange={e => setGeminiRegion(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.6rem 0.75rem',
                            borderRadius: '0.5rem',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(255,255,255,0.95)',
                            fontSize: '0.85rem'
                          }}
                        >
                          <option value="europe-west1">europe-west1 (Belgien)</option>
                          <option value="europe-west4">europe-west4 (Niederlande)</option>
                          <option value="europe-west3">europe-west3 (Frankfurt)</option>
                          <option value="europe-west9">europe-west9 (Paris)</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.25rem', display: 'block' }}>SERVICE ACCOUNT JSON</label>
                      <textarea
                        value={geminiServiceAccountJson}
                        onChange={e => setGeminiServiceAccountJson(e.target.value)}
                        placeholder='{"type": "service_account", "project_id": "...", ...}'
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.6rem 0.75rem',
                          borderRadius: '0.5rem',
                          border: '1px solid rgba(255,255,255,0.2)',
                          background: 'rgba(255,255,255,0.95)',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                          resize: 'vertical'
                        }}
                        required
                      />
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginTop: '0.25rem', display: 'block' }}>
                        Fügen Sie den vollständigen JSON-Inhalt Ihres Service Account Keys ein
                      </span>
                    </div>
                  </div>

                  {/* Models Grid - 2x2 Layout */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                    {/* Smart Model 1 (Primary) - Intent */}
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderLeft: '4px solid #4285f4',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: '600', color: '#1a73e8', fontSize: '0.9rem' }}>Smart 1</span>
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.15rem 0.5rem',
                          background: '#e8f0fe',
                          borderRadius: '1rem',
                          fontSize: '0.7rem',
                          color: '#1a73e8',
                          fontWeight: '500'
                        }}>Intent</span>
                      </div>
                      <select
                        value={geminiPrimaryModel}
                        onChange={e => setGeminiPrimaryModel(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
                      >
                        <option value="">None</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                        <option value="google/gemini-2.5-flash">Gemini 2.5 Flash</option>
                      </select>
                    </div>

                    {/* Mini Model 1 (Secondary) - Action */}
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderLeft: '4px solid #34a853',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: '600', color: '#188038', fontSize: '0.9rem' }}>Mini 1</span>
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.15rem 0.5rem',
                          background: '#e6f4ea',
                          borderRadius: '1rem',
                          fontSize: '0.7rem',
                          color: '#188038',
                          fontWeight: '500'
                        }}>Action</span>
                      </div>
                      <select
                        value={geminiSecondaryModel}
                        onChange={e => setGeminiSecondaryModel(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
                      >
                        <option value="">None</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                        <option value="google/gemini-2.5-flash">Gemini 2.5 Flash</option>
                      </select>
                    </div>

                    {/* Smart Model 2 (Tertiary) - Slots */}
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderLeft: '4px solid #fbbc04',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: '600', color: '#f9ab00', fontSize: '0.9rem' }}>Smart 2</span>
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.15rem 0.5rem',
                          background: '#fef7e0',
                          borderRadius: '1rem',
                          fontSize: '0.7rem',
                          color: '#f9ab00',
                          fontWeight: '500'
                        }}>Slots</span>
                      </div>
                      <select
                        value={geminiTertiaryModel}
                        onChange={e => setGeminiTertiaryModel(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
                      >
                        <option value="">None</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                        <option value="google/gemini-2.5-flash">Gemini 2.5 Flash</option>
                      </select>
                    </div>

                    {/* Mini Model 2 (Quaternary) - Chat */}
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderLeft: '4px solid #ea4335',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: '600', color: '#d93025', fontSize: '0.9rem' }}>Mini 2</span>
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.15rem 0.5rem',
                          background: '#fce8e6',
                          borderRadius: '1rem',
                          fontSize: '0.7rem',
                          color: '#d93025',
                          fontWeight: '500'
                        }}>Chat</span>
                      </div>
                      <select
                        value={geminiQuaternaryModel}
                        onChange={e => setGeminiQuaternaryModel(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
                      >
                        <option value="">None</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                        <option value="google/gemini-2.5-flash">Gemini 2.5 Flash</option>
                      </select>
                    </div>

                  </div>{/* End Models Grid */}
                </div>
              ) : (
                /* AWS Bedrock Configuration Box */
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 className="h3">AWS Bedrock Konfiguration</h3>
                  <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                    Unterstützt alle Frankfurt-basierten AWS Bedrock Modelle mit Dual-Model-Strategie.
                  </p>
                  <div className="form-group">
                    <label className="form-label">Primäres Modell (Tool-Routing)</label>
                  <select
                    value={primaryModel}
                    onChange={e => setPrimaryModel(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">None</option>
                    <option value="anthropic.claude-3-5-sonnet-20240620-v1:0">Claude 3.5 Sonnet (Empfohlen)</option>
                    <option value="anthropic.claude-3-haiku-20240307-v1:0">Claude 3 Haiku (Schnell/Günstig)</option>
                    <option value="anthropic.claude-3-opus-20240229-v1:0">Claude 3 Opus (Leistungsstark)</option>
                    <option value="eu.anthropic.claude-3-7-sonnet-20250219-v1:0">Claude 3.7 Sonnet (Neu)</option>
                    <option value="eu.anthropic.claude-sonnet-4-20250514-v1:0">Claude 4 Sonnet (Neueste)</option>
                    <option value="eu.amazon.nova-pro-v1:0">Amazon Nova Pro (Ausgewogen)</option>
                    <option value="eu.amazon.nova-lite-v1:0">Amazon Nova Lite (Schnell)</option>
                    <option value="eu.amazon.nova-micro-v1:0">Amazon Nova Micro (Sehr günstig)</option>
                    <option value="eu.mistral.pixtral-large-2502-v1:0">Mistral Pixtral Large</option>
                  </select>
                  <p className="form-help-text">Entscheidet, welche Tools aufgerufen werden sollen</p>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Sekundäres Modell (Antwort-Generierung)</label>
                  <select
                    value={secondaryModel}
                    onChange={e => setSecondaryModel(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">None</option>
                    <option value="anthropic.claude-3-5-sonnet-20240620-v1:0">Claude 3.5 Sonnet (Empfohlen)</option>
                    <option value="anthropic.claude-3-haiku-20240307-v1:0">Claude 3 Haiku (Schnell/Günstig)</option>
                    <option value="anthropic.claude-3-opus-20240229-v1:0">Claude 3 Opus (Leistungsstark)</option>
                    <option value="eu.anthropic.claude-3-7-sonnet-20250219-v1:0">Claude 3.7 Sonnet (Neu)</option>
                    <option value="eu.anthropic.claude-sonnet-4-20250514-v1:0">Claude 4 Sonnet (Neueste)</option>
                    <option value="eu.amazon.nova-pro-v1:0">Amazon Nova Pro (Ausgewogen)</option>
                    <option value="eu.amazon.nova-lite-v1:0">Amazon Nova Lite (Schnell)</option>
                    <option value="eu.amazon.nova-micro-v1:0">Amazon Nova Micro (Sehr günstig)</option>
                    <option value="eu.mistral.pixtral-large-2502-v1:0">Mistral Pixtral Large</option>
                  </select>
                  <p className="form-help-text">Generiert menschenähnliche Antworten für Anrufer</p>
                </div>
              </div>
              )}  {/* End of conditional provider configuration */}

              {/* Voice AI Configuration Box - STT and TTS Provider Selection */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="h3">Sprach-KI-Konfiguration</h3>

                {/* STT Provider Selection */}
                <div className="form-group">
                  <label className="form-label">Speech-to-Text Provider</label>
                  <select
                    value={sttProvider}
                    onChange={e => setSttProvider(e.target.value)}
                    className="form-input"
                  >
                    <option value="aws">AWS Transcribe</option>
                    <option value="azure">Azure Speech Services</option>
                    <option value="deepgram">Deepgram Flux (EU)</option>
                    <option value="kugel">Kugel Ear (EU)</option>
                  </select>
                </div>

                {/* Deepgram Flux Configuration */}
                {sttProvider === 'deepgram' && (
                  <div style={{
                    backgroundColor: '#fef2f2',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    border: '2px solid #ef4444'
                  }}>
                    <h4 className="h4">Deepgram Flux Konfiguration</h4>
                    <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                      EU-Endpoint (api.eu.deepgram.com), Modell flux-general-multi.
                      Sprach-Erkennung UND Sprechpausen-Erkennung (Turn-Detection) erfolgen
                      durch das Flux-Modell. Silero/Asterisk-VAD wird f&uuml;r diese Praxis umgangen.
                    </p>
                    <div className="form-group">
                      <label className="form-label">API Key</label>
                      <input
                        type="password"
                        value={deepgramApiKey}
                        onChange={e => setDeepgramApiKey(e.target.value)}
                        placeholder="Deepgram API Key"
                        className="form-input"
                      />
                    </div>
                  </div>
                )}

                {/* TTS Provider Selection */}
                <div className="form-group">
                  <label className="form-label">Text-to-Speech Provider</label>
                  <select
                    value={ttsProvider}
                    onChange={e => setTtsProvider(e.target.value)}
                    className="form-input"
                  >
                    <option value="aws">AWS Polly</option>
                    <option value="azure">Azure Text-to-Speech</option>
                    <option value="voxtral">Voxtral (Mistral AI)</option>
                    <option value="elevenlabs">ElevenLabs</option>
                    <option value="kugel">Kugel Audio</option>
                  </select>
                  <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    Wählen Sie den Anbieter für die Sprachausgabe (Text-to-Speech)
                  </p>
                </div>

                {/* Batch Transcription Provider Selection */}
                <div className="form-group">
                  <label className="form-label">Batch-Transkription Provider</label>
                  <select
                    value={batchTranscribeProvider}
                    onChange={e => setBatchTranscribeProvider(e.target.value)}
                    className="form-input"
                  >
                    <option value="azure">Azure Speech Services</option>
                    <option value="gpt4o">GPT-4o Transcribe (Azure OpenAI)</option>
                    <option value="gpt-transcribe">GPT-Transcribe (Azure OpenAI)</option>
                    <option value="voxtral">Voxtral (Mistral AI)</option>
                  </select>
                  <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    Verwendet für Nachtranskription (unclear_answer) und Voicemail-Verarbeitung
                  </p>
                </div>

                {/* GPT-4o Transcribe info - shown when gpt4o selected */}
                {batchTranscribeProvider === 'gpt4o' && (
                  <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem', marginLeft: '1rem', borderLeft: '2px solid #10b981', paddingLeft: '1rem' }}>
                    Nutzt die Azure-Verbindung aus "Batch-Transkription (GPT-4o)" Zuordnung oben.
                    Das Deployment der zugewiesenen Verbindung wird verwendet.
                  </p>
                )}

                {/* GPT-Transcribe info - shown when gpt-transcribe selected */}
                {batchTranscribeProvider === 'gpt-transcribe' && (
                  <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem', marginLeft: '1rem', borderLeft: '2px solid #10b981', paddingLeft: '1rem' }}>
                    Nachfolger von GPT-4o Transcribe (wird am 15.10.2026 abgeschaltet).
                    Nutzt dieselbe Azure-Verbindung aus "Batch-Transkription (GPT-4o)" Zuordnung oben,
                    kein neuer Schlüssel nötig. Sprache wird über <code>languages</code> gesetzt,
                    Fachbegriffe über <code>keywords</code>.
                  </p>
                )}

                {/* Emergency Transcription Provider - always visible */}
                <div className="form-group" style={{ marginTop: '1rem', borderTop: '1px dashed #e5e7eb', paddingTop: '1rem' }}>
                  <label className="form-label">Notfall-Transkription Provider</label>
                  <select
                    value={emergencyTranscribeProvider}
                    onChange={e => setEmergencyTranscribeProvider(e.target.value)}
                    className="form-input"
                  >
                    <option value="gpt4o">GPT-4o Transcribe</option>
                    <option value="gpt4o-mini">GPT-4o Mini Transcribe</option>
                    <option value="gpt-transcribe">GPT-Transcribe</option>
                    <option value="voxtral">Voxtral (Mistral AI)</option>
                  </select>
                  <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    Schnellerer Provider für Azure-Asterisk VAD Konflikte (kein Filler)
                  </p>
                </div>

                {/* AWS Credentials - Always shown, highlighted when active */}
                <div style={{
                  backgroundColor: (sttProvider === 'aws' || ttsProvider === 'aws') ? '#f0f9ff' : '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  marginTop: '1rem',
                  border: (sttProvider === 'aws' || ttsProvider === 'aws') ? '2px solid #3b82f6' : '1px solid #e5e7eb'
                }}>
                  <h4 className="h4">AWS Credentials (Transcribe/Polly) {(sttProvider === 'aws' || ttsProvider === 'aws') && '✓'}</h4>
                  <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                    {sttProvider === 'aws' && ttsProvider === 'aws' && 'Verwendet für AWS Transcribe (STT) und AWS Polly (TTS)'}
                    {sttProvider === 'aws' && ttsProvider !== 'aws' && 'Verwendet für AWS Transcribe (STT)'}
                    {sttProvider !== 'aws' && ttsProvider === 'aws' && 'Verwendet für AWS Polly (TTS)'}
                    {sttProvider !== 'aws' && ttsProvider !== 'aws' && 'Credentials gespeichert für schnelles Umschalten'}
                  </p>

                  <div className="form-group">
                    <label className="form-label">AWS Access Key ID</label>
                    <input
                      type="text"
                      value={awsAccessKeyId}
                      onChange={e => setAwsAccessKeyId(e.target.value)}
                      placeholder="AWS Access Key ID"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">AWS Secret Access Key</label>
                    <input
                      type="password"
                      value={awsSecretAccessKey}
                      onChange={e => setAwsSecretAccessKey(e.target.value)}
                      placeholder="AWS Secret Access Key"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">AWS Polly Stimme</label>
                    <select
                      className="form-input"
                      value={awsPollyVoice}
                      onChange={e => setAwsPollyVoice(e.target.value)}
                    >
                      <option value="Daniel">Daniel (Männlich, de-DE)</option>
                      <option value="Vicki">Vicki (Weiblich, de-DE)</option>
                      <option value="Hans">Hans (Männlich, de-DE)</option>
                      <option value="Marlene">Marlene (Weiblich, de-DE)</option>
                      <option value="Hannah">Hannah (Weiblich, de-AT)</option>
                      <option value="Sabrina">Sabrina (Weiblich, de-CH)</option>
                    </select>

                    {/* Generative Engine Checkbox - only show for supported voices */}
                    {['Daniel', 'Vicki', 'Hannah'].includes(awsPollyVoice) && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={awsPollyUseGenerative}
                            onChange={e => setAwsPollyUseGenerative(e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <span>Generative Engine verwenden (bessere Qualität, mehr Emotionen)</span>
                        </label>
                      </div>
                    )}

                    <p className="form-help-text">
                      Daniel, Vicki und Hannah bieten die beste Qualität mit Generative Engine.
                    </p>
                  </div>
                </div>

                {/* Azure Speech Credentials - Shared for both STT and TTS */}
                <div style={{
                  backgroundColor: (sttProvider === 'azure' || ttsProvider === 'azure') ? '#f0fdf4' : '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  marginTop: '1rem',
                  border: (sttProvider === 'azure' || ttsProvider === 'azure') ? '2px solid #10b981' : '1px solid #e5e7eb'
                }}>
                  <h4 className="h4">
                    Azure Speech Credentials
                    {(sttProvider === 'azure' || ttsProvider === 'azure') && ' ✓'}
                  </h4>
                  <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                    {(sttProvider === 'azure' || ttsProvider === 'azure')
                      ? 'Verwendet für Azure Speech Services (STT und/oder TTS)'
                      : 'Credentials gespeichert für schnelles Umschalten'}
                  </p>

                  <div className="form-group">
                    <label className="form-label">Azure Speech Key</label>
                    <input
                      type="password"
                      value={azureSpeechKey}
                      onChange={e => setAzureSpeechKey(e.target.value)}
                      placeholder="Key 1 oder Key 2 aus Azure Portal"
                      className="form-input"
                    />
                    <p className="form-help-text">
                      Kopieren Sie "Key 1" oder "Key 2" aus Ihrem Azure Speech Resource
                    </p>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Azure Speech Region</label>
                    <input
                      type="text"
                      value={azureSpeechRegion}
                      onChange={e => setAzureSpeechRegion(e.target.value)}
                      placeholder="z.B. germanywestcentral, westeurope"
                      className="form-input"
                    />
                    <p className="form-help-text">
                      Die Region/Location aus Ihrem Azure Speech Resource (z.B. "germanywestcentral")
                    </p>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Azure TTS Stimme</label>
                    <select
                      className="form-input"
                      value={azureTtsVoice}
                      onChange={e => setAzureTtsVoice(e.target.value)}
                    >
                      <option value="de-DE-Seraphina:DragonHDLatestNeural">Seraphina HD (Weiblich, Premium)</option>
                      <option value="de-DE-SeraphinaMultilingualNeural">Seraphina Multilingual (Weiblich)</option>
                      <option value="de-DE-FlorianMultilingualNeural">Florian (Männlich)</option>
                      <option value="de-DE-Katja:DragonHDLatestNeural">Katja HD (Weiblich)</option>
                      <option value="de-DE-ConradNeural">Conrad (Männlich, Standard)</option>
                    </select>
                    <p className="form-help-text">
                      Wählen Sie die Stimme für die Sprachausgabe. "HD" Stimmen haben bessere Qualität.
                    </p>
                  </div>
                </div>

                {/* Mistral AI Credentials - Shown when Voxtral is selected for any service */}
                {(ttsProvider === 'voxtral' || batchTranscribeProvider === 'voxtral' || emergencyTranscribeProvider === 'voxtral') && (
                  <div style={{
                    backgroundColor: '#fef3c7',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginTop: '1rem',
                    border: '2px solid #f59e0b'
                  }}>
                    <h4 className="h4">Mistral AI Credentials</h4>
                    <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                      {[
                        ttsProvider === 'voxtral' && 'Voxtral TTS',
                        batchTranscribeProvider === 'voxtral' && 'Voxtral Batch-Transkription',
                        emergencyTranscribeProvider === 'voxtral' && 'Voxtral Notfall-Transkription',
                      ].filter(Boolean).join(', ') || 'Mistral AI Services'}
                    </p>

                    <div className="form-group">
                      <label className="form-label">Mistral API Key</label>
                      <input
                        type="password"
                        value={mistralApiKey}
                        onChange={e => setMistralApiKey(e.target.value)}
                        placeholder="Mistral API Key"
                        className="form-input"
                      />
                      <p className="form-help-text">
                        API Key aus der Mistral AI Konsole (console.mistral.ai)
                      </p>
                    </div>
                  </div>
                )}

                {/* Voxtral TTS Voice Configuration */}
                {ttsProvider === 'voxtral' && (
                  <div style={{
                    backgroundColor: '#fef3c7',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginTop: '1rem',
                    border: '2px solid #f59e0b'
                  }}>
                    <h4 className="h4">Voxtral TTS Stimme</h4>
                    <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                      Eigene deutsche Stimme erstellen (unten) oder englische Preset-Stimme verwenden
                    </p>

                    <div className="form-group">
                      <label className="form-label">Stimme</label>
                      <select
                        className="form-input"
                        value={voxtralVoice}
                        onChange={e => setVoxtralVoice(e.target.value)}
                      >
                        <optgroup label="Marie (Weiblich, FR)">
                          <option value="fr_marie_happy">Marie - Happy</option>
                          <option value="fr_marie_neutral">Marie - Neutral</option>
                          <option value="fr_marie_sad">Marie - Sad</option>
                        </optgroup>
                        <optgroup label="Jane (Weiblich, GB)">
                          <option value="gb_jane_confident">Jane - Confident</option>
                          <option value="gb_jane_neutral">Jane - Neutral</option>
                          <option value="gb_jane_sad">Jane - Sad</option>
                        </optgroup>
                        <optgroup label="Oliver (Männlich, GB)">
                          <option value="gb_oliver_cheerful">Oliver - Cheerful</option>
                          <option value="gb_oliver_neutral">Oliver - Neutral</option>
                          <option value="gb_oliver_sad">Oliver - Sad</option>
                        </optgroup>
                        <optgroup label="Paul (Männlich, US)">
                          <option value="en_paul_happy">Paul - Happy</option>
                          <option value="en_paul_neutral">Paul - Neutral</option>
                          <option value="en_paul_sad">Paul - Sad</option>
                        </optgroup>
                        {voxtralVoices.filter(v => v.user_id != null).length > 0 && (
                          <optgroup label="Eigene Stimmen">
                            {voxtralVoices.filter(v => v.user_id != null).map(v => (
                              <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>

                    {/* Delete custom voice button */}
                    {voxtralVoices.some(v => v.user_id != null && v.id === voxtralVoice) && (
                      <button
                        type="button"
                        onClick={handleDeleteVoxtralVoice}
                        className="button-secondary"
                        style={{ marginBottom: '1rem', color: '#dc2626', borderColor: '#dc2626' }}
                      >
                        Stimme löschen
                      </button>
                    )}

                    {/* Collapsible custom voice section */}
                    <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setVoxtralCustomExpanded(!voxtralCustomExpanded)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#92400e',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <span style={{ fontSize: '0.7rem' }}>{voxtralCustomExpanded ? '\u25BC' : '\u25B6'}</span>
                        Eigene Stimme verwenden
                      </button>

                      {voxtralCustomExpanded && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <div style={{
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fbbf24',
                            borderRadius: '0.375rem',
                            padding: '0.75rem',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            lineHeight: '1.5',
                            color: '#92400e',
                          }}>
                            <strong>Vorlesesatz (ca. 20 Sek.):</strong>
                            <p style={{ margin: '0.5rem 0 0', fontStyle: 'italic' }}>
                              Sch&ouml;n, dass Sie anrufen. Wir w&uuml;rden uns freuen, wenn Sie vorbeikommen k&ouml;nnten.
                              F&uuml;r Ihre Krankengymnastik oder Manuelle Therapie bringen Sie bitte Ihre Verordnung mit.
                              Wie viele Sitzungen wurden Ihnen verschrieben, und wann wurde das Rezept ausgestellt?
                              Unsere Praxis ist w&ouml;chentlich ge&ouml;ffnet, auch f&uuml;r Vormittagstermine.
                              Vielen Dank f&uuml;r Ihr Verst&auml;ndnis, wir melden uns in K&uuml;rze bei Ihnen. Auf Wiederhören!
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Name der Stimme</label>
                            <input
                              type="text"
                              value={voxtralCustomName}
                              onChange={e => setVoxtralCustomName(e.target.value)}
                              placeholder="Name der Stimme"
                              className="form-input"
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                            <input
                              type="file"
                              accept="audio/*"
                              ref={voxtralFileInputRef}
                              onChange={handleVoxtralFileSelected}
                              style={{ display: 'none' }}
                            />
                            <button
                              type="button"
                              className="button-secondary"
                              onClick={() => voxtralFileInputRef.current?.click()}
                            >
                              Datei hochladen
                            </button>
                            <button
                              type="button"
                              className="button-secondary"
                              onClick={voxtralRecording ? stopVoxtralRecording : startVoxtralRecording}
                              style={voxtralRecording ? { color: '#dc2626', borderColor: '#dc2626' } : {}}
                            >
                              {voxtralRecording ? 'Aufnahme stoppen' : 'Aufnehmen'}
                            </button>
                            {voxtralPreviewUrl && (
                              <audio controls src={voxtralPreviewUrl} style={{ height: '2rem' }} />
                            )}
                          </div>

                          {voxtralCustomFileName && (
                            <p className="form-help-text" style={{ marginBottom: '0.75rem', color: '#6b7280' }}>
                              {voxtralCustomFileName}
                            </p>
                          )}

                          <button
                            type="button"
                            className="button-secondary"
                            disabled={voxtralSaving || !voxtralCustomName.trim() || !voxtralCustomAudio}
                            onClick={handleSaveVoxtralVoice}
                            style={{ fontWeight: 600 }}
                          >
                            {voxtralSaving ? 'Speichert...' : 'Stimme speichern'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ElevenLabs TTS Configuration */}
                {ttsProvider === 'elevenlabs' && (
                  <div style={{
                    backgroundColor: '#f5f3ff',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginTop: '1rem',
                    border: '2px solid #8b5cf6'
                  }}>
                    <h4 className="h4">ElevenLabs TTS Konfiguration</h4>
                    <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                      Hinweis: Standard-Endpoint (US). EU-Datenresidenz ben&ouml;tigt Enterprise-Vertrag.
                    </p>

                    <div className="form-group">
                      <label className="form-label">API Key</label>
                      <input
                        type="password"
                        value={elevenlabsApiKey}
                        onChange={e => setElevenlabsApiKey(e.target.value)}
                        placeholder="sk_..."
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Stimme</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                      <select
                        className="form-input"
                        style={{ flex: 1 }}
                        value={elevenlabsVoiceId}
                        onChange={e => setElevenlabsVoiceId(e.target.value)}
                      >
                        <optgroup label="Weiblich (Deutsch)">
                          <option value="NE7AIW5DoJ7lUosXV2KR">Ella (jung, lebhaft)</option>
                          <option value="6CS8keYmkwxkspesdyA7">Ramona (Kundenservice, professionell)</option>
                          <option value="iFJwt4O7E3aafIpJFfcu">Elena (konversationell)</option>
                          <option value="NX39CipaoYitJ3sMwH5I">Franziska Lenz (Nachrichten, neutral)</option>
                          <option value="UO10cLZGrZvYWlrKJ8Ef">Miriam (warm, natürlich)</option>
                          <option value="ViKqgJNeCiWZlYgHiAOO">Annika (ruhig, freundlich)</option>
                          <option value="ghgFyr7gmpr57xyTgX9q">Emilia (charmant, jung)</option>
                        </optgroup>
                        <optgroup label="Weiblich (mehrsprachig, engl. Akzent)">
                          <option value="XB0fDUnXU5powFXDhCwa">Charlotte (multilingual, warm)</option>
                          <option value="EXAVITQu4vr4xnSDxMaL">Sarah (sanft, professionell)</option>
                          <option value="pFZP5JQG7iQjIQuC4Bku">Lily (warm)</option>
                          <option value="Xb7hH8MSUJpSbSDYk0k2">Alice (selbstbewusst)</option>
                          <option value="XrExE9yKIg1WjnnlVkGX">Matilda (warme mittlere Stimme)</option>
                        </optgroup>
                        <optgroup label="Männlich">
                          <option value="JBFqnCBsd6RMkjVDRZzb">George (warm, britisch)</option>
                          <option value="nPczCjzI2devNBz1zQrb">Brian (tiefer Erzähler)</option>
                          <option value="onwK4e9ZLuTAKqWW03F9">Daniel (Nachrichtensprecher)</option>
                          <option value="bIHbv24MWmeRgasZH58o">Will (ruhig, freundlich)</option>
                          <option value="cjVigY5qzO86Huf0OWal">Eric (autoritativ)</option>
                        </optgroup>
                      </select>
                      <button
                        type="button"
                        onClick={() => playVoicePreview(elevenlabsVoiceId)}
                        disabled={voicePreviewLoading}
                        title="Ausgewählte Stimme anhören"
                        style={{
                          whiteSpace: 'nowrap',
                          padding: '0 1rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          background: voicePreviewLoading ? '#f3f4f6' : '#ffffff',
                          color: '#374151',
                          cursor: voicePreviewLoading ? 'default' : 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        {voicePreviewLoading ? '⏳ …' : '▶ Anhören'}
                      </button>
                      </div>
                      <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        Deutsche Stimmen aus der ElevenLabs Voice Library; die unteren sind mehrsprachige Premade-Stimmen (engl. Akzent). Erfordern Starter-Plan oder höher.
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Modell</label>
                      <select
                        className="form-input"
                        value={elevenlabsModelId}
                        onChange={e => setElevenlabsModelId(e.target.value)}
                      >
                        <option value="eleven_flash_v2_5">Flash v2.5 (~75ms, schnellste Antwort)</option>
                        <option value="eleven_multilingual_v2">Multilingual v2 (h&ouml;chste Qualit&auml;t)</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Kugel Audio TTS Configuration */}
                {ttsProvider === 'kugel' && (
                  <div style={{
                    backgroundColor: '#fff7ed',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginTop: '1rem',
                    border: '2px solid #f97316'
                  }}>
                    <h4 className="h4">Kugel Audio TTS Konfiguration</h4>
                    <p className="form-help-text" style={{ marginBottom: '1rem', color: '#6b7280' }}>
                      Deutscher Anbieter, in Europa gehostet (DSGVO-konform).
                    </p>

                    <div className="form-group">
                      <label className="form-label">API Key</label>
                      <input
                        type="password"
                        value={kugelApiKey}
                        onChange={e => setKugelApiKey(e.target.value)}
                        placeholder="Kugel API Key"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Stimme</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                      <select
                        className="form-input"
                        style={{ flex: 1 }}
                        value={kugelVoiceId}
                        onChange={e => setKugelVoiceId(e.target.value)}
                      >
                        <optgroup label="Weiblich (Deutsch)">
                          <option value="1704">Tanja</option>
                          <option value="973">Anna</option>
                        </optgroup>
                        <optgroup label="Männlich (Deutsch)">
                          <option value="1657">Jonas</option>
                          <option value="980">Marius</option>
                          <option value="1708">Ernst</option>
                          <option value="972">Tim</option>
                        </optgroup>
                      </select>
                      <button
                        type="button"
                        onClick={() => playVoicePreview(kugelVoiceId)}
                        disabled={voicePreviewLoading}
                        title="Ausgewählte Stimme anhören"
                        style={{
                          whiteSpace: 'nowrap',
                          padding: '0 1rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          background: voicePreviewLoading ? '#f3f4f6' : '#ffffff',
                          color: '#374151',
                          cursor: voicePreviewLoading ? 'default' : 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        {voicePreviewLoading ? '⏳ …' : '▶ Anhören'}
                      </button>
                      </div>
                      <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        Start-Auswahl deutscher Kugel-Stimmen; weitere Stimmen im Kugel-Dashboard.
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Modell</label>
                      <select
                        className="form-input"
                        value={kugelModelId}
                        onChange={e => setKugelModelId(e.target.value)}
                      >
                        <option value="kugel-3">Kugel 3 (h&ouml;chste Qualit&auml;t, Standard)</option>
                        <option value="kugel-2-turbo">Kugel Turbo (g&uuml;nstiger, h&ouml;rbar schlechter)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={kugelUseWebsocket}
                          onChange={e => setKugelUseWebsocket(e.target.checked)}
                          style={{ marginRight: '0.5em' }}
                        />
                        WebSocket-Streaming (bessere Stimmkonsistenz, experimentell)
                      </label>
                      <p className="form-help-text" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        Nutzt die persistente WebSocket-Verbindung von Kugel. F&auml;llt bei Fehlern automatisch auf die REST-Verbindung zur&uuml;ck.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Persist Option */}
              <div className="card">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={persistLlmConfig}
                      onChange={e => setPersistLlmConfig(e.target.checked)}
                      style={{ marginRight: '0.5em' }}
                    />
                    Für nächste Sitzung speichern
                  </label>
                </div>
              </div>
            </form>
          </div>
        )}
        {userIsSuperAdmin && activeTab === 'pvs' && (
          <div className="tab-content" style={{ position: 'relative' }}>
            <LoadingShroud loading={pvsLoadedFor !== practiceId} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
                <h2 className="h2" style={{ margin: 0 }}>PVS Integration</h2>
                {/* Filled by the save handler, which detects the PVS after persisting. */}
                {pvsSystemStatus.loading ? (
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>PVS wird ermittelt...</span>
                ) : pvsSystem.name ? (
                  <span
                    title={pvsDetectedAtLabel ? `Ermittelt am ${pvsDetectedAtLabel}` : ''}
                    style={{
                      padding: '0.15rem 0.6rem',
                      borderRadius: '999px',
                      backgroundColor: '#eef2ff',
                      border: '1px solid #c7d2fe',
                      color: '#3730a3',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    {pvsSystem.name}
                  </span>
                ) : pvsSystemStatus.message ? (
                  <span
                    title={pvsSystemStatus.message}
                    style={{ fontSize: '0.875rem', color: '#6b7280' }}
                  >
                    Kein PVS-Name gefunden
                  </span>
                ) : null}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {pvsSaveStatus.message && pvsSaveStatus.message !== 'Speichere...' && (
                  <span className={`save-status ${pvsSaveStatus.saved ? 'success' : 'error'}`}>
                    {pvsSaveStatus.message}
                  </span>
                )}
                <button
                  type="submit"
                  form="pvs-form"
                  className="save-btn"
                  title={formLoadedFor !== practiceId ? 'Praxisdaten werden noch geladen…' : 'PVS-Konfiguration speichern'}
                  disabled={formLoadedFor !== practiceId}
                >
                  Speichern
                </button>
              </div>
            </div>

            <form id="pvs-form" onSubmit={handlePvsConfigSave}>
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="h3">PVS-Anbindung</h3>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={pvsEnabled}
                      onChange={e => setPvsEnabled(e.target.checked)}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span>Mit einem PVS verbinden</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Base URL</label>
                  <input
                    type="text"
                    value={pvsBaseUrl}
                    onChange={e => setPvsBaseUrl(e.target.value)}
                    placeholder="https://pvs-system.example.com/api"
                    className="form-input"
                    disabled={!pvsEnabled}
                    style={!pvsEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">API Key</label>
                  <input
                    type="password"
                    value={pvsApiKey}
                    onChange={e => setPvsApiKey(e.target.value)}
                    placeholder="API-Schlüssel für PVS-Zugang"
                    className="form-input"
                    disabled={!pvsEnabled}
                    style={!pvsEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                  {!pvsEnabled && (
                    <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      Aktivieren Sie die Checkbox oben, um diese Felder zu bearbeiten.
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <button
                    type="button"
                    className="button test-button"
                    onClick={handleTestPvsConnection}
                    disabled={pvsHealthStatus.testing || !pvsEnabled}
                  >
                    {pvsHealthStatus.testing ? 'Teste...' : 'Verbindung testen'}
                  </button>
                  {pvsHealthStatus.message && (
                    <div className={`test-status ${pvsHealthStatus.success ? 'success' : 'error'}`}>
                      {pvsHealthStatus.message}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Google Chat Webhook URL</label>
                  <input
                    type="text"
                    value={pvsGchatWebhookUrl}
                    onChange={e => setPvsGchatWebhookUrl(e.target.value)}
                    placeholder="https://chat.googleapis.com/v1/spaces/..."
                    className="form-input"
                    disabled={!pvsEnabled}
                    style={!pvsEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Deliberately NOT gated on pvsEnabled like its neighbours: the
                    therapist notification works without a PVS, so gating would put
                    the token out of reach for a practice that runs without one. */}
                <div className="form-group">
                  <label className="form-label">Slack Bot-Token</label>
                  <input
                    type="password"
                    value={pvsSlackBotToken}
                    onChange={e => setPvsSlackBotToken(e.target.value)}
                    placeholder="xoxb-..."
                    className="form-input"
                  />
                  <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Für Therapeuten, die per Slack über kurzfristige Änderungen informiert werden. Die App braucht die Berechtigungen <code>chat:write</code> und, für Direktnachrichten, <code>im:write</code>.
                  </p>
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: pvsEnabled ? 'pointer' : 'not-allowed' }}>
                    <input
                      type="checkbox"
                      checked={gsheetEnabled}
                      onChange={e => setGsheetEnabled(e.target.checked)}
                      disabled={!pvsEnabled}
                      style={{ width: '20px', height: '20px', cursor: pvsEnabled ? 'pointer' : 'not-allowed' }}
                    />
                    <span>Anrufprobleme melden (Google Sheet und Google Chat)</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Google Service-Account JSON</label>
                  <textarea
                    value={gsheetServiceAccountJson}
                    onChange={e => setGsheetServiceAccountJson(e.target.value)}
                    placeholder='{"type": "service_account", "client_email": "...", ...}'
                    className="form-input"
                    rows={6}
                    disabled={!gsheetEnabled}
                    style={{
                      fontFamily: 'monospace',
                      ...(!gsheetEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {})
                    }}
                  />
                  {gsheetJsonError && (
                    <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.25rem' }}>
                      {gsheetJsonError}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Google Sheet ID oder URL</label>
                  <input
                    type="text"
                    value={gsheetSheetId}
                    onChange={e => setGsheetSheetId(e.target.value)}
                    placeholder="1AbC...XYZ/edit?gid=0#gid=0"
                    className="form-input"
                    disabled={!gsheetEnabled}
                    style={!gsheetEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                  <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    ID alleine genügt für den ersten Tab. Für einen bestimmten Tab: ID + <code>/edit?gid=...#gid=...</code> einfügen (so wie es in der URL steht).
                  </p>
                </div>

                <div className="form-group">
                  <button
                    type="button"
                    onClick={() => setPvsAdvancedTimeoutsOpen(o => !o)}
                    className="form-input"
                    style={{ textAlign: 'left', cursor: 'pointer', backgroundColor: '#f9fafb' }}
                  >
                    {pvsAdvancedTimeoutsOpen ? '▼' : '▶'} Erweiterte Timeouts
                  </button>
                  {pvsAdvancedTimeoutsOpen && (
                    <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }}>
                      {[
                        { label: 'Timeout Patient suchen (Sek.)', value: pvsTimeoutFind, setter: setPvsTimeoutFind, dflt: 30 },
                        { label: 'Timeout Freie Termine (Sek.)', value: pvsTimeoutFreeslots, setter: setPvsTimeoutFreeslots, dflt: 20 },
                        { label: 'Timeout Termine abrufen (Sek.)', value: pvsTimeoutFindAppointments, setter: setPvsTimeoutFindAppointments, dflt: 45 },
                        { label: 'Timeout Buchen (Sek.)', value: pvsTimeoutBook, setter: setPvsTimeoutBook, dflt: 120 },
                        { label: 'Timeout Stornieren (Sek.)', value: pvsTimeoutCancel, setter: setPvsTimeoutCancel, dflt: 60 },
                        { label: 'Timeout Verschieben (Sek.)', value: pvsTimeoutReschedule, setter: setPvsTimeoutReschedule, dflt: 30 },
                        { label: 'Timeout Patient anlegen (Sek.)', value: pvsTimeoutCreatePatient, setter: setPvsTimeoutCreatePatient, dflt: 90 },
                      ].map(({ label, value, setter, dflt }) => (
                        <div key={label} className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label">{label}</label>
                          <input
                            type="number"
                            min="5"
                            max="300"
                            step="1"
                            value={value}
                            onChange={e => setter(parseInt(e.target.value, 10) || dflt)}
                            className="form-input"
                            disabled={!pvsEnabled}
                            style={!pvsEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                          />
                          <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Standard: {dflt}. Erlaubt: 5-300 Sekunden.
                          </p>
                        </div>
                      ))}
                      {/* Sync-Lookup timeout: stored per-practice in ai_config
                          (NOT pvs_config) — persisted by handlePvsSave via the
                          practice-llm-config PUT. 0 is valid (fully async). */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          Timeout Sync-Lookup vor Anrufannahme (Sek.)
                          <InfoTooltip text="0 = Anruf sofort annehmen (Patientensuche läuft vollständig asynchron); 10 = bis zu 10 s auf den Patientenabgleich warten und dann namentlich begrüßen, sonst normale Begrüßung und die Daten werden nachträglich übernommen." />
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="30"
                          step="1"
                          value={pvsSyncTimeoutSeconds}
                          onChange={e => setPvsSyncTimeoutSeconds(parseInt(e.target.value, 10) || 0)}
                          className="form-input"
                          disabled={!pvsEnabled}
                          style={!pvsEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        />
                        <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          Standard: 10. Erlaubt: 0-30 Sekunden (0 = vollständig asynchron).
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {pvsEnabled && (
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Test-Modus</label>
                    <select
                      className="form-control"
                      value={pvsDebugMode}
                      onChange={e => setPvsDebugMode(e.target.value)}
                    >
                      <option value="off">Aus</option>
                      <option value="debug">Debug-Modus</option>
                      <option value="dry_run">Dry Run</option>
                    </select>
                    <p className="form-help-text" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      {pvsDebugMode === 'debug' && 'Keine API-Aufrufe an das PVS (nur Logging)'}
                      {pvsDebugMode === 'dry_run' && 'API-Aufrufe mit dry_run=true (keine echten Aktionen im PVS)'}
                      {pvsDebugMode === 'off' && 'Produktivmodus \u2013 echte Schreibzugriffe auf das PVS'}
                    </p>
                  </div>
                )}
              </div>
            </form>

            {/* DOC-IQ: the practice's connection to DOC-IQ intake forms. The practice
                connects itself in DOC-IQ and DeskBot switches it on by itself; this card
                is the admins' check and off switch. Outside the PVS form on purpose: it
                is stored the moment the button is pressed. Members see the outcome
                beside "Buchen + Link senden" in the Verordnungstypen dialog. */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h3 className="h3">DOC-IQ</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                <span
                  aria-hidden="true"
                  style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: docIqStatus?.enabled ? 'var(--color-quaternary)' : 'var(--border-color)' }}
                />
                {docIqStatus
                  ? (docIqStatus.enabled ? `Aktiv${docIqStatus.praxis_name ? `: ${docIqStatus.praxis_name}` : ''}` : 'Nicht aktiv')
                  : (docIqStatusState.for === practiceId && docIqStatusState.failed ? 'Status konnte nicht geladen werden' : 'Status wird geladen...')}
              </div>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 1rem' }}>
                Die Praxis verbindet DOC-IQ selbst (in DOC-IQ: „Mit Deskbot verbinden“), danach wird DOC-IQ hier automatisch aktiv. Solange DOC-IQ nicht aktiv ist, wird der allgemeine Formular-Link verschickt.
              </p>
              <button
                type="button"
                className="button test-button"
                onClick={() => handleDocIqConnection(!docIqStatus.enabled)}
                disabled={docIqBusy || !docIqStatus}
              >
                {docIqBusy ? 'Bitte warten...' : (docIqStatus?.enabled ? 'Deaktivieren' : 'Verbindung prüfen und aktivieren')}
              </button>
              {docIqError.for === practiceId && docIqError.text && (
                <div className="save-status error">{docIqError.text}</div>
              )}
            </div>
          </div>
        )}
        {canManageMembers && activeTab === 'members' && (
          <div className="tab-content">
            <h2 className="h2" style={{ marginTop: 0, marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Mitglieder
              <InfoTooltip text="Verwalten Sie die Mitglieder dieser Praxis. Eigentümer können Mitglieder hinzufügen, entfernen und Rollen ändern. Für neu erstellte Konten wird über die Schaltfläche Passwort generieren ein einmaliges temporäres Passwort erzeugt und angezeigt; es muss bei der ersten Anmeldung geändert werden." />
            </h2>

            {membersError && (
              <div className="save-status error" style={{ marginBottom: 12 }}>{membersError}</div>
            )}
            {membersSuccess && (
              <div className="save-status success" style={{ marginBottom: 12 }}>{membersSuccess}</div>
            )}

            {/* Reveal-once temporary-password card. Shown right after a password is
                generated/reset; disappears on refresh or when leaving the tab. */}
            {revealedCredential && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 10,
                background: '#F0F5FF',
                border: '1px solid #cdddf7',
                borderLeft: '3px solid #2F6BD7',
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#1f3a5f' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2F6BD7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                    Temporäres Passwort für{' '}<span style={{ fontWeight: 700 }}>{revealedCredential.username}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setRevealedCredential(null)}
                    title="Ausblenden"
                    aria-label="Ausblenden"
                    style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: 2 }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <code style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '1.15rem', letterSpacing: '0.06em', fontWeight: 600, color: '#111827',
                    background: '#fff', border: '1px solid #cdddf7', borderRadius: 6,
                    padding: '8px 14px', userSelect: 'all',
                  }}>
                    {revealedCredential.password}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyCredential}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
                      background: credentialCopied ? '#e7f6ec' : '#fff',
                      border: `1px solid ${credentialCopied ? '#1e9e57' : '#2F6BD7'}`,
                      color: credentialCopied ? '#1e9e57' : '#2F6BD7',
                      fontWeight: 600, transition: 'all 0.15s ease',
                    }}
                  >
                    {credentialCopied ? (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                        Kopiert
                      </>
                    ) : (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        Kopieren
                      </>
                    )}
                  </button>
                </div>
                <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>
                  Wird nur einmal angezeigt. Kopieren und dem Mitglied mitteilen, es muss bei der ersten Anmeldung geändert werden.
                </span>
              </div>
            )}

            {membersLoading ? (
              <div>Mitglieder werden geladen…</div>
            ) : (
              <div style={{ background: 'var(--surface-card, #fff)', border: '1px solid var(--surface-border, #e0e0e0)', borderRadius: 8, marginBottom: 24, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                      <th style={{ padding: '10px 12px' }}>Benutzername</th>
                      <th style={{ padding: '10px 12px' }}>Name</th>
                      <th style={{ padding: '10px 12px' }}>E-Mail</th>
                      <th style={{ padding: '10px 12px' }}>Rolle</th>
                      <th style={{ padding: '10px 12px' }} title="Kann in der Aufgabenliste als zuständig ausgewählt werden">Zuweisbar</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: '12px', color: '#666' }}>Keine Mitglieder gefunden.</td></tr>
                    ) : members.map((m) => (
                      <tr key={m.user_id} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ padding: '10px 12px' }}>
                          {editingUsername === m.user_id ? (
                            <span style={{ display: 'flex', gap: 6 }}>
                              <input
                                type="text"
                                value={editUsernameValue}
                                onChange={(e) => setEditUsernameValue(e.target.value)}
                                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                                autoFocus
                              />
                              <button
                                type="button"
                                className="save-btn"
                                style={{ padding: '2px 10px' }}
                                onClick={() => handleSaveUsername(m.user_id)}
                              >
                                Speichern
                              </button>
                              <button
                                type="button"
                                style={{ padding: '2px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
                                onClick={() => { setEditingUsername(null); setEditUsernameValue(''); }}
                              >
                                Abbrechen
                              </button>
                            </span>
                          ) : (
                            <span
                              style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
                              title="Klicken zum Bearbeiten"
                              onClick={() => { setEditingUsername(m.user_id); setEditUsernameValue(m.username || ''); }}
                            >
                              {m.username || '—'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {editingName === m.user_id ? (
                            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <input
                                type="text"
                                placeholder="Vorname"
                                value={editFirstNameValue}
                                onChange={(e) => setEditFirstNameValue(e.target.value)}
                                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, width: 110 }}
                                autoFocus
                              />
                              <input
                                type="text"
                                placeholder="Nachname"
                                value={editLastNameValue}
                                onChange={(e) => setEditLastNameValue(e.target.value)}
                                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, width: 110 }}
                              />
                              <button
                                type="button"
                                className="save-btn"
                                style={{ padding: '2px 10px' }}
                                onClick={() => handleSaveName(m.user_id)}
                              >
                                Speichern
                              </button>
                              <button
                                type="button"
                                style={{ padding: '2px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
                                onClick={() => { setEditingName(null); setEditFirstNameValue(''); setEditLastNameValue(''); }}
                              >
                                Abbrechen
                              </button>
                            </span>
                          ) : (
                            <span
                              style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
                              title="Klicken zum Bearbeiten"
                              onClick={() => { setEditingName(m.user_id); setEditFirstNameValue(m.first_name || ''); setEditLastNameValue(m.last_name || ''); }}
                            >
                              {`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {editingEmail === m.user_id ? (
                            <span style={{ display: 'flex', gap: 6 }}>
                              <input
                                type="text"
                                value={editEmailValue}
                                onChange={(e) => setEditEmailValue(e.target.value)}
                                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                                autoFocus
                              />
                              <button
                                type="button"
                                className="save-btn"
                                style={{ padding: '2px 10px' }}
                                onClick={() => handleSaveEmail(m.user_id)}
                              >
                                Speichern
                              </button>
                              <button
                                type="button"
                                style={{ padding: '2px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
                                onClick={() => { setEditingEmail(null); setEditEmailValue(''); }}
                              >
                                Abbrechen
                              </button>
                            </span>
                          ) : (
                            <span
                              style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
                              title="Klicken zum Bearbeiten"
                              onClick={() => { setEditingEmail(m.user_id); setEditEmailValue(m.email || ''); }}
                            >
                              {m.email || '—'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <select
                            value={m.role}
                            onChange={(e) => handleChangeRole(m.user_id, e.target.value)}
                            disabled={m.user_id === userId && m.role === 'owner'}
                            title={m.user_id === userId && m.role === 'owner'
                              ? 'Sie können sich nicht selbst herabstufen. Bitten Sie einen anderen Eigentümer.'
                              : undefined}
                            style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                          >
                            <option value="owner">owner</option>
                            <option value="member">member</option>
                            <option value="task_manager">task manager</option>
                          </select>
                        </td>
                        {/* Zuweisbar: purely a Tasks-page convenience. Unticking
                            hides the person from the assignee dropdown and the
                            "Zuständig" filter; it changes NO permission and leaves
                            tasks already assigned to them untouched. */}
                        <td style={{ padding: '10px 12px' }}>
                          <input
                            type="checkbox"
                            checked={m.assignable !== false}
                            onChange={(e) => handleToggleAssignable(m.user_id, e.target.checked)}
                            title="Kann in der Aufgabenliste als zuständig ausgewählt werden"
                            style={{ width: 16, height: 16, accentColor: '#2F6BD7', cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          {/* Owners & super_admins: mint a one-time temp password.
                              A freshly created account has none (has_password===false)
                              → the button invites "Passwort generieren" (brand-blue
                              call to action); once one exists it becomes the neutral
                              "Passwort zurücksetzen". Both hit the same endpoint. The
                              backend blocks an owner from resetting the platform
                              admin's password (the admin is L2-hidden from this list
                              anyway). */}
                          {canManageMembers && (() => {
                            const needsGenerate = m.has_password === false;
                            return (
                              <button
                                type="button"
                                onClick={() => handleResetPassword(m.user_id, m.username, !needsGenerate, m.shared)}
                                title={needsGenerate
                                  ? 'Einmaliges temporäres Passwort erzeugen'
                                  : 'Neues temporäres Passwort erzeugen (bisheriges wird ungültig)'}
                                style={{
                                  padding: '4px 10px',
                                  marginRight: 8,
                                  background: '#fff',
                                  border: `1px solid ${needsGenerate ? '#2F6BD7' : '#888'}`,
                                  color: needsGenerate ? '#2F6BD7' : '#333',
                                  fontWeight: needsGenerate ? 600 : 400,
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                }}
                              >
                                {needsGenerate ? 'Passwort generieren' : 'Passwort zurücksetzen'}
                              </button>
                            );
                          })()}
                          {/* Owners (and super_admins) cannot remove themselves —
                              the backend rejects it with 422, hiding the button here
                              avoids the dead-click. Members can still self-leave. */}
                          {m.user_id === userId && m.role === 'owner' ? (
                            <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.875rem' }}>
                              (Sie)
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(m.user_id)}
                              style={{
                                padding: '4px 10px',
                                background: '#fff',
                                border: '1px solid #d33',
                                color: '#d33',
                                borderRadius: 4,
                                cursor: 'pointer',
                              }}
                            >
                              Entfernen
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3 className="h3" style={{ marginTop: 24, marginBottom: 8 }}>Mitglied hinzufügen</h3>
            <form
              onSubmit={handleInviteMember}
              style={{
                background: 'var(--surface-card, #fff)',
                border: '1px solid var(--surface-border, #e0e0e0)',
                borderRadius: 8,
                padding: 16,
                display: 'grid',
                // Responsive: fields flow into as many columns as fit (min 180px
                // each) and wrap to new rows at narrow widths instead of crushing
                // and pushing the submit button outside the card. min-width:0 on
                // the children lets them shrink below their intrinsic size.
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span>Benutzername *</span>
                <input
                  type="text"
                  value={inviteUsername}
                  onChange={(e) => setInviteUsername(e.target.value)}
                  required
                  onInvalid={(e) => {
                    if (e.target.validity.valueMissing) {
                      e.target.setCustomValidity('Dieses Feld ist erforderlich.');
                    } else {
                      e.target.setCustomValidity('Ungültige Eingabe.');
                    }
                  }}
                  onInput={(e) => e.target.setCustomValidity('')}
                  style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span>E-Mail (optional)</span>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  // Not required (email is optional). type="email" still validates the
                  // shape IF something is entered. Override the browser-native English
                  // tooltip with German; cleared on each input so it re-validates.
                  onInvalid={(e) => {
                    if (e.target.validity.typeMismatch) {
                      e.target.setCustomValidity('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
                    } else {
                      e.target.setCustomValidity('Ungültige Eingabe.');
                    }
                  }}
                  onInput={(e) => e.target.setCustomValidity('')}
                  style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span>Vorname (optional)</span>
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span>Nachname (optional)</span>
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span>Rolle</span>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box' }}
                >
                  <option value="member">member</option>
                  <option value="owner">owner</option>
                  <option value="task_manager">task manager</option>
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, height: 38 }}>
                <input
                  type="checkbox"
                  checked={inviteAssignable}
                  onChange={(e) => setInviteAssignable(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#2F6BD7', cursor: 'pointer' }}
                />
                <span title="Kann in der Aufgabenliste als zuständig ausgewählt werden">Zuweisbar</span>
              </label>
              <button type="submit" className="save-btn" style={{ height: 38, justifySelf: 'start' }}>Hinzufügen</button>
            </form>

            {/* Session 10g: import panel — super_admin OR the practice owner may
                pull an existing user/owner from another practice without
                re-typing the email. Mirrors the tab's own gate above (so it is
                redundant, not a second boundary). The real scoping is
                server-side: /users/me/practices returns the caller's own
                memberships (all practices for a super_admin) and GET .../members
                requires membership of the source, so a practice the caller isn't
                assigned to can never be listed. */}
            {canManageMembers && (
              <div style={{ marginTop: 16 }}>
                {!importMemberOpen ? (
                  <button
                    type="button"
                    onClick={() => { setImportMemberOpen(true); setImportMemberSourceId(''); setImportMemberSourceList([]); }}
                    style={{
                      padding: '6px 12px',
                      background: '#fff',
                      border: '1px solid #2F6BD7',
                      color: '#2F6BD7',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    Aus anderer Praxis importieren
                  </button>
                ) : (
                  <div style={{
                    background: 'var(--surface-card, #fff)',
                    border: '1px solid var(--surface-border, #e0e0e0)',
                    borderRadius: 8,
                    padding: 16,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <h3 className="h3" style={{ margin: 0 }}>Aus anderer Praxis importieren</h3>
                      <button
                        type="button"
                        onClick={() => { setImportMemberOpen(false); setImportMemberSourceId(''); setImportMemberSourceList([]); }}
                        style={{ padding: '4px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                      >
                        Abbrechen
                      </button>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                      <span>Quellpraxis</span>
                      <select
                        value={importMemberSourceId}
                        onChange={(e) => {
                          const next = e.target.value;
                          setImportMemberSourceId(next);
                          loadImportMemberSource(next);
                        }}
                        style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                      >
                        <option value="">– bitte auswählen –</option>
                        {(availablePractices || [])
                          .filter((p) => p.practice_id !== practiceId)
                          .map((p) => (
                            <option key={p.practice_id} value={p.practice_id}>{p.practice_name}</option>
                          ))}
                      </select>
                    </label>

                    {importMemberSourceId && (
                      importMemberLoading ? (
                        <div style={{ color: '#666' }}>Mitglieder werden geladen…</div>
                      ) : importMemberSourceList.length === 0 ? (
                        <div style={{ color: '#666' }}>Keine Mitglieder in dieser Praxis.</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                              <th style={{ padding: '8px 10px' }}>Benutzername</th>
                              <th style={{ padding: '8px 10px' }}>E-Mail</th>
                              <th style={{ padding: '8px 10px' }}>Rolle</th>
                              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Aktion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importMemberSourceList.map((m) => {
                              const alreadyHere = members.some((existing) => existing.user_id === m.user_id);
                              return (
                                <tr key={m.user_id} style={{ borderTop: '1px solid #eee' }}>
                                  <td style={{ padding: '8px 10px' }}>{m.username || '—'}</td>
                                  <td style={{ padding: '8px 10px', color: '#666' }}>{m.email || '—'}</td>
                                  <td style={{ padding: '8px 10px' }}>{m.role}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                    {alreadyHere ? (
                                      <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.875rem' }}>
                                        bereits Mitglied
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        disabled={importMemberInFlight === m.user_id}
                                        onClick={() => handleImportMember(m)}
                                        className="save-btn"
                                        style={{ padding: '4px 10px', fontSize: '0.875rem' }}
                                      >
                                        {importMemberInFlight === m.user_id ? 'Importiere…' : 'Importieren'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Modal for Test Phone Call (Outbound/Inbound) */}
      <Modal
        isOpen={showOutboundModal}
        onRequestClose={closeOutboundModal}
        style={customModalStyles}
        contentLabel="Testanruf"
        ariaHideApp={false}
      >
        <h2 className="text-xl font-semibold mb-4">Testanruf</h2>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <button
            className={`button ${testCallTab === 'outbound' ? 'active' : ''}`}
            style={{ flex: 1, background: testCallTab === 'outbound' ? '#2563eb' : '#e5e7eb', color: testCallTab === 'outbound' ? '#fff' : '#222' }}
            onClick={() => setTestCallTab('outbound')}
          >
            Ausgehender Test
          </button>
          <button
            className={`button ${testCallTab === 'inbound' ? 'active' : ''}`}
            style={{ flex: 1, background: testCallTab === 'inbound' ? '#2563eb' : '#e5e7eb', color: testCallTab === 'inbound' ? '#fff' : '#222' }}
            onClick={() => setTestCallTab('inbound')}
          >
            Eingehender Test
          </button>
          <button
            className={`button ${testCallTab === 'forwarding' ? 'active' : ''}`}
            style={{ flex: 1, background: testCallTab === 'forwarding' ? '#2563eb' : '#e5e7eb', color: testCallTab === 'forwarding' ? '#fff' : '#222' }}
            onClick={() => setTestCallTab('forwarding')}
          >
            Weiterleitungstest
          </button>
        </div>
        {testCallTab === 'outbound' && (
          <div>
            <p className="mb-2 text-sm text-gray-600">Geben Sie eine Telefonnummer ein, die angerufen werden soll. Dies verwendet Ihre gespeicherte Fonial SIP-Konfiguration.</p>
            <input 
              type="text"
              value={outboundPhoneNumber}
              onChange={(e) => setOutboundPhoneNumber(e.target.value)}
              placeholder="z.B. +1234567890"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-4"
            />
            <div className="flex items-center justify-between">
              <button
                onClick={handleInitiateOutboundCall}
                disabled={outboundCallStatus.testing}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                {outboundCallStatus.testing ? 'Rufe an...' : 'Testanruf tätigen'}
              </button>
              <button 
                onClick={closeOutboundModal}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Abbrechen
              </button>
            </div>
            {outboundCallStatus.message && (
              <p className={`mt-4 text-sm ${outboundCallStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                {outboundCallStatus.message}
              </p>
            )}
          </div>
        )}
        {testCallTab === 'inbound' && (
          <div>
            <p>Um eingehende Anrufe zu testen, rufen Sie Ihre SIP-Telefonnummer von einem beliebigen Telefon an:</p>
            <div style={{ fontWeight: 'bold', fontSize: 18, margin: '8px 0' }}>{sipConfig.sip_phone_number || <span style={{ color: 'red' }}>[Nicht gesetzt]</span>}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button 
                className="button" 
                onClick={handleStartInboundTest} 
                disabled={inboundTestPolling || !sipConfig.sip_phone_number} 
                style={{ flex: 1 }}
              >
                {inboundTestPolling ? 'Warte auf eingehenden Anruf...' : 'Eingehenden Test starten'}
              </button>
              {inboundTestPolling && (
                <button 
                  className="button" 
                  onClick={handleStopInboundTest} 
                  style={{ flex: 1, background: '#dc2626' }}
                >
                  Test abbrechen
                </button>
              )}
            </div>
            {inboundTestStatus.status === 'waiting' && <div className="test-status">{inboundTestStatus.message}</div>}
            {inboundTestStatus.status === 'success' && <div className="test-status success">{inboundTestStatus.message}</div>}
            {inboundTestStatus.status === 'error' && <div className="test-status error">{inboundTestStatus.message}</div>}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={closeOutboundModal}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Schließen
              </button>
            </div>
          </div>
        )}
        {testCallTab === 'forwarding' && (
          <div>
            <p className="mb-2 text-sm text-gray-600">
              Geben Sie eine Zieltelefonnummer ein. Wenn Sie dann Ihre SIP-Nummer anrufen,
              wird der Anruf automatisch an die Zielnummer weitergeleitet (2 Sekunden Limit).
            </p>
            <div style={{ fontWeight: 'bold', fontSize: 14, margin: '8px 0' }}>
              Ihre SIP-Nummer: {sipConfig.sip_phone_number || <span style={{ color: 'red' }}>[Nicht gesetzt]</span>}
            </div>
            <input
              type="text"
              value={forwardingPhoneNumber}
              onChange={(e) => setForwardingPhoneNumber(e.target.value)}
              placeholder="z.B. +4915012345678"
              disabled={forwardingTestPolling}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-4"
              style={{ marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                className="button"
                onClick={handleStartForwardingTest}
                disabled={forwardingTestPolling || !sipConfig.sip_phone_number || !forwardingPhoneNumber}
                style={{ flex: 1, background: forwardingTestPolling ? '#9ca3af' : '#2563eb', color: '#fff' }}
              >
                {forwardingTestPolling ? 'Warte auf Anruf...' : 'Weiterleitungstest starten'}
              </button>
              {forwardingTestPolling && (
                <button
                  className="button"
                  onClick={handleStopForwardingTest}
                  style={{ flex: 1, background: '#dc2626', color: '#fff' }}
                >
                  Test abbrechen
                </button>
              )}
            </div>
            {forwardingTestStatus.status === 'waiting' && <div className="test-status" style={{ marginTop: 12 }}>{forwardingTestStatus.message}</div>}
            {forwardingTestStatus.status === 'success' && <div className="test-status success" style={{ marginTop: 12 }}>{forwardingTestStatus.message}</div>}
            {forwardingTestStatus.status === 'error' && <div className="test-status error" style={{ marginTop: 12 }}>{forwardingTestStatus.message}</div>}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={closeOutboundModal}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Schließen
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default ConfigPage; 